import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { usePermissions } from '../AuthContext';
import { getApp } from 'firebase/app';
import {
  Typography,
  Box,
  Paper,
  TextField,
  Button,
  Alert,
  FormControl,
  InputLabel,
  Select,
  Menu,
  MenuItem,
  ListSubheader,
  Checkbox,
  ListItemText,
  Tabs,
  Tab,
  Drawer,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Stack,
  IconButton,
  List,
  ListItemButton,
  Table,
  TableHead,
  TableBody,
  TableCell,
  TableRow,
  TableContainer,
  TableSortLabel,
  Chip,
  CircularProgress,
  Tooltip,
  Divider,
  Switch,
  FormControlLabel,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Autocomplete,
  Slider,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import ArrowUpwardOutlinedIcon from '@mui/icons-material/ArrowUpwardOutlined';
import ArrowDownwardOutlinedIcon from '@mui/icons-material/ArrowDownwardOutlined';
import ContentCopyOutlinedIcon from '@mui/icons-material/ContentCopyOutlined';
import ListAltOutlinedIcon from '@mui/icons-material/ListAltOutlined';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { getFunctions, httpsCallable } from 'firebase/functions';
import {
  addDoc,
  arrayRemove,
  arrayUnion,
  collection,
  getDoc,
  getDocsFromServer,
  getFirestore,
  onSnapshot,
  orderBy,
  query,
  serverTimestamp,
  updateDoc,
  doc,
  getDocs,
  where,
  deleteDoc,
  setDoc,
  writeBatch,
  limit,
  startAfter,
  documentId,
} from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL, deleteObject } from 'firebase/storage';
import { storage } from '../firebase';
import UserList from '../components/UserList';
import { useAuth } from '../AuthContext';
import ResizableRightDrawer from '../components/ResizableRightDrawer';
import WorkflowStudio from '../components/workflow/WorkflowStudio';
import {
  DEFAULT_OFFICE_LOAN_TEMPLATE_ID,
  DEFAULT_OFFICE_MAIN_TEMPLATE_ID,
  defaultOfficeLoanTemplate,
  defaultOfficeMainTemplate,
} from '../office/workflows';
import { buildPdfPayloadFromTemplate } from '../office/pdfPayloadBuilder';
import { applyTemplateTokens, resolveTemplateCalcs } from '../office/templateTokens';
import { buildPaymentTemplateTokens } from '../office/paymentPlaceholders';
import { paymentConditionHtml, validatePaymentCondition, type PaymentCondition } from '../office/paymentCondition';
import { computeGstAmountForUnit } from '../office/gst';
import {
  buildBookingShortRefNo,
  buildSequenceRefNo,
  normalizeOfficeRefPrefix,
  type OfficeDocRefMode,
} from '../office/documentRefs';
import { buildUniversalOfficeTemplateTokens } from '../office/universalTemplateTokens';
import { optimizePdfToMaxBytes } from '../office/pdfReducer';
import { renderWhatsAppFormattedText } from '../utils/messaging';
import OfficeUserManagementPage from './OfficeUserManagementPage';

const WHATSAPP_HEADER_PROJECT_LOGO_TOKEN = '{{project_logo_url}}';
const WHATSAPP_HEADER_VOUCHER_OR_PROJECT_LOGO_TOKEN = '{{voucher_or_project_logo_url}}';
const WHATSAPP_HEADER_PROJECT_BROCHURE_TOKEN = '{{project_brochure_url}}';
const WHATSAPP_HEADER_LEAD_REPORT_TOKEN = '{{lead_report_pdf_url}}';
const WHATSAPP_COEX_APP_ID_STORAGE_KEY = 'whatsapp_coex_app_id';
const WHATSAPP_COEX_CONFIG_ID_STORAGE_KEY = 'whatsapp_coex_config_id';

type WhatsappTemplateHeaderImageSource =
  | 'project_logo'
  | 'voucher_or_project_logo'
  | 'upload'
  | 'custom';

type WhatsappConversationActionReplyConfig = {
  messageText: string;
  buttonActionKeys: string[];
  responseType?: string;
  urlButtonText?: string;
  urlSource?: string;
  customUrl?: string;
};

type WhatsappConversationActionReplies = Record<string, Record<string, WhatsappConversationActionReplyConfig>>;

type WhatsappConversationFlowRow = {
  id: string;
  name?: string | null;
  type?: string | null;
  active?: boolean | null;
  priority?: number | null;
  projectIds?: string[] | null;
  actionKeys?: string[] | null;
  actionReplies?: any;
  startKeywords?: string[] | null;
  projectPromptText?: string | null;
  actionPromptText?: string | null;
};

type WhatsappAutomationRuleTrigger =
  | 'lead-created'
  | 'status-changed'
  | 'lead-reopened'
  | 'appointment-scheduled'
  | 'visit-completed'
  | 'existing-lead-reenquiry'
  | 'unknown-whatsapp-message'
  | 'inbound-whatsapp-message'
  | 'customer-payment-receipt';

type WhatsappAutomationAdvancedCondition = {
  field: 'lead_created_at' | 'lead_match';
  operator: 'within_last' | 'older_than' | 'any' | 'known' | 'unknown';
  value: string;
  unit: 'days' | 'none';
};

type GoogleContactsSyncMode = 'dry-run' | 'sample' | 'full';

type GoogleContactsSyncConfig = {
  enabled?: boolean;
  sharedAccountEmail?: string | null;
  sampleSize?: number | null;
  updatedAt?: unknown;
  updatedByEmail?: string | null;
  connected?: boolean;
  connectedEmail?: string | null;
  connectedScopes?: string[] | null;
  lastAuthAt?: unknown;
  lastSyncAt?: unknown;
  authStatus?: string | null;
};

type GoogleContactsSyncPreviewRow = {
  phone10?: string;
  displayName?: string;
  sourceType?: 'office-customer' | 'crm-lead' | 'closed-lead' | string;
  organization?: string;
  note?: string;
  groupNames?: string[];
  projectNames?: string[];
  unitLabels?: string[];
  leadId?: string | null;
  bookingIds?: string[];
  statusLabel?: string;
  updatedAtIso?: string | null;
};

type GoogleContactsSyncRunSummary = {
  mode?: GoogleContactsSyncMode;
  contactsPlanned?: number;
  officeCustomers?: number;
  crmLeads?: number;
  closedLeadContacts?: number;
  previewCount?: number;
  processedRows?: number;
  pendingRows?: number;
  skippedRows?: number;
  syncedRows?: number;
  created?: number;
  updated?: number;
  errors?: number;
};

type GoogleContactsSyncRun = {
  id: string;
  mode?: GoogleContactsSyncMode;
  status?: string;
  notice?: string | null;
  summary?: GoogleContactsSyncRunSummary;
  previewRows?: GoogleContactsSyncPreviewRow[];
  errorRows?: Array<{ phone10?: string; message?: string }>;
  createdAt?: unknown;
  updatedAt?: unknown;
  createdByEmail?: string | null;
};

const WHATSAPP_CONVERSATION_ACTION_OPTIONS = [
  { key: 'brochure', label: 'Brochure' },
  { key: 'location', label: 'Location' },
  { key: 'website', label: 'Website' },
  { key: 'call', label: 'Call' },
];

const WHATSAPP_CONVERSATION_ACTION_LABELS = WHATSAPP_CONVERSATION_ACTION_OPTIONS.reduce<Record<string, string>>((acc, action) => {
  acc[action.key] = action.label;
  return acc;
}, {});

const WHATSAPP_CONVERSATION_RESPONSE_TYPES = [
  { key: 'default', label: 'Default asset / text' },
  { key: 'text', label: 'Text only' },
  { key: 'reply_buttons', label: 'Text + reply buttons' },
  { key: 'url_button', label: 'Text + URL button' },
  { key: 'url_button_and_replies', label: 'Text + URL button + reply buttons' },
  { key: 'contact_card', label: 'Text + contact card' },
  { key: 'document', label: 'Native document' },
  { key: 'location', label: 'Native location' },
];

const WHATSAPP_CONVERSATION_URL_SOURCES = [
  { key: 'auto', label: 'Auto from action' },
  { key: 'brochure', label: 'Project brochure URL' },
  { key: 'website', label: 'Project website URL' },
  { key: 'location', label: 'Google Maps URL' },
  { key: 'custom', label: 'Custom URL' },
];

type UpcomingBirthdayRow = {
  id: string;
  type: 'Customer' | 'Broker' | 'Staff' | 'Contact';
  name: string;
  phone: string;
  projectName?: string;
  agencyName?: string;
  contactType?: string;
  organization?: string;
  dobLabel: string;
  nextDateLabel: string;
  daysUntil: number;
  sortKey: number;
};

const CONTACTS_BOOK_TYPES = [
  'Owner',
  'Owner Family',
  'Staff',
  'Staff Family',
  'Vendor',
  'Banker',
  'Advocate',
  'Consultant',
  'Friend',
  'Other',
];

const GOOGLE_CONTACT_SYNC_DEFAULT_SAMPLE_SIZE = 25;

const toJsDate = (value: unknown): Date | null => {
  if (value && typeof (value as { toDate?: unknown }).toDate === 'function') {
    const next = (value as { toDate: () => Date }).toDate();
    return Number.isNaN(next.getTime()) ? null : next;
  }
  if (value instanceof Date) {
    return Number.isNaN(value.getTime()) ? null : value;
  }
  if (typeof value === 'string') {
    const next = new Date(value);
    return Number.isNaN(next.getTime()) ? null : next;
  }
  if (typeof value === 'number' && Number.isFinite(value)) {
    const next = new Date(value);
    return Number.isNaN(next.getTime()) ? null : next;
  }
  return null;
};

const UpcomingBirthdayTable = ({
  rows,
  emptyText,
  showType = false,
}: {
  rows: UpcomingBirthdayRow[];
  emptyText: string;
  showType?: boolean;
}) => (
  <TableContainer>
    <Table size="small">
      <TableHead>
        <TableRow>
          {showType ? <TableCell>Type</TableCell> : null}
          <TableCell>Name</TableCell>
          <TableCell>Phone</TableCell>
          <TableCell>Project / Agency</TableCell>
          <TableCell>Birthday</TableCell>
          <TableCell>Next</TableCell>
        </TableRow>
      </TableHead>
      <TableBody>
        {rows.length ? rows.map((row) => (
          <TableRow key={`${row.type}_${row.id}`} hover>
            {showType ? (
              <TableCell>
                <Chip size="small" label={row.type} variant="outlined" />
              </TableCell>
            ) : null}
            <TableCell>
              <Typography variant="body2" sx={{ fontWeight: 700 }}>
                {row.name}
              </Typography>
            </TableCell>
            <TableCell>{row.phone || '—'}</TableCell>
            <TableCell>{row.projectName || row.agencyName || row.organization || row.contactType || '—'}</TableCell>
            <TableCell>{row.dobLabel}</TableCell>
            <TableCell>
              <Typography variant="body2" sx={{ fontWeight: 700 }}>
                {row.daysUntil === 0 ? 'Today' : `${row.nextDateLabel} (${row.daysUntil} days)`}
              </Typography>
            </TableCell>
          </TableRow>
        )) : (
          <TableRow>
            <TableCell colSpan={showType ? 6 : 5} align="center">
              {emptyText}
            </TableCell>
          </TableRow>
        )}
      </TableBody>
    </Table>
  </TableContainer>
);

const formatDateTimeLabel = (value: unknown): string => {
  const next = toJsDate(value);
  if (!next) return '—';
  return next.toLocaleString('en-IN', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

const formatPhoneForPreview = (value: string): string => {
  const digits = String(value || '').replace(/\D/g, '');
  if (digits.length !== 10) return value || '—';
  return `${digits.slice(0, 5)} ${digits.slice(5)}`;
};

const formatWhatsappPersonName = (value: string): string =>
  String(value || '')
    .trim()
    .toLowerCase()
    .split(/\s+/)
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ');

const formatWhatsappVariableValue = (sourceKey: string, value: string): string => {
  const raw = String(value || '').trim();
  if (!raw) return '';
  if (!['customer_name', 'customer_first_name', 'staff_name', 'contact_name', 'broker_name', 'agency_name', 'lead_name', 'name'].includes(String(sourceKey || '').trim())) {
    return raw;
  }
  const formatted = formatWhatsappPersonName(raw);
  if (String(sourceKey || '').trim() === 'customer_first_name') {
    return formatted.split(/\s+/).filter(Boolean)[0] || '';
  }
  return formatted;
};

const errorMessage = (err: unknown, fallback: string): string => {
  if (err instanceof Error && err.message) return err.message;
  return fallback;
};

const resolveCompanyLogoForTemplate = (companyRow: any, prefersBw: boolean): string => {
  const bwLogo = String(companyRow?.logoBwUrl || '').trim();
  const colorLogo = String(companyRow?.logoUrl || '').trim();
  if (prefersBw && bwLogo) return bwLogo;
  return colorLogo;
};

const OFFICE_DOC_FONT_OPTIONS = [
  'Arial',
  'Calibri',
  'Times New Roman',
  'Georgia',
  'Verdana',
  'Noto Sans Gujarati',
  'Noto Serif Gujarati',
  'Nirmala UI',
  'Shruti',
] as const;
// agency_sop: letterhead for Agency SOP PDFs; put {{sop_content}} in the body where the SOP goes.
const DEFAULT_DOCUMENT_GROUP_KEYS = ['demand_letter', 'payment_receipt_voucher', 'joining_letter', 'agency_sop'] as const;

const JOINING_LETTER_TEMPLATE_PRESET_HTML = `
<div style="font-size:13px;line-height:1.65;color:#111;">
  <p>To,<br/><strong>{{employee_name}}</strong><br/>{{employee_address}}</p>

  <p>Dear <strong>{{employee_name}}</strong>,</p>

  <p>
    We are pleased to appoint you as <strong>{{employee_designation}}</strong> with
    <strong>{{employee_company_name}}</strong> at <strong>{{employee_work_location}}</strong>,
    effective from <strong>{{employee_joining_date}}</strong>.
  </p>

  <table style="width:100%;border-collapse:collapse;margin:12px 0 14px;">
    <tr>
      <td style="width:34%;border:1px solid #999;padding:7px;font-weight:700;background:#f6f3ed;">Employee Name</td>
      <td style="border:1px solid #999;padding:7px;">{{employee_name}}</td>
    </tr>
    <tr>
      <td style="border:1px solid #999;padding:7px;font-weight:700;background:#f6f3ed;">Designation</td>
      <td style="border:1px solid #999;padding:7px;">{{employee_designation}}</td>
    </tr>
    <tr>
      <td style="border:1px solid #999;padding:7px;font-weight:700;background:#f6f3ed;">Joining Date</td>
      <td style="border:1px solid #999;padding:7px;">{{employee_joining_date}}</td>
    </tr>
    <tr>
      <td style="border:1px solid #999;padding:7px;font-weight:700;background:#f6f3ed;">Duty Timing</td>
      <td style="border:1px solid #999;padding:7px;">{{employee_duty_timing}}</td>
    </tr>
    <tr>
      <td style="border:1px solid #999;padding:7px;font-weight:700;background:#f6f3ed;">Monthly Salary</td>
      <td style="border:1px solid #999;padding:7px;">₹{{employee_salary}} ({{employee_salary_words}} only)</td>
    </tr>
    <tr>
      <td style="border:1px solid #999;padding:7px;font-weight:700;background:#f6f3ed;">Salary Calculation</td>
      <td style="border:1px solid #999;padding:7px;">{{employee_salary_mode}}</td>
    </tr>
  </table>

  <p><strong>Terms and Conditions</strong></p>
  <ol style="padding-left:18px;margin-top:0;">
    <li>Salary shall be calculated as per the salary calculation method assigned to the employee and as per monthly payroll records maintained by the company.</li>
    <li>For attendance-based salary, payable salary shall be calculated on the basis of present days and half days recorded for the month. The salary base shall be monthly calendar days less allowed holidays.</li>
    <li>Monthly allowed holidays include weekly holidays and festival holidays, except Diwali holidays as per company policy.</li>
    <li>If an employee remains present on any festival holiday, that day shall be counted as a working/present day, and the employee's available monthly holiday balance shall be reduced accordingly.</li>
    <li>No separate festival holiday benefit shall be applicable, except Diwali holidays as declared by the company.</li>
    <li>If attendance exceeds the salary base days, the excess payable amount may be treated as held overtime/holiday amount and settled as per company policy.</li>
    <li>For fixed monthly salary, monthly salary shall be payable as a fixed amount, subject to applicable deductions and company policy.</li>
    <li>TDS or any other statutory/company deductions, if applicable, shall be deducted from the monthly payable salary.</li>
    <li>You must follow company discipline, duty timings, site/project instructions, and reporting requirements.</li>
    <li>You must maintain confidentiality of company, customer, project, pricing, vendor, and internal records.</li>
    <li>The company may verify identity/address/experience documents. Incorrect information may lead to termination.</li>
    <li>Either party may end employment by giving notice as per company policy or by salary adjustment in lieu of notice, subject to final management decision.</li>
  </ol>

  <p>
    Please sign below as acceptance of this appointment and the above terms and conditions.
  </p>

  <table style="width:100%;border-collapse:collapse;margin-top:26px;">
    <tr>
      <td style="width:50%;vertical-align:bottom;">
        <div style="height:46px;"></div>
        <div style="border-top:1px solid #111;width:72%;padding-top:6px;">Employee Signature</div>
        <div style="font-weight:700;margin-top:4px;">{{employee_name}}</div>
      </td>
      <td style="width:50%;text-align:right;vertical-align:bottom;">
        <div style="height:46px;"></div>
        <div style="border-top:1px solid #111;width:72%;margin-left:auto;padding-top:6px;">Authorised Signatory</div>
        <div style="font-weight:700;margin-top:4px;">For, {{company_name}}</div>
      </td>
    </tr>
  </table>
</div>
`;

const PAYMENT_DETAILS_TEMPLATE_PRESET_HTML = `
<div style="box-sizing:border-box;color:#111;background:#fff;overflow:hidden;">
  <table style="width:100%;border-collapse:collapse;border:1px solid #999;margin-bottom:7mm;">
    <tr>
      <th colspan="4" style="background:#e6e6e6;border:1px solid #999;padding:6px 8px;font-weight:800;">
        <div style="display:flex;justify-content:space-between;align-items:center;width:100%;">
          <div style="text-align:left;">પેમેન્ટની વિગત</div>
          <div style="text-align:right;white-space:nowrap;">અવેજ: ₹{{unit_amount}}</div>
        </div>
      </th>
    </tr>
    <tr>
      <th style="background:#f7f7f7;border:1px solid #bbb;padding:6px 8px;text-align:center;font-weight:800;">બેંકનું નામ</th>
      <th style="background:#f7f7f7;border:1px solid #bbb;padding:6px 8px;text-align:center;font-weight:800;">ચેક નં</th>
      <th style="background:#f7f7f7;border:1px solid #bbb;padding:6px 8px;text-align:center;font-weight:800;">તારીખ</th>
      <th style="background:#f7f7f7;border:1px solid #bbb;padding:6px 8px;text-align:center;font-weight:800;">રકમ ₹</th>
    </tr>
    {{payments_rows}}
    <tr>
      <td colspan="3" style="border:1px solid #bbb;padding:6px 8px;text-align:right;font-weight:800;">ટોટલ પેમેન્ટ</td>
      <td style="border:1px solid #bbb;padding:6px 8px;text-align:right;font-weight:800;">₹{{pay_total}}</td>
    </tr>
    <tr>
      <td colspan="3" style="border:1px solid #bbb;padding:6px 8px;text-align:right;font-weight:800;">બાકી પેમેન્ટ</td>
      <td style="border:1px solid #bbb;padding:6px 8px;text-align:right;font-weight:800;">₹{{pay_balance}}</td>
    </tr>
  </table>

  <table style="width:100%;border-collapse:collapse;border:1px solid #999;margin-bottom:7mm;">
    <tr>
      <th colspan="4" style="background:#e6e6e6;border:1px solid #999;padding:6px 8px;font-weight:800;">
        <div style="display:flex;justify-content:space-between;align-items:center;width:100%;">
          <div style="text-align:left;">જી.એસ.ટી. ની વિગત</div>
          <div style="text-align:right;white-space:nowrap;">અવેજ: ₹{{gst}}</div>
        </div>
      </th>
    </tr>
    <tr>
      <th style="background:#f7f7f7;border:1px solid #bbb;padding:6px 8px;text-align:center;font-weight:800;">બેંકનું નામ</th>
      <th style="background:#f7f7f7;border:1px solid #bbb;padding:6px 8px;text-align:center;font-weight:800;">ચેક નં</th>
      <th style="background:#f7f7f7;border:1px solid #bbb;padding:6px 8px;text-align:center;font-weight:800;">તારીખ</th>
      <th style="background:#f7f7f7;border:1px solid #bbb;padding:6px 8px;text-align:center;font-weight:800;">રકમ ₹</th>
    </tr>
    {{gst_rows}}
    <tr>
      <td colspan="3" style="border:1px solid #bbb;padding:6px 8px;text-align:right;font-weight:800;">ટોટલ જી.એસ.ટી.</td>
      <td style="border:1px solid #bbb;padding:6px 8px;text-align:right;font-weight:800;">₹{{gst_total}}</td>
    </tr>
    <tr>
      <td colspan="3" style="border:1px solid #bbb;padding:6px 8px;text-align:right;font-weight:800;">બાકી જી.એસ.ટી.</td>
      <td style="border:1px solid #bbb;padding:6px 8px;text-align:right;font-weight:800;">₹{{gst_balance}}</td>
    </tr>
  </table>

  <table style="width:100%;border-collapse:collapse;border:1px solid #999;margin-bottom:7mm;">
    <tr>
      <th style="background:#e6e6e6;border:1px solid #999;padding:6px 8px;text-align:center;font-weight:800;">વિગત</th>
      <th style="background:#e6e6e6;border:1px solid #999;padding:6px 8px;text-align:center;font-weight:800;">રકમ</th>
      <th style="background:#e6e6e6;border:1px solid #999;padding:6px 8px;text-align:center;font-weight:800;">ચેક નંબર</th>
      <th style="background:#e6e6e6;border:1px solid #999;padding:6px 8px;text-align:center;font-weight:800;">તારીખ</th>
    </tr>
    <tr>
      <td style="border:1px solid #bbb;padding:6px 8px;">સ્ટેમ્પ ડ્યુટી</td>
      <td style="border:1px solid #bbb;padding:6px 8px;text-align:right;">₹{{stamp_duty}}</td>
      <td style="border:1px solid #bbb;padding:6px 8px;">{{stamp_cheque_no}}</td>
      <td style="border:1px solid #bbb;padding:6px 8px;text-align:center;">{{stamp_date}}</td>
    </tr>
    <tr>
      <td style="border:1px solid #bbb;padding:6px 8px;">રજીસ્ટ્રેશન</td>
      <td style="border:1px solid #bbb;padding:6px 8px;text-align:right;">₹{{registration_charges}}</td>
      <td style="border:1px solid #bbb;padding:6px 8px;">{{registration_cheque_no}}</td>
      <td style="border:1px solid #bbb;padding:6px 8px;text-align:center;">{{registration_date}}</td>
    </tr>
    <tr>
      <td style="border:1px solid #bbb;padding:6px 8px;">વકીલ ફી</td>
      <td style="border:1px solid #bbb;padding:6px 8px;text-align:right;">₹{{advocate_fee}}</td>
      <td style="border:1px solid #bbb;padding:6px 8px;">{{advocate_fee_cheque_no}}</td>
      <td style="border:1px solid #bbb;padding:6px 8px;text-align:center;">{{advocate_fee_date}}</td>
    </tr>
    <tr>
      <td style="border:1px solid #bbb;padding:6px 8px;">અન્ય</td>
      <td style="border:1px solid #bbb;padding:6px 8px;text-align:right;">₹{{other_amount}}</td>
      <td style="border:1px solid #bbb;padding:6px 8px;">{{other_cheque_no}}</td>
      <td style="border:1px solid #bbb;padding:6px 8px;text-align:center;">{{other_date}}</td>
    </tr>
  </table>

  <table style="width:100%;border-collapse:collapse;border:1px solid #999;">
    <tr>
      <th colspan="2" style="background:#e6e6e6;border:1px solid #999;padding:6px 8px;text-align:center;font-weight:800;">સામાન્ય વિગત</th>
      <th colspan="2" style="background:#e6e6e6;border:1px solid #999;padding:6px 8px;text-align:center;font-weight:800;">દરખાસ્તકર્તા વિગત</th>
    </tr>
    <tr>
      <td style="width:25%;border:1px solid #bbb;padding:6px 8px;font-weight:700;">સામાન્ય નંબર</td>
      <td style="width:25%;border:1px solid #bbb;padding:6px 8px;">{{common_number}}</td>
      <td style="width:25%;border:1px solid #bbb;padding:6px 8px;font-weight:700;">દરખાસ્તકર્તા નંબર</td>
      <td style="width:25%;border:1px solid #bbb;padding:6px 8px;">{{applicant_number}}</td>
    </tr>
    <tr>
      <td style="border:1px solid #bbb;padding:6px 8px;font-weight:700;">સામાન્ય તારીખ</td>
      <td style="border:1px solid #bbb;padding:6px 8px;">{{common_date}}</td>
      <td style="border:1px solid #bbb;padding:6px 8px;font-weight:700;">દરખાસ્તકર્તા તારીખ</td>
      <td style="border:1px solid #bbb;padding:6px 8px;">{{applicant_date}}</td>
    </tr>
    <tr>
      <td style="border:1px solid #bbb;padding:6px 8px;font-weight:700;">સામાન્ય રકમ</td>
      <td style="border:1px solid #bbb;padding:6px 8px;">{{common_amount}}</td>
      <td style="border:1px solid #bbb;padding:6px 8px;font-weight:700;">દરખાસ્તકર્તા રકમ</td>
      <td style="border:1px solid #bbb;padding:6px 8px;">{{applicant_amount}}</td>
    </tr>
  </table>

  <div style="margin-top:4mm;font-size:9pt;color:#333;">{{doc_serial_no}}</div>
</div>
`;

const PAYMENT_RECEIPT_VOUCHER_A5_TEMPLATE_PRESET_HTML = `
<div style="box-sizing:border-box;width:100%;min-height:210mm;padding:6mm;font-size:11px;line-height:1.45;color:#111;font-family:Arial,Helvetica,sans-serif;">
  <table style="width:100%;border-collapse:collapse;margin-bottom:4mm;">
    <tr>
      <td style="width:32%;vertical-align:top;">
        <img src="{{company_logo_url}}" alt="Company Logo" style="max-height:12mm;max-width:100%;object-fit:contain;">
      </td>
      <td style="width:36%;text-align:center;vertical-align:middle;">
        <div style="font-size:15px;font-weight:700;">PAYMENT RECEIPT</div>
        <div style="font-size:10px;">{{project_name}}</div>
      </td>
      <td style="width:32%;text-align:right;vertical-align:top;">
        <img src="{{project_logo_url}}" alt="Project Logo" style="max-height:12mm;max-width:100%;object-fit:contain;">
      </td>
    </tr>
  </table>

  <table style="width:100%;border-collapse:collapse;border:1px solid #999;margin-bottom:4mm;">
    <tr>
      <td style="border:1px solid #999;padding:5px 7px;width:50%;"><strong>Date:</strong> {{chq_date}}</td>
      <td style="border:1px solid #999;padding:5px 7px;width:50%;"><strong>Ref No:</strong> {{ref_no}}</td>
    </tr>
    <tr>
      <td style="border:1px solid #999;padding:5px 7px;"><strong>Unit No:</strong> {{unit_no}}</td>
      <td style="border:1px solid #999;padding:5px 7px;"><strong>Project:</strong> {{project_name}}</td>
    </tr>
    <tr>
      <td colspan="2" style="border:1px solid #999;padding:5px 7px;"><strong>Received From:</strong> {{allottee_names_comma_and}}</td>
    </tr>
    <tr>
      <td style="border:1px solid #999;padding:5px 7px;"><strong>Bank:</strong> {{bank}}</td>
      <td style="border:1px solid #999;padding:5px 7px;"><strong>Cheque / UTR:</strong> {{ref_chq_no}}</td>
    </tr>
    <tr>
      <td style="border:1px solid #999;padding:5px 7px;"><strong>Received Amount:</strong></td>
      <td style="border:1px solid #999;padding:5px 7px;text-align:right;"><strong>₹ {{deposit}}</strong></td>
    </tr>
  </table>

  <p style="margin:0 0 8mm 0;text-align:justify;">
    Received with thanks from <strong>{{allottee_names_comma_and}}</strong> towards Unit No <strong>{{unit_no}}</strong>.
  </p>

  <table style="width:100%;border-collapse:collapse;">
    <tr>
      <td style="width:50%;vertical-align:bottom;">
        <div><strong>For, {{company_name}}</strong></div>
        <div style="margin-top:12mm;">Authorised Signatory</div>
      </td>
      <td style="width:50%;text-align:right;vertical-align:bottom;">
        <div>Receiver Signature</div>
        <div style="margin-top:12mm;">{{allottee_primary_name}}</div>
      </td>
    </tr>
  </table>
</div>
`;

export default function SettingsPage({
  initialTab = 0,
  forceTab,
  hideTabs = false,
  hideTitle = false,
  plain = false,
  fixedProjectSettingsSection,
  hideProjectSettingsSections = false,
}: {
  initialTab?: number;
  forceTab?: number;
  hideTabs?: boolean;
  hideTitle?: boolean;
  /** Office: no outer card, so the page sits flat under the office top bar. */
  plain?: boolean;
  fixedProjectSettingsSection?: 'projects' | 'companyProfiles' | 'bankAccounts' | 'workflows' | 'documentTemplates' | 'documentConfig' | 'emailConfigs' | 'emailTemplates' | 'pdfTools' | 'whatsapp' | 'googleContacts' | 'contactsBook' | 'officeUsers';
  hideProjectSettingsSections?: boolean;
} = {}) {
  const { canPage, canAction } = usePermissions();
  const { currentUser, roleId, userRole } = useAuth();
  const location = useLocation();
  const theme = useTheme();
  const isProjectDialogMobile = useMediaQuery(theme.breakpoints.down('md'));
  const isTopAdmin = useMemo(() => {
    const role = String(roleId || userRole || '').trim().toLowerCase();
    return role === 'super-admin' || role === 'admin';
  }, [roleId, userRole]);

  // Tabs
  const [tab, setTab] = useState(() => initialTab);
  const [settingsMoreAnchor, setSettingsMoreAnchor] = useState<HTMLElement | null>(null);
  const [projectSettingsSection, setProjectSettingsSection] = useState<'projects' | 'companyProfiles' | 'bankAccounts' | 'workflows' | 'documentTemplates' | 'documentConfig' | 'emailConfigs' | 'emailTemplates' | 'pdfTools' | 'whatsapp' | 'googleContacts' | 'contactsBook' | 'officeUsers'>(() => fixedProjectSettingsSection || 'projects');
  const [metaSettingsSection, setMetaSettingsSection] = useState<'config' | 'pages' | 'mapping'>('config');
  const activeTab = typeof forceTab === 'number' ? forceTab : tab;
  useEffect(() => {
    if (typeof forceTab !== 'number') return;
    setTab(forceTab);
  }, [forceTab]);
  useEffect(() => {
    if (!fixedProjectSettingsSection) return;
    setProjectSettingsSection(fixedProjectSettingsSection);
  }, [fixedProjectSettingsSection]);
  useEffect(() => {
    const section = new URLSearchParams(location.search || '').get('section');
    if (fixedProjectSettingsSection) return;
    if (section !== 'contactsBook' && section !== 'officeUsers') return;
    if (typeof forceTab !== 'number') setTab(1);
    setProjectSettingsSection(section);
  }, [fixedProjectSettingsSection, forceTab, location.search]);

  // User Form State
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [password, setPassword] = useState('');
  const [role, setRole] = useState('sales-team');
  const [userDrawerOpen, setUserDrawerOpen] = useState(false);
  const [addUserTab, setAddUserTab] = useState<'profile' | 'access'>('profile');
  const [newUserReportsToId, setNewUserReportsToId] = useState('');
  const [newManagerDirectReportIds, setNewManagerDirectReportIds] = useState<string[]>([]);
  const [reportingUsers, setReportingUsers] = useState<{ id: string; name: string; email: string; role: string; roleId?: string; reportsToUserId?: string | null }[]>([]);
  const staticRoles = [
    { id: 'super-admin', label: 'Admin' },
    { id: 'sales-manager', label: 'Sales Manager' },
    { id: 'sales-team', label: 'Sales Team' },
    { id: 'trainee', label: 'Trainee' },
    { id: 'front-desk', label: 'Front Desk' },
  ];
  const [roles] = useState(staticRoles);
  const allPages = [
    { key: 'today', label: 'Today' },
    { key: 'frontDesk', label: 'Front Desk' },
    { key: 'dashboard', label: 'Dashboard' },
    { key: 'leads', label: 'Leads' },
    { key: 'followUps', label: 'Follow-ups' },
    { key: 'brokers', label: 'Brokers' },
    { key: 'jobApplications', label: 'Job Applications' },
    { key: 'messages', label: 'Messages' },
    { key: 'metaLeads', label: 'Meta Leads' },
    { key: 'reports', label: 'Reports' },
    { key: 'settings', label: 'Settings' },
  ];
  const pageActions: Record<string, { key: string; label: string }[]> = {
    today: [],
    frontDesk: [],
    dashboard: [],
    leads: [
      { key: 'addLead', label: 'Add Lead' },
      { key: 'addBroker', label: 'Add Broker' },
      { key: 'changeAssign', label: 'Assign Change' },
      { key: 'updateFollowUp', label: 'Update Follow-up' },
      { key: 'editLead', label: 'Edit Lead' },
    ],
    followUps: [
      { key: 'addLead', label: 'Add Lead' },
      { key: 'addBroker', label: 'Add Broker' },
      { key: 'changeAssign', label: 'Assign Change' },
      { key: 'updateFollowUp', label: 'Update Follow-up' },
    ],
    brokers: [
      { key: 'addBroker', label: 'Add Broker' },
      { key: 'editBroker', label: 'Edit Broker' },
      { key: 'deleteBroker', label: 'Delete Broker' },
    ],
    jobApplications: [],
    messages: [],
    reports: [],
    settings: [
      { key: 'addUser', label: 'Add User' },
      { key: 'addProject', label: 'Add Project' },
    ],
  };
  const [selectedPages, setSelectedPages] = useState<string[]>([]);
  const [selectedActions, setSelectedActions] = useState<string[]>([]);
  const [selectedProjectIds, setSelectedProjectIds] = useState<string[]>([]);
  const [userDateOfBirth, setUserDateOfBirth] = useState('');

  const toDomainAction = (pageKey: string, actionKey: string) => {
    const domain =
      pageKey === 'followUps' ? 'leads' :
      pageKey === 'leads' ? 'leads' :
      pageKey === 'brokers' ? 'brokers' :
      pageKey === 'settings' ? 'settings' :
      pageKey;
    return `${domain}.${actionKey}`;
  };

  // UI State
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [loading, setLoading] = useState(false);
  const [pdfReduceSourceFile, setPdfReduceSourceFile] = useState<File | null>(null);
  const [pdfReduceMaxMb, setPdfReduceMaxMb] = useState('1');
  const [pdfReduceBusy, setPdfReduceBusy] = useState(false);
  const [pdfReduceError, setPdfReduceError] = useState<string | null>(null);
  const [pdfReduceSuccess, setPdfReduceSuccess] = useState<string | null>(null);

  // Projects state
  const db = useMemo(() => getFirestore(), []);
  useEffect(() => {
    if (!userDrawerOpen) return;
    return onSnapshot(collection(db, 'users'), (snapshot) => {
      setReportingUsers(snapshot.docs.map((userDoc) => ({ id: userDoc.id, ...userDoc.data() })) as typeof reportingUsers);
    });
  }, [db, userDrawerOpen]);
  const [projects, setProjects] = useState<any[]>([]);
  const [projectsLoading, setProjectsLoading] = useState(true);
  const [projectsError, setProjectsError] = useState<string | null>(null);
  const [projectDrawerOpen, setProjectDrawerOpen] = useState(false);
  const [projectName, setProjectName] = useState('');
  const [projectLocation, setProjectLocation] = useState('');
  const [projectLatitude, setProjectLatitude] = useState('');
  const [projectLongitude, setProjectLongitude] = useState('');
  const [projectAddress, setProjectAddress] = useState('');
  const [projectWhatsappSubtitle, setProjectWhatsappSubtitle] = useState('');
  const [projectWebsiteSlug, setProjectWebsiteSlug] = useState('');
  const [projectBrochureUrl, setProjectBrochureUrl] = useState('');
  const [projectBackgroundImageUrl, setProjectBackgroundImageUrl] = useState('');
  const [projectDocumentImageUrl, setProjectDocumentImageUrl] = useState('');
  const [projectLogoUrl, setProjectLogoUrl] = useState('');
  const [projectLogoBwUrl, setProjectLogoBwUrl] = useState('');
  const [projectBackgroundImageFile, setProjectBackgroundImageFile] = useState<File | null>(null);
  const [projectDocumentImageFile, setProjectDocumentImageFile] = useState<File | null>(null);
  const [projectLogoBwFile, setProjectLogoBwFile] = useState<File | null>(null);
  const [projectAdvocateAgreementFee, setProjectAdvocateAgreementFee] = useState<string>('');
  const [projectAdvocateDeedFee, setProjectAdvocateDeedFee] = useState<string>('');
  const [projectBucReceivedAt, setProjectBucReceivedAt] = useState<string>('');
  const [projectBucBuildings, setProjectBucBuildings] = useState<string>('');
  const [projectSbAreaPercent, setProjectSbAreaPercent] = useState<string>('');
  const [unitTypes, setUnitTypes] = useState<string[]>([]);
  const [logoFile, setLogoFile] = useState<File | null>(null);
  const [projectSaving, setProjectSaving] = useState(false);
  const [editingProjectId, setEditingProjectId] = useState<string | null>(null);
	const [editingLogoPath, setEditingLogoPath] = useState<string | null>(null);
	const [editingBackgroundImagePath, setEditingBackgroundImagePath] = useState<string | null>(null);
  const [editingDocumentImagePath, setEditingDocumentImagePath] = useState<string | null>(null);
  const [editingLogoBwPath, setEditingLogoBwPath] = useState<string | null>(null);
  const [projectFormError, setProjectFormError] = useState<string | null>(null);

  const [groupAddress, setGroupAddress] = useState('');
  const [groupLatitude, setGroupLatitude] = useState('');
  const [groupLongitude, setGroupLongitude] = useState('');
  const [groupPhone, setGroupPhone] = useState('');
  const [groupEmail, setGroupEmail] = useState('');

  const [bookingDocsMigrationRunning, setBookingDocsMigrationRunning] = useState(false);
  const [bookingDocsMigrationResult, setBookingDocsMigrationResult] = useState<string | null>(null);
  const [projectUnitsCount, setProjectUnitsCount] = useState<number>(0);
  const [projectUnitsLastImportedAt, setProjectUnitsLastImportedAt] = useState<any | null>(null);
  // Project Units CSV import (replace-all)
  const [unitsCsvFile, setUnitsCsvFile] = useState<File | null>(null);
  const [unitsParsed, setUnitsParsed] = useState<any[]>([]);
  const [unitsParseError, setUnitsParseError] = useState<string | null>(null);
  const [unitsParseWarnings, setUnitsParseWarnings] = useState<string[]>([]);
  const [unitsExtraColumns, setUnitsExtraColumns] = useState<string[]>([]);
  const [unitsParsedHeaders, setUnitsParsedHeaders] = useState<string[]>([]);
  const [unitsIncludeExtraColumns, setUnitsIncludeExtraColumns] = useState<boolean>(true);
  const [unitsImporting, setUnitsImporting] = useState(false);
  const [unitsImportSuccess, setUnitsImportSuccess] = useState<string | null>(null);
  const [unitsDialogOpen, setUnitsDialogOpen] = useState(false);
  const [unitsProjectId, setUnitsProjectId] = useState<string | null>(null);
  const [unitsProjectName, setUnitsProjectName] = useState<string>('');
  const [unitsLoading, setUnitsLoading] = useState(false);
  const [unitsLoadError, setUnitsLoadError] = useState<string | null>(null);
  const [unitsRows, setUnitsRows] = useState<any[]>([]);
  const [unitsLastDoc, setUnitsLastDoc] = useState<any | null>(null);
  const [unitsHasMore, setUnitsHasMore] = useState(false);
  const [unitsSearch, setUnitsSearch] = useState('');
  const [projectGstDialogOpen, setProjectGstDialogOpen] = useState(false);
  const [projectGstProjectId, setProjectGstProjectId] = useState<string | null>(null);
  const [projectGstProjectName, setProjectGstProjectName] = useState<string>('');
  const [projectGstTypes, setProjectGstTypes] = useState<string[]>([]);
  const [projectGstValues, setProjectGstValues] = useState<Record<string, string>>({});
  const [projectDefaultGstPercentInput, setProjectDefaultGstPercentInput] = useState<string>('1');
  const [projectGstSaving, setProjectGstSaving] = useState(false);
  const [projectGstError, setProjectGstError] = useState<string | null>(null);
  const [projectSbTypes, setProjectSbTypes] = useState<string[]>([]);
  const [projectSbValues, setProjectSbValues] = useState<Record<string, string>>({});
  const [projectDefaultSbPercentInput, setProjectDefaultSbPercentInput] = useState<string>('0');
  const projectPercentTypes = useMemo(() => {
    const types = Array.from(
      new Set([...(projectGstTypes || []), ...(projectSbTypes || [])].map((v) => String(v || '').trim()).filter(Boolean))
    ).sort((a, b) => a.localeCompare(b, undefined, { numeric: true, sensitivity: 'base' }));
    return ['Default', ...types];
  }, [projectGstTypes, projectSbTypes]);
  const [officeWorkflowApplying, setOfficeWorkflowApplying] = useState(false);
  const [officeWorkflowApplyError, setOfficeWorkflowApplyError] = useState<string | null>(null);
  const [officeWorkflowApplySuccess, setOfficeWorkflowApplySuccess] = useState<string | null>(null);
  const [companyProfiles, setCompanyProfiles] = useState<any[]>([]);
  const [companyProfilesLoading, setCompanyProfilesLoading] = useState(false);
  const [companyProfilesError, setCompanyProfilesError] = useState<string | null>(null);
  const [companyProfileDrawerOpen, setCompanyProfileDrawerOpen] = useState(false);
  const [editingCompanyProfileId, setEditingCompanyProfileId] = useState<string | null>(null);
  const [companyProfileName, setCompanyProfileName] = useState('');
  const [companyProfileLogoUrl, setCompanyProfileLogoUrl] = useState('');
  const [companyProfileLogoPath, setCompanyProfileLogoPath] = useState('');
  const [companyProfileLogoBwUrl, setCompanyProfileLogoBwUrl] = useState('');
  const [companyProfileLogoBwPath, setCompanyProfileLogoBwPath] = useState('');
  const [companyProfileAddress, setCompanyProfileAddress] = useState('');
  const [companyProfileMobile, setCompanyProfileMobile] = useState('');
  const [companyProfileEmail, setCompanyProfileEmail] = useState('');
  const [companyProfileLogoFile, setCompanyProfileLogoFile] = useState<File | null>(null);
  const [companyProfileLogoBwFile, setCompanyProfileLogoBwFile] = useState<File | null>(null);
  // Group Profile (login page branding)
  const [groupLogoUrl, setGroupLogoUrl] = useState('');
  const [groupLogoPath, setGroupLogoPath] = useState('');
  const [groupLogoFile, setGroupLogoFile] = useState<File | null>(null);
  const [groupLoginBgUrl, setGroupLoginBgUrl] = useState('');
  const [groupLoginBgPath, setGroupLoginBgPath] = useState('');
  const [groupLoginBgFile, setGroupLoginBgFile] = useState<File | null>(null);
  const [groupProfileSaving, setGroupProfileSaving] = useState(false);
  const [groupProfileError, setGroupProfileError] = useState<string | null>(null);
  const [groupProfileSuccess, setGroupProfileSuccess] = useState(false);
  const [companyProfileFontFamily, setCompanyProfileFontFamily] = useState('');
  const [companyProfileFontUrl, setCompanyProfileFontUrl] = useState('');
  const [companyProfileFontPath, setCompanyProfileFontPath] = useState('');
  const [companyProfileFontFormat, setCompanyProfileFontFormat] = useState<'woff2' | 'woff' | 'ttf' | 'otf'>('woff2');
  const [companyProfileFontFile, setCompanyProfileFontFile] = useState<File | null>(null);
  const [companyProfileGujaratiFontFamily, setCompanyProfileGujaratiFontFamily] = useState('');
  const [companyProfileGujaratiFontUrl, setCompanyProfileGujaratiFontUrl] = useState('');
  const [companyProfileGujaratiFontPath, setCompanyProfileGujaratiFontPath] = useState('');
  const [companyProfileGujaratiFontFormat, setCompanyProfileGujaratiFontFormat] = useState<'woff2' | 'woff' | 'ttf' | 'otf'>('woff2');
  const [companyProfileGujaratiFontFile, setCompanyProfileGujaratiFontFile] = useState<File | null>(null);
  const [companyProfileSaving, setCompanyProfileSaving] = useState(false);
  const [companyProfileFormError, setCompanyProfileFormError] = useState<string | null>(null);
  const [projectCompanyProfileId, setProjectCompanyProfileId] = useState<string>('');

  // Project Banks (bank accounts can be shared across projects)
  const [bankAccountsAll, setBankAccountsAll] = useState<any[]>([]);
  const [bankAccountsAssigned, setBankAccountsAssigned] = useState<any[]>([]);
  const [bankAccountsLoading, setBankAccountsLoading] = useState(false);
  const [bankAccountsError, setBankAccountsError] = useState<string | null>(null);
  const [bankDrawerOpen, setBankDrawerOpen] = useState(false);
  const [editingBankId, setEditingBankId] = useState<string | null>(null);
  const [bankLabel, setBankLabel] = useState('');
  const [bankName, setBankName] = useState('');
  const [bankAccountLast4, setBankAccountLast4] = useState('');
  const [bankIfsc, setBankIfsc] = useState('');
  const [bankHolderName, setBankHolderName] = useState('');
  const [bankAccountRole, setBankAccountRole] = useState<'collection' | 'operating30' | 'rera70' | 'dummy_advocate' | 'other'>('other');
  const [bankAllowCustomerReceipts, setBankAllowCustomerReceipts] = useState(false);
  const [bankAllowGstReceipts, setBankAllowGstReceipts] = useState(true);
  const [bankAllowPayments, setBankAllowPayments] = useState(true);
  const [bankRestrictedOutflow, setBankRestrictedOutflow] = useState(false);
  const [bankProjectIds, setBankProjectIds] = useState<string[]>([]);
  const [bankSaving, setBankSaving] = useState(false);
  const [bankFormError, setBankFormError] = useState<string | null>(null);
  const [assignExistingBankId, setAssignExistingBankId] = useState<string>('');

  // Global Office Workflow Templates (copy into projects)
  const [officeWorkflows, setOfficeWorkflows] = useState<any[]>([]);
  const [officeWorkflowsLoading, setOfficeWorkflowsLoading] = useState(false);
  const [officeWorkflowsError, setOfficeWorkflowsError] = useState<string | null>(null);
  const [officeWorkflowDrawerOpen, setOfficeWorkflowDrawerOpen] = useState(false);
  const [editingOfficeWorkflowId, setEditingOfficeWorkflowId] = useState<string | null>(null);
  const [officeWorkflowName, setOfficeWorkflowName] = useState('');
  const [officeWorkflowType, setOfficeWorkflowType] = useState<'main' | 'loan'>('main');
  const [officeWorkflowVersion, setOfficeWorkflowVersion] = useState<number>(1);
  const [officeWorkflowSteps, setOfficeWorkflowSteps] = useState<any[]>([]);
  const [officeWorkflowProjectIds, setOfficeWorkflowProjectIds] = useState<string[]>([]);
  const [officeWorkflowSaving, setOfficeWorkflowSaving] = useState(false);
  const [officeWorkflowSaveError, setOfficeWorkflowSaveError] = useState<string | null>(null);
  const [officeWorkflowSaveSuccess, setOfficeWorkflowSaveSuccess] = useState<string | null>(null);
  const [officeWorkflowApplyingToProjects, setOfficeWorkflowApplyingToProjects] = useState(false);
  const [officeWorkflowApplyToProjectsError, setOfficeWorkflowApplyToProjectsError] = useState<string | null>(null);
  const [officeWorkflowApplyToProjectsSuccess, setOfficeWorkflowApplyToProjectsSuccess] = useState<string | null>(null);
  const [officeWorkflowSelectedStepIdx, setOfficeWorkflowSelectedStepIdx] = useState<number>(0);
  const [officeDocumentTemplates, setOfficeDocumentTemplates] = useState<any[]>([]);
  const [officeDocumentTemplatesLoading, setOfficeDocumentTemplatesLoading] = useState(false);
  const [officeDocumentTemplatesError, setOfficeDocumentTemplatesError] = useState<string | null>(null);
  const [officeEmailConfigs, setOfficeEmailConfigs] = useState<any[]>([]);
  const [officeEmailConfigsLoading, setOfficeEmailConfigsLoading] = useState(false);
  const [officeEmailConfigsError, setOfficeEmailConfigsError] = useState<string | null>(null);
  const [officeEmailTemplates, setOfficeEmailTemplates] = useState<any[]>([]);
  const [officeEmailTemplatesLoading, setOfficeEmailTemplatesLoading] = useState(false);
  const [officeEmailTemplatesError, setOfficeEmailTemplatesError] = useState<string | null>(null);
  const [officeEmailConfigDrawerOpen, setOfficeEmailConfigDrawerOpen] = useState(false);
  const [editingOfficeEmailConfigId, setEditingOfficeEmailConfigId] = useState<string | null>(null);
  const [officeEmailConfigName, setOfficeEmailConfigName] = useState('');
  const [officeEmailConfigProvider, setOfficeEmailConfigProvider] = useState<'gmail' | 'custom'>('gmail');
  const [officeEmailConfigFromName, setOfficeEmailConfigFromName] = useState('');
  const [officeEmailConfigFromEmail, setOfficeEmailConfigFromEmail] = useState('');
  const [officeEmailConfigReplyTo, setOfficeEmailConfigReplyTo] = useState('');
  const [officeEmailConfigSmtpHost, setOfficeEmailConfigSmtpHost] = useState('smtp.gmail.com');
  const [officeEmailConfigSmtpPort, setOfficeEmailConfigSmtpPort] = useState<string>('465');
  const [officeEmailConfigSecure, setOfficeEmailConfigSecure] = useState(true);
  const [officeEmailConfigSmtpUser, setOfficeEmailConfigSmtpUser] = useState('');
  const [officeEmailConfigSmtpPass, setOfficeEmailConfigSmtpPass] = useState('');
  const [officeEmailConfigProjectIds, setOfficeEmailConfigProjectIds] = useState<string[]>([]);
  const [officeEmailConfigIsActive, setOfficeEmailConfigIsActive] = useState(true);
  const [officeEmailConfigSaving, setOfficeEmailConfigSaving] = useState(false);
  const [officeEmailConfigSaveError, setOfficeEmailConfigSaveError] = useState<string | null>(null);
  const [officeEmailTemplateDrawerOpen, setOfficeEmailTemplateDrawerOpen] = useState(false);
  const [editingOfficeEmailTemplateId, setEditingOfficeEmailTemplateId] = useState<string | null>(null);
  const [officeEmailTemplateName, setOfficeEmailTemplateName] = useState('');
  const [officeEmailTemplateKey, setOfficeEmailTemplateKey] = useState('');
  const [officeEmailTemplateConfigId, setOfficeEmailTemplateConfigId] = useState('');
  const [officeEmailTemplateProjectIds, setOfficeEmailTemplateProjectIds] = useState<string[]>([]);
  const [officeEmailTemplateSubject, setOfficeEmailTemplateSubject] = useState('');
  const [officeEmailTemplateBodyHtml, setOfficeEmailTemplateBodyHtml] = useState('');
  const [officeEmailTemplateAttachmentDocKeys, setOfficeEmailTemplateAttachmentDocKeys] = useState<string[]>([]);
  const [officeEmailTemplateToMode, setOfficeEmailTemplateToMode] = useState<'primary_customer' | 'all_customers' | 'custom'>('primary_customer');
  const [officeEmailTemplateCustomTo, setOfficeEmailTemplateCustomTo] = useState('');
  const [officeEmailTemplateType, setOfficeEmailTemplateType] = useState<'unit' | 'general'>('unit');
  const [officeEmailTemplateIsActive, setOfficeEmailTemplateIsActive] = useState(true);
  const [officeEmailTemplateSaving, setOfficeEmailTemplateSaving] = useState(false);
  const [officeEmailTemplateSaveError, setOfficeEmailTemplateSaveError] = useState<string | null>(null);
  const [officeEmailTemplateTestDialogOpen, setOfficeEmailTemplateTestDialogOpen] = useState(false);
  const [officeEmailTemplateTestProjectId, setOfficeEmailTemplateTestProjectId] = useState('');
  const [officeEmailTemplateTestUnitId, setOfficeEmailTemplateTestUnitId] = useState('');
  const [officeEmailTemplateTestUnits, setOfficeEmailTemplateTestUnits] = useState<any[]>([]);
  const [officeEmailTemplateTestUnitsLoading, setOfficeEmailTemplateTestUnitsLoading] = useState(false);
  const [officeEmailTemplateTestSending, setOfficeEmailTemplateTestSending] = useState(false);
  const [officeEmailTemplateTestError, setOfficeEmailTemplateTestError] = useState<string | null>(null);
  const [officeEmailTemplateTestSuccess, setOfficeEmailTemplateTestSuccess] = useState<string | null>(null);
  const [officeEmailTemplatePreviewOpen, setOfficeEmailTemplatePreviewOpen] = useState(false);
  const [officeEmailTemplateSplitPct, setOfficeEmailTemplateSplitPct] = useState<number>(56);
  const [officeDocumentUploadTypes, setOfficeDocumentUploadTypes] = useState<any[]>([]);
  const [officeDocumentUploadTypesLoading, setOfficeDocumentUploadTypesLoading] = useState(false);
  const [officeDocumentUploadTypesError, setOfficeDocumentUploadTypesError] = useState<string | null>(null);
  const [officeDocumentUploadTypeDrawerOpen, setOfficeDocumentUploadTypeDrawerOpen] = useState(false);
  const [editingOfficeDocumentUploadTypeId, setEditingOfficeDocumentUploadTypeId] = useState<string | null>(null);
  const [officeDocumentUploadTypeKey, setOfficeDocumentUploadTypeKey] = useState('');
  const [officeDocumentUploadTypeKeyTouched, setOfficeDocumentUploadTypeKeyTouched] = useState(false);
  const [officeDocumentUploadTypeLabel, setOfficeDocumentUploadTypeLabel] = useState('');
  const [officeDocumentUploadTypeDisplayNameTemplate, setOfficeDocumentUploadTypeDisplayNameTemplate] = useState('');
  const [officeDocumentUploadTypeDisplaySection, setOfficeDocumentUploadTypeDisplaySection] = useState<'main_documents' | 'loan_documents' | 'draft_documents'>('main_documents');
  const [officeDocumentUploadTypeLinkedDraftTemplateId, setOfficeDocumentUploadTypeLinkedDraftTemplateId] = useState('');
  const [officeDocumentUploadTypeAllowedExtensions, setOfficeDocumentUploadTypeAllowedExtensions] = useState('pdf,jpg,jpeg,png');
  const [officeDocumentUploadTypeMaxSizeMb, setOfficeDocumentUploadTypeMaxSizeMb] = useState<number>(15);
  const [officeDocumentUploadTypeAllowMultiple, setOfficeDocumentUploadTypeAllowMultiple] = useState<boolean>(true);
  const [officeDocumentUploadTypeProjectIds, setOfficeDocumentUploadTypeProjectIds] = useState<string[]>([]);
  const [officeDocumentUploadTypeSaving, setOfficeDocumentUploadTypeSaving] = useState(false);
  const [officeDocumentUploadTypeSaveError, setOfficeDocumentUploadTypeSaveError] = useState<string | null>(null);
  const [officeDocumentUploadTypeSaveSuccess, setOfficeDocumentUploadTypeSaveSuccess] = useState<string | null>(null);
  const [officeDocumentTemplateDrawerOpen, setOfficeDocumentTemplateDrawerOpen] = useState(false);
  const [officeDocInsertFieldsOpen, setOfficeDocInsertFieldsOpen] = useState(true);
  const [editingOfficeDocumentTemplateId, setEditingOfficeDocumentTemplateId] = useState<string | null>(null);
  const [officeDocumentTemplateName, setOfficeDocumentTemplateName] = useState('');
  const [officeDocumentTemplateVersion, setOfficeDocumentTemplateVersion] = useState<number>(1);
  const [officeDocumentTemplateGroupKey, setOfficeDocumentTemplateGroupKey] = useState('');
  const [officeDocumentTemplateBodyHtml, setOfficeDocumentTemplateBodyHtml] = useState('');
  const [officeDocumentTemplateDraftFileNameTemplate, setOfficeDocumentTemplateDraftFileNameTemplate] = useState('');
  const [officeDocumentTemplateLinkToBank, setOfficeDocumentTemplateLinkToBank] = useState<boolean>(false);
  const [officeDocumentTemplateLinkedBanks, setOfficeDocumentTemplateLinkedBanks] = useState<string[]>([]);
  const [officeDocumentTemplateUseProjectLogoBw, setOfficeDocumentTemplateUseProjectLogoBw] = useState<boolean>(false);
  const [officeDocumentTemplateProjectIds, setOfficeDocumentTemplateProjectIds] = useState<string[]>([]);
  const [officeDocumentTemplateRequestTypes, setOfficeDocumentTemplateRequestTypes] = useState<string[]>([]);
  const [officeDocumentTemplateSaving, setOfficeDocumentTemplateSaving] = useState(false);
  const [officeDocumentTemplateSaveError, setOfficeDocumentTemplateSaveError] = useState<string | null>(null);
  const [officeDocumentTemplateSaveSuccess, setOfficeDocumentTemplateSaveSuccess] = useState<string | null>(null);
  const [officeDocumentTemplateAssignOpen, setOfficeDocumentTemplateAssignOpen] = useState(false);
  const [officeDocumentTemplateAssignTemplateId, setOfficeDocumentTemplateAssignTemplateId] = useState<string | null>(null);
  const [officeDocumentTemplateAssignDraft, setOfficeDocumentTemplateAssignDraft] = useState('');
  const [officeDocumentTemplateAssignSaving, setOfficeDocumentTemplateAssignSaving] = useState(false);
  const [officeDocEditorMode, setOfficeDocEditorMode] = useState<'visual' | 'html'>('visual');
  const [officeDocPaperSize, setOfficeDocPaperSize] = useState<'A4' | 'A5'>('A4');
  const [officeDocVisualTitle, setOfficeDocVisualTitle] = useState('Allotment Letter');
  const [officeDocVisualSubtitle, setOfficeDocVisualSubtitle] = useState('');
  const [officeDocShowTitle, setOfficeDocShowTitle] = useState<boolean>(true);
  const [officeDocShowRefNo, setOfficeDocShowRefNo] = useState<boolean>(true);
  const [officeDocShowDate, setOfficeDocShowDate] = useState<boolean>(true);
  const [officeDocShowPageNumbers, setOfficeDocShowPageNumbers] = useState<boolean>(true);
  const [officeDocTitleAlign, setOfficeDocTitleAlign] = useState<'left' | 'center' | 'right'>('left');
  const [officeDocFontFamily, setOfficeDocFontFamily] = useState<string>('Arial');
  const [officeDocLetterSpacingPx, setOfficeDocLetterSpacingPx] = useState<number>(0);
  const [officeDocUseCompanyFont, setOfficeDocUseCompanyFont] = useState<boolean>(false);
  const [officeDocRefNo, setOfficeDocRefNo] = useState('');
  const [officeDocRefPrefix, setOfficeDocRefPrefix] = useState('GEN');
  const [officeDocRefMode, setOfficeDocRefMode] = useState<OfficeDocRefMode>('sequence');
  const [officeDocGeneratedOutputType, setOfficeDocGeneratedOutputType] = useState<'draft' | 'final'>('draft');
  const [officeDocShowInSection, setOfficeDocShowInSection] = useState<'main_documents' | 'draft_documents' | 'loan_documents' | 'hidden'>('draft_documents');
  const [officeDocHeaderHeightCm, setOfficeDocHeaderHeightCm] = useState<number>(3.8);
  const [officeDocFooterHeightCm, setOfficeDocFooterHeightCm] = useState<number>(1.5);
  const [officeDocHeaderTopMarginCm, setOfficeDocHeaderTopMarginCm] = useState<number>(0);
  const [officeDocFooterBottomMarginCm, setOfficeDocFooterBottomMarginCm] = useState<number>(0);
  const [officeDocHeaderFooterSideMarginCm, setOfficeDocHeaderFooterSideMarginCm] = useState<number>(0);
  const [officeDocHeaderHorizontalOffsetCm, setOfficeDocHeaderHorizontalOffsetCm] = useState<number>(0);
  const [officeDocHeaderVerticalOffsetCm, setOfficeDocHeaderVerticalOffsetCm] = useState<number>(0);
  const [officeDocFooterHorizontalOffsetCm, setOfficeDocFooterHorizontalOffsetCm] = useState<number>(0);
  const [officeDocFooterVerticalOffsetCm, setOfficeDocFooterVerticalOffsetCm] = useState<number>(0);
  const [officeDocFooterOverlayEnabled, setOfficeDocFooterOverlayEnabled] = useState<boolean>(false);
  const [officeDocFooterOverlayText, setOfficeDocFooterOverlayText] = useState<string>('');
  const [officeDocFooterOverlayFontSizePx, setOfficeDocFooterOverlayFontSizePx] = useState<number>(12);
  const [officeDocFooterOverlayOffsetXCm, setOfficeDocFooterOverlayOffsetXCm] = useState<number>(0);
  const [officeDocFooterOverlayOffsetYCm, setOfficeDocFooterOverlayOffsetYCm] = useState<number>(0);
  const [officeDocFooterOverlayPageScope, setOfficeDocFooterOverlayPageScope] = useState<'all' | 'first' | 'last' | 'custom'>('all');
  const [officeDocFooterOverlayPageNumbers, setOfficeDocFooterOverlayPageNumbers] = useState<string>('');
  const [officeDocMainContentOffsetCm, setOfficeDocMainContentOffsetCm] = useState<number>(0);
  const [officeDocMarginTopCm, setOfficeDocMarginTopCm] = useState<number>(0);
  const [officeDocMarginRightCm, setOfficeDocMarginRightCm] = useState<number>(0.9);
  const [officeDocMarginBottomCm, setOfficeDocMarginBottomCm] = useState<number>(0);
  const [officeDocMarginLeftCm, setOfficeDocMarginLeftCm] = useState<number>(1.6);
  const [officeDocCompanyLogoHeightCm, setOfficeDocCompanyLogoHeightCm] = useState<number>(2.8);
  const [officeDocProjectLogoHeightCm, setOfficeDocProjectLogoHeightCm] = useState<number>(1.9);
  const [officeDocCompanyLogoOffsetXCm, setOfficeDocCompanyLogoOffsetXCm] = useState<number>(0);
  const [officeDocCompanyLogoOffsetYCm, setOfficeDocCompanyLogoOffsetYCm] = useState<number>(0);
  const [officeDocProjectLogoOffsetXCm, setOfficeDocProjectLogoOffsetXCm] = useState<number>(0);
  const [officeDocProjectLogoOffsetYCm, setOfficeDocProjectLogoOffsetYCm] = useState<number>(0);
  const [officeDocCompanyHeaderContentType, setOfficeDocCompanyHeaderContentType] = useState<'logo' | 'text' | 'none'>('logo');
  const [officeDocProjectHeaderContentType, setOfficeDocProjectHeaderContentType] = useState<'logo' | 'text' | 'none'>('logo');
  const [officeDocCompanyHeaderText, setOfficeDocCompanyHeaderText] = useState<string>('');
  const [officeDocProjectHeaderText, setOfficeDocProjectHeaderText] = useState<string>('');
  const [officeDocCompanyHeaderTextCss, setOfficeDocCompanyHeaderTextCss] = useState<string>('font-size:12px;line-height:1.2;color:#111;white-space:nowrap;');
  const [officeDocProjectHeaderTextCss, setOfficeDocProjectHeaderTextCss] = useState<string>('font-size:12px;line-height:1.2;color:#111;white-space:nowrap;');
  const [officeDocHeaderCenterHtml, setOfficeDocHeaderCenterHtml] = useState<string>('');
  const [officeDocShowFooterContent, setOfficeDocShowFooterContent] = useState<boolean>(true);
  const [officeDocBackgroundImageUrl, setOfficeDocBackgroundImageUrl] = useState('');
  const [officeDocBackgroundOpacity, setOfficeDocBackgroundOpacity] = useState<number>(0.08);
  const [officeDocBackgroundSize, setOfficeDocBackgroundSize] = useState<'cover' | 'contain'>('contain');
  const [officeDocBackgroundScale, setOfficeDocBackgroundScale] = useState<number>(72);
  const [officeDocBackgroundPosX, setOfficeDocBackgroundPosX] = useState<number>(-44);
  const [officeDocBackgroundPosY, setOfficeDocBackgroundPosY] = useState<number>(109);
  const [officeDocVisualBody, setOfficeDocVisualBody] = useState('');
  const [officeDocVisualFooter, setOfficeDocVisualFooter] = useState('');
  const [officeDocActiveField, setOfficeDocActiveField] = useState<'title' | 'subtitle' | 'body' | 'footer'>('body');
  const officeDocPreviewContainerRef = useRef<HTMLDivElement | null>(null);
  const officeDocxImportInputRef = useRef<HTMLInputElement | null>(null);
  const officeDocBodyEditorRef = useRef<HTMLDivElement | null>(null);
  const officeDocBodySelectionRef = useRef<Range | null>(null);
  const [officeDocRichFontFamily, setOfficeDocRichFontFamily] = useState<string>('Arial');
  const [officeDocRichFontSize, setOfficeDocRichFontSize] = useState<string>('16px');
  const [officeDocImportingDocx, setOfficeDocImportingDocx] = useState(false);
  const [officeDocImportError, setOfficeDocImportError] = useState<string | null>(null);
  const [officeDocSampleDownloading, setOfficeDocSampleDownloading] = useState(false);
  const [officeDocPreviewMode, setOfficeDocPreviewMode] = useState<'visual' | 'pdf'>('visual');
  const [officeDocPdfPreviewLoading, setOfficeDocPdfPreviewLoading] = useState(false);
  const [officeDocPdfPreviewError, setOfficeDocPdfPreviewError] = useState<string | null>(null);
  const [officeDocPdfPreviewUrl, setOfficeDocPdfPreviewUrl] = useState<string>('');
  const [officeDocPreviewSampleData, setOfficeDocPreviewSampleData] = useState<any | null>(null);
  const [officeDocPreviewSampleLoading, setOfficeDocPreviewSampleLoading] = useState(false);
  const [officeDocPreviewUnits, setOfficeDocPreviewUnits] = useState<any[]>([]);
  const [officeDocPreviewUnitId, setOfficeDocPreviewUnitId] = useState<string>('');
  const [officeDocumentTemplateExternalBankOptions, setOfficeDocumentTemplateExternalBankOptions] = useState<string[]>([]);
  const [officeDocumentTemplateExternalBankLoading, setOfficeDocumentTemplateExternalBankLoading] = useState(false);
  const normalizeUploadTypeKey = useCallback(
    (raw: string): string =>
      String(raw || '')
        .trim()
        .toLowerCase()
        .replace(/[^a-z0-9_]+/g, '_')
        .replace(/^_+|_+$/g, ''),
    []
  );
  const officeDocumentTemplateBankOptions = useMemo(
    () =>
      Array.from(
        new Set([
          ...officeDocumentTemplateExternalBankOptions
            .map((value: any) => String(value || '').trim())
            .filter(Boolean),
          ...officeDocumentTemplates
            .flatMap((row: any) =>
              Array.isArray(row?.linkedBanks) ? row.linkedBanks : Array.isArray(row?.visualModel?.linkedBanks) ? row.visualModel.linkedBanks : []
            )
            .map((value: any) => String(value || '').trim())
            .filter(Boolean),
        ])
      ).sort((a, b) => a.localeCompare(b, undefined, { sensitivity: 'base' })),
    [officeDocumentTemplateExternalBankOptions, officeDocumentTemplates]
  );
  const officeDocumentTemplateGroupKeyOptions = useMemo(
    () =>
      Array.from(
        new Set(
          [
            ...DEFAULT_DOCUMENT_GROUP_KEYS,
            ...officeDocumentTemplates
              .map((row: any) =>
                String(
                  row?.documentGroupKey ||
                    row?.visualModel?.documentGroupKey ||
                    row?.templateId ||
                    ''
                ).trim()
              )
              .filter(Boolean),
          ]
        )
      ).sort((a, b) => a.localeCompare(b, undefined, { sensitivity: 'base' })),
    [officeDocumentTemplates]
  );
  const officeDocumentUploadTypeKeySuggestionOptions = useMemo(() => {
    const configured = new Set(
      (officeDocumentUploadTypes || [])
        .map((row: any) => normalizeUploadTypeKey(String(row?.key || row?.id || '')))
        .filter(Boolean)
    );
    const currentKey = normalizeUploadTypeKey(officeDocumentUploadTypeKey);
    const candidateSet = new Set<string>();
    const addCandidate = (raw: unknown) => {
      const key = normalizeUploadTypeKey(String(raw || ''));
      if (!key) return;
      candidateSet.add(key);
    };
    (officeDocumentTemplates || []).forEach((row: any) => {
      addCandidate(row?.documentGroupKey);
      addCandidate(row?.visualModel?.documentGroupKey);
      addCandidate(row?.templateId);
      addCandidate(row?.id);
    });
    (officeWorkflows || []).forEach((workflow: any) => {
      (Array.isArray(workflow?.steps) ? workflow.steps : []).forEach((step: any) => {
        addCandidate(step?.docKey);
      });
    });
    ['sanction_letter', 'booking_form_signed', 'sale_agreement_signed', 'allotment_letter_signed'].forEach(addCandidate);
    return Array.from(candidateSet)
      .filter((key) => key && (!configured.has(key) || key === currentKey))
      .sort((a, b) => a.localeCompare(b, undefined, { sensitivity: 'base' }));
  }, [
    normalizeUploadTypeKey,
    officeDocumentUploadTypeKey,
    officeDocumentTemplates,
    officeDocumentUploadTypes,
    officeWorkflows,
  ]);
  const [whatsappSenders, setWhatsappSenders] = useState<any[]>([]);
  const [whatsappSendersLoading, setWhatsappSendersLoading] = useState(false);
  const [whatsappSendersError, setWhatsappSendersError] = useState<string | null>(null);
  const [whatsappSettingsSubSection, setWhatsappSettingsSubSection] = useState<'senders' | 'templates' | 'quickReplies' | 'automation' | 'conversationFlows' | 'birthdayWishes' | 'testMessage'>('senders');
  const [whatsappMessages, setWhatsappMessages] = useState<any[]>([]);
  const [whatsappMessagesLoading, setWhatsappMessagesLoading] = useState(false);
  const [, setWhatsappMessagesError] = useState<string | null>(null);
  const [whatsappTemplates, setWhatsappTemplates] = useState<any[]>([]);
  const [whatsappTemplatesLoading, setWhatsappTemplatesLoading] = useState(false);
  const [whatsappTemplatesError, setWhatsappTemplatesError] = useState<string | null>(null);
  const [whatsappQuickReplies, setWhatsappQuickReplies] = useState<any[]>([]);
  const [whatsappQuickRepliesLoading, setWhatsappQuickRepliesLoading] = useState(false);
  const [whatsappQuickRepliesError, setWhatsappQuickRepliesError] = useState<string | null>(null);
  const [googleContactsSyncConfig, setGoogleContactsSyncConfig] = useState<GoogleContactsSyncConfig>({
    enabled: false,
    sharedAccountEmail: '',
    sampleSize: GOOGLE_CONTACT_SYNC_DEFAULT_SAMPLE_SIZE,
  });
  const [googleContactsSyncConfigLoading, setGoogleContactsSyncConfigLoading] = useState(false);
  const [googleContactsSyncConfigSaving, setGoogleContactsSyncConfigSaving] = useState(false);
  const [googleContactsSyncConfigError, setGoogleContactsSyncConfigError] = useState<string | null>(null);
  const [googleContactsSyncConfigSuccess, setGoogleContactsSyncConfigSuccess] = useState<string | null>(null);
  const [googleContactsSyncRuns, setGoogleContactsSyncRuns] = useState<GoogleContactsSyncRun[]>([]);
  const [googleContactsSyncRunsLoading, setGoogleContactsSyncRunsLoading] = useState(false);
  const [googleContactsSyncRunsError, setGoogleContactsSyncRunsError] = useState<string | null>(null);
  const [googleContactsSyncRunLoading, setGoogleContactsSyncRunLoading] = useState<GoogleContactsSyncMode | null>(null);
  const [googleContactsSyncRunNotice, setGoogleContactsSyncRunNotice] = useState<string | null>(null);
  const [googleContactsConnectLoading, setGoogleContactsConnectLoading] = useState(false);
  const [googleContactsDisconnectLoading, setGoogleContactsDisconnectLoading] = useState(false);
  const [googleContactsSyncLoading, setGoogleContactsSyncLoading] = useState<'sample' | 'full' | null>(null);
  const [whatsappAutomationEnabled, setWhatsappAutomationEnabled] = useState(true);
  const [whatsappAutomationProjectScope, setWhatsappAutomationProjectScope] = useState<'selected' | 'all'>('selected');
  const [whatsappAutomationProjectIds, setWhatsappAutomationProjectIds] = useState<string[]>([]);
  const [whatsappAutomationStopStatusIds, setWhatsappAutomationStopStatusIds] = useState<string[]>([]);
  const [whatsappAutomationNewLeadDndBypass, setWhatsappAutomationNewLeadDndBypass] = useState(true);
  const [whatsappAutomationTestPhone, setWhatsappAutomationTestPhone] = useState('');
  const [whatsappAutomationSaving, setWhatsappAutomationSaving] = useState(false);
  const [whatsappAutomationError, setWhatsappAutomationError] = useState<string | null>(null);
  const [whatsappAutomationRules, setWhatsappAutomationRules] = useState<any[]>([]);
  const [whatsappAutomationRulesLoading, setWhatsappAutomationRulesLoading] = useState(false);
  const [whatsappAutomationRulesError, setWhatsappAutomationRulesError] = useState<string | null>(null);
  const [whatsappAutomationDispatches, setWhatsappAutomationDispatches] = useState<any[]>([]);
  const [whatsappAutomationDispatchesLoading, setWhatsappAutomationDispatchesLoading] = useState(false);
  const [whatsappAutomationDispatchesError, setWhatsappAutomationDispatchesError] = useState<string | null>(null);
  const [birthdayWishesEnabled, setBirthdayWishesEnabled] = useState(false);
  const [birthdayWishesIncludeProjectCustomers, setBirthdayWishesIncludeProjectCustomers] = useState(true);
  const [birthdayWishesIncludeBrokers, setBirthdayWishesIncludeBrokers] = useState(false);
  const [birthdayWishesIncludeStaff, setBirthdayWishesIncludeStaff] = useState(false);
  const [birthdayWishesIncludeContactsBook, setBirthdayWishesIncludeContactsBook] = useState(false);
  const [birthdayWishesCustomerTemplateId, setBirthdayWishesCustomerTemplateId] = useState('');
  const [birthdayWishesBrokerTemplateId, setBirthdayWishesBrokerTemplateId] = useState('');
  const [birthdayWishesStaffTemplateId, setBirthdayWishesStaffTemplateId] = useState('');
  const [birthdayWishesContactTemplateId, setBirthdayWishesContactTemplateId] = useState('');
  const [birthdayWishesSaving, setBirthdayWishesSaving] = useState(false);
  const [birthdayWishesError, setBirthdayWishesError] = useState<string | null>(null);
  const [birthdayWishesSuccess, setBirthdayWishesSuccess] = useState<string | null>(null);
  const [birthdayUpcomingCustomers, setBirthdayUpcomingCustomers] = useState<UpcomingBirthdayRow[]>([]);
  const [birthdayUpcomingBrokers, setBirthdayUpcomingBrokers] = useState<UpcomingBirthdayRow[]>([]);
  const [birthdayUpcomingStaff, setBirthdayUpcomingStaff] = useState<UpcomingBirthdayRow[]>([]);
  const [birthdayUpcomingContacts, setBirthdayUpcomingContacts] = useState<UpcomingBirthdayRow[]>([]);
  const [birthdayUpcomingLoading, setBirthdayUpcomingLoading] = useState(false);
  const [birthdayUpcomingError, setBirthdayUpcomingError] = useState<string | null>(null);
  const [contactsBookRows, setContactsBookRows] = useState<any[]>([]);
  const [contactsBookSortBy, setContactsBookSortBy] = useState<'name' | 'type' | 'dob' | 'anniversary' | 'status'>('name');
  const [contactsBookSortDirection, setContactsBookSortDirection] = useState<'asc' | 'desc'>('asc');
  const [contactsBookLoading, setContactsBookLoading] = useState(false);
  const [contactsBookError, setContactsBookError] = useState<string | null>(null);
  const [contactsBookDialogOpen, setContactsBookDialogOpen] = useState(false);
  const [editingContactsBookId, setEditingContactsBookId] = useState<string | null>(null);
  const [contactsBookName, setContactsBookName] = useState('');
  const [contactsBookPhone, setContactsBookPhone] = useState('');
  const [contactsBookType, setContactsBookType] = useState('Other');
  const [contactsBookDateOfBirth, setContactsBookDateOfBirth] = useState('');
  const [contactsBookAnniversaryDate, setContactsBookAnniversaryDate] = useState('');
  const [contactsBookOrganization, setContactsBookOrganization] = useState('');
  const [contactsBookProjectId, setContactsBookProjectId] = useState('');
  const [contactsBookNotes, setContactsBookNotes] = useState('');
  const [contactsBookActive, setContactsBookActive] = useState(true);
  const [contactsBookSaving, setContactsBookSaving] = useState(false);
  const [contactsBookSaveError, setContactsBookSaveError] = useState<string | null>(null);
  const [whatsappConversationFlows, setWhatsappConversationFlows] = useState<WhatsappConversationFlowRow[]>([]);
  const [selectedWhatsappConversationFlowId, setSelectedWhatsappConversationFlowId] = useState('project_selection');
  const [whatsappConversationFlowName, setWhatsappConversationFlowName] = useState('Project Selection Flow');
  const [whatsappConversationFlowPriority, setWhatsappConversationFlowPriority] = useState('10');
  const [whatsappConversationFlowActive, setWhatsappConversationFlowActive] = useState(false);
  const [whatsappConversationFlowProjectIds, setWhatsappConversationFlowProjectIds] = useState<string[]>([]);
  const [whatsappConversationFlowActionKeys, setWhatsappConversationFlowActionKeys] = useState<string[]>(['brochure', 'location', 'website', 'call']);
  const [whatsappConversationFlowStartKeywords, setWhatsappConversationFlowStartKeywords] = useState('Select Project, select_project');
  const [whatsappConversationFlowProjectPrompt, setWhatsappConversationFlowProjectPrompt] = useState('Please select the project you are interested in.');
  const [whatsappConversationFlowActionPrompt, setWhatsappConversationFlowActionPrompt] = useState('What would you like to see for this project?');
  const [whatsappConversationFlowActionReplies, setWhatsappConversationFlowActionReplies] = useState<WhatsappConversationActionReplies>({});
  const [whatsappConversationFlowSaving, setWhatsappConversationFlowSaving] = useState(false);
  const [whatsappConversationFlowError, setWhatsappConversationFlowError] = useState<string | null>(null);
  const [whatsappConversationFlowSuccess, setWhatsappConversationFlowSuccess] = useState<string | null>(null);
  const [whatsappAutomationRuleDialogOpen, setWhatsappAutomationRuleDialogOpen] = useState(false);
  const [editingWhatsappAutomationRuleId, setEditingWhatsappAutomationRuleId] = useState<string | null>(null);
  const [whatsappAutomationRuleName, setWhatsappAutomationRuleName] = useState('');
  const [whatsappAutomationRuleMode, setWhatsappAutomationRuleMode] = useState<'inactive' | 'test' | 'active'>('active');
  const [whatsappAutomationRuleTrigger, setWhatsappAutomationRuleTrigger] = useState<WhatsappAutomationRuleTrigger>('lead-created');
  const [whatsappAutomationRuleRecipient, setWhatsappAutomationRuleRecipient] = useState<'customer' | 'broker'>('customer');
  const [whatsappAutomationRuleTemplateId, setWhatsappAutomationRuleTemplateId] = useState('');
  const [whatsappAutomationRulePriority, setWhatsappAutomationRulePriority] = useState('10');
  const [whatsappAutomationRuleDelayAmount, setWhatsappAutomationRuleDelayAmount] = useState('0');
  const [whatsappAutomationRuleDelayUnit, setWhatsappAutomationRuleDelayUnit] = useState<'minutes' | 'hours' | 'days'>('minutes');
  const [whatsappAutomationRuleDelayDirection, setWhatsappAutomationRuleDelayDirection] = useState<'after' | 'before'>('after');
  const [whatsappAutomationRuleScheduleAnchor, setWhatsappAutomationRuleScheduleAnchor] = useState<'event' | 'leadCreated' | 'appointment'>('event');
  const [whatsappAutomationRuleRepeatMode, setWhatsappAutomationRuleRepeatMode] = useState<'once_per_lead' | 'once_per_event'>('once_per_lead');
  const [whatsappAutomationRuleDndMode, setWhatsappAutomationRuleDndMode] = useState<'respect' | 'bypass'>('respect');
  const [whatsappAutomationRuleSendWindow, setWhatsappAutomationRuleSendWindow] = useState<'any' | 'working_hours' | 'after_hours'>('any');
  const [whatsappAutomationRuleSourceValues, setWhatsappAutomationRuleSourceValues] = useState<string[]>([]);
  const [whatsappAutomationRuleStatusValues, setWhatsappAutomationRuleStatusValues] = useState<string[]>([]);
  const [whatsappAutomationRuleEnquiryTypes, setWhatsappAutomationRuleEnquiryTypes] = useState('');
  const [whatsappAutomationRuleAppointmentTypes, setWhatsappAutomationRuleAppointmentTypes] = useState<string[]>([]);
  const [whatsappAutomationRuleReceiptTypes, setWhatsappAutomationRuleReceiptTypes] = useState<string[]>(['customer']);
  const [whatsappAutomationRuleSiteVisitState, setWhatsappAutomationRuleSiteVisitState] = useState<'any' | 'yes' | 'no'>('any');
  const [whatsappAutomationRuleUpcomingAppointmentState, setWhatsappAutomationRuleUpcomingAppointmentState] = useState<'any' | 'yes' | 'no'>('any');
  const [whatsappAutomationRuleAdvancedConditions, setWhatsappAutomationRuleAdvancedConditions] = useState<WhatsappAutomationAdvancedCondition[]>([]);
  const [whatsappAutomationRuleCooldownAmount, setWhatsappAutomationRuleCooldownAmount] = useState('24');
  const [whatsappAutomationRuleCooldownUnit, setWhatsappAutomationRuleCooldownUnit] = useState<'minutes' | 'hours' | 'days'>('hours');
  const [whatsappAutomationRuleSaving, setWhatsappAutomationRuleSaving] = useState(false);
  const [whatsappAutomationRuleSaveError, setWhatsappAutomationRuleSaveError] = useState<string | null>(null);
  const [whatsappSenderDialogOpen, setWhatsappSenderDialogOpen] = useState(false);
  const [editingWhatsappSenderId, setEditingWhatsappSenderId] = useState<string | null>(null);
  const [whatsappSenderName, setWhatsappSenderName] = useState('');
  const [whatsappSenderPhoneNumberId, setWhatsappSenderPhoneNumberId] = useState('');
  const [whatsappSenderBusinessAccountId, setWhatsappSenderBusinessAccountId] = useState('');
  const [whatsappSenderAccessToken, setWhatsappSenderAccessToken] = useState('');
  const [whatsappSenderActive, setWhatsappSenderActive] = useState(true);
  const [whatsappSenderDefault, setWhatsappSenderDefault] = useState(false);
  const [whatsappSenderSaving, setWhatsappSenderSaving] = useState(false);
  const [whatsappSenderSaveError, setWhatsappSenderSaveError] = useState<string | null>(null);
  const [whatsappSenderSaveSuccess, setWhatsappSenderSaveSuccess] = useState<string | null>(null);
  const [whatsappStatusLoading, setWhatsappStatusLoading] = useState(false);
  const [whatsappStatusError, setWhatsappStatusError] = useState<string | null>(null);
  const [whatsappStatusData, setWhatsappStatusData] = useState<any | null>(null);
  const [whatsappTestSenderId, setWhatsappTestSenderId] = useState('');
  const [whatsappTestTo, setWhatsappTestTo] = useState('');
  const [whatsappTestMode, setWhatsappTestMode] = useState<'text' | 'template'>('text');
  const [whatsappTestText, setWhatsappTestText] = useState('');
  const [whatsappTestTemplateName, setWhatsappTestTemplateName] = useState('');
  const [whatsappTestTemplateLanguage, setWhatsappTestTemplateLanguage] = useState('en');
  const [whatsappTestTemplateId, setWhatsappTestTemplateId] = useState('');
  const [whatsappTestTemplateValues, setWhatsappTestTemplateValues] = useState<Record<string, string>>({});
  const [whatsappPrefillProjectId, setWhatsappPrefillProjectId] = useState('');
  const [whatsappPrefillUnitNo, setWhatsappPrefillUnitNo] = useState('');
  const [whatsappPrefillTxns, setWhatsappPrefillTxns] = useState<any[]>([]);
  const [whatsappPrefillTxnsLoading, setWhatsappPrefillTxnsLoading] = useState(false);
  const [whatsappPrefillTxnsError, setWhatsappPrefillTxnsError] = useState<string | null>(null);
  const [whatsappPrefillTxnId, setWhatsappPrefillTxnId] = useState('');
  const [whatsappTestSending, setWhatsappTestSending] = useState(false);
  const [whatsappTestError, setWhatsappTestError] = useState<string | null>(null);
  const [whatsappTestSuccess, setWhatsappTestSuccess] = useState<string | null>(null);
  const [whatsappTemplateDialogOpen, setWhatsappTemplateDialogOpen] = useState(false);
  const [editingWhatsappTemplateId, setEditingWhatsappTemplateId] = useState<string | null>(null);
  const [whatsappTemplateLabel, setWhatsappTemplateLabel] = useState('');
  const [whatsappTemplateName, setWhatsappTemplateName] = useState('');
  const [whatsappTemplateLanguage, setWhatsappTemplateLanguage] = useState('en');
  const [whatsappTemplateHeaderFormat, setWhatsappTemplateHeaderFormat] = useState<'none' | 'image' | 'document' | 'location'>('none');
  const [whatsappTemplateHeaderImageSource, setWhatsappTemplateHeaderImageSource] = useState<WhatsappTemplateHeaderImageSource>('project_logo');
  const [whatsappTemplateHeaderImageUrl, setWhatsappTemplateHeaderImageUrl] = useState('');
  const [whatsappTemplateHeaderImageUploadPath, setWhatsappTemplateHeaderImageUploadPath] = useState('');
  const [whatsappTemplateHeaderImageFile, setWhatsappTemplateHeaderImageFile] = useState<File | null>(null);
  const [whatsappTemplateHeaderDocumentSource, setWhatsappTemplateHeaderDocumentSource] = useState<'project_brochure' | 'lead_report' | 'custom'>('project_brochure');
  const [whatsappTemplateHeaderDocumentUrl, setWhatsappTemplateHeaderDocumentUrl] = useState('');
  const [whatsappTemplateHeaderLatitude, setWhatsappTemplateHeaderLatitude] = useState('');
  const [whatsappTemplateHeaderLongitude, setWhatsappTemplateHeaderLongitude] = useState('');
  const [whatsappTemplateHeaderLocationName, setWhatsappTemplateHeaderLocationName] = useState('');
  const [whatsappTemplateHeaderLocationAddress, setWhatsappTemplateHeaderLocationAddress] = useState('');
  const [whatsappTemplateHeaderLocationSource, setWhatsappTemplateHeaderLocationSource] = useState<'auto' | 'project' | 'office' | 'custom'>('auto');
  const [whatsappTemplateVariables, setWhatsappTemplateVariables] = useState<Array<{key: string; label: string; defaultValue: string; source: string}>>([]);
  const [whatsappTemplateButtonVariables, setWhatsappTemplateButtonVariables] = useState<Array<{index: string; key: string; label: string; defaultValue: string; source: string}>>([]);
  const [whatsappTemplateShowInLeadChat, setWhatsappTemplateShowInLeadChat] = useState(false);
  const [whatsappTemplateBodyText, setWhatsappTemplateBodyText] = useState('');
  const [whatsappTemplateSaving, setWhatsappTemplateSaving] = useState(false);
  const [whatsappTemplateSaveError, setWhatsappTemplateSaveError] = useState<string | null>(null);
  const [whatsappQuickReplyDialogOpen, setWhatsappQuickReplyDialogOpen] = useState(false);
  const [editingWhatsappQuickReplyId, setEditingWhatsappQuickReplyId] = useState<string | null>(null);
  const [whatsappQuickReplyLabel, setWhatsappQuickReplyLabel] = useState('');
  const [whatsappQuickReplyText, setWhatsappQuickReplyText] = useState('');
  const [whatsappQuickReplyCategory, setWhatsappQuickReplyCategory] = useState('');
  const [whatsappQuickReplyScope, setWhatsappQuickReplyScope] = useState<'global' | 'project'>('global');
  const [whatsappQuickReplyProjectId, setWhatsappQuickReplyProjectId] = useState('');
  const [whatsappQuickReplyActive, setWhatsappQuickReplyActive] = useState(true);
  const [whatsappQuickReplySortOrder, setWhatsappQuickReplySortOrder] = useState('0');
  const [whatsappQuickReplySaving, setWhatsappQuickReplySaving] = useState(false);
  const [whatsappQuickReplySaveError, setWhatsappQuickReplySaveError] = useState<string | null>(null);
  const [whatsappMetaTemplates, setWhatsappMetaTemplates] = useState<any[]>([]);
  const [whatsappMetaTemplatesLoading, setWhatsappMetaTemplatesLoading] = useState(false);
  const [whatsappMetaTemplatesFetched, setWhatsappMetaTemplatesFetched] = useState(false);
  const [whatsappCoexAppId, setWhatsappCoexAppId] = useState(() => {
    try {
      return String(localStorage.getItem(WHATSAPP_COEX_APP_ID_STORAGE_KEY) || '');
    } catch {
      return '';
    }
  });
  const [whatsappCoexConfigId, setWhatsappCoexConfigId] = useState(() => {
    try {
      return String(localStorage.getItem(WHATSAPP_COEX_CONFIG_ID_STORAGE_KEY) || '');
    } catch {
      return '';
    }
  });
  const [whatsappCoexSdkLoading, setWhatsappCoexSdkLoading] = useState(false);
  const [whatsappCoexSdkReady, setWhatsappCoexSdkReady] = useState(false);
  const [whatsappCoexLaunching, setWhatsappCoexLaunching] = useState(false);
  const [, setWhatsappCoexError] = useState<string | null>(null);
  const [whatsappCoexResult, setWhatsappCoexResult] = useState<any | null>(null);
  const [whatsappCoexEvent, setWhatsappCoexEvent] = useState<any | null>(null);
  const [whatsappCoexCompletionLoading, setWhatsappCoexCompletionLoading] = useState(false);
  const [whatsappCoexCompletionResult, setWhatsappCoexCompletionResult] = useState<any | null>(null);
  const [whatsappCoexRedirectCopySuccess, setWhatsappCoexRedirectCopySuccess] = useState(false);
  const whatsappCoexEventSeenRef = useRef(false);
  const whatsappCoexEventRef = useRef<any | null>(null);
  const whatsappCoexCompletionSuccessRef = useRef(false);
  const whatsappCoexCompletionStartedRef = useRef(false);
  const whatsappCoexRunIdRef = useRef(0);
  const whatsappCoexPopupTimeoutRef = useRef<number | null>(null);
  const whatsappCoexNoEventTimeoutRef = useRef<number | null>(null);
  const whatsappVariableSourceGroups = useMemo(
    () => [
      {
        label: 'CRM Lead Data',
        sources: [
          { key: 'customer_name', label: 'Customer Name' },
          { key: 'customer_first_name', label: 'Customer First Name' },
          { key: 'staff_name', label: 'Staff Name' },
          { key: 'contact_name', label: 'Contact Name' },
          { key: 'broker_name', label: 'Broker Name' },
          { key: 'agency_name', label: 'Agency Name' },
          { key: 'project_name', label: 'Project Name' },
          { key: 'project_slug', label: 'Project Website Slug' },
          { key: 'project_brochure_url', label: 'Project Brochure URL' },
          { key: 'date', label: 'Date' },
          { key: 'time', label: 'Time' },
          { key: 'event_date', label: 'Event Date' },
          { key: 'event_time', label: 'Event Time' },
          { key: 'visit_date', label: 'Visit Date' },
          { key: 'visit_time', label: 'Visit Time' },
          { key: 'appointment_type', label: 'Appointment Type' },
          { key: 'location', label: 'Location / Address' },
          { key: 'unit_type', label: 'Unit Type' },
          { key: 'visit_with', label: 'Visit With' },
        ],
      },
      {
        label: 'Lead Report Data',
        sources: [
          { key: 'report_period', label: 'Report Period / Date Range' },
          { key: 'lead_count', label: 'Lead Count' },
        ],
      },
      {
        label: 'Customer Payment Data',
        sources: [
          { key: 'amount', label: 'Amount' },
          { key: 'ref_no', label: 'Cheque/Ref No' },
          { key: 'payment_type', label: 'Payment Type' },
          { key: 'unit_no', label: 'Unit No' },
        ],
      },
      {
        label: 'Custom',
        sources: [
          { key: 'custom', label: 'Custom (Default Value)' },
        ],
      },
    ],
    []
  );
  const whatsappVariableSources = useMemo(
    () => whatsappVariableSourceGroups.flatMap((group) => group.sources),
    [whatsappVariableSourceGroups]
  );
  const availableUnitTypes = useMemo(
    () => Array.from(new Set(projects.flatMap((p: any) => p.unitTypes || []))),
    [projects]
  );

  const projectNameById = useMemo(() => {
    const m = new Map<string, string>();
    projects.forEach((p: any) => {
      const id = p?.id ? String(p.id) : '';
      if (!id) return;
      m.set(id, String(p?.name || id));
    });
    return m;
  }, [projects]);

  const birthdayUpcomingAll = useMemo(
    () => [
      ...birthdayUpcomingCustomers,
      ...birthdayUpcomingBrokers,
      ...birthdayUpcomingStaff,
      ...birthdayUpcomingContacts,
    ]
      .sort((a, b) => a.sortKey - b.sortKey || a.name.localeCompare(b.name))
      .slice(0, 5),
    [
      birthdayUpcomingBrokers,
      birthdayUpcomingContacts,
      birthdayUpcomingCustomers,
      birthdayUpcomingStaff,
    ]
  );

  const whatsappAutomationSourceOptions = useMemo(
    () => [
      'website-contact-form',
      'website-careers-page',
      'meta-lead-ad',
      'Social Ad',
      'WhatsApp',
      'Walk In',
      'With Broker',
      'Old Lead',
      'Call',
    ],
    []
  );

  const clearWhatsappCoexTimers = useCallback(() => {
    if (whatsappCoexPopupTimeoutRef.current) {
      window.clearTimeout(whatsappCoexPopupTimeoutRef.current);
      whatsappCoexPopupTimeoutRef.current = null;
    }
    if (whatsappCoexNoEventTimeoutRef.current) {
      window.clearTimeout(whatsappCoexNoEventTimeoutRef.current);
      whatsappCoexNoEventTimeoutRef.current = null;
    }
  }, []);

  const companyProfileNameById = useMemo(() => {
    const m = new Map<string, string>();
    companyProfiles.forEach((c: any) => {
      const id = c?.id ? String(c.id) : '';
      if (!id) return;
      m.set(id, String(c?.name || id));
    });
    return m;
  }, [companyProfiles]);

  const resetWhatsappSenderForm = useCallback(() => {
    setEditingWhatsappSenderId(null);
    setWhatsappSenderName('');
    setWhatsappSenderPhoneNumberId('');
    setWhatsappSenderBusinessAccountId('');
    setWhatsappSenderAccessToken('');
    setWhatsappSenderActive(true);
    setWhatsappSenderDefault(false);
    setWhatsappSenderSaveError(null);
    setWhatsappSenderSaveSuccess(null);
  }, []);

  const resetWhatsappTemplateForm = useCallback(() => {
    setEditingWhatsappTemplateId(null);
    setWhatsappTemplateLabel('');
    setWhatsappTemplateName('');
    setWhatsappTemplateLanguage('en');
    setWhatsappTemplateHeaderFormat('none');
    setWhatsappTemplateHeaderImageSource('project_logo');
    setWhatsappTemplateHeaderImageUrl('');
    setWhatsappTemplateHeaderImageUploadPath('');
    setWhatsappTemplateHeaderImageFile(null);
    setWhatsappTemplateHeaderDocumentSource('project_brochure');
    setWhatsappTemplateHeaderDocumentUrl('');
    setWhatsappTemplateHeaderLatitude('');
    setWhatsappTemplateHeaderLongitude('');
    setWhatsappTemplateHeaderLocationName('');
    setWhatsappTemplateHeaderLocationAddress('');
    setWhatsappTemplateHeaderLocationSource('auto');
    setWhatsappTemplateVariables([]);
    setWhatsappTemplateButtonVariables([]);
    setWhatsappTemplateShowInLeadChat(false);
    setWhatsappTemplateBodyText('');
    setWhatsappTemplateSaveError(null);
  }, []);

  const resetWhatsappQuickReplyForm = useCallback(() => {
    setEditingWhatsappQuickReplyId(null);
    setWhatsappQuickReplyLabel('');
    setWhatsappQuickReplyText('');
    setWhatsappQuickReplyCategory('');
    setWhatsappQuickReplyScope('global');
    setWhatsappQuickReplyProjectId('');
    setWhatsappQuickReplyActive(true);
    setWhatsappQuickReplySortOrder('0');
    setWhatsappQuickReplySaveError(null);
  }, []);

  const resetWhatsappAutomationRuleForm = useCallback(() => {
    setEditingWhatsappAutomationRuleId(null);
    setWhatsappAutomationRuleName('');
    setWhatsappAutomationRuleMode('active');
    setWhatsappAutomationRuleTrigger('lead-created');
    setWhatsappAutomationRuleRecipient('customer');
    setWhatsappAutomationRuleTemplateId('');
    setWhatsappAutomationRulePriority('10');
    setWhatsappAutomationRuleDelayAmount('0');
    setWhatsappAutomationRuleDelayUnit('minutes');
    setWhatsappAutomationRuleDelayDirection('after');
    setWhatsappAutomationRuleScheduleAnchor('event');
    setWhatsappAutomationRuleRepeatMode('once_per_lead');
    setWhatsappAutomationRuleDndMode('respect');
    setWhatsappAutomationRuleSendWindow('any');
    setWhatsappAutomationRuleSourceValues([]);
    setWhatsappAutomationRuleStatusValues([]);
    setWhatsappAutomationRuleEnquiryTypes('');
    setWhatsappAutomationRuleAppointmentTypes([]);
    setWhatsappAutomationRuleReceiptTypes(['customer']);
    setWhatsappAutomationRuleSiteVisitState('any');
    setWhatsappAutomationRuleUpcomingAppointmentState('any');
    setWhatsappAutomationRuleAdvancedConditions([]);
    setWhatsappAutomationRuleCooldownAmount('24');
    setWhatsappAutomationRuleCooldownUnit('hours');
    setWhatsappAutomationRuleSaveError(null);
  }, []);

  const openNewWhatsappSender = useCallback(() => {
    resetWhatsappSenderForm();
    setWhatsappSenderDialogOpen(true);
  }, [resetWhatsappSenderForm]);

  const openNewWhatsappTemplate = useCallback(() => {
    resetWhatsappTemplateForm();
    setWhatsappTemplateDialogOpen(true);
  }, [resetWhatsappTemplateForm]);

  const openNewWhatsappQuickReply = useCallback(() => {
    resetWhatsappQuickReplyForm();
    setWhatsappQuickReplyDialogOpen(true);
  }, [resetWhatsappQuickReplyForm]);

  const openNewWhatsappAutomationRule = useCallback(() => {
    resetWhatsappAutomationRuleForm();
    setWhatsappAutomationRuleDialogOpen(true);
  }, [resetWhatsappAutomationRuleForm]);

  const openEditWhatsappSender = useCallback((id: string) => {
    const row = whatsappSenders.find((sender: any) => String(sender?.id || '') === String(id || ''));
    if (!row) return;
    setEditingWhatsappSenderId(String(row.id));
    setWhatsappSenderName(String(row.name || ''));
    setWhatsappSenderPhoneNumberId(String(row.phoneNumberId || ''));
    setWhatsappSenderBusinessAccountId(String(row.businessAccountId || ''));
    setWhatsappSenderAccessToken(String(row.accessToken || ''));
    setWhatsappSenderActive(row.active !== false);
    setWhatsappSenderDefault(!!row.isDefault);
    setWhatsappSenderSaveError(null);
    setWhatsappSenderSaveSuccess(null);
    setWhatsappSenderDialogOpen(true);
  }, [whatsappSenders]);

  const openEditWhatsappTemplate = useCallback((id: string) => {
    const row = whatsappTemplates.find((template: any) => String(template?.id || '') === String(id || ''));
    if (!row) return;
    setEditingWhatsappTemplateId(String(row.id));
    setWhatsappTemplateLabel(String(row.label || row.name || ''));
    setWhatsappTemplateName(String(row.name || ''));
    setWhatsappTemplateLanguage(String(row.language || 'en'));
    const hf = String(row.headerFormat || '').toUpperCase();
    setWhatsappTemplateHeaderFormat(
      hf === 'IMAGE' ? 'image' : hf === 'DOCUMENT' ? 'document' : hf === 'LOCATION' ? 'location' : 'none'
    );
    const loadedHeaderImageUrl = String(row.headerImageUrl || '').trim();
    const loadedHeaderImageUploadPath = String(row.headerImageUploadPath || '').trim();
    const useProjectLogo = loadedHeaderImageUrl === WHATSAPP_HEADER_PROJECT_LOGO_TOKEN;
    const useVoucherOrProjectLogo = loadedHeaderImageUrl === WHATSAPP_HEADER_VOUCHER_OR_PROJECT_LOGO_TOKEN;
    setWhatsappTemplateHeaderImageSource(
      useVoucherOrProjectLogo ? 'voucher_or_project_logo' :
        useProjectLogo ? 'project_logo' :
          loadedHeaderImageUploadPath ? 'upload' :
            'custom'
    );
    setWhatsappTemplateHeaderImageUrl((useProjectLogo || useVoucherOrProjectLogo) ? '' : loadedHeaderImageUrl);
    setWhatsappTemplateHeaderImageUploadPath(loadedHeaderImageUploadPath);
    setWhatsappTemplateHeaderImageFile(null);
    const loadedHeaderDocumentUrl = String(row.headerDocumentUrl || '').trim();
    const useProjectBrochure = loadedHeaderDocumentUrl === WHATSAPP_HEADER_PROJECT_BROCHURE_TOKEN;
    const useLeadReport = loadedHeaderDocumentUrl === WHATSAPP_HEADER_LEAD_REPORT_TOKEN;
    setWhatsappTemplateHeaderDocumentSource(useLeadReport ? 'lead_report' : (useProjectBrochure ? 'project_brochure' : 'custom'));
    setWhatsappTemplateHeaderDocumentUrl((useProjectBrochure || useLeadReport) ? '' : loadedHeaderDocumentUrl);
    setWhatsappTemplateHeaderLatitude(String(row.headerLatitude || ''));
    setWhatsappTemplateHeaderLongitude(String(row.headerLongitude || ''));
    setWhatsappTemplateHeaderLocationName(String(row.headerLocationName || ''));
    setWhatsappTemplateHeaderLocationAddress(String(row.headerLocationAddress || ''));
    setWhatsappTemplateHeaderLocationSource(row.headerLocationSource || 'auto');
    const vars = Array.isArray(row.variables)
      ? row.variables.map((entry: any) => ({
        key: String(entry?.key || '').trim(),
        label: String(entry?.label || entry?.key || '').trim(),
        defaultValue: String(entry?.defaultValue || '').trim(),
        source: String(entry?.source || entry?.key || 'custom').trim(),
      })).filter((entry: any) => !!entry.key)
      : [];
    setWhatsappTemplateVariables(vars);
    const buttonVars = Array.isArray(row.buttonVariables)
      ? row.buttonVariables.map((entry: any) => ({
        index: String(entry?.index ?? '0').trim() || '0',
        key: String(entry?.key || '').trim(),
        label: String(entry?.label || entry?.key || '').trim(),
        defaultValue: String(entry?.defaultValue || '').trim(),
        source: String(entry?.source || entry?.key || 'custom').trim(),
      })).filter((entry: any) => !!entry.key)
      : [];
    setWhatsappTemplateButtonVariables(buttonVars);
    setWhatsappTemplateSaveError(null);
    setWhatsappTemplateShowInLeadChat(!!row.showInLeadChat);
    setWhatsappTemplateBodyText(String(row.bodyText || ''));
    setWhatsappTemplateDialogOpen(true);
  }, [whatsappTemplates]);

  const openEditWhatsappQuickReply = useCallback((id: string) => {
    const row = whatsappQuickReplies.find((reply: any) => String(reply?.id || '') === String(id || ''));
    if (!row) return;
    setEditingWhatsappQuickReplyId(String(row.id));
    setWhatsappQuickReplyLabel(String(row.label || ''));
    setWhatsappQuickReplyText(String(row.text || ''));
    setWhatsappQuickReplyCategory(String(row.category || ''));
    setWhatsappQuickReplyScope(String(row.scope || 'global') === 'project' ? 'project' : 'global');
    setWhatsappQuickReplyProjectId(String(row.projectId || ''));
    setWhatsappQuickReplyActive(row.active !== false);
    setWhatsappQuickReplySortOrder(String(Number(row.sortOrder || 0)));
    setWhatsappQuickReplySaveError(null);
    setWhatsappQuickReplyDialogOpen(true);
  }, [whatsappQuickReplies]);

  const openEditWhatsappAutomationRule = useCallback((id: string) => {
    const row = whatsappAutomationRules.find(
      (rule: any) => String(rule?.id || '') === String(id || '')
    );
    if (!row) return;
    setEditingWhatsappAutomationRuleId(String(row.id));
    setWhatsappAutomationRuleName(String(row.name || ''));
    setWhatsappAutomationRuleMode(
      row.active === false ? 'inactive' :
        String(row.mode || 'active') === 'test' ? 'test' :
          'active'
    );
    setWhatsappAutomationRuleTrigger(
      String(row.trigger || 'lead-created') as WhatsappAutomationRuleTrigger
    );
    setWhatsappAutomationRuleRecipient(String(row.recipient || 'customer') === 'broker' ? 'broker' : 'customer');
    setWhatsappAutomationRuleTemplateId(String(row.templateId || ''));
    setWhatsappAutomationRulePriority(String(Number(row.priority || 10)));
    setWhatsappAutomationRuleDelayAmount(String(Number(row.delayAmount || 0)));
    setWhatsappAutomationRuleDelayUnit(
      ['minutes', 'hours', 'days'].includes(String(row.delayUnit || '')) ?
        String(row.delayUnit) as 'minutes' | 'hours' | 'days' :
        'minutes'
    );
    setWhatsappAutomationRuleDelayDirection(
      String(row.delayDirection || 'after') === 'before' ? 'before' : 'after'
    );
    setWhatsappAutomationRuleScheduleAnchor(
      ['event', 'leadCreated', 'appointment'].includes(String(row.scheduleAnchor || '')) ?
        String(row.scheduleAnchor) as 'event' | 'leadCreated' | 'appointment' :
        'event'
    );
    setWhatsappAutomationRuleRepeatMode(
      String(row.repeatMode || 'once_per_lead') === 'once_per_event' ? 'once_per_event' : 'once_per_lead'
    );
    setWhatsappAutomationRuleDndMode(
      String(row.dndMode || 'respect') === 'bypass' ? 'bypass' : 'respect'
    );
    setWhatsappAutomationRuleSendWindow(
      String(row.sendWindow || 'any') === 'working_hours' ? 'working_hours' :
        String(row.sendWindow || 'any') === 'after_hours' ? 'after_hours' :
          'any'
    );
    setWhatsappAutomationRuleSourceValues(
      Array.isArray(row?.conditions?.sourceValues) ?
        row.conditions.sourceValues.map((value: any) => String(value || '').trim()).filter(Boolean) :
        []
    );
    setWhatsappAutomationRuleStatusValues(
      Array.isArray(row?.conditions?.statusValues) ?
        row.conditions.statusValues.map((value: any) => String(value || '').trim()).filter(Boolean) :
        []
    );
    setWhatsappAutomationRuleEnquiryTypes(
      Array.isArray(row?.conditions?.enquiryTypes) ?
        row.conditions.enquiryTypes.map((value: any) => String(value || '').trim()).filter(Boolean).join(', ') :
        ''
    );
    setWhatsappAutomationRuleAppointmentTypes(
      Array.isArray(row?.conditions?.appointmentTypes) ?
        row.conditions.appointmentTypes.map((value: any) => String(value || '').trim()).filter(Boolean) :
        []
    );
    setWhatsappAutomationRuleReceiptTypes(
      Array.isArray(row?.conditions?.receiptTypes) && row.conditions.receiptTypes.length ?
        row.conditions.receiptTypes.map((value: any) => String(value || '').trim()).filter(Boolean) :
        ['customer']
    );
    setWhatsappAutomationRuleSiteVisitState(
      ['yes', 'no'].includes(String(row?.conditions?.siteVisitState || '')) ?
        String(row.conditions.siteVisitState) as 'yes' | 'no' :
        'any'
    );
    setWhatsappAutomationRuleUpcomingAppointmentState(
      ['yes', 'no'].includes(String(row?.conditions?.upcomingAppointmentState || '')) ?
        String(row.conditions.upcomingAppointmentState) as 'yes' | 'no' :
        'any'
    );
    setWhatsappAutomationRuleAdvancedConditions(
      Array.isArray(row?.conditions?.advancedConditions) ?
        row.conditions.advancedConditions
          .map((condition: any) => {
            const field = String(condition?.field || '') === 'lead_match' ? 'lead_match' as const : 'lead_created_at' as const;
            if (field === 'lead_match') {
              const operatorRaw = String(condition?.operator || '').trim();
              const operator = operatorRaw === 'known' || operatorRaw === 'unknown' ? operatorRaw as 'known' | 'unknown' : 'any';
              return { field, operator, value: '', unit: 'none' as const };
            }
            return {
              field,
              operator: String(condition?.operator || '') === 'older_than' ? 'older_than' as const : 'within_last' as const,
              value: String(Number(condition?.value || condition?.amount || 0) || ''),
              unit: 'days' as const,
            };
          })
          .filter((condition: WhatsappAutomationAdvancedCondition) =>
            condition.field === 'lead_match' ? condition.operator !== 'any' : Number(condition.value || 0) > 0
          ) :
        []
    );
    setWhatsappAutomationRuleCooldownAmount(String(Number(row?.conditions?.cooldownAmount || 0) || 0));
    setWhatsappAutomationRuleCooldownUnit(
      ['minutes', 'hours', 'days'].includes(String(row?.conditions?.cooldownUnit || '')) ?
        String(row.conditions.cooldownUnit) as 'minutes' | 'hours' | 'days' :
        'hours'
    );
    setWhatsappAutomationRuleSaveError(null);
    setWhatsappAutomationRuleDialogOpen(true);
  }, [whatsappAutomationRules]);

  const formatWhatsappTs = useCallback((value: any): string => {
    if (!value) return '—';
    const date = value?.toDate && typeof value.toDate === 'function' ? value.toDate() : value;
    if (!(date instanceof Date) || Number.isNaN(date.getTime())) return '—';
    return date.toLocaleString('en-IN');
  }, []);

  useEffect(() => {
    if (activeTab !== 1 || projectSettingsSection !== 'whatsapp') return;
    setWhatsappSendersLoading(true);
    setWhatsappSendersError(null);
    const unsub = onSnapshot(
      collection(db, 'whatsappSenders'),
      (snap) => {
        const rows = snap.docs.map((d) => ({ id: d.id, ...(d.data() as any) }));
        rows.sort((a: any, b: any) => String(a?.name || a?.id || '').localeCompare(String(b?.name || b?.id || '')));
        setWhatsappSenders(rows);
        setWhatsappSendersLoading(false);
      },
      (err) => {
        console.error('Load whatsapp senders failed', err);
        setWhatsappSendersError(err?.message || 'Failed to load WhatsApp senders.');
        setWhatsappSendersLoading(false);
      }
    );
    return () => unsub();
  }, [activeTab, db, projectSettingsSection]);

  useEffect(() => {
    if (activeTab !== 1 || projectSettingsSection !== 'whatsapp') return;
    setWhatsappTemplatesLoading(true);
    setWhatsappTemplatesError(null);
    const q = query(collection(db, 'whatsappTemplates'), orderBy('updatedAt', 'desc'));
    const unsub = onSnapshot(
      q,
      (snap) => {
        setWhatsappTemplates(snap.docs.map((d) => ({ id: d.id, ...(d.data() as any) })));
        setWhatsappTemplatesLoading(false);
      },
      (err) => {
        console.error('Load whatsapp templates failed', err);
        setWhatsappTemplatesError(err?.message || 'Failed to load WhatsApp templates.');
        setWhatsappTemplatesLoading(false);
      }
    );
    return () => unsub();
  }, [activeTab, db, projectSettingsSection]);

  useEffect(() => {
    if (activeTab !== 1 || projectSettingsSection !== 'whatsapp') return;
    setWhatsappQuickRepliesLoading(true);
    setWhatsappQuickRepliesError(null);
    const q = query(collection(db, 'whatsappQuickReplies'), orderBy('sortOrder', 'asc'));
    const unsub = onSnapshot(
      q,
      (snap) => {
        const rows = snap.docs.map((d) => ({ id: d.id, ...(d.data() as any) }));
        setWhatsappQuickReplies(rows);
        setWhatsappQuickRepliesLoading(false);
      },
      (err) => {
        console.error('Load WhatsApp quick replies failed', err);
        setWhatsappQuickRepliesError(err?.message || 'Failed to load WhatsApp quick replies.');
        setWhatsappQuickRepliesLoading(false);
      }
    );
    return () => unsub();
  }, [activeTab, db, projectSettingsSection]);

  useEffect(() => {
    if (activeTab !== 1 || projectSettingsSection !== 'whatsapp') return;
    setWhatsappAutomationError(null);
    const unsub = onSnapshot(
      doc(db, 'config', 'whatsappAutomation'),
      (snap) => {
        const data: any = snap.exists() ? snap.data() : {};
        setWhatsappAutomationEnabled(data?.enabled !== false);
        setWhatsappAutomationProjectScope(String(data?.projectScope || 'selected') === 'all' ? 'all' : 'selected');
        setWhatsappAutomationProjectIds(
          Array.isArray(data?.projectIds) ?
            data.projectIds.map((value: any) => String(value || '').trim()).filter(Boolean) :
            []
        );
        setWhatsappAutomationStopStatusIds(
          Array.isArray(data?.stopStatusIds) ?
            data.stopStatusIds.map((value: any) => String(value || '').trim()).filter(Boolean) :
            []
        );
        setWhatsappAutomationNewLeadDndBypass(data?.newLeadDndBypass !== false);
        setWhatsappAutomationTestPhone(String(data?.testPhone || ''));
      },
      (err) => {
        console.error('Load WhatsApp automation config failed', err);
        setWhatsappAutomationError(err?.message || 'Failed to load WhatsApp automation config.');
      }
    );
    return () => unsub();
  }, [activeTab, db, projectSettingsSection]);

  useEffect(() => {
    if (activeTab !== 1 || projectSettingsSection !== 'whatsapp') return;
    setWhatsappAutomationRulesLoading(true);
    setWhatsappAutomationRulesError(null);
    const q = query(collection(db, 'whatsappAutomationRules'), orderBy('updatedAt', 'desc'));
    const unsub = onSnapshot(
      q,
      (snap) => {
        const rows = snap.docs
          .map((d) => ({ id: d.id, ...(d.data() as any) }))
          .sort((a: any, b: any) => Number(a?.priority || 0) - Number(b?.priority || 0));
        setWhatsappAutomationRules(rows);
        setWhatsappAutomationRulesLoading(false);
      },
      (err) => {
        console.error('Load WhatsApp automation rules failed', err);
        setWhatsappAutomationRulesError(err?.message || 'Failed to load WhatsApp automation rules.');
        setWhatsappAutomationRulesLoading(false);
      }
    );
    return () => unsub();
  }, [activeTab, db, projectSettingsSection]);

  useEffect(() => {
    if (activeTab !== 1 || projectSettingsSection !== 'whatsapp') return;
    setWhatsappAutomationDispatchesLoading(true);
    setWhatsappAutomationDispatchesError(null);
    const q = query(
      collection(db, 'whatsappAutomationDispatches'),
      orderBy('createdAt', 'desc'),
      limit(80)
    );
    const unsub = onSnapshot(
      q,
      (snap) => {
        setWhatsappAutomationDispatches(snap.docs.map((d) => ({ id: d.id, ...(d.data() as any) })));
        setWhatsappAutomationDispatchesLoading(false);
      },
      (err) => {
        console.error('Load WhatsApp automation dispatches failed', err);
        setWhatsappAutomationDispatchesError(err?.message || 'Failed to load automation dispatches.');
        setWhatsappAutomationDispatchesLoading(false);
      }
    );
    return () => unsub();
  }, [activeTab, db, projectSettingsSection]);

  useEffect(() => {
    if (activeTab !== 1 || projectSettingsSection !== 'whatsapp') return;
    setBirthdayWishesError(null);
    const unsub = onSnapshot(
      doc(db, 'birthdayWishConfig', 'main'),
      (snap) => {
        const data = snap.data() || {};
        setBirthdayWishesEnabled(data.enabled === true);
        setBirthdayWishesIncludeProjectCustomers(data.includeProjectCustomers !== false);
        setBirthdayWishesIncludeBrokers(data.includeBrokers === true);
        setBirthdayWishesIncludeStaff(data.includeStaff === true);
        setBirthdayWishesIncludeContactsBook(data.includeContactsBook === true);
        setBirthdayWishesCustomerTemplateId(String(data.customerTemplateId || ''));
        setBirthdayWishesBrokerTemplateId(String(data.brokerTemplateId || ''));
        setBirthdayWishesStaffTemplateId(String(data.staffTemplateId || ''));
        setBirthdayWishesContactTemplateId(String(data.contactTemplateId || ''));
      },
      (err) => {
        console.error('Load birthday wishes config failed', err);
        setBirthdayWishesError(err?.message || 'Failed to load birthday wishes config.');
      }
    );
    return () => unsub();
  }, [activeTab, db, projectSettingsSection]);

  useEffect(() => {
    if (activeTab !== 1 || projectSettingsSection !== 'whatsapp' || whatsappSettingsSubSection !== 'birthdayWishes') return;
    let cancelled = false;
    const loadUpcomingBirthdays = async () => {
      setBirthdayUpcomingLoading(true);
      setBirthdayUpcomingError(null);
      try {
        const functions = getFunctions();
        const getUpcomingBirthdays = httpsCallable(functions, 'getUpcomingBirthdays');
        const result = await getUpcomingBirthdays({});
        if (cancelled) return;
        const payload = (result.data || {}) as {
          customers?: UpcomingBirthdayRow[];
          brokers?: UpcomingBirthdayRow[];
          staff?: UpcomingBirthdayRow[];
          contacts?: UpcomingBirthdayRow[];
        };
        setBirthdayUpcomingCustomers(Array.isArray(payload.customers) ? payload.customers : []);
        setBirthdayUpcomingBrokers(Array.isArray(payload.brokers) ? payload.brokers : []);
        setBirthdayUpcomingStaff(Array.isArray(payload.staff) ? payload.staff : []);
        setBirthdayUpcomingContacts(Array.isArray(payload.contacts) ? payload.contacts : []);
      } catch (err: any) {
        console.error('Load upcoming birthdays failed', err);
        if (!cancelled) {
          setBirthdayUpcomingError(err?.message || 'Failed to load upcoming birthdays.');
        }
      } finally {
        if (!cancelled) setBirthdayUpcomingLoading(false);
      }
    };
    loadUpcomingBirthdays();
    return () => {
      cancelled = true;
    };
  }, [activeTab, projectSettingsSection, whatsappSettingsSubSection]);

  useEffect(() => {
    if (activeTab !== 1 || projectSettingsSection !== 'contactsBook') return;
    setContactsBookLoading(true);
    setContactsBookError(null);
    const unsub = onSnapshot(
      query(collection(db, 'contactsBook'), orderBy('nameLower', 'asc')),
      (snap) => {
        setContactsBookRows(snap.docs.map((docSnap) => ({ id: docSnap.id, ...docSnap.data() })));
        setContactsBookLoading(false);
      },
      (err) => {
        console.error('Load contacts book failed', err);
        setContactsBookError(err?.message || 'Failed to load contacts book.');
        setContactsBookLoading(false);
      }
    );
    return () => unsub();
  }, [activeTab, db, projectSettingsSection]);

  useEffect(() => {
    if (activeTab !== 1 || projectSettingsSection !== 'whatsapp') return;
    setWhatsappConversationFlowError(null);
    const unsub = onSnapshot(
      collection(db, 'whatsappConversationFlows'),
      (snap) => {
        const rows = snap.docs
          .map((docSnap) => ({ id: docSnap.id, ...docSnap.data() } as WhatsappConversationFlowRow))
          .filter((row) => String(row.type || 'project_selection') === 'project_selection')
          .sort((a, b) => Number(a.priority ?? 10) - Number(b.priority ?? 10) || String(a.name || a.id).localeCompare(String(b.name || b.id)));
        setWhatsappConversationFlows(rows);
        setSelectedWhatsappConversationFlowId((prev) => {
          if (rows.some((row) => row.id === prev)) return prev;
          return rows[0]?.id || 'project_selection';
        });
      },
      (err) => {
        console.error('Load WhatsApp conversation flows failed', err);
        setWhatsappConversationFlowError(err?.message || 'Failed to load conversation flows.');
      }
    );
    return () => unsub();
  }, [activeTab, db, projectSettingsSection]);

  useEffect(() => {
    const data = whatsappConversationFlows.find((row) => row.id === selectedWhatsappConversationFlowId) || null;
    const projectIds = Array.isArray(data?.projectIds) ? (data?.projectIds || []) : [];
    const actionKeys = Array.isArray(data?.actionKeys) ? (data?.actionKeys || []) : [];
    const startKeywords = Array.isArray(data?.startKeywords) ? (data?.startKeywords || []) : [];
    setWhatsappConversationFlowName(String(data?.name || 'Project Selection Flow'));
    setWhatsappConversationFlowPriority(String(Number(data?.priority ?? 10) || 10));
    setWhatsappConversationFlowActive(data?.active === true);
    setWhatsappConversationFlowProjectIds(
      projectIds.length ?
        projectIds.map((value: any) => String(value || '').trim()).filter(Boolean) :
        []
    );
    setWhatsappConversationFlowActionKeys(
      actionKeys.length ?
        actionKeys.map((value: any) => String(value || '').trim()).filter(Boolean) :
        ['brochure', 'location', 'website', 'call']
    );
    setWhatsappConversationFlowStartKeywords(
      startKeywords.length ?
        startKeywords.map((value: any) => String(value || '').trim()).filter(Boolean).join(', ') :
        'Select Project, select_project'
    );
    setWhatsappConversationFlowProjectPrompt(String(data?.projectPromptText || 'Please select the project you are interested in.'));
    setWhatsappConversationFlowActionPrompt(String(data?.actionPromptText || 'What would you like to see for this project?'));
    const nextReplies: WhatsappConversationActionReplies = {};
    const rawReplies = data?.actionReplies && typeof data.actionReplies === 'object' ? data.actionReplies : {};
    Object.entries(rawReplies).forEach(([projectId, actions]: [string, any]) => {
      if (!actions || typeof actions !== 'object') return;
      Object.entries(actions).forEach(([actionKey, config]: [string, any]) => {
        const pid = String(projectId || '').trim();
        const key = String(actionKey || '').trim();
        if (!pid || !key) return;
        if (!nextReplies[pid]) nextReplies[pid] = {};
        nextReplies[pid][key] = {
          messageText: String(config?.messageText || ''),
          buttonActionKeys: Array.isArray(config?.buttonActionKeys) ?
            config.buttonActionKeys.map((value: any) => String(value || '').trim()).filter(Boolean) :
            [],
          responseType: String(config?.responseType || 'default'),
          urlButtonText: String(config?.urlButtonText || ''),
          urlSource: String(config?.urlSource || 'auto'),
          customUrl: String(config?.customUrl || ''),
        };
      });
    });
    setWhatsappConversationFlowActionReplies(nextReplies);
  }, [selectedWhatsappConversationFlowId, whatsappConversationFlows]);

  useEffect(() => {
    if (activeTab !== 1 || projectSettingsSection !== 'whatsapp') return;
    setWhatsappMessagesLoading(true);
    setWhatsappMessagesError(null);
    const q = query(collection(db, 'whatsappMessages'), orderBy('createdAt', 'desc'), limit(60));
    const unsub = onSnapshot(
      q,
      (snap) => {
        setWhatsappMessages(snap.docs.map((d) => ({ id: d.id, ...(d.data() as any) })));
        setWhatsappMessagesLoading(false);
      },
      (err) => {
        console.error('Load whatsapp logs failed', err);
        setWhatsappMessagesError(err?.message || 'Failed to load WhatsApp logs.');
        setWhatsappMessagesLoading(false);
      }
    );
    return () => unsub();
  }, [activeTab, db, projectSettingsSection]);

  useEffect(() => {
    if (!whatsappSenders.length) {
      setWhatsappTestSenderId('');
      return;
    }
    if (whatsappTestSenderId && whatsappSenders.some((sender: any) => String(sender?.id || '') === whatsappTestSenderId)) return;
    const preferred = whatsappSenders.find((sender: any) => sender?.active && sender?.isDefault)
      || whatsappSenders.find((sender: any) => sender?.active)
      || whatsappSenders[0];
    setWhatsappTestSenderId(String(preferred?.id || ''));
  }, [whatsappSenders, whatsappTestSenderId]);

  const selectedWhatsappTemplate = useMemo(
    () => whatsappTemplates.find((template: any) => String(template?.id || '') === whatsappTestTemplateId) || null,
    [whatsappTemplates, whatsappTestTemplateId]
  );

  useEffect(() => {
    if (!whatsappTemplates.length) {
      setWhatsappTestTemplateId('');
      return;
    }
    if (whatsappTestTemplateId && whatsappTemplates.some((template: any) => String(template?.id || '') === whatsappTestTemplateId)) return;
    setWhatsappTestTemplateId(String(whatsappTemplates[0]?.id || ''));
  }, [whatsappTemplates, whatsappTestTemplateId]);

  useEffect(() => {
    if (!selectedWhatsappTemplate) {
      setWhatsappTestTemplateName('');
      setWhatsappTestTemplateLanguage('en');
      setWhatsappTestTemplateValues({});
      return;
    }
    const templateName = String(selectedWhatsappTemplate?.name || '').trim();
    const templateLanguage = String(selectedWhatsappTemplate?.language || 'en').trim() || 'en';
    setWhatsappTestTemplateName(templateName);
    setWhatsappTestTemplateLanguage(templateLanguage);
    const variables = Array.isArray(selectedWhatsappTemplate?.variables) ? selectedWhatsappTemplate.variables : [];
    setWhatsappTestTemplateValues((prev) => {
      const next: Record<string, string> = {};
      variables.forEach((entry: any) => {
        const key = String(entry?.key || '').trim();
        if (!key) return;
        next[key] = String(prev?.[key] || entry?.defaultValue || '');
      });
      return next;
    });
  }, [selectedWhatsappTemplate]);

  useEffect(() => {
    if (!projects.length) {
      setWhatsappPrefillProjectId('');
      return;
    }
    if (whatsappPrefillProjectId && projects.some((project: any) => String(project?.id || '') === whatsappPrefillProjectId)) return;
    setWhatsappPrefillProjectId(String(projects[0]?.id || ''));
  }, [projects, whatsappPrefillProjectId]);

  useEffect(() => {
    if (activeTab !== 1 || projectSettingsSection !== 'whatsapp') return;
    const projectId = String(whatsappPrefillProjectId || '').trim();
    const unitNo = String(whatsappPrefillUnitNo || '').trim();
    if (!projectId || !unitNo) {
      setWhatsappPrefillTxns([]);
      setWhatsappPrefillTxnId('');
      setWhatsappPrefillTxnsError(null);
      return;
    }
    let cancelled = false;
    const run = async () => {
      setWhatsappPrefillTxnsLoading(true);
      setWhatsappPrefillTxnsError(null);
      try {
        const q = query(
          collection(db, 'projects', projectId, 'bankTransactions'),
          where('unitNumber', '==', unitNo),
          limit(100)
        );
        const snap = await getDocs(q);
        if (cancelled) return;
        const rows = snap.docs.map((d) => ({ id: d.id, ...(d.data() as any) }))
          .filter((row: any) => String(row?.txnType || '').toLowerCase() === 'receipt')
          .sort((a: any, b: any) => {
            const av = a?.txnDate?.toMillis ? a.txnDate.toMillis() : (a?.createdAt?.toMillis ? a.createdAt.toMillis() : 0);
            const bv = b?.txnDate?.toMillis ? b.txnDate.toMillis() : (b?.createdAt?.toMillis ? b.createdAt.toMillis() : 0);
            return bv - av;
          });
        setWhatsappPrefillTxns(rows);
        setWhatsappPrefillTxnId(String(rows[0]?.id || ''));
      } catch (err: any) {
        if (cancelled) return;
        console.error('Load whatsapp prefill txns failed', err);
        setWhatsappPrefillTxnsError(err?.message || 'Failed to load transactions.');
        setWhatsappPrefillTxns([]);
        setWhatsappPrefillTxnId('');
      } finally {
        if (!cancelled) setWhatsappPrefillTxnsLoading(false);
      }
    };
    run();
    return () => { cancelled = true; };
  }, [activeTab, db, projectSettingsSection, whatsappPrefillProjectId, whatsappPrefillUnitNo]);

  useEffect(() => {
    try {
      localStorage.setItem(WHATSAPP_COEX_APP_ID_STORAGE_KEY, String(whatsappCoexAppId || '').trim());
    } catch {}
  }, [whatsappCoexAppId]);

  useEffect(() => {
    try {
      localStorage.setItem(WHATSAPP_COEX_CONFIG_ID_STORAGE_KEY, String(whatsappCoexConfigId || '').trim());
    } catch {}
  }, [whatsappCoexConfigId]);

  useEffect(() => {
    if (activeTab !== 1 || projectSettingsSection !== 'whatsapp') return;
    const hasFb = !!(window as any)?.FB;
    if (hasFb) {
      setWhatsappCoexSdkReady(true);
      setWhatsappCoexSdkLoading(false);
      return;
    }
    setWhatsappCoexSdkLoading(true);
    const existing = document.getElementById('facebook-jssdk');
    let pollTimer: number | null = null;
    const finishReady = () => {
      setWhatsappCoexSdkReady(true);
      setWhatsappCoexSdkLoading(false);
      setWhatsappCoexError(null);
    };
    if (existing) {
      pollTimer = window.setInterval(() => {
        if ((window as any)?.FB) {
          if (pollTimer) window.clearInterval(pollTimer);
          finishReady();
        }
      }, 300);
      return () => {
        if (pollTimer) window.clearInterval(pollTimer);
      };
    }
    const prevInit = (window as any).fbAsyncInit;
    (window as any).fbAsyncInit = () => {
      try {
        if (typeof prevInit === 'function') prevInit();
      } catch {}
      finishReady();
    };
    const js = document.createElement('script');
    js.id = 'facebook-jssdk';
    js.async = true;
    js.defer = true;
    js.src = 'https://connect.facebook.net/en_US/sdk.js';
    js.onerror = () => {
      setWhatsappCoexSdkLoading(false);
      setWhatsappCoexSdkReady(false);
      setWhatsappCoexError('Failed to load Facebook SDK.');
    };
    const firstScript = document.getElementsByTagName('script')[0];
    if (firstScript?.parentNode) firstScript.parentNode.insertBefore(js, firstScript);
    else document.body.appendChild(js);
  }, [activeTab, projectSettingsSection]);

  useEffect(() => {
    if (activeTab !== 1 || projectSettingsSection !== 'whatsapp') return;
    const onMessage = (event: MessageEvent) => {
      if (!whatsappCoexRunIdRef.current) return;
      const origin = String(event.origin || '');
      if (origin !== 'https://www.facebook.com' && origin !== 'https://web.facebook.com') return;
      let parsed: any = event.data;
      if (typeof parsed === 'string') {
        try {
          parsed = JSON.parse(parsed);
        } catch {
          return;
        }
      }
      if (!parsed || typeof parsed !== 'object') return;
      if (String(parsed?.type || '') !== 'WA_EMBEDDED_SIGNUP') return;
      whatsappCoexEventSeenRef.current = true;
      setWhatsappCoexEvent(parsed);
      whatsappCoexEventRef.current = parsed;
      const evt = String(parsed?.event || '').toUpperCase();
      if (evt === 'ERROR') {
        setWhatsappCoexError(
          String(parsed?.data?.error_message || parsed?.data?.error || 'Embedded Signup returned an error.')
        );
      } else if (evt === 'CANCEL') {
        setWhatsappCoexError('Embedded Signup was cancelled before completion.');
      } else if (evt === 'FINISH') {
        setWhatsappCoexError(null);
      }
    };
    window.addEventListener('message', onMessage);
    return () => window.removeEventListener('message', onMessage);
  }, [activeTab, projectSettingsSection]);

  useEffect(() => {
    return () => {
      clearWhatsappCoexTimers();
      whatsappCoexRunIdRef.current = 0;
    };
  }, [clearWhatsappCoexTimers]);

  const defaultCompanyProfile = useMemo(() => companyProfiles[0] || null, [companyProfiles]);
  useEffect(() => {
    return () => {
      if (officeDocPdfPreviewUrl) URL.revokeObjectURL(officeDocPdfPreviewUrl);
    };
  }, [officeDocPdfPreviewUrl]);

  const activeDocCompanyProfile = useMemo(() => {
    const firstProjectId = String(officeDocumentTemplateProjectIds[0] || '').trim();
    if (!firstProjectId) return defaultCompanyProfile;
    const project = projects.find((p: any) => String(p?.id || '') === firstProjectId);
    const cpId = String(project?.companyProfileId || '').trim();
    if (!cpId) return defaultCompanyProfile;
    return companyProfiles.find((c: any) => String(c?.id || '') === cpId) || defaultCompanyProfile;
  }, [companyProfiles, defaultCompanyProfile, officeDocumentTemplateProjectIds, projects]);
  const activeDocProject = useMemo(() => {
    const firstProjectId = String(officeDocumentTemplateProjectIds[0] || '').trim();
    if (!firstProjectId) return null;
    return projects.find((p: any) => String(p?.id || '') === firstProjectId) || null;
  }, [officeDocumentTemplateProjectIds, projects]);

  useEffect(() => {
    if (!officeDocumentTemplateDrawerOpen) return;
    const projectIds = Array.from(
      new Set(
        (officeDocumentTemplateProjectIds || [])
          .map((value) => String(value || '').trim())
          .filter(Boolean)
      )
    );
    if (!projectIds.length) {
      setOfficeDocumentTemplateExternalBankOptions([]);
      setOfficeDocumentTemplateExternalBankLoading(false);
      return;
    }
    let cancelled = false;
    const normalize = (value: any) => String(value || '').trim();
    const run = async () => {
      setOfficeDocumentTemplateExternalBankLoading(true);
      const nameSet = new Set<string>();
      for (const projectId of projectIds) {
        try {
          const txSnap = await getDocs(
            query(collection(db, 'projects', projectId, 'bankTransactions'), limit(1200))
          );
          txSnap.docs.forEach((docSnap) => {
            const row = docSnap.data() as any;
            [row?.payerBankName, row?.bank].forEach((value) => {
              const bankName = normalize(value);
              if (bankName) nameSet.add(bankName);
            });
          });
        } catch (err) {
          console.warn('Load bank names from transactions failed', err);
        }
        try {
          const unitSnap = await getDocs(
            query(collection(db, 'projects', projectId, 'units'), limit(800))
          );
          const unitIds = unitSnap.docs.map((unitDoc) => String(unitDoc.id || '').trim()).filter(Boolean);
          const casePromises = unitIds.map((unitId) =>
            getDocs(query(collection(db, 'projects', projectId, 'units', unitId, 'cases'), limit(20)))
          );
          const caseSnapshots = await Promise.all(casePromises);
          caseSnapshots.forEach((caseSnap) => {
            caseSnap.docs.forEach((caseDoc) => {
              if (!String(caseDoc.id || '').startsWith('loan_')) return;
              const row = caseDoc.data() as any;
              const stepData = (((row?.stepData || {}) as Record<string, any>) || {});
              Object.values(stepData).forEach((value: any) => {
                const bankName = normalize(value?.sanctionBankName);
                if (bankName) nameSet.add(bankName);
              });
            });
          });
        } catch (err) {
          console.warn('Load bank names from sanction forms failed', err);
        }
      }
      if (cancelled) return;
      setOfficeDocumentTemplateExternalBankOptions(
        Array.from(nameSet).sort((a, b) => a.localeCompare(b, undefined, { sensitivity: 'base' }))
      );
      setOfficeDocumentTemplateExternalBankLoading(false);
    };
    run().catch((err) => {
      if (cancelled) return;
      console.warn('Load external bank names failed', err);
      setOfficeDocumentTemplateExternalBankLoading(false);
    });
    return () => {
      cancelled = true;
    };
  }, [db, officeDocumentTemplateDrawerOpen, officeDocumentTemplateProjectIds]);

  useEffect(() => {
    // Draft output is always shown under Draft Documents by default.
    if (officeDocGeneratedOutputType === 'draft' && officeDocShowInSection !== 'draft_documents') {
      setOfficeDocShowInSection('draft_documents');
    }
  }, [officeDocGeneratedOutputType, officeDocShowInSection]);

  const officeDocPlaceholders = useMemo(
    () => [
      '{{project_name}}',
      '{{unit_no}}',
      '{{unit_number}}',
      '{{allottee_names}}',
      '{{allottee_name}}',
      '{{allottee_primary_name}}',
      '{{allottee_names_lines}}',
      '{{allottee_names_comma_and}}',
      '{{allottee_names_signature_block}}',
      '{{amount}}',
      '{{unit_amount}}',
      '{{payment_condition_roadmap}}',
      '{{issue_date}}',
      '{{ref_no}}',
      '{{carpet_area}}',
      '{{area_of_exclusive_balcony}}',
      '{{area_of_exclusive_wash}}',
      '{{area_of_exclusive_open_terrace}}',
      '{{vanvechayel_area}}',
      '{{built_up_area}}',
      '{{building_no}}',
      '{{floor_eng}}',
      '{{customer_mobile}}',
      '{{loan_bank_name}}',
      '{{loan_sanction_amount}}',
      '{{reason_not_taking_sbi}}',
      '{{pending_amount}}',
      '{{gst}}',
      '{{stamp_duty}}',
      '{{registration_charges}}',
      '{{agreement_date}}',
      '{{sale_agreement_date}}',
      '{{deed_date}}',
      '{{agreement_number}}',
      '{{deed_number}}',
      '{{advocate_agreement_fee}}',
      '{{advocate_deed_fee}}',
      '{{advocate_agreement_fee_cheque_no}}',
      '{{advocate_agreement_fee_date}}',
      '{{advocate_deed_fee_cheque_no}}',
      '{{advocate_deed_fee_date}}',
      '{{chq_date}}',
      '{{ref_chq_no}}',
      '{{bank}}',
      '{{deposit}}',
      '{{deposit_in_words}}',
      '{{payment_sequence_no}}',
      '{{payment_no}}',
      '{{no_of_payment}}',
      '{{receipt_no}}',
      '{{receipt_towards}}',
      '{{payments_rows}}',
      '{{gst_rows}}',
      '{{pay_total}}',
      '{{pay_balance}}',
      '{{gst_total}}',
      '{{gst_balance}}',
      '{{pay_bank_name_1}}',
      '{{pay_cheque_no_1}}',
      '{{pay_date_1}}',
      '{{pay_amount_1}}',
      '{{pay_bank_name_2}}',
      '{{pay_cheque_no_2}}',
      '{{pay_date_2}}',
      '{{pay_amount_2}}',
      '{{pay_bank_name_3}}',
      '{{pay_cheque_no_3}}',
      '{{pay_date_3}}',
      '{{pay_amount_3}}',
      '{{gst_bank_name_1}}',
      '{{gst_cheque_no_1}}',
      '{{gst_date_1}}',
      '{{gst_amount_1}}',
      '{{gst_bank_name_2}}',
      '{{gst_cheque_no_2}}',
      '{{gst_date_2}}',
      '{{gst_amount_2}}',
      '{{gst_bank_name_3}}',
      '{{gst_cheque_no_3}}',
      '{{gst_date_3}}',
      '{{gst_amount_3}}',
      '{{stamp_cheque_no}}',
      '{{stamp_date}}',
      '{{registration_cheque_no}}',
      '{{registration_date}}',
      '{{advocate_fee}}',
      '{{advocate_fee_cheque_no}}',
      '{{advocate_fee_date}}',
      '{{other_amount}}',
      '{{other_cheque_no}}',
      '{{other_date}}',
      '{{common_number}}',
      '{{applicant_number}}',
      '{{common_date}}',
      '{{applicant_date}}',
      '{{common_amount}}',
      '{{applicant_amount}}',
      '{{doc_serial_no}}',
      '{{request_date}}',
      '{{request_time}}',
      '{{request_datetime}}',
      '{{request_datetime_ampm}}',
      '{{company_logo_url}}',
      '{{project_logo_url}}',
      '{{project_document_image_url}}',
      '{{header_left_text}}',
      '{{header_right_text}}',
      '{{company_address}}',
      '{{company_mobile}}',
      '{{company_email}}',
      '{{company_name}}',
      '{{employee_name}}',
      '{{employee_mobile}}',
      '{{employee_address}}',
      '{{employee_designation}}',
      '{{employee_duty_timing}}',
      '{{employee_joining_date}}',
      '{{employee_salary}}',
      '{{employee_salary_words}}',
      '{{employee_company_name}}',
      '{{employee_project_name}}',
      '{{employee_work_location}}',
      '{{employee_salary_mode}}',
      '{{employee_allowed_holidays}}',
      '{{employee_tds_percent}}',
    ],
    []
  );

  const officeDocPaperConfig = useMemo(() => {
    const bySize: Record<'A4' | 'A5', { widthMm: number; heightMm: number }> = {
      A4: { widthMm: 210, heightMm: 297 },
      A5: { widthMm: 148, heightMm: 210 },
    };
    return bySize[officeDocPaperSize] || bySize.A4;
  }, [officeDocPaperSize]);

  const officeDocPaperWidthPx = useMemo(() => officeDocPaperConfig.widthMm * 3.7795275591, [officeDocPaperConfig.widthMm]);
  const officeDocPaperHeightPx = useMemo(() => officeDocPaperConfig.heightMm * 3.7795275591, [officeDocPaperConfig.heightMm]);

  const formatDdMmYyyy = useCallback((value: Date) => {
    const day = String(value.getDate()).padStart(2, '0');
    const month = String(value.getMonth() + 1).padStart(2, '0');
    const year = String(value.getFullYear());
    return `${day}-${month}-${year}`;
  }, []);

  const formatContactsBookDate = useCallback((value: any) => {
    if (!value) return '—';
    if (value?.toDate) {
      const date = value.toDate();
      return date instanceof Date && !Number.isNaN(date.getTime()) ? formatDdMmYyyy(date) : '—';
    }
    if (value instanceof Date) {
      return !Number.isNaN(value.getTime()) ? formatDdMmYyyy(value) : '—';
    }
    const raw = String(value || '').trim();
    if (!raw) return '—';
    const ymd = raw.match(/^(\d{4})-(\d{2})-(\d{2})$/);
    if (ymd) return `${ymd[3]}-${ymd[2]}-${ymd[1]}`;
    const dmy = raw.match(/^(\d{1,2})[/-](\d{1,2})[/-](\d{4})$/);
    if (dmy) return `${String(dmy[1]).padStart(2, '0')}-${String(dmy[2]).padStart(2, '0')}-${dmy[3]}`;
    const parsed = new Date(raw);
    return !Number.isNaN(parsed.getTime()) ? formatDdMmYyyy(parsed) : raw;
  }, [formatDdMmYyyy]);

  const contactsBookDateSortValue = useCallback((value: any) => {
    if (!value) return null;
    if (value?.toDate) {
      const date = value.toDate();
      return date instanceof Date && !Number.isNaN(date.getTime()) ? date.getTime() : null;
    }
    if (value instanceof Date) return !Number.isNaN(value.getTime()) ? value.getTime() : null;
    const raw = String(value || '').trim();
    if (!raw) return null;
    const ymd = raw.match(/^(\d{4})-(\d{2})-(\d{2})$/);
    if (ymd) {
      const date = new Date(Number(ymd[1]), Number(ymd[2]) - 1, Number(ymd[3]));
      return !Number.isNaN(date.getTime()) ? date.getTime() : null;
    }
    const dmy = raw.match(/^(\d{1,2})[/-](\d{1,2})[/-](\d{4})$/);
    if (dmy) {
      const date = new Date(Number(dmy[3]), Number(dmy[2]) - 1, Number(dmy[1]));
      return !Number.isNaN(date.getTime()) ? date.getTime() : null;
    }
    const parsed = new Date(raw);
    return !Number.isNaN(parsed.getTime()) ? parsed.getTime() : null;
  }, []);

  const sortedContactsBookRows = useMemo(() => {
    const direction = contactsBookSortDirection === 'desc' ? -1 : 1;
    const textValue = (value: any) => String(value || '').trim().toLowerCase();
    return [...contactsBookRows].sort((a: any, b: any) => {
      let result = 0;
      if (contactsBookSortBy === 'dob' || contactsBookSortBy === 'anniversary') {
        const aDate = contactsBookDateSortValue(contactsBookSortBy === 'dob' ? (a.dateOfBirth || a.dob) : a.anniversaryDate);
        const bDate = contactsBookDateSortValue(contactsBookSortBy === 'dob' ? (b.dateOfBirth || b.dob) : b.anniversaryDate);
        if (aDate == null && bDate == null) result = 0;
        else if (aDate == null) result = 1;
        else if (bDate == null) result = -1;
        else result = aDate - bDate;
      } else if (contactsBookSortBy === 'status') {
        result = Number(a.active === false) - Number(b.active === false);
      } else {
        const aText = contactsBookSortBy === 'type'
          ? textValue(a.type || a.contactType || 'Other')
          : textValue(a.name || a.displayName);
        const bText = contactsBookSortBy === 'type'
          ? textValue(b.type || b.contactType || 'Other')
          : textValue(b.name || b.displayName);
        result = aText.localeCompare(bText);
      }
      if (result === 0) {
        result = textValue(a.name || a.displayName).localeCompare(textValue(b.name || b.displayName));
      }
      return result * direction;
    });
  }, [contactsBookDateSortValue, contactsBookRows, contactsBookSortBy, contactsBookSortDirection]);

  const toggleContactsBookSort = useCallback((key: 'name' | 'type' | 'dob' | 'anniversary' | 'status') => {
    setContactsBookSortBy((current) => {
      if (current !== key) {
        setContactsBookSortDirection('asc');
        return key;
      }
      setContactsBookSortDirection((direction) => direction === 'asc' ? 'desc' : 'asc');
      return current;
    });
  }, []);

  const formatDateTimeAmPm = useCallback((value: Date) => {
    const day = String(value.getDate()).padStart(2, '0');
    const month = String(value.getMonth() + 1).padStart(2, '0');
    const year = String(value.getFullYear());
    const hours = value.getHours();
    const minutes = String(value.getMinutes()).padStart(2, '0');
    const ampm = hours >= 12 ? 'PM' : 'AM';
    const hour12 = String((hours % 12) || 12).padStart(2, '0');
    return `${day}-${month}-${year} ${hour12}:${minutes} ${ampm}`;
  }, []);
  const formatDateTimeAmPmCompact = useCallback(
    (value: Date) => formatDateTimeAmPm(value).replace(/[-:\s]/g, ''),
    [formatDateTimeAmPm]
  );

  const generateOfficeDocRefNo = (projectIds: string[]) => {
    const projectId = String(projectIds?.[0] || 'GEN').trim().toUpperCase().replace(/[^A-Z0-9]/g, '');
    const projectSeed = (projectId || 'GEN').slice(0, 6);
    const unique = `${Date.now()}${Math.floor(Math.random() * 1000)}`.slice(-8);
    return `${projectSeed}/${unique}`;
  };

  const joinNamesCommaAnd = (names: string[]) => {
    const clean = (names || []).map((n) => String(n || '').trim()).filter(Boolean);
    if (clean.length <= 1) return clean[0] || '';
    if (clean.length === 2) return `${clean[0]} and ${clean[1]}`;
    return `${clean.slice(0, -1).join(', ')} and ${clean[clean.length - 1]}`;
  };

  const buildAllotteeSignatureBlock = (names: string[]) => {
    const clean = (names || []).map((n) => String(n || '').trim()).filter(Boolean);
    if (!clean.length) return '';
    const esc = (value: string) =>
      String(value || '')
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&#39;');
    return clean
      .map(
        (name) => `
<div style="margin-top:16px;">
  <div style="height:36px;"></div>
  <div style="border-top:1px solid #000;max-width:260px;"></div>
  <div style="margin-top:4px;">${esc(name)}</div>
</div>`
      )
      .join('');
  };

  useEffect(() => {
    let cancelled = false;
    const loadPreviewUnits = async () => {
      const projectId = String(activeDocProject?.id || '').trim();
      if (!officeDocumentTemplateDrawerOpen || !projectId) {
        setOfficeDocPreviewUnits([]);
        setOfficeDocPreviewUnitId('');
        setOfficeDocPreviewSampleData(null);
        return;
      }
      setOfficeDocPreviewSampleLoading(true);
      try {
        const unitsSnap = await getDocs(
          query(
            collection(db, 'projects', projectId, 'units'),
            where('bookingStatus', 'in', ['blocked', 'booked', 'sold']),
            limit(25)
          )
        );
        const units = unitsSnap.docs.map((d) => ({ id: d.id, ...(d.data() as any) }))
          .sort((a, b) => String(a?.unitNumber || a?.id || '').localeCompare(String(b?.unitNumber || b?.id || '')));
        if (cancelled) return;
        setOfficeDocPreviewUnits(units);
        if (!units.length) {
          setOfficeDocPreviewUnitId('');
          setOfficeDocPreviewSampleData(null);
          return;
        }
        setOfficeDocPreviewUnitId((prev) =>
          units.some((row) => String(row.id) === String(prev || '')) ? prev : String(units[0].id || '')
        );
      } catch {
        if (!cancelled) {
          setOfficeDocPreviewUnits([]);
          setOfficeDocPreviewUnitId('');
          setOfficeDocPreviewSampleData(null);
        }
      } finally {
        if (!cancelled) setOfficeDocPreviewSampleLoading(false);
      }
    };
    loadPreviewUnits();
    return () => {
      cancelled = true;
    };
  }, [activeDocProject?.id, db, officeDocumentTemplateDrawerOpen]);

  useEffect(() => {
    let cancelled = false;
    const loadBookedUnitPreview = async () => {
      const projectId = String(activeDocProject?.id || '').trim();
      const selectedUnitId = String(officeDocPreviewUnitId || '').trim();
      if (!officeDocumentTemplateDrawerOpen || !projectId || !selectedUnitId) {
        setOfficeDocPreviewSampleData(null);
        return;
      }
      const pickedUnit = officeDocPreviewUnits.find((row) => String(row?.id || '') === selectedUnitId) || null;
      if (!pickedUnit) {
        setOfficeDocPreviewSampleData(null);
        return;
      }
      setOfficeDocPreviewSampleLoading(true);
      try {
        const buildCustomerDisplayName = (row: any) => {
          const direct = String(row?.displayName || row?.name || '').trim();
          if (direct) return direct;
          return [
            String(row?.firstName || '').trim(),
            String(row?.middleName || '').trim(),
            String(row?.surname || row?.lastName || '').trim(),
          ]
            .filter(Boolean)
            .join(' ')
            .trim();
        };

        let bookingId = String(pickedUnit.activeBookingId || '').trim();
        let activeBookingRow: any = null;
        if (!bookingId) {
          const bookingsSnap = await getDocs(
            query(collection(db, 'projects', projectId, 'units', pickedUnit.id, 'bookings'), limit(20))
          );
          const bookingRows = bookingsSnap.docs.map((d) => ({ id: d.id, ...(d.data() as any) }));
          const pickBooking = (rows: any[]) =>
            rows
              .slice()
              .sort((a, b) => {
                const toMillis = (value: any) =>
                  typeof value?.toMillis === 'function'
                    ? value.toMillis()
                    : typeof value?.seconds === 'number'
                    ? value.seconds * 1000
                    : 0;
                const updatedA = toMillis(a?.updatedAt || a?.createdAt);
                const updatedB = toMillis(b?.updatedAt || b?.createdAt);
                if (updatedA !== updatedB) return updatedB - updatedA;
                return String(b?.id || '').localeCompare(String(a?.id || ''));
              })[0] || null;
          activeBookingRow =
            bookingRows.find((row) => row?.isActive === true) ||
            bookingRows.find((row) => String(row?.status || '').trim().toLowerCase() === 'active') ||
            pickBooking(bookingRows);
          bookingId = String(activeBookingRow?.id || '').trim();
        } else {
          activeBookingRow = { id: bookingId, shortBookingCode: null };
        }
        if (bookingId && (!activeBookingRow || !activeBookingRow.shortBookingCode)) {
          const bookingSnap = await getDoc(
            doc(db, 'projects', projectId, 'units', pickedUnit.id, 'bookings', bookingId)
          );
          if (bookingSnap.exists()) {
            activeBookingRow = { id: bookingSnap.id, ...(bookingSnap.data() as any) };
          }
        }

        let customers: any[] = [];
        if (bookingId) {
          const customersSnap = await getDocs(
            query(
              collection(db, 'projects', projectId, 'units', pickedUnit.id, 'bookings', bookingId, 'customers'),
              limit(10)
            )
          );
          customers = customersSnap.docs.map((d) => ({ id: d.id, ...(d.data() as any) }));
        }
        const primaryCustomer = (customers[0] || null) as any;

        const txSnap = await getDocs(
          query(collection(db, 'projects', projectId, 'bankTransactions'), where('unitId', '==', pickedUnit.id), limit(50))
        );
        const txRows = txSnap.docs.map((d) => ({ id: d.id, ...(d.data() as any) }));
        const customerReceiptTotal = txRows.reduce((sum: number, row: any) => {
          const amount = Number(row?.amount || 0);
          if (!Number.isFinite(amount) || amount <= 0) return sum;
          return String(row?.receiptCategory || '').trim().toLowerCase() === 'customer'
            ? sum + amount
            : sum;
        }, 0);
        const mainCaseSnap =
          bookingId
            ? await getDoc(doc(db, 'projects', projectId, 'units', pickedUnit.id, 'cases', bookingId))
            : null;
        const mainCaseRow = mainCaseSnap?.exists() ? (mainCaseSnap.data() as any) : null;
        const loanCaseSnap =
          bookingId
            ? await getDoc(doc(db, 'projects', projectId, 'units', pickedUnit.id, 'cases', `loan_${bookingId}`))
            : null;
        const loanCaseRow = loanCaseSnap?.exists() ? (loanCaseSnap.data() as any) : null;
        const parsePreviewDate = (raw: any): Date | null => {
          if (!raw) return null;
          if (typeof raw?.toDate === 'function') {
            const dt = raw.toDate();
            return dt instanceof Date && !Number.isNaN(dt.getTime()) ? dt : null;
          }
          if (raw instanceof Date) return Number.isNaN(raw.getTime()) ? null : raw;
          if (typeof raw === 'number') {
            const dt = new Date(raw);
            return Number.isNaN(dt.getTime()) ? null : dt;
          }
          const text = String(raw || '').trim();
          if (!text) return null;
          const ymd = text.match(/^(\d{4})-(\d{1,2})-(\d{1,2})$/);
          if (ymd) {
            const dt = new Date(Number(ymd[1]), Number(ymd[2]) - 1, Number(ymd[3]));
            return Number.isNaN(dt.getTime()) ? null : dt;
          }
          const dmy = text.match(/^(\d{1,2})[/-](\d{1,2})[/-](\d{2,4})$/);
          if (dmy) {
            const yyyy = dmy[3].length === 2 ? Number(`20${dmy[3]}`) : Number(dmy[3]);
            const dt = new Date(yyyy, Number(dmy[2]) - 1, Number(dmy[1]));
            return Number.isNaN(dt.getTime()) ? null : dt;
          }
          const dt = new Date(text);
          return Number.isNaN(dt.getTime()) ? null : dt;
        };
        const pickAgreementDate = (): Date | null => {
          const mainStepData = ((mainCaseRow?.stepData || {}) as Record<string, any>) || {};
          const saleStep = mainStepData?.sale_agreement_date || {};
          const directCandidates = [
            saleStep?.sale_agreement_date,
            saleStep?.agreement_date,
            saleStep?.agreementDate,
            saleStep?.saleAgreementDate,
          ];
          for (const candidate of directCandidates) {
            const parsed = parsePreviewDate(candidate);
            if (parsed) return parsed;
          }
          const allStepRows = Object.values(mainStepData).filter((row) => row && typeof row === 'object');
          for (const row of allStepRows) {
            const parsed =
              parsePreviewDate((row as any)?.sale_agreement_date) ||
              parsePreviewDate((row as any)?.agreement_date) ||
              parsePreviewDate((row as any)?.agreementDate) ||
              parsePreviewDate((row as any)?.saleAgreementDate);
            if (parsed) return parsed;
          }
          return (
            parsePreviewDate((activeBookingRow as any)?.sale_agreement_date) ||
            parsePreviewDate((activeBookingRow as any)?.agreement_date) ||
            parsePreviewDate((activeBookingRow as any)?.agreementDate) ||
            parsePreviewDate((activeBookingRow as any)?.saleAgreementDate) ||
            parsePreviewDate((pickedUnit as any)?.sale_agreement_date) ||
            parsePreviewDate((pickedUnit as any)?.agreement_date) ||
            parsePreviewDate((pickedUnit as any)?.agreementDate) ||
            parsePreviewDate((pickedUnit as any)?.saleAgreementDate) ||
            null
          );
        };
        const agreementDate = pickAgreementDate();
        const loanStepDataMap = ((loanCaseRow?.stepData || {}) as Record<string, any>) || {};
        const latestLoanSanctionData =
          Object.values(loanStepDataMap)
            .filter((row: any) => row && typeof row === 'object')
            .map((row: any) => ({
              sanctionBankName: String(row?.sanctionBankName || '').trim(),
              sanctionAmount: Number(row?.sanctionAmount || 0) || 0,
              nonSbiReason: String(row?.nonSbiReason || '').trim(),
              sanctionDate: String(row?.sanctionDate || '').trim(),
            }))
            .filter((row) => row.sanctionBankName || row.sanctionAmount > 0 || row.nonSbiReason || row.sanctionDate)
            .sort((a, b) => String(b.sanctionDate || '').localeCompare(String(a.sanctionDate || '')))[0] || null;
        const receiptRows = txRows.filter((row) => String(row?.txnType || '').toLowerCase() === 'receipt');
        const toMs = (value: any) =>
          value?.toDate?.() instanceof Date
            ? value.toDate().getTime()
            : typeof value?.seconds === 'number'
            ? value.seconds * 1000
            : typeof value === 'number'
            ? value
            : 0;
        receiptRows.sort((a, b) => {
          const aMs = toMs(a?.txnDate) || toMs(a?.createdAt);
          const bMs = toMs(b?.txnDate) || toMs(b?.createdAt);
          return aMs - bMs;
        });
        const firstReceipt =
          receiptRows.find((row) => String(row?.receiptCategory || '').toLowerCase() === 'customer') ||
          receiptRows[0] ||
          null;
        const firstReceiptDate = firstReceipt?.txnDate?.toDate?.() instanceof Date
          ? firstReceipt.txnDate.toDate()
          : new Date();
        const unitAmount = Number(pickedUnit?.unitAmount || 0) || 0;
        const gstAmount = computeGstAmountForUnit(unitAmount, pickedUnit as any, activeDocProject as any, 1);
        const ceilHundred = (value: number) => Math.ceil(value / 100) * 100;
        const hasMale = customers.some((c) => String(c?.gender || '').trim().toLowerCase() === 'male');
        const stampDuty = unitAmount > 0 ? ceilHundred(ceilHundred(unitAmount) * 0.049) : 0;
        const registrationCharges =
          unitAmount <= 0
            ? 0
            : customers.length === 0 || hasMale
            ? ceilHundred(ceilHundred(unitAmount) * 0.01) + 1200
            : 1200;

        const previewData = {
          projectName: String(activeDocProject?.name || projectId),
          unitNumber: String(pickedUnit?.unitNumber || pickedUnit?.id || ''),
          shortBookingCode: String((activeBookingRow as any)?.shortBookingCode || ''),
          paymentCondition: (activeBookingRow as any)?.paymentCondition || null,
          bookingFormDate: String((activeBookingRow as any)?.bookingFormDate || ''),
          allotteeNames: customers.map((c) => buildCustomerDisplayName(c)).filter(Boolean),
          unitAmount,
          carpetArea: Number(pickedUnit?.carpetArea || 0) || 0,
          exclusiveBalconyArea: Number(pickedUnit?.exclusiveBalconyArea || 0) || 0,
          exclusiveWashArea:
            Number(
              (pickedUnit as any)?.exclusiveWashArea ||
              (pickedUnit as any)?.areaOfExclusiveWash ||
              (pickedUnit as any)?.area_of_exclusive_wash ||
              (pickedUnit as any)?.customFields?.area_of_exclusive_wash ||
              (pickedUnit as any)?.['AREA OF EXCLUSIVE WASH'] ||
              0
            ) || 0,
          openTerraceArea:
            Number(
              (pickedUnit as any)?.openTerraceArea ||
              (pickedUnit as any)?.areaOfExclusiveOpenTerrace ||
              (pickedUnit as any)?.area_of_exclusive_open_terrace ||
              (pickedUnit as any)?.customFields?.area_of_exclusive_open_terrace ||
              (pickedUnit as any)?.['AREA OF EXCLUSIVE OPEN TERRACE'] ||
              0
            ) || 0,
          vanvechayelArea:
            Number((pickedUnit as any)?.vanvechayelArea || (pickedUnit as any)?.vanvechayel_area || 0) || 0,
          builtUpArea:
            Number((pickedUnit as any)?.builtUpArea || (pickedUnit as any)?.built_up_area || 0) || 0,
          buildingNo: String((pickedUnit as any)?.buildingNo || (pickedUnit as any)?.building_no || ''),
          floorEng: String((pickedUnit as any)?.floorEng || (pickedUnit as any)?.floor_eng || ''),
          customerMobile: String(
            primaryCustomer?.mobile ||
            primaryCustomer?.mobileNo ||
            primaryCustomer?.phone ||
            primaryCustomer?.phoneNumber ||
            ''
          ).trim(),
          loanBankName: String(latestLoanSanctionData?.sanctionBankName || '').trim(),
          loanSanctionAmount: Number(latestLoanSanctionData?.sanctionAmount || 0) || 0,
          reasonNotTakingSbi: String(latestLoanSanctionData?.nonSbiReason || '').trim(),
          customerReceiptTotal,
          gst: gstAmount,
          stampDuty,
          registrationCharges,
          bookingId: bookingId || '',
          agreementDate,
          chqDate: firstReceiptDate,
          refChqNo: String(firstReceipt?.referenceNo || ''),
          bank: String(firstReceipt?.bankName || firstReceipt?.payerBankName || ''),
          deposit: Number(firstReceipt?.amount || 0) || 0,
          transactions: txRows,
        };
        if (!cancelled) setOfficeDocPreviewSampleData(previewData);
      } catch {
        if (!cancelled) setOfficeDocPreviewSampleData(null);
      } finally {
        if (!cancelled) setOfficeDocPreviewSampleLoading(false);
      }
    };
    loadBookedUnitPreview();
    return () => {
      cancelled = true;
    };
  }, [
    activeDocProject,
    db,
    officeDocumentTemplateDrawerOpen,
    officeDocPreviewUnitId,
    officeDocPreviewUnits,
  ]);

  const officeDocResolvedPreviewRefNo = useMemo(() => {
    const normalizedPrefix = normalizeOfficeRefPrefix(officeDocRefPrefix || '');
    if (officeDocRefMode === 'unit_short_booking_code') {
      return buildBookingShortRefNo(
        normalizedPrefix,
        String((officeDocPreviewSampleData as any)?.shortBookingCode || '').trim(),
        '123456'
      );
    }
    return buildSequenceRefNo(normalizedPrefix, 1);
  }, [officeDocPreviewSampleData, officeDocRefMode, officeDocRefPrefix]);

  const officeDocPreviewTokenValues = useMemo(() => {
    const sample = officeDocPreviewSampleData;
    const amountToInr = (value: number) => (Number(value || 0) > 0 ? Number(value || 0).toLocaleString('en-IN') : '');
    const allotteeNames = (sample?.allotteeNames || [])
      .map((value: any) => String(value || '').trim())
      .filter(Boolean);
    const issueDateValue = formatDdMmYyyy(new Date());
    const refNoValue = String(officeDocResolvedPreviewRefNo || officeDocRefNo || '').trim();
    const now = new Date();
    const requestDateValue = formatDdMmYyyy(now);
    const requestTimeValue = now.toLocaleTimeString('en-IN', {
      hour: '2-digit',
      minute: '2-digit',
      hour12: true,
    });
    const requestDateTimeValue = formatDateTimeAmPm(now);
    const requestDateTimeAmPmCompactValue = formatDateTimeAmPmCompact(now);
    const agreementDatePreviewValue =
      sample?.agreementDate instanceof Date
        ? formatDdMmYyyy(sample.agreementDate)
        : '';
    const paymentTokens = buildPaymentTemplateTokens({
      transactions: Array.isArray((sample as any)?.transactions) ? ((sample as any).transactions as any[]) : [],
      unitAmount: Number(sample?.unitAmount || 0) || 0,
      gstAmount: Number(sample?.gst || 0) || 0,
      formatDate: formatDdMmYyyy,
      formatAmount: (value) => amountToInr(value),
    });
    const stampDutyToken = String(paymentTokens['{{stamp_duty}}'] || '').trim();
    const registrationChargesToken = String(paymentTokens['{{registration_charges}}'] || '').trim();
    const advocateAgreementFeeToken = String(paymentTokens['{{advocate_agreement_fee}}'] || '').trim();
    const advocateDeedFeeToken = String(paymentTokens['{{advocate_deed_fee}}'] || '').trim();
    const advocateFeeToken = String(paymentTokens['{{advocate_fee}}'] || '').trim();
    return {
      ...paymentTokens,
      ...buildUniversalOfficeTemplateTokens({
        primaryCustomerMobile: String((sample as any)?.customerMobile || ''),
        loanBankName: String((sample as any)?.loanBankName || ''),
        loanSanctionAmount: Number((sample as any)?.loanSanctionAmount || 0) || 0,
        reasonNotTakingSbi: String((sample as any)?.reasonNotTakingSbi || ''),
        pendingAmount:
          (Number(sample?.unitAmount || 0) || 0) -
          (Number((sample as any)?.customerReceiptTotal || 0) || 0),
        formatAmount: (value) => amountToInr(value),
      }),
      '{{project_name}}': String(sample?.projectName || activeDocProject?.name || ''),
      '{{unit_no}}': String(sample?.unitNumber || ''),
      '{{unit_number}}': String(sample?.unitNumber || ''),
      '{{allottee_names}}': String(allotteeNames.join(', ')),
      '{{allottee_name}}': String(allotteeNames.join(', ')),
      '{{allottee_primary_name}}': String(allotteeNames[0] || ''),
      '{{allottee_names_lines}}': allotteeNames.join('<br/>'),
      '{{allottee_names_comma_and}}': joinNamesCommaAnd(allotteeNames),
      '{{allottee_names_signature_block}}': buildAllotteeSignatureBlock(allotteeNames),
      '{{amount}}': amountToInr(sample?.unitAmount || 0),
      '{{unit_amount}}': amountToInr(sample?.unitAmount || 0),
      '{{payment_condition_roadmap}}': sample?.paymentCondition && !validatePaymentCondition(sample.paymentCondition as PaymentCondition)
        ? paymentConditionHtml(sample.paymentCondition as PaymentCondition, String(sample.bookingFormDate || ''))
        : '',
      '{{issue_date}}': issueDateValue,
      '{{ref_no}}': refNoValue,
      '{{receipt_no}}': `${refNoValue}-${String(paymentTokens['{{payment_sequence_no}}'] || '1')}`,
      '{{receipt_towards}}': 'Sale consideration of the above flat',
      '{{carpet_area}}': String(sample?.carpetArea ?? ''),
      '{{area_of_exclusive_balcony}}': String(sample?.exclusiveBalconyArea ?? ''),
      '{{area_of_exclusive_wash}}': String(sample?.exclusiveWashArea ?? ''),
      '{{area_of_exclusive_open_terrace}}': String(sample?.openTerraceArea ?? ''),
      '{{vanvechayel_area}}': String(sample?.vanvechayelArea ?? ''),
      '{{built_up_area}}': String(sample?.builtUpArea ?? ''),
      '{{building_no}}': String(sample?.buildingNo || ''),
      '{{floor_eng}}': String(sample?.floorEng || ''),
      '{{gst}}': amountToInr(sample?.gst || 0),
      '{{stamp_duty}}': stampDutyToken || amountToInr(sample?.stampDuty || 0),
      '{{registration_charges}}': registrationChargesToken || amountToInr(sample?.registrationCharges || 0),
      '{{agreement_date}}': agreementDatePreviewValue,
      '{{sale_agreement_date}}': agreementDatePreviewValue,
      '{{deed_date}}': issueDateValue,
      '{{agreement_number}}': '',
      '{{deed_number}}': '',
      '{{advocate_agreement_fee}}':
        advocateAgreementFeeToken || amountToInr(Number(activeDocProject?.advocateAgreementFee || 0) || 0),
      '{{advocate_deed_fee}}':
        advocateDeedFeeToken || amountToInr(Number(activeDocProject?.advocateDeedFee || 0) || 0),
      '{{advocate_fee}}':
        advocateFeeToken ||
        amountToInr(
          (Number(activeDocProject?.advocateAgreementFee || 0) || 0) +
            (Number(activeDocProject?.advocateDeedFee || 0) || 0)
        ),
      '{{chq_date}}': sample?.chqDate instanceof Date ? formatDdMmYyyy(sample.chqDate) : '',
      '{{ref_chq_no}}': String(sample?.refChqNo || ''),
      '{{bank}}': String(sample?.bank || ''),
      '{{deposit}}': amountToInr(sample?.deposit || 0),
      '{{request_date}}': requestDateValue,
      '{{request_time}}': requestTimeValue,
      '{{request_datetime}}': requestDateTimeValue,
      '{{request_datetime_ampm}}': requestDateTimeAmPmCompactValue,
      '{{header_left_text}}': String(officeDocCompanyHeaderText || ''),
      '{{header_right_text}}': String(officeDocProjectHeaderText || ''),
      '{{company_address}}': String(activeDocCompanyProfile?.address || ''),
      '{{company_mobile}}': String(activeDocCompanyProfile?.mobile || ''),
      '{{company_email}}': String(activeDocCompanyProfile?.email || ''),
      '{{company_name}}': String(activeDocCompanyProfile?.name || ''),
      '{{employee_name}}': 'Sample Employee',
      '{{employee_mobile}}': '9876543210',
      '{{employee_address}}': 'Employee address',
      '{{employee_designation}}': 'Site Supervisor',
      '{{employee_duty_timing}}': '10:00 AM to 7:00 PM',
      '{{employee_joining_date}}': issueDateValue,
      '{{employee_salary}}': '18,000',
      '{{employee_salary_words}}': 'Eighteen Thousand Rupees',
      '{{employee_company_name}}': String(activeDocCompanyProfile?.name || activeDocProject?.companyName || ''),
      '{{employee_project_name}}': String(activeDocProject?.name || ''),
      '{{employee_work_location}}': String(activeDocProject?.name || 'Office / Project Site'),
      '{{employee_salary_mode}}': 'Attendance based',
      '{{employee_allowed_holidays}}': '2',
      '{{employee_tds_percent}}': '0',
      '{{company_logo_url}}': String(
        resolveCompanyLogoForTemplate(activeDocCompanyProfile, Boolean(officeDocumentTemplateUseProjectLogoBw)) || ''
      ),
      '{{project_logo_url}}': String(
        (officeDocumentTemplateUseProjectLogoBw && String(activeDocProject?.logoBwUrl || '').trim()) ||
          activeDocProject?.logoUrl ||
          ''
      ),
      '{{project_document_image_url}}': String(activeDocProject?.documentImageUrl || ''),
    } as Record<string, string>;
  }, [
    activeDocCompanyProfile,
    activeDocProject?.documentImageUrl,
    activeDocProject?.logoUrl,
    activeDocProject?.logoBwUrl,
    activeDocProject?.name,
    activeDocProject?.companyName,
    activeDocProject?.advocateAgreementFee,
    activeDocProject?.advocateDeedFee,
    officeDocCompanyHeaderText,
    officeDocPreviewSampleData,
    officeDocProjectHeaderText,
    officeDocumentTemplateUseProjectLogoBw,
    officeDocRefNo,
    officeDocResolvedPreviewRefNo,
    formatDateTimeAmPm,
    formatDateTimeAmPmCompact,
    formatDdMmYyyy,
  ]);

  const applyOfficeDocPreviewTokens = useCallback(
    (input: string, highlight = false) => {
      const source = resolveTemplateCalcs(String(input || ''), officeDocPreviewTokenValues);
      if (!source.trim()) return source;
      const htmlTokens = new Set(['{{allottee_names_lines}}', '{{allottee_names_signature_block}}']);
      const parsed = new DOMParser().parseFromString(source, 'text/html');
      parsed.querySelectorAll('*').forEach((element) => {
        Array.from(element.attributes || []).forEach((attr) => {
          let value = String(attr.value || '');
          Object.entries(officeDocPreviewTokenValues).forEach(([token, tokenValue]) => {
            value = value.split(token).join(tokenValue);
          });
          if (value !== attr.value) element.setAttribute(attr.name, value);
        });
      });
      const walker = parsed.createTreeWalker(parsed.body, NodeFilter.SHOW_TEXT);
      const textNodes: Text[] = [];
      while (walker.nextNode()) textNodes.push(walker.currentNode as Text);
      textNodes.forEach((node) => {
        const raw = String(node.nodeValue || '');
        if (!raw) return;
        let changed = false;
        let html = escapeHtml(raw);
        Object.entries(officeDocPreviewTokenValues).forEach(([token, tokenValue]) => {
          const tokenEscaped = escapeHtml(token);
          if (!html.includes(tokenEscaped)) return;
          changed = true;
          const missingToken = !String(tokenValue || '').trim();
          const replacement = htmlTokens.has(token)
            ? (
              highlight ?
                (missingToken ?
                  `<span style="background:#ffe6e6;border:1px solid #e57373;color:#b71c1c;border-radius:3px;padding:0 2px;" title="Missing value">${escapeHtml(token)}</span>` :
                  `${tokenValue}<span style="background:#fff3b0;border:1px solid #ead27a;border-radius:3px;padding:0 2px;margin-left:4px;" title="${escapeHtml(token)}">token</span>`
                ) :
                tokenValue
            )
            : (highlight
              ? (
                missingToken ?
                  `<span style="background:#ffe6e6;border:1px solid #e57373;color:#b71c1c;border-radius:3px;padding:0 2px;" title="Missing value">${escapeHtml(token)}</span>` :
                  `<span style="background:#fff3b0;border:1px solid #ead27a;border-radius:3px;padding:0 2px;" title="${escapeHtml(token)}">${escapeHtml(tokenValue)}</span>`
              )
              : escapeHtml(tokenValue));
          html = html.split(tokenEscaped).join(replacement);
        });
        if (!changed) return;
        const wrapper = parsed.createElement('span');
        wrapper.innerHTML = html;
        node.parentNode?.replaceChild(wrapper, node);
      });
      return String(parsed.body?.innerHTML || source);
    },
    [officeDocPreviewTokenValues]
  );

  const extractVisualBodyFromHtml = (html: string) => {
    let source = String(html || '').trim();
    if (!source) return '';
    if (!hasHtmlMarkup(source) && hasEncodedHtmlMarkup(source)) {
      const decoded = new DOMParser().parseFromString(source, 'text/html');
      source = String(decoded.documentElement?.textContent || source).trim();
    }
    const withLineBreakHints = source
      .replace(/<\s*br\s*\/?\s*>/gi, '\n')
      .replace(/<\/(p|div|h1|h2|h3|h4|h5|h6|li|tr|table|section|article|header|footer|main)>/gi, '\n');
    const parsed = new DOMParser().parseFromString(withLineBreakHints, 'text/html');
    return String(parsed.body?.textContent || '')
      .replace(/\u00a0/g, ' ')
      .replace(/[ \t]+\n/g, '\n')
      .replace(/\n[ \t]+/g, '\n')
      .replace(/\n{3,}/g, '\n\n')
      .trim();
  };

  const hasHtmlMarkup = (value: string) => /<\s*\/?\s*[a-zA-Z][^>]*>/.test(String(value || ''));
  const hasEncodedHtmlMarkup = (value: string) => /&lt;\s*\/?\s*[a-zA-Z][^&]*&gt;/.test(String(value || ''));
  const escapeHtml = (value: string) =>
    String(value || '')
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');

  const toHtmlBodyFragment = (html: string) => {
    const source = String(html || '').trim();
    if (!source) return '';
    const parsed = new DOMParser().parseFromString(source, 'text/html');
    return String(parsed.body?.innerHTML || source).trim();
  };

  const deriveVisualBodyFromHtml = (html: string) => {
    const normalized = toHtmlBodyFragment(html);
    if (!normalized) return '';
    if (hasHtmlMarkup(normalized)) return normalized;
    if (hasEncodedHtmlMarkup(normalized)) {
      const decoded = new DOMParser().parseFromString(normalized, 'text/html');
      const decodedHtml = String(decoded.documentElement?.textContent || '').trim();
      if (hasHtmlMarkup(decodedHtml)) return decodedHtml;
    }
    return extractVisualBodyFromHtml(normalized);
  };

  const normalizeImportedDocxHtml = (html: string) => {
    const parsed = new DOMParser().parseFromString(String(html || ''), 'text/html');
    parsed.querySelectorAll('table').forEach((table) => {
      const style = table.getAttribute('style') || '';
      table.setAttribute(
        'style',
        `${style}${style && !style.trim().endsWith(';') ? ';' : ''}width:100%;border-collapse:collapse;border-spacing:0;`
      );
    });
    parsed.querySelectorAll('th, td').forEach((cell) => {
      const style = cell.getAttribute('style') || '';
      cell.setAttribute(
        'style',
        `${style}${style && !style.trim().endsWith(';') ? ';' : ''}border:1px solid #bbb;padding:6px;vertical-align:top;`
      );
    });
    return String(parsed.body?.innerHTML || '').trim();
  };

  const activeDocGujaratiFontUrl = String((activeDocCompanyProfile as any)?.gujaratiFontUrl || '').trim();
  const activeDocGujaratiFontFamily = String((activeDocCompanyProfile as any)?.gujaratiFontFamily || '').trim();
  const activeDocGujaratiFontFormat = String((activeDocCompanyProfile as any)?.gujaratiFontFormat || '').trim().toLowerCase();

  const officeDocVisualHtml = useMemo(() => {
    const esc = (value: string) =>
      String(value || '')
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&#39;');
    const toHtml = (value: string) => {
      const raw = String(value || '').trim();
      if (!raw) return '';
      if (hasHtmlMarkup(raw)) return raw;
      if (hasEncodedHtmlMarkup(raw)) {
        const decoded = new DOMParser().parseFromString(raw, 'text/html');
        const decodedHtml = String(decoded.documentElement?.textContent || '').trim();
        if (hasHtmlMarkup(decodedHtml)) return decodedHtml;
      }
      return raw
        .split(/\n{2,}/)
        .map((chunk) => chunk.trim())
        .filter(Boolean)
        .map((chunk) => `<p>${esc(chunk).replace(/\n/g, '<br/>')}</p>`)
        .join('');
    };
    const formatIndianFooterMobile = (value: string) => {
      const raw = String(value || '').trim();
      if (!raw) return '';
      const digits = raw.replace(/\D/g, '');
      if (!digits) return raw;
      const normalized = digits.length > 10 ? digits.slice(-10) : digits;
      if (!normalized) return raw;
      if (normalized.length <= 4) return `+91 ${normalized}`;
      if (normalized.length <= 7) return `+91 ${normalized.slice(0, 4)} ${normalized.slice(4)}`;
      return `+91 ${normalized.slice(0, 4)} ${normalized.slice(4, 7)} ${normalized.slice(7, 10)}`;
    };
    const title = esc(officeDocVisualTitle || 'Allotment Letter');
    const subtitle = esc(officeDocVisualSubtitle || '');
    const showTitle = Boolean(officeDocShowTitle);
    const showRefNo = Boolean(officeDocShowRefNo);
    const showDate = Boolean(officeDocShowDate);
    const showPageNumbers = Boolean(officeDocShowPageNumbers);
    const titleAlign = officeDocTitleAlign || 'left';
    const selectedDocFontFamily = String(officeDocFontFamily || 'Arial').trim() || 'Arial';
    const letterSpacingPx = Math.max(-2, Math.min(10, Number(officeDocLetterSpacingPx || 0) || 0));
    const body = normalizeImportedDocxHtml(toHtml(officeDocVisualBody));
    const footer = toHtml(officeDocVisualFooter);
    const issueDate = esc(formatDdMmYyyy(new Date()));
    const refNo = esc(officeDocResolvedPreviewRefNo || officeDocRefNo || '{{ref_no}}');
    const footerHeightMm = Math.max(0, Number(officeDocFooterHeightCm || 0) * 10);
    const companyLogoHeightMm = Math.max(0, Number(officeDocCompanyLogoHeightCm || 0) * 10);
    const projectLogoHeightMm = Math.max(0, Number(officeDocProjectLogoHeightCm || 0) * 10);
    const companyLogoOffsetXMm = Math.max(-300, Math.min(300, Number(officeDocCompanyLogoOffsetXCm || 0) * 10));
    const companyLogoOffsetYMm = Math.max(-300, Math.min(300, Number(officeDocCompanyLogoOffsetYCm || 0) * 10));
    const projectLogoOffsetXMm = Math.max(-300, Math.min(300, Number(officeDocProjectLogoOffsetXCm || 0) * 10));
    const projectLogoOffsetYMm = Math.max(-300, Math.min(300, Number(officeDocProjectLogoOffsetYCm || 0) * 10));
    const headerHorizontalOffsetMm = Math.max(-300, Math.min(300, Number(officeDocHeaderHorizontalOffsetCm || 0) * 10));
    const headerVerticalOffsetMm = Math.max(-300, Math.min(300, Number(officeDocHeaderVerticalOffsetCm || 0) * 10));
    const footerHorizontalOffsetMm = Math.max(-300, Math.min(300, Number(officeDocFooterHorizontalOffsetCm || 0) * 10));
    const footerVerticalOffsetMm = Math.max(-300, Math.min(300, Number(officeDocFooterVerticalOffsetCm || 0) * 10));
    const mainContentOffsetMm = Math.max(-100, Math.min(100, Number(officeDocMainContentOffsetCm || 0) * 10));
    const marginTopMm = Math.max(0, Number(officeDocMarginTopCm || 0) * 10);
    const marginRightMm = Math.max(0, Number(officeDocMarginRightCm || 0) * 10);
    const marginBottomMm = Math.max(0, Number(officeDocMarginBottomCm || 0) * 10);
    const marginLeftMm = Math.max(0, Number(officeDocMarginLeftCm || 0) * 10);
    const backgroundImageUrl = esc(
      String(activeDocProject?.backgroundImageUrl || '').trim()
    );
    const backgroundOpacity = Math.max(0, Math.min(0.4, Number(officeDocBackgroundOpacity || 0)));
    const backgroundScale = Math.max(10, Math.min(300, Number(officeDocBackgroundScale || 72)));
    const backgroundPosX = Math.max(-300, Math.min(300, Number(officeDocBackgroundPosX ?? -44)));
    const backgroundPosY = Math.max(-300, Math.min(300, Number(officeDocBackgroundPosY ?? 109)));
    const companyLogo = esc(
      String(resolveCompanyLogoForTemplate(activeDocCompanyProfile, Boolean(officeDocumentTemplateUseProjectLogoBw)) || '') ||
        '{{company_logo_url}}'
    );
    const projectLogo = esc(
      String(
        (officeDocumentTemplateUseProjectLogoBw && String(activeDocProject?.logoBwUrl || '').trim()) ||
          activeDocProject?.logoUrl ||
          ''
      ) || '{{project_logo_url}}'
    );
    const companyHeaderContentType = (
      officeDocCompanyHeaderContentType === 'text' || officeDocCompanyHeaderContentType === 'none'
        ? officeDocCompanyHeaderContentType
        : 'logo'
    ) as 'logo' | 'text' | 'none';
    const projectHeaderContentType = (
      officeDocProjectHeaderContentType === 'text' || officeDocProjectHeaderContentType === 'none'
        ? officeDocProjectHeaderContentType
        : 'logo'
    ) as 'logo' | 'text' | 'none';
    const companyHeaderText = esc(officeDocCompanyHeaderText || '');
    const projectHeaderText = esc(officeDocProjectHeaderText || '');
    const companyHeaderTextCss = esc(
      String(officeDocCompanyHeaderTextCss || 'font-size:12px;line-height:1.2;color:#111;white-space:nowrap;')
    );
    const projectHeaderTextCss = esc(
      String(officeDocProjectHeaderTextCss || 'font-size:12px;line-height:1.2;color:#111;white-space:nowrap;')
    );
    const headerCenterRaw = String(officeDocHeaderCenterHtml || '').trim();
    const headerCenterHtml = headerCenterRaw
      ? (hasHtmlMarkup(headerCenterRaw) ? headerCenterRaw : esc(headerCenterRaw).replace(/\n/g, '<br/>'))
      : '';
    const footerAddressRaw = String(activeDocCompanyProfile?.address || '');
    const footerMobileValue = String(activeDocCompanyProfile?.mobile || '');
    const footerEmailRaw = String(activeDocCompanyProfile?.email || '');
    const footerAddress = esc(footerAddressRaw || '{{company_address}}');
    const footerMobile = esc(formatIndianFooterMobile(footerMobileValue) || footerMobileValue);
    const footerEmail = esc(footerEmailRaw || '{{company_email}}');
    const footerOverlayRaw = String(officeDocFooterOverlayText || '').trim();
    const footerOverlayEnabled = Boolean(officeDocFooterOverlayEnabled && footerOverlayRaw);
    const footerOverlayFontSizePx = Math.max(
      8,
      Math.min(72, Number(officeDocFooterOverlayFontSizePx || 12) || 12)
    );
    const footerOverlayOffsetXMm = Math.max(-300, Math.min(300, Number(officeDocFooterOverlayOffsetXCm || 0) * 10));
    const footerOverlayOffsetYMm = Math.max(-300, Math.min(300, Number(officeDocFooterOverlayOffsetYCm || 0) * 10));
    const footerOverlayPageScope = officeDocFooterOverlayPageScope === 'first' ?
      'first' :
      officeDocFooterOverlayPageScope === 'last' ?
        'last' :
        officeDocFooterOverlayPageScope === 'custom' ? 'custom' : 'all';
    const footerOverlayHtml = hasHtmlMarkup(footerOverlayRaw)
      ? footerOverlayRaw
      : esc(footerOverlayRaw).replace(/\n/g, '<br/>');
    const mapFontFormat = (value: string) => {
      const format = String(value || '').trim().toLowerCase();
      if (format === 'otf') return 'opentype';
      if (format === 'ttf') return 'truetype';
      if (format === 'woff' || format === 'woff2') return format;
      return 'woff2';
    };
    const companyFontUrl = String(activeDocCompanyProfile?.fontUrl || '').trim();
    const companyFontFamily = String(activeDocCompanyProfile?.fontFamily || '').trim();
    const companyFontFormat = String(activeDocCompanyProfile?.fontFormat || '').trim().toLowerCase();
    const gujaratiFontUrl = activeDocGujaratiFontUrl;
    const gujaratiFontFamily = activeDocGujaratiFontFamily;
    const gujaratiFontFormat = activeDocGujaratiFontFormat;
    const isGujaratiSelection = /gujarati/i.test(selectedDocFontFamily) || ['Shruti', 'Nirmala UI'].includes(selectedDocFontFamily);
    const effectiveCompanyFontFamily = companyFontFamily || (companyFontUrl ? 'CompanyCustomFont' : '');
    const effectiveGujaratiFontFamily = gujaratiFontFamily || (gujaratiFontUrl ? selectedDocFontFamily : '');
    const docFontFamily =
      officeDocUseCompanyFont && isGujaratiSelection && effectiveGujaratiFontFamily
        ? effectiveGujaratiFontFamily
        : officeDocUseCompanyFont && effectiveCompanyFontFamily
          ? effectiveCompanyFontFamily
          : selectedDocFontFamily;
    const customFontFaceParts: string[] = [];
    if (companyFontUrl && effectiveCompanyFontFamily) {
      const fontUrlSafe = companyFontUrl.replace(/'/g, '%27');
      const fontFormatSafe = mapFontFormat(companyFontFormat);
      customFontFaceParts.push(
        `@font-face{font-family:'${esc(effectiveCompanyFontFamily)}';src:url('${fontUrlSafe}') format('${fontFormatSafe}');font-display:swap;}`
      );
    }
    if (gujaratiFontUrl && isGujaratiSelection) {
      const gujaratiUrlSafe = gujaratiFontUrl.replace(/'/g, '%27');
      const gujaratiFormatSafe = mapFontFormat(gujaratiFontFormat);
      const gujaratiFaceFamily = effectiveGujaratiFontFamily || selectedDocFontFamily;
      customFontFaceParts.push(
        `@font-face{font-family:'${esc(gujaratiFaceFamily)}';src:url('${gujaratiUrlSafe}') format('${gujaratiFormatSafe}');font-display:swap;}`
      );
      if (!officeDocUseCompanyFont && gujaratiFaceFamily !== selectedDocFontFamily) {
        customFontFaceParts.push(
          `@font-face{font-family:'${esc(selectedDocFontFamily)}';src:url('${gujaratiUrlSafe}') format('${gujaratiFormatSafe}');font-display:swap;}`
        );
      }
    }
    const customFontFaceCss = customFontFaceParts.join('');
    const fixedHeaderHeightMm = 42;
    const headerZoneMm = fixedHeaderHeightMm;
    const footerZoneMm = officeDocShowFooterContent ? footerHeightMm : 0;
    const effectiveCompanyLogoHeightMm = Math.min(companyLogoHeightMm, headerZoneMm);
    const effectiveProjectLogoHeightMm = Math.min(projectLogoHeightMm, headerZoneMm);
    const companySlotHeightMm = companyHeaderContentType === 'logo' ?
      effectiveCompanyLogoHeightMm :
      Math.max(8, Math.min(headerZoneMm, companyLogoHeightMm || 16));
    const projectSlotHeightMm = projectHeaderContentType === 'logo' ?
      effectiveProjectLogoHeightMm :
      Math.max(8, Math.min(headerZoneMm, projectLogoHeightMm || 16));
    const companySlotTopMm = Math.max(
      0,
      Math.min(
        headerZoneMm - companySlotHeightMm,
        (headerZoneMm - companySlotHeightMm) / 2 + companyLogoOffsetYMm
      )
    );
    const projectSlotTopMm = Math.max(
      0,
      Math.min(
        headerZoneMm - projectSlotHeightMm,
        (headerZoneMm - projectSlotHeightMm) / 2 + projectLogoOffsetYMm
      )
    );
    const contentTopMm = Math.max(headerZoneMm, Math.max(0, marginTopMm + mainContentOffsetMm));
    const footerReserveGapMm = officeDocShowFooterContent ? 6 : 0;
    const contentBottomMm = Math.max(
      footerHeightMm + footerReserveGapMm,
      Math.max(0, marginBottomMm)
    );
    const printContentTopMm = contentTopMm;
    const printContentBottomMm = contentBottomMm;
    const printHeaderAnchorTopMm = printContentTopMm;
    const printFooterAnchorBottomMm = marginBottomMm;
    const headerPaddingLeftMm = 16;
    const headerPaddingRightMm = 11;
    const headerTopPaddingMm = 0;
    const footerSidePaddingMm = 0;
    const footerBottomPaddingMm = 0;
    const headerRowOverflow = 'visible';
    const headerFooterLayerZ = 5;
    const headerFooterBg = 'transparent';
    const companyHeaderSlotHtml =
      companyHeaderContentType === 'none'
        ? ''
        : companyHeaderContentType === 'text'
          ? `<div style="position:absolute;left:${companyLogoOffsetXMm}mm;top:${companySlotTopMm}mm;height:${companySlotHeightMm}mm;display:flex;align-items:center;${companyHeaderTextCss}">${companyHeaderText}</div>`
          : `<img src="${companyLogo}" alt="Company Logo" style="position:absolute;left:${companyLogoOffsetXMm}mm;top:${companySlotTopMm}mm;height:${effectiveCompanyLogoHeightMm}mm;object-fit:contain;" />`;
    const projectHeaderSlotHtml =
      projectHeaderContentType === 'none'
        ? ''
        : projectHeaderContentType === 'text'
          ? `<div style="position:absolute;right:${-projectLogoOffsetXMm}mm;top:${projectSlotTopMm}mm;height:${projectSlotHeightMm}mm;display:flex;align-items:center;justify-content:flex-end;text-align:right;${projectHeaderTextCss}">${projectHeaderText}</div>`
          : `<img src="${projectLogo}" alt="Project Logo" style="position:absolute;right:${-projectLogoOffsetXMm}mm;top:${projectSlotTopMm}mm;height:${effectiveProjectLogoHeightMm}mm;object-fit:contain;" />`;
    const useFullWidthHeaderCenter =
      companyHeaderContentType === 'none' && projectHeaderContentType === 'none';
    const headerCenterSlotHtml = headerCenterHtml
      ? useFullWidthHeaderCenter
        ? `<div style="position:absolute;left:0;right:0;top:50%;transform:translateY(-50%);text-align:center;line-height:1.25;padding:0 2mm;box-sizing:border-box;z-index:${headerFooterLayerZ + 1};">${headerCenterHtml}</div>`
        : `<div style="position:absolute;left:50%;top:50%;transform:translate(-50%, -50%);max-width:60%;text-align:center;line-height:1.25;z-index:${headerFooterLayerZ + 1};">${headerCenterHtml}</div>`
      : '';
    const headerHtml = [
      `<header class="doc-page-header" style="position:absolute;left:0;right:0;top:0;height:${headerZoneMm}mm;padding:0 ${headerPaddingRightMm}mm 0 ${headerPaddingLeftMm}mm;box-sizing:border-box;background:${headerFooterBg};display:flex;flex-direction:column;transform:translate(${headerHorizontalOffsetMm}mm, ${headerVerticalOffsetMm}mm);z-index:${headerFooterLayerZ};pointer-events:none;">`,
      `<div style="position:relative;flex:1;min-height:0;overflow:${headerRowOverflow};padding-top:${headerTopPaddingMm}mm;box-sizing:border-box;">`,
      companyHeaderSlotHtml,
      projectHeaderSlotHtml,
      headerCenterSlotHtml,
      `</div>`,
      `<div style="border-bottom:1px solid #bbb;"></div>`,
      '</header>',
    ].join('');
    const footerContactLine = [footerMobile, footerEmail].filter(Boolean).join(' | ');
    const footerFixedHtml = officeDocShowFooterContent ? [
      `<footer class="doc-page-footer" style="position:absolute;left:0;right:0;bottom:0;height:${footerZoneMm}mm;padding:0 ${footerSidePaddingMm}mm ${footerBottomPaddingMm}mm;box-sizing:border-box;background:${headerFooterBg};z-index:${headerFooterLayerZ};pointer-events:none;transform:translate(${footerHorizontalOffsetMm}mm, ${footerVerticalOffsetMm}mm);">`,
      `<div style="border-top:1px solid #bbb;height:100%;display:flex;align-items:center;justify-content:center;flex-direction:column;text-align:center;box-sizing:border-box;margin:0 ${headerPaddingRightMm}mm 0 ${headerPaddingLeftMm}mm;">`,
      `<div style="height:100%;display:flex;align-items:center;justify-content:center;flex-direction:column;text-align:center;width:100%;">`,
      `<div style="font-size:11px;line-height:1.35;">${footerAddress}</div>`,
      `<div style="font-size:11px;line-height:1.35;">${footerContactLine}</div>`,
      '</div>',
      `</div>`,
      showPageNumbers
        ? `<div style="position:absolute;right:${headerPaddingRightMm}mm;bottom:1.2mm;font-size:10px;line-height:1;color:#444;z-index:${headerFooterLayerZ + 1};">Page 1 of 1</div>`
        : '',
      footerOverlayEnabled &&
      (
        footerOverlayPageScope === 'all' ||
        footerOverlayPageScope === 'first' ||
        footerOverlayPageScope === 'last' ||
        footerOverlayPageScope === 'custom'
      )
        ? `<div style="position:absolute;left:${headerPaddingLeftMm}mm;right:${headerPaddingRightMm}mm;top:0;bottom:0;display:flex;align-items:center;justify-content:center;text-align:center;font-size:${footerOverlayFontSizePx}px;transform:translate(${footerOverlayOffsetXMm}mm, ${footerOverlayOffsetYMm}mm);z-index:${headerFooterLayerZ + 2};pointer-events:none;">${footerOverlayHtml}</div>`
        : '',
      '</footer>',
    ].join('') : '';
    const refDateItems: string[] = [];
    if (showRefNo) refDateItems.push(`<div>Ref No: ${refNo}</div>`);
    if (showDate) refDateItems.push(`<div>Date: ${issueDate}</div>`);

    return [
      `<div data-paper-size="${officeDocPaperSize}" style="width:${officeDocPaperConfig.widthMm}mm;min-height:${officeDocPaperConfig.heightMm}mm;margin:0 auto;box-sizing:border-box;background:#fff;color:#111;font-family:${docFontFamily},sans-serif;line-height:1.5;letter-spacing:${letterSpacingPx}px;position:relative;">`,
      `<style>${customFontFaceCss}
@page{
  size:${officeDocPaperSize};
  margin:${printContentTopMm}mm ${marginRightMm}mm ${printContentBottomMm}mm ${marginLeftMm}mm;
}
@media screen {
  [data-paper-size]{
    background-image:repeating-linear-gradient(
      to bottom,
      transparent 0,
      transparent calc(${officeDocPaperConfig.heightMm}mm - 1px),
      #d8dee5 calc(${officeDocPaperConfig.heightMm}mm - 1px),
      #d8dee5 ${officeDocPaperConfig.heightMm}mm
    );
    background-size:100% ${officeDocPaperConfig.heightMm}mm;
  }
}
@media print {
  html,body{
    margin:0 !important;
    padding:0 !important;
    background:#fff !important;
    -webkit-print-color-adjust:exact !important;
    print-color-adjust:exact !important;
  }
  [data-paper-size]{
    width:auto !important;
    min-height:auto !important;
    margin:0 !important;
    overflow:visible !important;
    background:none !important;
    break-inside:auto !important;
  }
  .doc-page-shell{
    min-height:0 !important;
    padding:0 !important;
    display:block !important;
    position:static !important;
  }
  .doc-brand-bg{
    position:fixed !important;
    left:-${marginLeftMm}mm !important;
    right:-${marginRightMm}mm !important;
    top:-${printContentTopMm}mm !important;
    bottom:-${printContentBottomMm}mm !important;
  }
  .doc-page-header{
    position:fixed !important;
    left:-${marginLeftMm}mm !important;
    right:-${marginRightMm}mm !important;
    top:-${printHeaderAnchorTopMm}mm !important;
    height:${headerZoneMm}mm !important;
    z-index:${headerFooterLayerZ} !important;
  }
  .doc-page-footer{
    position:fixed !important;
    left:-${marginLeftMm}mm !important;
    right:-${marginRightMm}mm !important;
    bottom:-${printFooterAnchorBottomMm}mm !important;
    height:${footerZoneMm}mm !important;
    z-index:${headerFooterLayerZ} !important;
  }
  .doc-body{
    position:static !important;
    margin:0 !important;
    padding:0 !important;
    box-sizing:border-box !important;
    overflow:visible !important;
    height:auto !important;
  }
}
</style>`,
      backgroundImageUrl
        ? `<div class="doc-brand-bg" style="position:absolute;inset:0;background-image:url('${backgroundImageUrl}');background-repeat:no-repeat;background-position:${backgroundPosX}% ${backgroundPosY}%;background-size:${officeDocBackgroundSize};transform:scale(${backgroundScale / 100});transform-origin:${backgroundPosX}% ${backgroundPosY}%;opacity:${backgroundOpacity};pointer-events:none;z-index:0;"></div>`
        : '',
      `<div class="doc-page-shell" style="position:relative;z-index:1;min-height:${officeDocPaperConfig.heightMm}mm;box-sizing:border-box;display:block;">`,
      headerHtml,
      `<main class="doc-body" style="position:relative;z-index:1;margin:0;box-sizing:border-box;padding:${contentTopMm}mm ${marginRightMm}mm ${contentBottomMm}mm ${marginLeftMm}mm;text-align:justify;text-justify:inter-word;">`,
      refDateItems.length
        ? `<div style="display:flex;justify-content:${refDateItems.length > 1 ? 'space-between' : 'flex-start'};align-items:flex-start;gap:8px;margin:0 0 10px;font-size:12px;">${refDateItems.join('')}</div>`
        : '',
      showTitle ? `<h1 style="margin:0 0 10px;font-size:22px;text-align:${titleAlign};">${title}</h1>` : '',
      subtitle ? `<p style="margin:0 0 14px;font-weight:600;text-align:${titleAlign};">${subtitle}</p>` : '',
      body || '<p></p>',
      footer ? `<div style="margin-top:16px;">${footer}</div>` : '',
      '</main>',
      footerFixedHtml,
      '</div>',
      '</div>',
    ].join('');
  }, [activeDocCompanyProfile, activeDocGujaratiFontFamily, activeDocGujaratiFontFormat, activeDocGujaratiFontUrl, activeDocProject?.backgroundImageUrl, activeDocProject?.logoUrl, activeDocProject?.logoBwUrl, formatDdMmYyyy, officeDocBackgroundOpacity, officeDocBackgroundPosX, officeDocBackgroundPosY, officeDocBackgroundScale, officeDocBackgroundSize, officeDocCompanyHeaderContentType, officeDocCompanyHeaderText, officeDocCompanyHeaderTextCss, officeDocCompanyLogoHeightCm, officeDocCompanyLogoOffsetXCm, officeDocCompanyLogoOffsetYCm, officeDocFooterHeightCm, officeDocFooterHorizontalOffsetCm, officeDocFooterOverlayEnabled, officeDocFooterOverlayFontSizePx, officeDocFooterOverlayOffsetXCm, officeDocFooterOverlayOffsetYCm, officeDocFooterOverlayPageScope, officeDocFooterOverlayText, officeDocFooterVerticalOffsetCm, officeDocFontFamily, officeDocHeaderCenterHtml, officeDocHeaderHorizontalOffsetCm, officeDocHeaderVerticalOffsetCm, officeDocLetterSpacingPx, officeDocMainContentOffsetCm, officeDocMarginBottomCm, officeDocMarginLeftCm, officeDocMarginRightCm, officeDocMarginTopCm, officeDocPaperConfig.heightMm, officeDocPaperConfig.widthMm, officeDocPaperSize, officeDocProjectHeaderContentType, officeDocProjectHeaderText, officeDocProjectHeaderTextCss, officeDocProjectLogoHeightCm, officeDocProjectLogoOffsetXCm, officeDocProjectLogoOffsetYCm, officeDocRefNo, officeDocResolvedPreviewRefNo, officeDocShowDate, officeDocShowFooterContent, officeDocShowPageNumbers, officeDocShowRefNo, officeDocShowTitle, officeDocTitleAlign, officeDocUseCompanyFont, officeDocVisualBody, officeDocVisualFooter, officeDocVisualSubtitle, officeDocVisualTitle, officeDocumentTemplateUseProjectLogoBw]);

  const syncOfficeDocBodyFromEditor = () => {
    const editor = officeDocBodyEditorRef.current;
    if (!editor) return;
    setOfficeDocVisualBody(editor.innerHTML || '');
  };

  const saveOfficeDocBodySelection = () => {
    const editor = officeDocBodyEditorRef.current;
    const selection = window.getSelection();
    if (!editor || !selection || selection.rangeCount === 0) return;
    const range = selection.getRangeAt(0);
    if (!editor.contains(range.commonAncestorContainer)) return;
    officeDocBodySelectionRef.current = range.cloneRange();
  };

  const restoreOfficeDocBodySelection = () => {
    const editor = officeDocBodyEditorRef.current;
    const selection = window.getSelection();
    const range = officeDocBodySelectionRef.current;
    if (!editor || !selection || !range) return;
    selection.removeAllRanges();
    selection.addRange(range);
  };

  const focusOfficeBodyEditor = () => {
    if (!officeDocBodyEditorRef.current) return;
    officeDocBodyEditorRef.current.focus();
    restoreOfficeDocBodySelection();
    setOfficeDocActiveField('body');
  };

  const onOfficeDocToolbarMouseDown = (
    event: React.MouseEvent,
    action: () => void,
  ) => {
    event.preventDefault();
    action();
  };

  const applyOfficeBodyCommand = (command: string, value?: string) => {
    focusOfficeBodyEditor();
    document.execCommand(command, false, value);
    saveOfficeDocBodySelection();
    syncOfficeDocBodyFromEditor();
  };

  const applyOfficeBodyInlineStyle = (styleText: string) => {
    focusOfficeBodyEditor();
    document.execCommand('styleWithCSS', false, 'true');
    const selectedText = document.getSelection()?.toString() || '';
    document.execCommand('insertHTML', false, `<span style="${styleText}">${selectedText || '&nbsp;'}</span>`);
    syncOfficeDocBodyFromEditor();
  };

  const insertOfficeBodyTable = () => {
    const rowInput = window.prompt('Rows', '2');
    const colInput = window.prompt('Columns', '2');
    const rows = Math.max(1, Number(rowInput || 0) || 2);
    const cols = Math.max(1, Number(colInput || 0) || 2);
    const cells = Array.from({ length: cols })
      .map(() => '<td style="border:1px solid #bbb;padding:6px;">&nbsp;</td>')
      .join('');
    const body = Array.from({ length: rows }).map(() => `<tr>${cells}</tr>`).join('');
    const tableHtml = `<table style="width:100%;border-collapse:collapse;border-spacing:0;margin:8px 0;">${body}</table><p></p>`;
    applyOfficeBodyCommand('insertHTML', tableHtml);
  };

  useEffect(() => {
    if (!officeDocBodyEditorRef.current) return;
    const editor = officeDocBodyEditorRef.current;
    if (document.activeElement === editor) return;
    const raw = String(officeDocVisualBody || '').trim();
    if (!raw) {
      if ((editor.innerHTML || '') !== '') editor.innerHTML = '';
      return;
    }
    let normalized = raw;
    if (!hasHtmlMarkup(normalized) && hasEncodedHtmlMarkup(normalized)) {
      const decoded = new DOMParser().parseFromString(normalized, 'text/html');
      const decodedHtml = String(decoded.documentElement?.textContent || '').trim();
      if (hasHtmlMarkup(decodedHtml)) {
        normalized = decodedHtml;
      } else {
        normalized = (decodedHtml || normalized)
          .split(/\n{2,}/)
          .map((chunk) => chunk.trim())
          .filter(Boolean)
          .map((chunk) => `<p>${escapeHtml(chunk).replace(/\n/g, '<br/>')}</p>`)
          .join('');
      }
    } else if (!hasHtmlMarkup(normalized)) {
      normalized = normalized
        .split(/\n{2,}/)
        .map((chunk) => chunk.trim())
        .filter(Boolean)
        .map((chunk) => `<p>${escapeHtml(chunk).replace(/\n/g, '<br/>')}</p>`)
        .join('');
    }
    if ((editor.innerHTML || '') !== normalized) {
      editor.innerHTML = normalized;
    }
  }, [officeDocVisualBody]);

  // Lead Status Management state
  const [statuses, setStatuses] = useState<any[]>([]);
  const [statusDrawerOpen, setStatusDrawerOpen] = useState(false);
  const [statusLabel, setStatusLabel] = useState('');
  const [statusOrder, setStatusOrder] = useState<number>(0);
  const [statusColor, setStatusColor] = useState<string>('');
  const [statusAllowedNext, setStatusAllowedNext] = useState<string[]>([]);
  const [statusPrompts, setStatusPrompts] = useState<{ id: string; label: string; type: string; options?: string[] }[]>([]);
  const [promptLabel, setPromptLabel] = useState('');
  const [promptType, setPromptType] = useState<'text' | 'date' | 'number' | 'select'>('text');
  const [promptOptionsInput, setPromptOptionsInput] = useState('');
  const [editingStatusId, setEditingStatusId] = useState<string | null>(null);
  const [statusSaving, setStatusSaving] = useState(false);
  const [statusError, setStatusError] = useState<string | null>(null);

  // Meta Lead Ads (Facebook/Instagram instant forms) config (non-secret UI settings)
  const [metaEnabled, setMetaEnabled] = useState(false);
  const [metaDefaultSource, setMetaDefaultSource] = useState('Social Ad');
  const [metaDefaultStatus, setMetaDefaultStatus] = useState('New');
  const [metaConfigSaving, setMetaConfigSaving] = useState(false);
  const [metaConfigError, setMetaConfigError] = useState<string | null>(null);
  const [metaSecretsStatus, setMetaSecretsStatus] = useState<{
    hasVerifyToken: boolean;
    hasPageToken: boolean;
    hasSystemUserToken?: boolean;
    hasAppSecret: boolean;
  } | null>(null);
  const [metaForms, setMetaForms] = useState<any[]>([]);
  const [metaFormsError, setMetaFormsError] = useState<string | null>(null);
  const [metaFormDrawerOpen, setMetaFormDrawerOpen] = useState(false);
  const [metaFormId, setMetaFormId] = useState('');
  const [metaFormProjectId, setMetaFormProjectId] = useState<string>('');
  const [metaFormActive, setMetaFormActive] = useState(true);
  const [editingMetaFormId, setEditingMetaFormId] = useState<string | null>(null);
  const [metaPages, setMetaPages] = useState<any[]>([]);
  const [metaPagesError, setMetaPagesError] = useState<string | null>(null);
  const [metaPageDrawerOpen, setMetaPageDrawerOpen] = useState(false);
  const [metaPageId, setMetaPageId] = useState('');
  const [metaPageLabel, setMetaPageLabel] = useState('');
  const [metaPageActive, setMetaPageActive] = useState(true);
  const [metaPageType, setMetaPageType] = useState<'own' | 'agency'>('own');
  const [editingMetaPageId, setEditingMetaPageId] = useState<string | null>(null);

  const metaWebhookUrl = useMemo(() => {
    try {
      const pid = getApp()?.options?.projectId;
      if (!pid) return '';
      return `https://asia-south1-${pid}.cloudfunctions.net/metaWebhook`;
    } catch {
      return '';
    }
  }, []);

  const handleAddUser = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    setLoading(true);

    if (!name || !email || !phone || !password) {
      setError('All fields are required.');
      setLoading(false);
      return;
    }
    if (role === 'sales-team' && newUserReportsToId && !reportingUsers.some((user) => user.id === newUserReportsToId && (user.roleId || user.role) === 'sales-manager')) {
      setError('Choose an active Sales Manager.');
      setLoading(false);
      return;
    }

    try {
      const functions = getFunctions();
      const createCrmUser = httpsCallable(functions, 'createCrmUser');
      const result = await createCrmUser({ name, email, phone, password, role });

      // @ts-ignore
      setSuccess(result.data.message);
      // After creation, attach fine-grained permissions to user doc by email
      try {
        const usersRef = collection(db, 'users');
        const snap = await getDocs(query(usersRef, where('email', '==', email.trim())));
        if (!snap.empty) {
          const userDocRef = snap.docs[0].ref;
          const batch = writeBatch(db);
          batch.update(userDocRef, {
            roleId: role,
            pageAccess: selectedPages,
            actionAccess: selectedActions,
            projectIds: selectedProjectIds,
            dateOfBirth: userDateOfBirth || null,
            dob: userDateOfBirth || null,
            reportsToUserId: role === 'sales-team' ? newUserReportsToId || null : null,
          });
          if (role === 'sales-manager') {
            reportingUsers.filter((user) => (user.roleId || user.role) === 'sales-team' && newManagerDirectReportIds.includes(user.id)).forEach((user) => {
              batch.update(doc(db, 'users', user.id), { reportsToUserId: userDocRef.id });
            });
          }
          await batch.commit();
        }
      } catch (permErr) {
        console.warn('Could not update permissions on user doc', permErr);
      }
      // Reset form
      setName('');
      setEmail('');
      setPhone('');
      setPassword('');
      setRole('sales-team');
      setUserDateOfBirth('');
      setNewUserReportsToId('');
      setNewManagerDirectReportIds([]);
      setAddUserTab('profile');
      setSelectedPages([]);
      setSelectedActions([]);
      setSelectedProjectIds([]);
      setUserDrawerOpen(false);
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // Projects: realtime fetch
  useEffect(() => {
    const projectsRef = collection(db, 'projects');
    let active = true;
    let receivedServerSnapshot = false;
    const applyProjects = (snapshot: any) => {
      const data = snapshot.docs.map((doc: any) => ({ id: doc.id, ...doc.data() })) as any[];
      data.sort((a: any, b: any) => {
        const av = a?.createdAt?.toMillis ? a.createdAt.toMillis() : 0;
        const bv = b?.createdAt?.toMillis ? b.createdAt.toMillis() : 0;
        return bv - av;
      });
      setProjects(data);
      setProjectsLoading(false);
    };
    const unsubscribe = onSnapshot(
      projectsRef,
      { includeMetadataChanges: true },
      (snapshot) => {
        if (snapshot.metadata.fromCache && !receivedServerSnapshot) return;
        if (!snapshot.metadata.fromCache) receivedServerSnapshot = true;
        applyProjects(snapshot);
      },
      (err) => {
        console.error('Error fetching projects:', err);
        setProjectsError('Failed to load projects.');
        setProjectsLoading(false);
      }
    );
    getDocsFromServer(projectsRef)
      .then((snapshot) => {
        if (!active) return;
        receivedServerSnapshot = true;
        applyProjects(snapshot);
      })
      .catch((err) => {
        console.error('Error fetching projects from server:', err);
      });
    return () => {
      active = false;
      unsubscribe();
    };
  }, [db]);

  // Bank accounts (global, can be assigned to multiple projects)
  useEffect(() => {
    if (activeTab !== 1) return undefined;
    setBankAccountsError(null);
    setBankAccountsLoading(true);
    const refCol = collection(db, 'bankAccounts');
    const q = query(refCol, orderBy('name', 'asc'));
    const unsub = onSnapshot(
      q,
      (snap) => {
        const all = snap.docs.map((d) => ({ id: d.id, ...(d.data() as any) }));
        setBankAccountsAll(all);
        setBankAccountsLoading(false);
      },
      (err) => {
        console.error('Load bank accounts failed', err);
        setBankAccountsAll([]);
        setBankAccountsError(err?.message || 'Failed to load bank accounts.');
        setBankAccountsLoading(false);
      }
    );
    return () => unsub();
  }, [activeTab, db]);

  useEffect(() => {
    if (!editingProjectId) {
      setBankAccountsAssigned([]);
      return;
    }
    setBankAccountsAssigned(
      bankAccountsAll.filter((b: any) => Array.isArray(b.projectIds) && b.projectIds.includes(editingProjectId))
    );
  }, [bankAccountsAll, editingProjectId]);

  const openNewBankDrawer = (defaultProjectId?: string | null) => {
    setEditingBankId(null);
    setBankLabel('');
    setBankName('');
    setBankAccountLast4('');
    setBankIfsc('');
    setBankHolderName('');
    setBankAccountRole('other');
    // Defaults: do NOT allow customer receipts unless explicitly marked as the collection account.
    setBankAllowCustomerReceipts(false);
    // GST receipts can be received in any current/OD account (configurable), so default to allowed.
    setBankAllowGstReceipts(true);
    setBankAllowPayments(true);
    setBankRestrictedOutflow(false);
    setBankProjectIds(defaultProjectId ? [String(defaultProjectId)] : []);
    setBankFormError(null);
    setBankDrawerOpen(true);
  };

  const openEditBankDrawer = (id: string) => {
    const b = bankAccountsAll.find((x: any) => x.id === id) || null;
    if (!b) return;
    const roleRaw = String(b.accountRole || '').toLowerCase();
    const role: 'collection' | 'operating30' | 'rera70' | 'dummy_advocate' | 'other' =
      roleRaw === 'collection' ? 'collection' :
      roleRaw === 'operating30' ? 'operating30' :
      roleRaw === 'rera70' ? 'rera70' :
      roleRaw === 'dummy_advocate' ? 'dummy_advocate' :
      'other';
    setEditingBankId(id);
    setBankLabel(String(b.name || '').trim());
    setBankName(String(b.bankName || '').trim());
    setBankAccountLast4(String(b.accountLast4 || b.accountNoLast4 || '').trim());
    setBankIfsc(String(b.ifsc || '').trim());
    setBankHolderName(String(b.holderName || '').trim());
    setBankAccountRole(role);
    // Back-compat: if flags are missing, infer reasonable defaults from role.
    setBankAllowCustomerReceipts(
      typeof b.allowCustomerReceipts === 'boolean'
        ? b.allowCustomerReceipts
        : role === 'collection'
    );
    setBankAllowGstReceipts(
      typeof b.allowGstReceipts === 'boolean'
        ? b.allowGstReceipts
        : role !== 'rera70' && role !== 'collection' && role !== 'dummy_advocate'
    );
    setBankAllowPayments(
      typeof b.allowPayments === 'boolean'
        ? b.allowPayments
        : role === 'operating30' || role === 'other'
    );
    setBankRestrictedOutflow(
      typeof b.restrictedOutflow === 'boolean'
        ? b.restrictedOutflow
        : role === 'rera70'
    );
    setBankProjectIds(Array.isArray(b.projectIds) ? b.projectIds.filter(Boolean).map((x: any) => String(x)) : []);
    setBankFormError(null);
    setBankDrawerOpen(true);
  };

  const handleSaveBankAccount = async () => {
    if (!currentUser?.email) {
      setBankFormError('Not signed in.');
      return;
    }
    const label = bankLabel.trim();
    if (!label) {
      setBankFormError('Bank label is required.');
      return;
    }
    const last4 = bankAccountLast4.replace(/\D+/g, '').slice(-4);
    if (bankAccountLast4.trim() && last4.length !== 4) {
      setBankFormError('Account last 4 must be 4 digits.');
      return;
    }
    setBankFormError(null);
    setBankSaving(true);
    try {
      const payload: any = {
        name: label,
        bankName: bankName.trim() || null,
        accountLast4: last4 || null,
        ifsc: bankIfsc.trim() || null,
        holderName: bankHolderName.trim() || null,
        accountRole: bankAccountRole,
        allowCustomerReceipts: !!bankAllowCustomerReceipts,
        allowGstReceipts: !!bankAllowGstReceipts,
        allowPayments: !!bankAllowPayments,
        restrictedOutflow: !!bankRestrictedOutflow,
        updatedAt: serverTimestamp(),
        updatedBy: currentUser.email,
      };
      if (editingBankId) {
        await updateDoc(doc(db, 'bankAccounts', editingBankId), {
          ...payload,
          projectIds: bankProjectIds,
        });
      } else {
        await addDoc(collection(db, 'bankAccounts'), {
          ...payload,
          projectIds: bankProjectIds,
          createdAt: serverTimestamp(),
          createdBy: currentUser.email,
        });
      }
      setBankDrawerOpen(false);
    } catch (err: any) {
      console.error('Save bank account failed', err);
      setBankFormError(err?.message || 'Failed to save bank account.');
    } finally {
      setBankSaving(false);
    }
  };

  const handleAssignExistingBank = async () => {
    if (!editingProjectId) return;
    if (!currentUser?.email) {
      setBankAccountsError('Not signed in.');
      return;
    }
    const id = String(assignExistingBankId || '').trim();
    if (!id) return;
    setBankAccountsError(null);
    try {
      await updateDoc(doc(db, 'bankAccounts', id), {
        projectIds: arrayUnion(editingProjectId),
        updatedAt: serverTimestamp(),
        updatedBy: currentUser.email,
      });
      setAssignExistingBankId('');
    } catch (err: any) {
      console.error('Assign bank failed', err);
      setBankAccountsError(err?.message || 'Failed to assign bank.');
    }
  };

  const handleRemoveBankFromProject = async (id: string) => {
    if (!editingProjectId) return;
    if (!currentUser?.email) {
      setBankAccountsError('Not signed in.');
      return;
    }
    setBankAccountsError(null);
    try {
      await updateDoc(doc(db, 'bankAccounts', id), {
        projectIds: arrayRemove(editingProjectId),
        updatedAt: serverTimestamp(),
        updatedBy: currentUser.email,
      });
    } catch (err: any) {
      console.error('Remove bank failed', err);
      setBankAccountsError(err?.message || 'Failed to remove bank from project.');
    }
  };

  // Company profiles (reusable across projects)
  useEffect(() => {
    if (activeTab !== 1) return;
    setCompanyProfilesLoading(true);
    setCompanyProfilesError(null);
    const refCol = collection(db, 'companyProfiles');
    const q = query(refCol, orderBy('name', 'asc'));
    const unsub = onSnapshot(
      q,
      (snap) => {
        const rows = snap.docs.map((d) => ({ id: d.id, ...(d.data() as any) }));
        setCompanyProfiles(rows);
        setCompanyProfilesLoading(false);
      },
      (err) => {
        console.error('Load company profiles failed', err);
        setCompanyProfiles([]);
        setCompanyProfilesError(err?.message || 'Failed to load company profiles.');
        setCompanyProfilesLoading(false);
      }
    );
    return () => unsub();
  }, [activeTab, db]);

  const resetCompanyProfileForm = () => {
    setEditingCompanyProfileId(null);
    setCompanyProfileName('');
    setCompanyProfileLogoUrl('');
    setCompanyProfileLogoPath('');
    setCompanyProfileLogoBwUrl('');
    setCompanyProfileLogoBwPath('');
    setCompanyProfileAddress('');
    setCompanyProfileMobile('');
    setCompanyProfileEmail('');
    setCompanyProfileLogoFile(null);
    setCompanyProfileLogoBwFile(null);
    setCompanyProfileFontFamily('');
    setCompanyProfileFontUrl('');
    setCompanyProfileFontPath('');
    setCompanyProfileFontFormat('woff2');
    setCompanyProfileFontFile(null);
    setCompanyProfileGujaratiFontFamily('');
    setCompanyProfileGujaratiFontUrl('');
    setCompanyProfileGujaratiFontPath('');
    setCompanyProfileGujaratiFontFormat('woff2');
    setCompanyProfileGujaratiFontFile(null);
    setCompanyProfileFormError(null);
  };

  // ── Group Profile ────────────────────────────────────────────────────────────
  const loadGroupProfile = React.useCallback(async () => {
    try {
      const snap = await getDoc(doc(db, 'config', 'loginPage'));
      if (snap.exists()) {
        const d = snap.data() as any;
        setGroupLogoUrl(String(d?.logoUrl || ''));
        setGroupLogoPath(String(d?.logoPath || ''));
        setGroupLoginBgUrl(String(d?.loginBgUrl || ''));
        setGroupLoginBgPath(String(d?.loginBgPath || ''));
        setGroupAddress(String(d?.officeAddress || ''));
        setGroupLatitude(String(d?.officeLatitude || ''));
        setGroupLongitude(String(d?.officeLongitude || ''));
        setGroupPhone(String(d?.officePhone || ''));
        setGroupEmail(String(d?.officeEmail || ''));
      }
    } catch { /* ignore */ }
  }, [db]);

  const saveGroupProfile = async () => {
    if (!currentUser?.email) return;
    setGroupProfileSaving(true);
    setGroupProfileError(null);
    setGroupProfileSuccess(false);
    try {
      let nextLogoUrl = groupLogoUrl.trim();
      let nextLogoPath = groupLogoPath.trim();
      let nextBgUrl = groupLoginBgUrl.trim();
      let nextBgPath = groupLoginBgPath.trim();

      if (groupLogoFile) {
        const safeName = groupLogoFile.name.replace(/\s+/g, '_');
        const path = `group-profile/logo/${Date.now()}-${safeName}`;
        const storageRef = ref(storage, path);
        await uploadBytes(storageRef, groupLogoFile);
        nextLogoUrl = await getDownloadURL(storageRef);
        nextLogoPath = path;
        setGroupLogoUrl(nextLogoUrl);
        setGroupLogoPath(nextLogoPath);
        setGroupLogoFile(null);
      }
      if (groupLoginBgFile) {
        const safeName = groupLoginBgFile.name.replace(/\s+/g, '_');
        const path = `group-profile/login-bg/${Date.now()}-${safeName}`;
        const storageRef = ref(storage, path);
        await uploadBytes(storageRef, groupLoginBgFile);
        nextBgUrl = await getDownloadURL(storageRef);
        nextBgPath = path;
        setGroupLoginBgUrl(nextBgUrl);
        setGroupLoginBgPath(nextBgPath);
        setGroupLoginBgFile(null);
      }

      await setDoc(doc(db, 'config', 'loginPage'), {
        logoUrl: nextLogoUrl || null,
        logoPath: nextLogoPath || null,
        loginBgUrl: nextBgUrl || null,
        loginBgPath: nextBgPath || null,
        officeAddress: groupAddress.trim() || null,
        officeLatitude: groupLatitude.trim() || null,
        officeLongitude: groupLongitude.trim() || null,
        officePhone: groupPhone.trim() || null,
        officeEmail: groupEmail.trim() || null,
        updatedAt: serverTimestamp(),
        updatedBy: currentUser.email,
      }, { merge: true });

      setGroupProfileSuccess(true);
      setTimeout(() => setGroupProfileSuccess(false), 3000);
    } catch (err: any) {
      setGroupProfileError(err?.message || 'Failed to save group profile.');
    } finally {
      setGroupProfileSaving(false);
    }
  };
  // ─────────────────────────────────────────────────────────────────────────────

  // Load Group Profile when tab 4 is active
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { if (tab === 4) loadGroupProfile(); }, [tab]);

  const openNewCompanyProfile = () => {
    resetCompanyProfileForm();
    setCompanyProfileDrawerOpen(true);
  };

  const openEditCompanyProfile = (id: string) => {
    const row = companyProfiles.find((c: any) => c.id === id);
    if (!row) return;
    resetCompanyProfileForm();
    setEditingCompanyProfileId(id);
    setCompanyProfileName(String(row.name || ''));
    setCompanyProfileLogoUrl(String(row.logoUrl || ''));
    setCompanyProfileLogoPath(String(row.logoPath || ''));
    setCompanyProfileLogoBwUrl(String(row.logoBwUrl || ''));
    setCompanyProfileLogoBwPath(String(row.logoBwPath || ''));
    setCompanyProfileAddress(String(row.address || ''));
    setCompanyProfileMobile(String(row.mobile || ''));
    setCompanyProfileEmail(String(row.email || ''));
    const existingFontUrl = String(row.fontUrl || '').trim();
    const inferredFontFamily = existingFontUrl
      ? existingFontUrl
          .split('/')
          .pop()
          ?.split('?')[0]
          ?.replace(/\.[^.]+$/, '')
          ?.replace(/[^a-zA-Z0-9_-]+/g, ' ')
          ?.trim() || ''
      : '';
    setCompanyProfileFontFamily(String(row.fontFamily || inferredFontFamily || ''));
    setCompanyProfileFontUrl(String(row.fontUrl || ''));
    setCompanyProfileFontPath(String(row.fontPath || ''));
    setCompanyProfileFontFormat(
      (String(row.fontFormat || 'woff2').toLowerCase() as 'woff2' | 'woff' | 'ttf' | 'otf')
    );
    const existingGujaratiFontUrl = String(row.gujaratiFontUrl || '').trim();
    const inferredGujaratiFontFamily = existingGujaratiFontUrl
      ? existingGujaratiFontUrl
          .split('/')
          .pop()
          ?.split('?')[0]
          ?.replace(/\.[^.]+$/, '')
          ?.replace(/[^a-zA-Z0-9_-]+/g, ' ')
          ?.trim() || ''
      : '';
    setCompanyProfileGujaratiFontFamily(String(row.gujaratiFontFamily || inferredGujaratiFontFamily || ''));
    setCompanyProfileGujaratiFontUrl(String(row.gujaratiFontUrl || ''));
    setCompanyProfileGujaratiFontPath(String(row.gujaratiFontPath || ''));
    setCompanyProfileGujaratiFontFormat(
      (String(row.gujaratiFontFormat || 'woff2').toLowerCase() as 'woff2' | 'woff' | 'ttf' | 'otf')
    );
    setCompanyProfileDrawerOpen(true);
  };

  const saveCompanyProfile = async () => {
    setCompanyProfileFormError(null);
    if (!currentUser?.email) {
      setCompanyProfileFormError('Not signed in.');
      return;
    }
    const name = companyProfileName.trim();
    if (!name) {
      setCompanyProfileFormError('Company profile name is required.');
      return;
    }
    setCompanyProfileSaving(true);
    try {
      let nextLogoUrl = companyProfileLogoUrl.trim();
      let nextLogoPath = companyProfileLogoPath.trim();
      let nextLogoBwUrl = companyProfileLogoBwUrl.trim();
      let nextLogoBwPath = companyProfileLogoBwPath.trim();
      let nextFontUrl = companyProfileFontUrl.trim();
      let nextFontPath = companyProfileFontPath.trim();
      let nextFontFormat = (companyProfileFontFormat || 'woff2') as 'woff2' | 'woff' | 'ttf' | 'otf';
      let nextFontFamily = companyProfileFontFamily.trim();
      let nextGujaratiFontUrl = companyProfileGujaratiFontUrl.trim();
      let nextGujaratiFontPath = companyProfileGujaratiFontPath.trim();
      let nextGujaratiFontFormat = (companyProfileGujaratiFontFormat || 'woff2') as 'woff2' | 'woff' | 'ttf' | 'otf';
      let nextGujaratiFontFamily = companyProfileGujaratiFontFamily.trim();
      if (companyProfileLogoFile) {
        const safeName = companyProfileLogoFile.name.replace(/\s+/g, '_');
        const path = `company-profiles/${editingCompanyProfileId || 'new'}/${Date.now()}-${safeName}`;
        const storageRef = ref(storage, path);
        await uploadBytes(storageRef, companyProfileLogoFile);
        nextLogoUrl = await getDownloadURL(storageRef);
        nextLogoPath = path;
      }
      if (companyProfileLogoBwFile) {
        const safeName = companyProfileLogoBwFile.name.replace(/\s+/g, '_');
        const path = `company-profiles-bw/${editingCompanyProfileId || 'new'}/${Date.now()}-${safeName}`;
        const storageRef = ref(storage, path);
        await uploadBytes(storageRef, companyProfileLogoBwFile);
        nextLogoBwUrl = await getDownloadURL(storageRef);
        nextLogoBwPath = path;
      }
      if (companyProfileFontFile) {
        const safeName = companyProfileFontFile.name.replace(/\s+/g, '_');
        const ext = safeName.split('.').pop()?.toLowerCase() || '';
        const allowedExt = ['woff2', 'woff', 'ttf', 'otf'];
        if (!allowedExt.includes(ext)) {
          throw new Error('Only .woff2, .woff, .ttf, .otf fonts are supported.');
        }
        nextFontFormat = ext as 'woff2' | 'woff' | 'ttf' | 'otf';
        const path = `company-profile-fonts/${editingCompanyProfileId || 'new'}/${Date.now()}-${safeName}`;
        const storageRef = ref(storage, path);
        await uploadBytes(storageRef, companyProfileFontFile);
        nextFontUrl = await getDownloadURL(storageRef);
        nextFontPath = path;
        if (!nextFontFamily) {
          nextFontFamily = safeName
            .replace(/\.[^.]+$/, '')
            .replace(/[^a-zA-Z0-9_-]+/g, ' ')
            .trim();
        }
      }
      if (companyProfileGujaratiFontFile) {
        const safeName = companyProfileGujaratiFontFile.name.replace(/\s+/g, '_');
        const ext = safeName.split('.').pop()?.toLowerCase() || '';
        const allowedExt = ['woff2', 'woff', 'ttf', 'otf'];
        if (!allowedExt.includes(ext)) {
          throw new Error('Only .woff2, .woff, .ttf, .otf Gujarati fonts are supported.');
        }
        nextGujaratiFontFormat = ext as 'woff2' | 'woff' | 'ttf' | 'otf';
        const path = `company-profile-gujarati-fonts/${editingCompanyProfileId || 'new'}/${Date.now()}-${safeName}`;
        const storageRef = ref(storage, path);
        await uploadBytes(storageRef, companyProfileGujaratiFontFile);
        nextGujaratiFontUrl = await getDownloadURL(storageRef);
        nextGujaratiFontPath = path;
        if (!nextGujaratiFontFamily) {
          nextGujaratiFontFamily = safeName
            .replace(/\.[^.]+$/, '')
            .replace(/[^a-zA-Z0-9_-]+/g, ' ')
            .trim();
        }
      }
      const payload = {
        name,
        logoUrl: nextLogoUrl || null,
        logoPath: nextLogoPath || null,
        logoBwUrl: nextLogoBwUrl || null,
        logoBwPath: nextLogoBwPath || null,
        address: companyProfileAddress.trim() || null,
        mobile: companyProfileMobile.trim() || null,
        email: companyProfileEmail.trim() || null,
        fontFamily: nextFontFamily || null,
        fontUrl: nextFontUrl || null,
        fontPath: nextFontPath || null,
        fontFormat: nextFontUrl ? nextFontFormat : null,
        gujaratiFontFamily: nextGujaratiFontFamily || null,
        gujaratiFontUrl: nextGujaratiFontUrl || null,
        gujaratiFontPath: nextGujaratiFontPath || null,
        gujaratiFontFormat: nextGujaratiFontUrl ? nextGujaratiFontFormat : null,
        updatedAt: serverTimestamp(),
        updatedBy: currentUser.email,
      };
      if (editingCompanyProfileId) {
        await updateDoc(doc(db, 'companyProfiles', editingCompanyProfileId), payload);
      } else {
        await addDoc(collection(db, 'companyProfiles'), {
          ...payload,
          createdAt: serverTimestamp(),
          createdBy: currentUser.email,
        });
      }
      setCompanyProfileDrawerOpen(false);
      resetCompanyProfileForm();
    } catch (err: any) {
      console.error('Save company profile failed', err);
      setCompanyProfileFormError(err?.message || 'Failed to save company profile.');
    } finally {
      setCompanyProfileSaving(false);
    }
  };

  const deleteCompanyProfile = async (id: string, name: string) => {
    const confirmed = window.confirm(`Delete company profile "${name}"?`);
    if (!confirmed) return;
    try {
      const row = companyProfiles.find((c: any) => c.id === id);
      await deleteDoc(doc(db, 'companyProfiles', id));
      if (row?.logoPath) {
        try { await deleteObject(ref(storage, row.logoPath)); } catch (e) { /* ignore */ }
      }
      if (row?.logoBwPath) {
        try { await deleteObject(ref(storage, row.logoBwPath)); } catch (e) { /* ignore */ }
      }
      if (row?.fontPath) {
        try { await deleteObject(ref(storage, row.fontPath)); } catch (e) { /* ignore */ }
      }
      if (row?.gujaratiFontPath) {
        try { await deleteObject(ref(storage, row.gujaratiFontPath)); } catch (e) { /* ignore */ }
      }
    } catch (err) {
      console.error('Delete company profile failed', err);
      setCompanyProfilesError('Failed to delete company profile.');
    }
  };

  // Office workflows: global templates

  useEffect(() => {
    if (activeTab !== 1) return;
    setOfficeWorkflowsLoading(true);
    const refCol = collection(db, 'officeWorkflowTemplates');
    const unsub = onSnapshot(
      refCol,
      (snap) => {
        const rows = snap.docs.map((d) => ({ id: d.id, ...(d.data() as any) }));
        rows.sort((a: any, b: any) => {
          const av = a?.updatedAt?.toMillis ? a.updatedAt.toMillis() : 0;
          const bv = b?.updatedAt?.toMillis ? b.updatedAt.toMillis() : 0;
          return bv - av;
        });
        setOfficeWorkflows(rows);
        setOfficeWorkflowsLoading(false);
      },
      (err) => {
        console.error('Load office workflows failed', err);
        setOfficeWorkflowsError(err?.message || 'Failed to load office workflows.');
        setOfficeWorkflowsLoading(false);
      }
    );
    return () => unsub();
  }, [activeTab, db]);

  useEffect(() => {
    if (activeTab !== 1) return;
    setOfficeDocumentTemplatesLoading(true);
    const refCol = collection(db, 'officeDocumentTemplates');
    const unsub = onSnapshot(
      refCol,
      (snap) => {
        const rows = snap.docs.map((d) => ({ id: d.id, ...(d.data() as any) }));
        rows.sort((a: any, b: any) => {
          const av = a?.updatedAt?.toMillis ? a.updatedAt.toMillis() : 0;
          const bv = b?.updatedAt?.toMillis ? b.updatedAt.toMillis() : 0;
          return bv - av;
        });
        setOfficeDocumentTemplates(rows);
        setOfficeDocumentTemplatesLoading(false);
      },
      (err) => {
        console.error('Load office document templates failed', err);
        setOfficeDocumentTemplatesError(err?.message || 'Failed to load document templates.');
        setOfficeDocumentTemplatesLoading(false);
      }
    );
    return () => unsub();
  }, [activeTab, db]);

  useEffect(() => {
    if (activeTab !== 1) return;
    setOfficeDocumentUploadTypesLoading(true);
    const refCol = collection(db, 'officeDocumentUploadTypes');
    const unsub = onSnapshot(
      refCol,
      (snap) => {
        const rows = snap.docs.map((d) => ({ id: d.id, ...(d.data() as any) }));
        rows.sort((a: any, b: any) => {
          const av = a?.updatedAt?.toMillis ? a.updatedAt.toMillis() : 0;
          const bv = b?.updatedAt?.toMillis ? b.updatedAt.toMillis() : 0;
          return bv - av;
        });
        setOfficeDocumentUploadTypes(rows);
        setOfficeDocumentUploadTypesLoading(false);
      },
      (err) => {
        console.error('Load office document upload types failed', err);
        setOfficeDocumentUploadTypesError(err?.message || 'Failed to load document config.');
        setOfficeDocumentUploadTypesLoading(false);
      }
    );
    return () => unsub();
  }, [activeTab, db]);

  useEffect(() => {
    if (activeTab !== 1) return;
    setOfficeEmailConfigsLoading(true);
    const refCol = collection(db, 'officeEmailConfigs');
    const unsub = onSnapshot(
      refCol,
      (snap) => {
        const rows = snap.docs.map((d) => ({ id: d.id, ...(d.data() as any) }));
        rows.sort((a: any, b: any) => {
          const av = a?.updatedAt?.toMillis ? a.updatedAt.toMillis() : 0;
          const bv = b?.updatedAt?.toMillis ? b.updatedAt.toMillis() : 0;
          return bv - av;
        });
        setOfficeEmailConfigs(rows);
        setOfficeEmailConfigsLoading(false);
      },
      (err) => {
        console.error('Load office email configs failed', err);
        setOfficeEmailConfigsError(err?.message || 'Failed to load email configurations.');
        setOfficeEmailConfigsLoading(false);
      }
    );
    return () => unsub();
  }, [activeTab, db]);

  useEffect(() => {
    if (activeTab !== 1) return;
    setOfficeEmailTemplatesLoading(true);
    const refCol = collection(db, 'officeEmailTemplates');
    const unsub = onSnapshot(
      refCol,
      (snap) => {
        const rows = snap.docs.map((d) => ({ id: d.id, ...(d.data() as any) }));
        rows.sort((a: any, b: any) => {
          const av = a?.updatedAt?.toMillis ? a.updatedAt.toMillis() : 0;
          const bv = b?.updatedAt?.toMillis ? b.updatedAt.toMillis() : 0;
          return bv - av;
        });
        setOfficeEmailTemplates(rows);
        setOfficeEmailTemplatesLoading(false);
      },
      (err) => {
        console.error('Load office email templates failed', err);
        setOfficeEmailTemplatesError(err?.message || 'Failed to load email templates.');
        setOfficeEmailTemplatesLoading(false);
      }
    );
    return () => unsub();
  }, [activeTab, db]);

  // Lead statuses: realtime fetch
  useEffect(() => {
    if (activeTab !== 2 && !(activeTab === 1 && projectSettingsSection === 'whatsapp')) return;
    const statusesRef = collection(db, 'leadStatuses');
    const q = query(statusesRef, orderBy('order', 'asc'));
    const unsub = onSnapshot(
      q,
      (snap) => {
        const data = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
        setStatuses(data);
      },
      (err) => {
        console.error('Error fetching statuses', err);
        setStatusError('Failed to load statuses.');
      }
    );
    return () => unsub();
  }, [activeTab, db, projectSettingsSection]);

  // Meta Lead Ads: config fetch (non-secret settings)
  useEffect(() => {
    if (activeTab !== 3) return;
    const cfgRef = doc(db, 'integrations', 'metaLeadAds');
    const unsub = onSnapshot(
      cfgRef,
      (snap) => {
        const d: any = snap.exists() ? snap.data() : {};
        setMetaEnabled(!!d.enabled);
        setMetaDefaultSource(
          typeof d.defaultSource === 'string' && d.defaultSource.trim() ? d.defaultSource : 'Social Ad'
        );
        setMetaDefaultStatus(
          typeof d.defaultStatus === 'string' && d.defaultStatus.trim() ? d.defaultStatus : 'New'
        );
      },
      (err) => {
        console.error('Error fetching Meta Lead config', err);
        setMetaConfigError('Failed to load Meta Lead settings.');
      }
    );
    return () => unsub();
  }, [activeTab, db]);

  // Meta Lead Ads: form -> project mappings
  useEffect(() => {
    if (activeTab !== 3) return;
    const formsRef = collection(db, 'metaLeadForms');
    const q = query(formsRef, orderBy('updatedAt', 'desc'));
    const unsub = onSnapshot(
      q,
      (snap) => {
        const data = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
        setMetaForms(data);
      },
      (err) => {
        console.error('Error fetching Meta Lead forms', err);
        setMetaFormsError('Failed to load form mappings.');
      }
    );
    return () => unsub();
  }, [activeTab, db]);

  // Meta Lead Ads: pages allowlist (multi-page)
  useEffect(() => {
    if (activeTab !== 3) return;
    const pagesRef = collection(db, 'metaPages');
    const q = query(pagesRef, orderBy('updatedAt', 'desc'));
    const unsub = onSnapshot(
      q,
      (snap) => {
        const data = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
        setMetaPages(data);
      },
      (err) => {
        console.error('Error fetching Meta Pages', err);
        setMetaPagesError('Failed to load pages.');
      }
    );
    return () => unsub();
  }, [activeTab, db]);


	  const resetProjectForm = () => {
	    setProjectName('');
	    setProjectLocation('');
	    setProjectLatitude('');
	    setProjectLongitude('');
	    setProjectAddress('');
      setProjectWhatsappSubtitle('');
      setProjectWebsiteSlug('');
      setProjectBrochureUrl('');
	    setProjectBackgroundImageUrl('');
	    setProjectDocumentImageUrl('');
      setProjectLogoUrl('');
      setProjectLogoBwUrl('');
	    setProjectBackgroundImageFile(null);
      setProjectDocumentImageFile(null);
      setProjectLogoBwFile(null);
      setProjectAdvocateAgreementFee('');
      setProjectAdvocateDeedFee('');
      setProjectBucReceivedAt('');
      setProjectBucBuildings('');
      setProjectSbAreaPercent('');
	    setProjectCompanyProfileId('');
	    setUnitTypes([]);
	    setLogoFile(null);
	    setEditingProjectId(null);
	    setEditingLogoPath(null);
	    setEditingBackgroundImagePath(null);
      setEditingDocumentImagePath(null);
      setEditingLogoBwPath(null);
	    setProjectUnitsCount(0);
	    setProjectUnitsLastImportedAt(null);
	    setUnitsCsvFile(null);
	    setUnitsParsed([]);
	    setUnitsParseError(null);
	    setUnitsParseWarnings([]);
	    setUnitsImporting(false);
	    setUnitsImportSuccess(null);
	    setUnitsDialogOpen(false);
	    setUnitsProjectId(null);
	    setUnitsProjectName('');
	    setUnitsLoading(false);
	    setUnitsLoadError(null);
	    setUnitsRows([]);
	    setUnitsLastDoc(null);
	    setUnitsHasMore(false);
	    setUnitsSearch('');
	    setBankAccountsAll([]);
	    setBankAccountsAssigned([]);
	    setBankAccountsLoading(false);
	    setBankAccountsError(null);
	    setAssignExistingBankId('');
	    setBankDrawerOpen(false);
	    setEditingBankId(null);
	    setBankLabel('');
	    setBankName('');
	    setBankAccountLast4('');
	    setBankIfsc('');
	    setBankHolderName('');
	    setBankSaving(false);
	    setBankFormError(null);
	  };

  const resetOfficeEmailConfigForm = () => {
    setEditingOfficeEmailConfigId(null);
    setOfficeEmailConfigName('');
    setOfficeEmailConfigProvider('gmail');
    setOfficeEmailConfigFromName('');
    setOfficeEmailConfigFromEmail('');
    setOfficeEmailConfigReplyTo('');
    setOfficeEmailConfigSmtpHost('smtp.gmail.com');
    setOfficeEmailConfigSmtpPort('465');
    setOfficeEmailConfigSecure(true);
    setOfficeEmailConfigSmtpUser('');
    setOfficeEmailConfigSmtpPass('');
    setOfficeEmailConfigProjectIds([]);
    setOfficeEmailConfigIsActive(true);
    setOfficeEmailConfigSaveError(null);
  };

  const openNewOfficeEmailConfig = () => {
    resetOfficeEmailConfigForm();
    setOfficeEmailConfigDrawerOpen(true);
  };

  const openEditOfficeEmailConfig = (id: string) => {
    const row = officeEmailConfigs.find((item: any) => String(item.id) === String(id));
    if (!row) return;
    setEditingOfficeEmailConfigId(String(row.id));
    setOfficeEmailConfigName(String(row.name || ''));
    setOfficeEmailConfigProvider(String(row.provider || '').trim().toLowerCase() === 'custom' ? 'custom' : 'gmail');
    setOfficeEmailConfigFromName(String(row.fromName || ''));
    setOfficeEmailConfigFromEmail(String(row.fromEmail || ''));
    setOfficeEmailConfigReplyTo(String(row.replyTo || ''));
    setOfficeEmailConfigSmtpHost(String(row.smtpHost || 'smtp.gmail.com'));
    setOfficeEmailConfigSmtpPort(String(row.smtpPort || '465'));
    setOfficeEmailConfigSecure(Boolean(row.secure ?? String(row.smtpPort || '') === '465'));
    setOfficeEmailConfigSmtpUser(String(row.smtpUser || ''));
    setOfficeEmailConfigSmtpPass(String(row.smtpPass || ''));
    setOfficeEmailConfigProjectIds(Array.isArray(row.assignedProjectIds) ? row.assignedProjectIds.map((v: any) => String(v || '')).filter(Boolean) : []);
    setOfficeEmailConfigIsActive(row.active !== false);
    setOfficeEmailConfigSaveError(null);
    setOfficeEmailConfigDrawerOpen(true);
  };

  const saveOfficeEmailConfig = async () => {
    setOfficeEmailConfigSaveError(null);
    if (!currentUser?.email) {
      setOfficeEmailConfigSaveError('Not signed in.');
      return;
    }
    const name = String(officeEmailConfigName || '').trim();
    const fromEmail = String(officeEmailConfigFromEmail || '').trim().toLowerCase();
    const smtpHost = String(officeEmailConfigSmtpHost || '').trim();
    const smtpPort = Number(String(officeEmailConfigSmtpPort || '').trim());
    const smtpUser = String(officeEmailConfigSmtpUser || '').trim();
    const smtpPass = String(officeEmailConfigSmtpPass || '').trim();
    if (!name) {
      setOfficeEmailConfigSaveError('Configuration name is required.');
      return;
    }
    if (!fromEmail || !fromEmail.includes('@')) {
      setOfficeEmailConfigSaveError('Valid From Email is required.');
      return;
    }
    if (!smtpHost) {
      setOfficeEmailConfigSaveError('SMTP Host is required.');
      return;
    }
    if (!Number.isFinite(smtpPort) || smtpPort <= 0) {
      setOfficeEmailConfigSaveError('Valid SMTP Port is required.');
      return;
    }
    if (!smtpUser || !smtpPass) {
      setOfficeEmailConfigSaveError('SMTP username and password are required.');
      return;
    }

    setOfficeEmailConfigSaving(true);
    try {
      const payload = {
        name,
        provider: officeEmailConfigProvider,
        fromName: String(officeEmailConfigFromName || '').trim() || null,
        fromEmail,
        replyTo: String(officeEmailConfigReplyTo || '').trim() || null,
        smtpHost,
        smtpPort,
        secure: !!officeEmailConfigSecure,
        smtpUser,
        smtpPass,
        assignedProjectIds: officeEmailConfigProjectIds,
        active: !!officeEmailConfigIsActive,
        updatedAt: serverTimestamp(),
        updatedBy: currentUser.email,
      };
      if (editingOfficeEmailConfigId) {
        await updateDoc(doc(db, 'officeEmailConfigs', editingOfficeEmailConfigId), payload);
      } else {
        await addDoc(collection(db, 'officeEmailConfigs'), {
          ...payload,
          createdAt: serverTimestamp(),
          createdBy: currentUser.email,
        });
      }
      setOfficeEmailConfigDrawerOpen(false);
      resetOfficeEmailConfigForm();
    } catch (err: any) {
      setOfficeEmailConfigSaveError(err?.message || 'Failed to save email configuration.');
    } finally {
      setOfficeEmailConfigSaving(false);
    }
  };

  const deleteOfficeEmailConfig = async (id: string, name: string) => {
    if (!window.confirm(`Delete email configuration "${name}"?`)) return;
    try {
      await deleteDoc(doc(db, 'officeEmailConfigs', id));
    } catch (err: any) {
      setOfficeEmailConfigsError(err?.message || 'Failed to delete email configuration.');
    }
  };

  const normalizeEmailTemplateKey = (value: string) =>
    String(value || '')
      .trim()
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '_')
      .replace(/^_+|_+$/g, '');

  const renderEmailTemplateTokens = useCallback(
    (template: string, tokenValues: Record<string, string>) =>
      String(template || '').replace(/\{\{\s*([^{}]+)\s*\}\}/g, (_all, rawKey) => {
        const normalizedKey = normalizeEmailTemplateKey(String(rawKey || ''));
        if (!normalizedKey) return '';
        return String(tokenValues[normalizedKey] ?? '');
      }),
    []
  );

  const officeEmailPreviewTokens = useMemo(() => {
    const projectId = String(officeEmailTemplateProjectIds?.[0] || editingProjectId || '').trim();
    const projectRow = (projects || []).find((p: any) => String(p?.id || '') === projectId);
    const companyProfileId = String(projectRow?.companyProfileId || '').trim();
    const companyProfileRow =
      (companyProfiles || []).find((cp: any) => String(cp?.id || '') === companyProfileId) ||
      (companyProfiles || [])[0] ||
      null;
    const emailConfig = officeEmailConfigs.find((cfg: any) => String(cfg?.id || '') === String(officeEmailTemplateConfigId || '').trim());
    return {
      unit_no: 'C-403',
      unit_number: 'C-403',
      project_name: String(projectRow?.name || 'Swarna Avsar'),
      allottee_primary_name: 'Kamini Vijay Tamakuwala',
      allottee_name: 'Kamini Vijay Tamakuwala',
      allottee_names_comma_and: 'Kamini Vijay Tamakuwala',
      customer_mobile: '9999999999',
      customer_email: 'customer@example.com',
      sender_name: String(emailConfig?.fromName || 'Swarna Infra'),
      company_name: 'Swarna Infra',
      sender_designation: 'Office Team',
      company_logo_url: String(companyProfileRow?.logoUrl || ''),
      project_logo_url: String(projectRow?.logoUrl || ''),
      issue_date: new Date().toLocaleDateString('en-GB'),
    } as Record<string, string>;
  }, [companyProfiles, editingProjectId, officeEmailConfigs, officeEmailTemplateConfigId, officeEmailTemplateProjectIds, projects]);

  const officeEmailPreviewSubject = useMemo(
    () => renderEmailTemplateTokens(officeEmailTemplateSubject, officeEmailPreviewTokens),
    [officeEmailPreviewTokens, officeEmailTemplateSubject, renderEmailTemplateTokens]
  );

  const officeEmailPreviewBodyHtml = useMemo(
    () => renderEmailTemplateTokens(officeEmailTemplateBodyHtml, officeEmailPreviewTokens),
    [officeEmailPreviewTokens, officeEmailTemplateBodyHtml, renderEmailTemplateTokens]
  );

  const resetOfficeEmailTemplateForm = () => {
    setEditingOfficeEmailTemplateId(null);
    setOfficeEmailTemplateName('');
    setOfficeEmailTemplateKey('');
    setOfficeEmailTemplateConfigId('');
    setOfficeEmailTemplateProjectIds([]);
    setOfficeEmailTemplateSubject('');
    setOfficeEmailTemplateBodyHtml('');
    setOfficeEmailTemplateAttachmentDocKeys([]);
    setOfficeEmailTemplateToMode('primary_customer');
    setOfficeEmailTemplateCustomTo('');
    setOfficeEmailTemplateType('unit');
    setOfficeEmailTemplateIsActive(true);
    setOfficeEmailTemplateSaveError(null);
  };

  const openNewOfficeEmailTemplate = () => {
    resetOfficeEmailTemplateForm();
    setOfficeEmailTemplateDrawerOpen(true);
  };

  const openEditOfficeEmailTemplate = (id: string) => {
    const row = officeEmailTemplates.find((item: any) => String(item.id) === String(id));
    if (!row) return;
    setEditingOfficeEmailTemplateId(String(row.id));
    setOfficeEmailTemplateName(String(row.name || ''));
    setOfficeEmailTemplateKey(String(row.templateKey || row.id || ''));
    setOfficeEmailTemplateConfigId(String(row.emailConfigId || ''));
    setOfficeEmailTemplateProjectIds(Array.isArray(row.assignedProjectIds) ? row.assignedProjectIds.map((v: any) => String(v || '')).filter(Boolean) : []);
    setOfficeEmailTemplateSubject(String(row.subjectTemplate || ''));
    setOfficeEmailTemplateBodyHtml(String(row.bodyHtml || ''));
    setOfficeEmailTemplateAttachmentDocKeys(
      Array.isArray(row.attachmentDocKeys) ? row.attachmentDocKeys.map((v: any) => String(v || '')).filter(Boolean) : []
    );
    const templateType = String(row.templateType || '').trim().toLowerCase();
    setOfficeEmailTemplateType(templateType === 'general' ? 'general' : 'unit');
    const mode = String(row.toMode || '').trim();
    setOfficeEmailTemplateToMode(mode === 'all_customers' || mode === 'custom' ? mode : 'primary_customer');
    setOfficeEmailTemplateCustomTo(
      Array.isArray(row.customToEmails) ? row.customToEmails.join(', ') : String(row.customToEmails || '')
    );
    setOfficeEmailTemplateIsActive(row.active !== false);
    setOfficeEmailTemplateSaveError(null);
    setOfficeEmailTemplateDrawerOpen(true);
  };

  const saveOfficeEmailTemplate = async () => {
    setOfficeEmailTemplateSaveError(null);
    if (!currentUser?.email) {
      setOfficeEmailTemplateSaveError('Not signed in.');
      return;
    }
    const name = String(officeEmailTemplateName || '').trim();
    const templateKey = normalizeEmailTemplateKey(officeEmailTemplateKey || officeEmailTemplateName);
    const emailConfigId = String(officeEmailTemplateConfigId || '').trim();
    const subjectTemplate = String(officeEmailTemplateSubject || '').trim();
    const bodyHtml = String(officeEmailTemplateBodyHtml || '').trim();
    const customToEmails = String(officeEmailTemplateCustomTo || '')
      .split(',')
      .map((v) => String(v || '').trim().toLowerCase())
      .filter(Boolean);
    const templateType = officeEmailTemplateType === 'general' ? 'general' : 'unit';
    if (!name) {
      setOfficeEmailTemplateSaveError('Template name is required.');
      return;
    }
    if (!templateKey) {
      setOfficeEmailTemplateSaveError('Template key is required.');
      return;
    }
    if (!emailConfigId) {
      setOfficeEmailTemplateSaveError('Email configuration is required.');
      return;
    }
    if (!subjectTemplate) {
      setOfficeEmailTemplateSaveError('Subject template is required.');
      return;
    }
    if (!bodyHtml) {
      setOfficeEmailTemplateSaveError('Body HTML is required.');
      return;
    }
    if (officeEmailTemplateToMode === 'custom' && customToEmails.length === 0) {
      setOfficeEmailTemplateSaveError('Add at least one recipient email for custom mode.');
      return;
    }

    setOfficeEmailTemplateSaving(true);
    try {
      const payload = {
        name,
        templateKey,
        emailConfigId,
        assignedProjectIds: officeEmailTemplateProjectIds,
        subjectTemplate,
        bodyHtml,
        attachmentDocKeys: officeEmailTemplateAttachmentDocKeys,
        templateType,
        toMode: officeEmailTemplateToMode,
        customToEmails: officeEmailTemplateToMode === 'custom' ? customToEmails : [],
        active: !!officeEmailTemplateIsActive,
        updatedAt: serverTimestamp(),
        updatedBy: currentUser.email,
      };
      if (editingOfficeEmailTemplateId) {
        await updateDoc(doc(db, 'officeEmailTemplates', editingOfficeEmailTemplateId), payload);
      } else {
        await addDoc(collection(db, 'officeEmailTemplates'), {
          ...payload,
          createdAt: serverTimestamp(),
          createdBy: currentUser.email,
        });
      }
      setOfficeEmailTemplateDrawerOpen(false);
      resetOfficeEmailTemplateForm();
    } catch (err: any) {
      setOfficeEmailTemplateSaveError(err?.message || 'Failed to save email template.');
    } finally {
      setOfficeEmailTemplateSaving(false);
    }
  };

  const openOfficeEmailTemplateTestDialog = () => {
    setOfficeEmailTemplateTestError(null);
    setOfficeEmailTemplateTestSuccess(null);
    const candidateProjectId =
      String(officeEmailTemplateProjectIds?.[0] || '').trim() ||
      String(editingProjectId || '').trim() ||
      String((projects || [])[0]?.id || '').trim();
    setOfficeEmailTemplateTestProjectId(candidateProjectId);
    setOfficeEmailTemplateTestUnitId('');
    setOfficeEmailTemplateTestDialogOpen(true);
  };

  useEffect(() => {
    if (!officeEmailTemplateTestDialogOpen) return;
    const pid = String(officeEmailTemplateTestProjectId || '').trim();
    if (!pid) {
      setOfficeEmailTemplateTestUnits([]);
      return;
    }
    let cancelled = false;
    const run = async () => {
      setOfficeEmailTemplateTestUnitsLoading(true);
      try {
        let rows: any[] = [];
        try {
          const snap = await getDocs(
            query(collection(db, 'projects', pid, 'units'), orderBy('unitNumberLower', 'asc'), limit(800))
          );
          rows = snap.docs.map((d) => ({ id: d.id, ...(d.data() as any) }));
        } catch {
          const snap = await getDocs(
            query(collection(db, 'projects', pid, 'units'), orderBy(documentId()), limit(800))
          );
          rows = snap.docs.map((d) => ({ id: d.id, ...(d.data() as any) }));
        }
        if (!cancelled) {
          setOfficeEmailTemplateTestUnits(rows);
        }
      } catch (err: any) {
        if (!cancelled) {
          setOfficeEmailTemplateTestError(err?.message || 'Failed to load units.');
          setOfficeEmailTemplateTestUnits([]);
        }
      } finally {
        if (!cancelled) setOfficeEmailTemplateTestUnitsLoading(false);
      }
    };
    run();
    return () => {
      cancelled = true;
    };
  }, [db, officeEmailTemplateTestDialogOpen, officeEmailTemplateTestProjectId]);

  const sendOfficeEmailTemplateTest = async () => {
    setOfficeEmailTemplateTestError(null);
    setOfficeEmailTemplateTestSuccess(null);
    if (!editingOfficeEmailTemplateId) {
      setOfficeEmailTemplateTestError('Save template first, then send test.');
      return;
    }
    const pid = String(officeEmailTemplateTestProjectId || '').trim();
    const uid = String(officeEmailTemplateTestUnitId || '').trim();
    if (!pid) {
      setOfficeEmailTemplateTestError('Select project.');
      return;
    }
    if (!uid) {
      setOfficeEmailTemplateTestError('Select unit for test.');
      return;
    }
    setOfficeEmailTemplateTestSending(true);
    try {
      const functions = getFunctions();
      const callable = httpsCallable(functions, 'sendEmailTemplateTest');
      const result = await callable({
        templateId: editingOfficeEmailTemplateId,
        projectId: pid,
        unitId: uid,
      });
      const data = (result?.data || {}) as any;
      const sentTo = Array.isArray(data?.to) ? data.to.join(', ') : '';
      setOfficeEmailTemplateTestSuccess(
        sentTo ? `Test email sent to: ${sentTo}` : 'Test email sent successfully.'
      );
    } catch (err: any) {
      setOfficeEmailTemplateTestError(err?.message || 'Failed to send test email.');
    } finally {
      setOfficeEmailTemplateTestSending(false);
    }
  };

  const deleteOfficeEmailTemplate = async (id: string, name: string) => {
    if (!window.confirm(`Delete email template "${name}"?`)) return;
    try {
      await deleteDoc(doc(db, 'officeEmailTemplates', id));
    } catch (err: any) {
      setOfficeEmailTemplatesError(err?.message || 'Failed to delete email template.');
    }
  };

  const saveWhatsappSender = async () => {
    const nameValue = String(whatsappSenderName || '').trim();
    const phoneIdValue = String(whatsappSenderPhoneNumberId || '').trim();
    if (!nameValue || !phoneIdValue) {
      setWhatsappSenderSaveError('Sender Name and Phone Number ID are required.');
      return;
    }
    setWhatsappSenderSaving(true);
    setWhatsappSenderSaveError(null);
    setWhatsappSenderSaveSuccess(null);
    try {
      const now = serverTimestamp();
      const by = String(currentUser?.email || currentUser?.uid || 'unknown');
      const senderRef = editingWhatsappSenderId
        ? doc(db, 'whatsappSenders', editingWhatsappSenderId)
        : doc(collection(db, 'whatsappSenders'));
      const batch = writeBatch(db);
      if (whatsappSenderDefault && whatsappSenderActive) {
        const defaultsSnap = await getDocs(query(collection(db, 'whatsappSenders'), where('isDefault', '==', true)));
        defaultsSnap.docs.forEach((d) => {
          if (d.id === senderRef.id) return;
          batch.set(d.ref, { isDefault: false, updatedAt: now, updatedBy: by }, { merge: true });
        });
      }
      batch.set(
        senderRef,
        {
          name: nameValue,
          phoneNumberId: phoneIdValue,
          businessAccountId: String(whatsappSenderBusinessAccountId || '').trim(),
          accessToken: String(whatsappSenderAccessToken || '').trim(),
          active: !!whatsappSenderActive,
          isDefault: !!whatsappSenderDefault && !!whatsappSenderActive,
          updatedAt: now,
          updatedBy: by,
          ...(editingWhatsappSenderId ? {} : { createdAt: now, createdBy: by }),
        },
        { merge: true }
      );
      await batch.commit();
      setWhatsappSenderSaveSuccess('Sender saved.');
      setWhatsappSenderDialogOpen(false);
      resetWhatsappSenderForm();
    } catch (err: any) {
      console.error('Save WhatsApp sender failed', err);
      setWhatsappSenderSaveError(err?.message || 'Failed to save sender.');
    } finally {
      setWhatsappSenderSaving(false);
    }
  };

  const fetchMetaTemplates = async () => {
    setWhatsappMetaTemplatesLoading(true);
    setWhatsappTemplateSaveError(null);
    try {
      const functions = getFunctions();
      const callable = httpsCallable(functions, 'whatsappFetchTemplates');
      const result = await callable({});
      const data = result.data as any;
      const templates = Array.isArray(data?.templates) ? data.templates : [];
      setWhatsappMetaTemplates(templates);
      setWhatsappMetaTemplatesFetched(true);
    } catch (err: any) {
      setWhatsappTemplateSaveError(`Failed to fetch templates: ${err?.message || 'Unknown error'}`);
    } finally {
      setWhatsappMetaTemplatesLoading(false);
    }
  };

  const findWhatsappTemplateByMeta = useCallback((name: string, language: string) => {
    const normalizedName = String(name || '').trim().toLowerCase();
    const normalizedLanguage = String(language || 'en').trim().toLowerCase() || 'en';
    if (!normalizedName) return null;
    return whatsappTemplates.find((template: any) =>
      String(template?.name || '').trim().toLowerCase() === normalizedName &&
      (String(template?.language || 'en').trim().toLowerCase() || 'en') === normalizedLanguage
    ) || null;
  }, [whatsappTemplates]);

  const applyMetaTemplate = (metaTemplate: any) => {
    const metaName = String(metaTemplate.name || '').trim();
    const metaLanguage = String(metaTemplate.language || 'en').trim() || 'en';
    const existingTemplate = findWhatsappTemplateByMeta(metaName, metaLanguage);
    setEditingWhatsappTemplateId(existingTemplate?.id ? String(existingTemplate.id) : null);
    setWhatsappTemplateName(metaName);
    setWhatsappTemplateLanguage(metaLanguage);
    if (existingTemplate?.label) {
      setWhatsappTemplateLabel(String(existingTemplate.label || ''));
    } else if (!whatsappTemplateLabel.trim()) {
      setWhatsappTemplateLabel(
        String(metaTemplate.name || '').replace(/_/g, ' ').replace(/\b\w/g, (c: string) => c.toUpperCase())
      );
    }
    setWhatsappTemplateShowInLeadChat(!!existingTemplate?.showInLeadChat);
    const components = Array.isArray(metaTemplate.components) ? metaTemplate.components : [];
    const headerComp = components.find((c: any) => c.type === 'HEADER');
    if (headerComp) {
      const format = String(headerComp.format || '').toUpperCase();
      if (format === 'IMAGE') {
        setWhatsappTemplateHeaderFormat('image');
        const existingHeaderImageUrl = String(existingTemplate?.headerImageUrl || '').trim();
        const existingHeaderImageUploadPath = String(existingTemplate?.headerImageUploadPath || '').trim();
        const useProjectLogo = existingHeaderImageUrl === WHATSAPP_HEADER_PROJECT_LOGO_TOKEN;
        const useVoucherOrProjectLogo = existingHeaderImageUrl === WHATSAPP_HEADER_VOUCHER_OR_PROJECT_LOGO_TOKEN;
        setWhatsappTemplateHeaderImageSource(
          useVoucherOrProjectLogo ? 'voucher_or_project_logo' :
            (useProjectLogo || !existingHeaderImageUrl) ? 'project_logo' :
              existingHeaderImageUploadPath ? 'upload' :
                'custom'
        );
        setWhatsappTemplateHeaderImageUrl((useProjectLogo || useVoucherOrProjectLogo) ? '' : existingHeaderImageUrl);
        setWhatsappTemplateHeaderImageUploadPath(existingHeaderImageUploadPath);
        setWhatsappTemplateHeaderImageFile(null);
      } else if (format === 'DOCUMENT') {
        setWhatsappTemplateHeaderFormat('document');
        const existingHeaderDocumentUrl = String(existingTemplate?.headerDocumentUrl || '').trim();
        const useProjectBrochure = existingHeaderDocumentUrl === WHATSAPP_HEADER_PROJECT_BROCHURE_TOKEN;
        const useLeadReport = existingHeaderDocumentUrl === WHATSAPP_HEADER_LEAD_REPORT_TOKEN;
        setWhatsappTemplateHeaderDocumentSource(useLeadReport ? 'lead_report' : (useProjectBrochure || !existingHeaderDocumentUrl ? 'project_brochure' : 'custom'));
        setWhatsappTemplateHeaderDocumentUrl((useProjectBrochure || useLeadReport) ? '' : existingHeaderDocumentUrl);
      } else if (format === 'LOCATION') {
        setWhatsappTemplateHeaderFormat('location');
      } else {
        setWhatsappTemplateHeaderFormat('none');
      }
    } else {
      setWhatsappTemplateHeaderFormat('none');
    }
    const bodyComp = components.find((c: any) => c.type === 'BODY');
    if (bodyComp) {
      const bodyText = String(bodyComp.text || '');
      setWhatsappTemplateBodyText(bodyText);
      const varMatches = bodyText.match(/\{\{\d+\}\}/g) || [];
      const existingVariables = Array.isArray(existingTemplate?.variables) ? existingTemplate.variables : [];
      const vars = varMatches.map((match: string, idx: number) => ({
        key: String(existingVariables[idx]?.key || `var${idx + 1}`),
        label: String(existingVariables[idx]?.label || `Variable ${idx + 1}`),
        defaultValue: String(existingVariables[idx]?.defaultValue || ''),
        source: String(existingVariables[idx]?.source || 'custom'),
      }));
      setWhatsappTemplateVariables(vars);
    } else {
      setWhatsappTemplateBodyText('');
      setWhatsappTemplateVariables([]);
    }
    const buttonsComp = components.find((c: any) => c.type === 'BUTTONS');
    const buttons = Array.isArray(buttonsComp?.buttons) ? buttonsComp.buttons : [];
    const existingButtonVariables = Array.isArray(existingTemplate?.buttonVariables)
      ? existingTemplate.buttonVariables
      : [];
    setWhatsappTemplateButtonVariables(
      buttons
        .map((button: any, idx: number) => {
          if (String(button?.type || '').toUpperCase() !== 'URL') return null;
          if (!/\{\{\d+\}\}/.test(String(button?.url || ''))) return null;
          const existingButtonVariable = existingButtonVariables.find((entry: any) => String(entry?.index ?? '') === String(idx));
          return {
            index: String(idx),
            key: String(existingButtonVariable?.key || `button${idx + 1}`),
            label: String(existingButtonVariable?.label || button?.text || `Button ${idx + 1}`),
            defaultValue: String(existingButtonVariable?.defaultValue || ''),
            source: String(existingButtonVariable?.source || 'project_slug'),
          };
        })
        .filter(Boolean) as Array<{index: string; key: string; label: string; defaultValue: string; source: string}>
    );
  };

  const saveWhatsappTemplate = async () => {
    const labelValue = String(whatsappTemplateLabel || '').trim();
    const nameValue = String(whatsappTemplateName || '').trim();
    const languageValue = String(whatsappTemplateLanguage || 'en').trim() || 'en';
    if (!labelValue || !nameValue) {
      setWhatsappTemplateSaveError('Template Label and Meta Template Name are required.');
      return;
    }
    const normalizedVariables = whatsappTemplateVariables
      .map((entry) => ({
        key: String(entry?.key || '').trim(),
        label: String(entry?.label || entry?.key || '').trim(),
        defaultValue: String(entry?.defaultValue || '').trim(),
        source: String(entry?.source || 'custom').trim(),
      }))
      .filter((entry) => !!entry.key);
    const normalizedButtonVariables = whatsappTemplateButtonVariables
      .map((entry) => ({
        index: String(entry?.index ?? '0').trim() || '0',
        key: String(entry?.key || '').trim(),
        label: String(entry?.label || entry?.key || '').trim(),
        defaultValue: String(entry?.defaultValue || '').trim(),
        source: String(entry?.source || 'custom').trim(),
      }))
      .filter((entry) => !!entry.key);
    const hfLower = String(whatsappTemplateHeaderFormat || 'none').toLowerCase();
    const normalizedHeaderFormat =
      hfLower === 'image' ? 'IMAGE' : hfLower === 'document' ? 'DOCUMENT' : hfLower === 'location' ? 'LOCATION' : 'NONE';
    let normalizedHeaderImageUrl = whatsappTemplateHeaderImageSource === 'project_logo'
      ? WHATSAPP_HEADER_PROJECT_LOGO_TOKEN
      : whatsappTemplateHeaderImageSource === 'voucher_or_project_logo'
        ? WHATSAPP_HEADER_VOUCHER_OR_PROJECT_LOGO_TOKEN
        : String(whatsappTemplateHeaderImageUrl || '').trim();
    let normalizedHeaderImageUploadPath = String(whatsappTemplateHeaderImageUploadPath || '').trim();
    const normalizedHeaderDocumentUrl = whatsappTemplateHeaderDocumentSource === 'project_brochure'
      ? WHATSAPP_HEADER_PROJECT_BROCHURE_TOKEN
      : whatsappTemplateHeaderDocumentSource === 'lead_report'
        ? WHATSAPP_HEADER_LEAD_REPORT_TOKEN
        : String(whatsappTemplateHeaderDocumentUrl || '').trim();
    if (normalizedHeaderFormat === 'IMAGE' && whatsappTemplateHeaderImageSource === 'upload' && !whatsappTemplateHeaderImageFile && !normalizedHeaderImageUrl) {
      setWhatsappTemplateSaveError('Upload an image or choose another Image Source.');
      return;
    }
    if (normalizedHeaderFormat === 'IMAGE' && whatsappTemplateHeaderImageSource === 'custom' && !normalizedHeaderImageUrl) {
      setWhatsappTemplateSaveError('Header image URL is required when Header Format is Image.');
      return;
    }
    if (normalizedHeaderFormat === 'DOCUMENT' && whatsappTemplateHeaderDocumentSource === 'custom' && !normalizedHeaderDocumentUrl) {
      setWhatsappTemplateSaveError('Header document URL is required when Header Format is Document.');
      return;
    }
    if (normalizedHeaderFormat === 'LOCATION' && whatsappTemplateHeaderLocationSource === 'custom' && (!whatsappTemplateHeaderLatitude.trim() || !whatsappTemplateHeaderLongitude.trim())) {
      setWhatsappTemplateSaveError('Latitude and Longitude are required when Location Source is Custom.');
      return;
    }
    const uniqueKeys = new Set(normalizedVariables.map((entry) => entry.key));
    if (uniqueKeys.size !== normalizedVariables.length) {
      setWhatsappTemplateSaveError('Variable keys must be unique.');
      return;
    }
    setWhatsappTemplateSaving(true);
    setWhatsappTemplateSaveError(null);
    try {
      const now = serverTimestamp();
      const by = String(currentUser?.email || currentUser?.uid || 'unknown');
      const existingTemplate = findWhatsappTemplateByMeta(nameValue, languageValue);
      const targetTemplateId = editingWhatsappTemplateId || (
        existingTemplate?.id ? String(existingTemplate.id) : ''
      );
      const templateRef = targetTemplateId
        ? doc(db, 'whatsappTemplates', targetTemplateId)
        : doc(collection(db, 'whatsappTemplates'));
      if (normalizedHeaderFormat === 'IMAGE' && whatsappTemplateHeaderImageSource === 'upload' && whatsappTemplateHeaderImageFile) {
        const ext = whatsappTemplateHeaderImageFile.name.includes('.')
          ? whatsappTemplateHeaderImageFile.name.split('.').pop()
          : 'jpg';
        const safeExt = String(ext || 'jpg').replace(/[^a-zA-Z0-9]/g, '').toLowerCase() || 'jpg';
        const safeName = String(nameValue || templateRef.id).replace(/[^a-zA-Z0-9_-]/g, '_');
        const uploadPath = `whatsapp/template-header-images/${templateRef.id}/${Date.now()}_${safeName}.${safeExt}`;
        const uploadRef = ref(storage, uploadPath);
        await uploadBytes(uploadRef, whatsappTemplateHeaderImageFile, {
          contentType: whatsappTemplateHeaderImageFile.type || 'image/jpeg',
        });
        normalizedHeaderImageUrl = await getDownloadURL(uploadRef);
        normalizedHeaderImageUploadPath = uploadPath;
      }
      await setDoc(
        templateRef,
        {
          label: labelValue,
          name: nameValue,
          language: languageValue,
          headerFormat: normalizedHeaderFormat,
          headerImageUrl: normalizedHeaderFormat === 'IMAGE' ? normalizedHeaderImageUrl : '',
          headerImageUploadPath: normalizedHeaderFormat === 'IMAGE' && whatsappTemplateHeaderImageSource === 'upload' ? normalizedHeaderImageUploadPath : '',
          headerDocumentUrl: normalizedHeaderFormat === 'DOCUMENT' ? normalizedHeaderDocumentUrl : '',
          headerLatitude: normalizedHeaderFormat === 'LOCATION' ? whatsappTemplateHeaderLatitude.trim() : '',
          headerLongitude: normalizedHeaderFormat === 'LOCATION' ? whatsappTemplateHeaderLongitude.trim() : '',
          headerLocationName: normalizedHeaderFormat === 'LOCATION' ? whatsappTemplateHeaderLocationName.trim() : '',
          headerLocationAddress: normalizedHeaderFormat === 'LOCATION' ? whatsappTemplateHeaderLocationAddress.trim() : '',
          headerLocationSource: normalizedHeaderFormat === 'LOCATION' ? whatsappTemplateHeaderLocationSource : 'auto',
          variables: normalizedVariables,
          buttonVariables: normalizedButtonVariables,
          trigger: 'none',
          autoSend: false,
          showInLeadChat: whatsappTemplateShowInLeadChat,
          bodyText: whatsappTemplateBodyText.trim() || null,
          updatedAt: now,
          updatedBy: by,
          ...(targetTemplateId ? {} : { createdAt: now, createdBy: by }),
        },
        { merge: true }
      );
      setWhatsappTemplateDialogOpen(false);
      resetWhatsappTemplateForm();
    } catch (err: any) {
      console.error('Save WhatsApp template failed', err);
      setWhatsappTemplateSaveError(err?.message || 'Failed to save template.');
    } finally {
      setWhatsappTemplateSaving(false);
    }
  };

  const deleteWhatsappTemplate = async (id: string, labelValue: string) => {
    const confirmed = window.confirm(`Delete WhatsApp template "${labelValue}"?`);
    if (!confirmed) return;
    try {
      await deleteDoc(doc(db, 'whatsappTemplates', id));
    } catch (err: any) {
      console.error('Delete WhatsApp template failed', err);
      setWhatsappTemplatesError(err?.message || 'Failed to delete template.');
    }
  };

  const saveWhatsappQuickReply = async () => {
    const labelValue = String(whatsappQuickReplyLabel || '').trim();
    const textValue = String(whatsappQuickReplyText || '').trim();
    const categoryValue = String(whatsappQuickReplyCategory || '').trim();
    const sortOrderValue = Number(whatsappQuickReplySortOrder || 0);
    if (!labelValue || !textValue) {
      setWhatsappQuickReplySaveError('Reply label and message text are required.');
      return;
    }
    if (whatsappQuickReplyScope === 'project' && !String(whatsappQuickReplyProjectId || '').trim()) {
      setWhatsappQuickReplySaveError('Select a project for project-specific quick reply.');
      return;
    }
    setWhatsappQuickReplySaving(true);
    setWhatsappQuickReplySaveError(null);
    try {
      const now = serverTimestamp();
      const by = String(currentUser?.email || currentUser?.uid || 'unknown');
      const replyRef = editingWhatsappQuickReplyId
        ? doc(db, 'whatsappQuickReplies', editingWhatsappQuickReplyId)
        : doc(collection(db, 'whatsappQuickReplies'));
      await setDoc(
        replyRef,
        {
          label: labelValue,
          text: textValue,
          category: categoryValue || 'General',
          scope: whatsappQuickReplyScope,
          projectId: whatsappQuickReplyScope === 'project' ? String(whatsappQuickReplyProjectId || '').trim() : null,
          active: whatsappQuickReplyActive,
          sortOrder: Number.isFinite(sortOrderValue) ? sortOrderValue : 0,
          updatedAt: now,
          updatedBy: by,
          ...(editingWhatsappQuickReplyId ? {} : { createdAt: now, createdBy: by }),
        },
        { merge: true }
      );
      setWhatsappQuickReplyDialogOpen(false);
      resetWhatsappQuickReplyForm();
    } catch (err: any) {
      console.error('Save WhatsApp quick reply failed', err);
      setWhatsappQuickReplySaveError(err?.message || 'Failed to save quick reply.');
    } finally {
      setWhatsappQuickReplySaving(false);
    }
  };

  const deleteWhatsappQuickReply = async (id: string, labelValue: string) => {
    const confirmed = window.confirm(`Delete WhatsApp quick reply "${labelValue}"?`);
    if (!confirmed) return;
    try {
      await deleteDoc(doc(db, 'whatsappQuickReplies', id));
    } catch (err: any) {
      console.error('Delete WhatsApp quick reply failed', err);
      setWhatsappQuickRepliesError(err?.message || 'Failed to delete quick reply.');
    }
  };

  const saveWhatsappAutomationConfig = async (partial: Record<string, any>) => {
    setWhatsappAutomationSaving(true);
    setWhatsappAutomationError(null);
    try {
      await setDoc(
        doc(db, 'config', 'whatsappAutomation'),
        {
          ...partial,
          updatedAt: serverTimestamp(),
          updatedBy: String(currentUser?.email || currentUser?.uid || 'unknown'),
        },
        { merge: true }
      );
    } catch (err: any) {
      console.error('Save WhatsApp automation config failed', err);
      setWhatsappAutomationError(err?.message || 'Failed to update WhatsApp automation.');
    } finally {
      setWhatsappAutomationSaving(false);
    }
  };

  const saveWhatsappAutomationEnabled = async (nextEnabled: boolean) => {
    const prevEnabled = whatsappAutomationEnabled;
    setWhatsappAutomationEnabled(nextEnabled);
    try {
      await saveWhatsappAutomationConfig({ enabled: nextEnabled });
    } catch {
      setWhatsappAutomationEnabled(prevEnabled);
    }
  };

  const saveBirthdayWishesConfig = async () => {
    setBirthdayWishesSaving(true);
    setBirthdayWishesError(null);
    setBirthdayWishesSuccess(null);
    try {
      await setDoc(
        doc(db, 'birthdayWishConfig', 'main'),
        {
          enabled: birthdayWishesEnabled,
          includeProjectCustomers: birthdayWishesIncludeProjectCustomers,
          includeBrokers: birthdayWishesIncludeBrokers,
          includeStaff: birthdayWishesIncludeStaff,
          includeContactsBook: birthdayWishesIncludeContactsBook,
          customerTemplateId: birthdayWishesCustomerTemplateId || null,
          brokerTemplateId: birthdayWishesBrokerTemplateId || null,
          staffTemplateId: birthdayWishesStaffTemplateId || null,
          contactTemplateId: birthdayWishesContactTemplateId || null,
          sendTime: '09:30',
          channel: 'whatsapp',
          updatedAt: serverTimestamp(),
          updatedBy: String(currentUser?.email || currentUser?.uid || 'unknown'),
        },
        { merge: true }
      );
      setBirthdayWishesSuccess('Birthday wishes settings saved.');
    } catch (err: any) {
      console.error('Save birthday wishes config failed', err);
      setBirthdayWishesError(err?.message || 'Failed to save birthday wishes settings.');
    } finally {
      setBirthdayWishesSaving(false);
    }
  };

  const resetContactsBookForm = () => {
    setEditingContactsBookId(null);
    setContactsBookName('');
    setContactsBookPhone('');
    setContactsBookType('Other');
    setContactsBookDateOfBirth('');
    setContactsBookAnniversaryDate('');
    setContactsBookOrganization('');
    setContactsBookProjectId('');
    setContactsBookNotes('');
    setContactsBookActive(true);
    setContactsBookSaveError(null);
  };

  const openNewContactsBook = () => {
    resetContactsBookForm();
    setContactsBookDialogOpen(true);
  };

  const openEditContactsBook = (row: any) => {
    setEditingContactsBookId(String(row?.id || ''));
    setContactsBookName(String(row?.name || row?.displayName || row?.contactName || ''));
    setContactsBookPhone(String(row?.phone || row?.phoneE164 || row?.whatsappNumber || row?.mobile || ''));
    setContactsBookType(String(row?.type || row?.contactType || 'Other'));
    setContactsBookDateOfBirth(String(row?.dateOfBirth || row?.dob || ''));
    setContactsBookAnniversaryDate(String(row?.anniversaryDate || ''));
    setContactsBookOrganization(String(row?.organization || row?.company || ''));
    setContactsBookProjectId(String(row?.projectId || ''));
    setContactsBookNotes(String(row?.notes || ''));
    setContactsBookActive(row?.active !== false);
    setContactsBookSaveError(null);
    setContactsBookDialogOpen(true);
  };

  const saveContactsBook = async () => {
    const contactName = contactsBookName.trim();
    if (!contactName) {
      setContactsBookSaveError('Contact name is required.');
      return;
    }
    setContactsBookSaving(true);
    setContactsBookSaveError(null);
    try {
      const payload = {
        name: contactName,
        displayName: contactName,
        nameLower: contactName.toLowerCase(),
        phone: contactsBookPhone.trim(),
        phone10: contactsBookPhone.replace(/\D/g, '').slice(-10),
        type: contactsBookType.trim() || 'Other',
        dateOfBirth: contactsBookDateOfBirth || null,
        dob: contactsBookDateOfBirth || null,
        anniversaryDate: contactsBookAnniversaryDate || null,
        organization: contactsBookOrganization.trim(),
        projectId: contactsBookProjectId || null,
        notes: contactsBookNotes.trim(),
        active: contactsBookActive,
        updatedAt: serverTimestamp(),
        updatedBy: String(currentUser?.email || currentUser?.uid || 'unknown'),
      };
      if (editingContactsBookId) {
        await updateDoc(doc(db, 'contactsBook', editingContactsBookId), payload);
      } else {
        await addDoc(collection(db, 'contactsBook'), {
          ...payload,
          createdAt: serverTimestamp(),
          createdBy: String(currentUser?.email || currentUser?.uid || 'unknown'),
        });
      }
      setContactsBookDialogOpen(false);
      resetContactsBookForm();
    } catch (err: any) {
      console.error('Save contacts book failed', err);
      setContactsBookSaveError(err?.message || 'Failed to save contact.');
    } finally {
      setContactsBookSaving(false);
    }
  };

  const deleteContactsBook = async (row: any) => {
    const contactId = String(row?.id || '');
    if (!contactId) return;
    const contactName = String(row?.name || row?.displayName || 'this contact');
    if (!window.confirm(`Delete ${contactName} from Contacts Book?`)) return;
    try {
      await deleteDoc(doc(db, 'contactsBook', contactId));
    } catch (err: any) {
      console.error('Delete contacts book failed', err);
      setContactsBookError(err?.message || 'Failed to delete contact.');
    }
  };

  const saveWhatsappAutomationRule = async () => {
    const nameValue = String(whatsappAutomationRuleName || '').trim();
    const templateIdValue = String(whatsappAutomationRuleTemplateId || '').trim();
    const priorityValue = Number(whatsappAutomationRulePriority || 0);
    const delayAmountValue = Number(whatsappAutomationRuleDelayAmount || 0);
    const cooldownAmountValue = Number(whatsappAutomationRuleCooldownAmount || 0);
    if (!nameValue || !templateIdValue) {
      setWhatsappAutomationRuleSaveError('Rule name and template are required.');
      return;
    }
    setWhatsappAutomationRuleSaving(true);
    setWhatsappAutomationRuleSaveError(null);
    try {
      const now = serverTimestamp();
      const by = String(currentUser?.email || currentUser?.uid || 'unknown');
      const ruleRef = editingWhatsappAutomationRuleId
        ? doc(db, 'whatsappAutomationRules', editingWhatsappAutomationRuleId)
        : doc(collection(db, 'whatsappAutomationRules'));
      await setDoc(
        ruleRef,
        {
          name: nameValue,
          active: whatsappAutomationRuleMode !== 'inactive',
          mode: whatsappAutomationRuleMode,
          trigger: whatsappAutomationRuleTrigger,
          recipient: whatsappAutomationRuleRecipient,
          templateId: templateIdValue,
          priority: Number.isFinite(priorityValue) ? priorityValue : 10,
          delayAmount: Number.isFinite(delayAmountValue) ? Math.max(0, delayAmountValue) : 0,
          delayUnit: whatsappAutomationRuleDelayUnit,
          delayDirection: whatsappAutomationRuleDelayDirection,
          scheduleAnchor: whatsappAutomationRuleScheduleAnchor,
          repeatMode: whatsappAutomationRuleRepeatMode,
          dndMode: whatsappAutomationRuleDndMode,
          sendWindow: whatsappAutomationRuleSendWindow,
          conditions: {
            sourceValues: whatsappAutomationRuleSourceValues.filter(Boolean),
            projectIds: [],
            statusValues: whatsappAutomationRuleStatusValues.filter(Boolean),
            enquiryTypes: String(whatsappAutomationRuleEnquiryTypes || '')
              .split(',')
              .map((value) => value.trim())
              .filter(Boolean),
            appointmentTypes:
              whatsappAutomationRuleTrigger === 'appointment-scheduled' || whatsappAutomationRuleTrigger === 'visit-completed' ?
                whatsappAutomationRuleAppointmentTypes.filter(Boolean) :
                [],
            receiptTypes:
              whatsappAutomationRuleTrigger === 'customer-payment-receipt' ?
                whatsappAutomationRuleReceiptTypes.filter(Boolean) :
                [],
            siteVisitState: whatsappAutomationRuleSiteVisitState,
            upcomingAppointmentState: whatsappAutomationRuleUpcomingAppointmentState,
            advancedConditions: whatsappAutomationRuleAdvancedConditions
              .map((condition) => condition.field === 'lead_match'
                ? {
                    field: condition.field,
                    operator: condition.operator,
                    value: 0,
                    unit: condition.unit,
                  }
                : {
                    field: condition.field,
                    operator: condition.operator,
                    value: Number(condition.value || 0) || 0,
                    unit: condition.unit,
                  })
              .filter((condition) =>
                condition.field === 'lead_match' ?
                  condition.operator !== 'any' :
                  condition.value > 0
              ),
            cooldownAmount:
              Number.isFinite(cooldownAmountValue) ?
                Math.max(0, cooldownAmountValue) :
                0,
            cooldownUnit: whatsappAutomationRuleCooldownUnit,
          },
          updatedAt: now,
          updatedBy: by,
          ...(editingWhatsappAutomationRuleId ? {} : { createdAt: now, createdBy: by }),
        },
        { merge: true }
      );
      setWhatsappAutomationRuleDialogOpen(false);
      resetWhatsappAutomationRuleForm();
    } catch (err: any) {
      console.error('Save WhatsApp automation rule failed', err);
      setWhatsappAutomationRuleSaveError(err?.message || 'Failed to save automation rule.');
    } finally {
      setWhatsappAutomationRuleSaving(false);
    }
  };

  const updateWhatsappConversationActionReply = (
    projectId: string,
    actionKey: string,
    patch: Partial<WhatsappConversationActionReplyConfig>
  ) => {
    setWhatsappConversationFlowActionReplies((prev) => {
      const existing = prev[projectId]?.[actionKey] || { messageText: '', buttonActionKeys: [] };
      return {
        ...prev,
        [projectId]: {
          ...(prev[projectId] || {}),
          [actionKey]: {
            ...existing,
            ...patch,
          },
        },
      };
    });
  };

  const saveWhatsappConversationFlow = async () => {
    setWhatsappConversationFlowSaving(true);
    setWhatsappConversationFlowError(null);
    setWhatsappConversationFlowSuccess(null);
    try {
      const by = String(currentUser?.email || currentUser?.uid || 'unknown');
      const selectedProjectIds = whatsappConversationFlowProjectIds.filter(Boolean);
      const selectedActionKeys = whatsappConversationFlowActionKeys.filter(Boolean);
      const selectedActionSet = new Set(selectedActionKeys);
      const cleanedActionReplies: WhatsappConversationActionReplies = {};
      selectedProjectIds.forEach((projectId) => {
        selectedActionKeys.forEach((actionKey) => {
          const config = whatsappConversationFlowActionReplies[projectId]?.[actionKey];
          if (!config) return;
          const messageText = String(config.messageText || '').trim();
          const responseType = String(config.responseType || 'default').trim() || 'default';
          const urlButtonText = String(config.urlButtonText || '').trim();
          const urlSource = String(config.urlSource || 'auto').trim() || 'auto';
          const customUrl = String(config.customUrl || '').trim();
          const buttonActionKeys = (config.buttonActionKeys || [])
            .map((value) => String(value || '').trim())
            .filter((value) => value && value !== actionKey && selectedActionSet.has(value))
            .slice(0, 3);
          const hasCustomResponse =
            responseType !== 'default' ||
            !!urlButtonText ||
            urlSource !== 'auto' ||
            !!customUrl;
          if (!messageText && !buttonActionKeys.length && !hasCustomResponse) return;
          if (!cleanedActionReplies[projectId]) cleanedActionReplies[projectId] = {};
          cleanedActionReplies[projectId][actionKey] = {
            messageText,
            buttonActionKeys,
            responseType,
            urlButtonText,
            urlSource,
            customUrl,
          };
        });
      });
      await setDoc(
        doc(db, 'whatsappConversationFlows', selectedWhatsappConversationFlowId || 'project_selection'),
        {
          name: String(whatsappConversationFlowName || '').trim() || 'Project Selection Flow',
          type: 'project_selection',
          priority: Number(whatsappConversationFlowPriority) || 10,
          active: whatsappConversationFlowActive,
          projectIds: selectedProjectIds,
          actionKeys: selectedActionKeys,
          actionReplies: cleanedActionReplies,
          startKeywords: String(whatsappConversationFlowStartKeywords || '')
            .split(',')
            .map((value) => value.trim())
            .filter(Boolean),
          projectPromptText: String(whatsappConversationFlowProjectPrompt || '').trim() || 'Please select the project you are interested in.',
          actionPromptText: String(whatsappConversationFlowActionPrompt || '').trim() || 'What would you like to see for this project?',
          updatedAt: serverTimestamp(),
          updatedBy: by,
        },
        { merge: true }
      );
      setWhatsappConversationFlowSuccess('Conversation flow saved.');
    } catch (err: any) {
      console.error('Save WhatsApp conversation flow failed', err);
      setWhatsappConversationFlowError(err?.message || 'Failed to save conversation flow.');
    } finally {
      setWhatsappConversationFlowSaving(false);
    }
  };

  const createWhatsappConversationFlow = async () => {
    setWhatsappConversationFlowSaving(true);
    setWhatsappConversationFlowError(null);
    setWhatsappConversationFlowSuccess(null);
    try {
      const by = String(currentUser?.email || currentUser?.uid || 'unknown');
      const docRef = await addDoc(collection(db, 'whatsappConversationFlows'), {
        name: `Project Selection Flow ${whatsappConversationFlows.length + 1}`,
        type: 'project_selection',
        active: false,
        priority: 20 + whatsappConversationFlows.length,
        projectIds: [],
        actionKeys: ['brochure', 'location', 'website', 'call'],
        actionReplies: {},
        startKeywords: ['Select Project'],
        projectPromptText: 'Please select the project you are interested in.',
        actionPromptText: 'What would you like to see for this project?',
        createdAt: serverTimestamp(),
        createdBy: by,
        updatedAt: serverTimestamp(),
        updatedBy: by,
      });
      setSelectedWhatsappConversationFlowId(docRef.id);
      setWhatsappConversationFlowSuccess('New conversation flow created.');
    } catch (err: any) {
      console.error('Create WhatsApp conversation flow failed', err);
      setWhatsappConversationFlowError(err?.message || 'Failed to create conversation flow.');
    } finally {
      setWhatsappConversationFlowSaving(false);
    }
  };

  const deleteWhatsappConversationFlow = async () => {
    const id = String(selectedWhatsappConversationFlowId || '').trim();
    if (!id) return;
    if (id === 'project_selection') {
      setWhatsappConversationFlowError('Default Project Selection Flow cannot be deleted. You can make it inactive.');
      return;
    }
    const label = String(whatsappConversationFlowName || id).trim();
    if (!window.confirm(`Delete WhatsApp conversation flow "${label}"?`)) return;
    setWhatsappConversationFlowSaving(true);
    setWhatsappConversationFlowError(null);
    setWhatsappConversationFlowSuccess(null);
    try {
      await deleteDoc(doc(db, 'whatsappConversationFlows', id));
      const next = whatsappConversationFlows.find((row) => row.id !== id)?.id || 'project_selection';
      setSelectedWhatsappConversationFlowId(next);
      setWhatsappConversationFlowSuccess('Conversation flow deleted.');
    } catch (err: any) {
      console.error('Delete WhatsApp conversation flow failed', err);
      setWhatsappConversationFlowError(err?.message || 'Failed to delete conversation flow.');
    } finally {
      setWhatsappConversationFlowSaving(false);
    }
  };

  const deleteWhatsappAutomationRule = async (id: string, nameValue: string) => {
    const confirmed = window.confirm(`Delete WhatsApp automation rule "${nameValue}"?`);
    if (!confirmed) return;
    try {
      await deleteDoc(doc(db, 'whatsappAutomationRules', id));
    } catch (err: any) {
      console.error('Delete WhatsApp automation rule failed', err);
      setWhatsappAutomationRulesError(err?.message || 'Failed to delete automation rule.');
    }
  };

  const deleteWhatsappSender = async (id: string, nameValue: string) => {
    const confirmed = window.confirm(`Delete WhatsApp sender "${nameValue}"?`);
    if (!confirmed) return;
    try {
      await deleteDoc(doc(db, 'whatsappSenders', id));
    } catch (err: any) {
      console.error('Delete WhatsApp sender failed', err);
      setWhatsappSendersError(err?.message || 'Failed to delete sender.');
    }
  };

  const autoFillWhatsappTemplateValues = () => {
    const tx = whatsappPrefillTxns.find((row: any) => String(row?.id || '') === whatsappPrefillTxnId) || null;
    if (!tx || !selectedWhatsappTemplate) {
      setWhatsappTestError('Select Project, Unit and Transaction first.');
      return;
    }
    const projectName = projectNameById.get(String(whatsappPrefillProjectId || '').trim()) || '';
    const amountValue = Number(tx?.amount || 0) || 0;
    const dateObj = tx?.chqDate?.toDate && typeof tx.chqDate.toDate === 'function'
      ? tx.chqDate.toDate()
      : tx?.txnDate?.toDate && typeof tx.txnDate.toDate === 'function'
        ? tx.txnDate.toDate()
        : null;
    const dateText = dateObj instanceof Date && !Number.isNaN(dateObj.getTime())
      ? dateObj.toLocaleDateString('en-GB')
      : '';
    const refText = String(tx?.referenceNo || tx?.refNo || '').trim();
    const hasUnitNumber = !!String(tx?.unitNumber || tx?.unitNo || '').trim();
    const paymentType = String(tx?.receiptCategory || '').toLowerCase() === 'gst'
      ? 'GST Payment'
      : hasUnitNumber
        ? 'Property Payment'
        : String(tx?.mode || tx?.receiptCategory || tx?.txnType || '').trim();
    const partyName = String(tx?.partyName || '').trim();
    const variables = Array.isArray(selectedWhatsappTemplate?.variables) ? selectedWhatsappTemplate.variables : [];
    setWhatsappTestTemplateValues((prev) => {
      const next: Record<string, string> = { ...prev };
      variables.forEach((entry: any) => {
        const key = String(entry?.key || '').trim();
        if (!key) return;
        const label = String(entry?.label || key).toLowerCase();
        const alias = `${key} ${label}`;
        if (/customer|name|party/.test(alias)) next[key] = partyName || next[key] || '';
        else if (/amount|amt|rupee|rs/.test(alias)) next[key] = amountValue ? amountValue.toLocaleString('en-IN') : (next[key] || '');
        else if (/date|txn/.test(alias)) next[key] = dateText || next[key] || '';
        else if (/ref|cheq|chq|utr|no/.test(alias)) next[key] = refText || next[key] || '';
        else if (/payment type|mode|type/.test(alias)) next[key] = paymentType || next[key] || '';
        else if (/project/.test(alias)) next[key] = projectName || next[key] || '';
        else if (/unit|flat/.test(alias)) next[key] = String(whatsappPrefillUnitNo || '').trim() || next[key] || '';
        else if (!String(next[key] || '').trim() && String(entry?.defaultValue || '').trim()) next[key] = String(entry.defaultValue).trim();
      });
      return next;
    });
    setWhatsappTestError(null);
    setWhatsappTestSuccess('Variables auto-filled from selected receipt.');
  };

  const runWhatsappStatusCheck = async () => {
    setWhatsappStatusLoading(true);
    setWhatsappStatusError(null);
    setWhatsappStatusData(null);
    try {
      const functions = getFunctions();
      const callable = httpsCallable(functions, 'whatsappGetStatus');
      const result = await callable({});
      setWhatsappStatusData(result?.data || null);
    } catch (err: any) {
      console.error('WhatsApp status check failed', err);
      setWhatsappStatusError(err?.message || 'Failed to check WhatsApp status.');
    } finally {
      setWhatsappStatusLoading(false);
    }
  };

  const sendWhatsappTestMessage = async () => {
    const toValue = String(whatsappTestTo || '').trim();
    if (!toValue) {
      setWhatsappTestError('Recipient number is required.');
      return;
    }
    if (whatsappTestMode === 'text' && !String(whatsappTestText || '').trim()) {
      setWhatsappTestError('Message text is required.');
      return;
    }
    if (whatsappTestMode === 'template' && !String(whatsappTestTemplateName || '').trim()) {
      setWhatsappTestError('Template name is required.');
      return;
    }
    setWhatsappTestSending(true);
    setWhatsappTestError(null);
    setWhatsappTestSuccess(null);
    try {
      const functions = getFunctions();
      const callable = httpsCallable(functions, 'whatsappSendMessage');
      const templateVariables = Array.isArray(selectedWhatsappTemplate?.variables)
        ? selectedWhatsappTemplate.variables
        : [];
      const buttonVariables = Array.isArray(selectedWhatsappTemplate?.buttonVariables)
        ? selectedWhatsappTemplate.buttonVariables
        : [];
      const templateComponents: any[] = [];
      if (whatsappTestMode === 'template') {
        const headerFormat = String(selectedWhatsappTemplate?.headerFormat || '').toUpperCase();
        const headerImageUrlRaw = String(selectedWhatsappTemplate?.headerImageUrl || '').trim();
        const headerDocumentUrlRaw = String(selectedWhatsappTemplate?.headerDocumentUrl || '').trim();
        const selectedProjectId = String(whatsappPrefillProjectId || '').trim();
        const selectedProject = selectedProjectId
          ? projects.find((p: any) => String(p?.id || '') === selectedProjectId)
          : null;
        const selectedProjectLogoUrl = String(selectedProject?.logoUrl || '').trim();
        const selectedProjectBrochureUrl = String(selectedProject?.brochureUrl || '').trim();
        const selectedVoucherUrl = String((whatsappPrefillTxns.find((row: any) => String(row?.id || '') === String(whatsappPrefillTxnId || ''))?.voucherUrl || '')).trim();
        const headerImageUrl =
          headerImageUrlRaw === WHATSAPP_HEADER_PROJECT_LOGO_TOKEN
            ? selectedProjectLogoUrl
            : headerImageUrlRaw === WHATSAPP_HEADER_VOUCHER_OR_PROJECT_LOGO_TOKEN
              ? (selectedVoucherUrl || selectedProjectLogoUrl)
              : headerImageUrlRaw;
        if (headerFormat === 'IMAGE') {
          if (!headerImageUrl) {
            setWhatsappTestError('Selected template requires Header Image URL (Voucher/Project Logo URL is empty).');
            setWhatsappTestSending(false);
            return;
          }
          templateComponents.push({
            type: 'header',
            parameters: [{ type: 'image', image: { link: headerImageUrl } }],
          });
        } else if (headerFormat === 'DOCUMENT') {
          const documentUrl = headerDocumentUrlRaw === WHATSAPP_HEADER_PROJECT_BROCHURE_TOKEN
            ? selectedProjectBrochureUrl
            : headerDocumentUrlRaw === WHATSAPP_HEADER_LEAD_REPORT_TOKEN
              ? ''
            : headerDocumentUrlRaw;
          if (!documentUrl) {
            setWhatsappTestError(
              headerDocumentUrlRaw === WHATSAPP_HEADER_LEAD_REPORT_TOKEN
                ? 'Generated Lead Report PDF is available only from Reports > Bulk Send Broker Reports.'
                : 'Selected template requires Project Brochure URL or Header Document URL.'
            );
            setWhatsappTestSending(false);
            return;
          }
          templateComponents.push({
            type: 'header',
            parameters: [{ type: 'document', document: { link: documentUrl, filename: 'Project Brochure.pdf' } }],
          });
        } else if (headerFormat === 'LOCATION') {
          const lat = parseFloat(String(selectedWhatsappTemplate?.headerLatitude || ''));
          const lng = parseFloat(String(selectedWhatsappTemplate?.headerLongitude || ''));
          if (isNaN(lat) || isNaN(lng)) {
            setWhatsappTestError('Template location header has invalid latitude/longitude.');
            setWhatsappTestSending(false);
            return;
          }
          templateComponents.push({
            type: 'header',
            parameters: [{
              type: 'location',
              location: {
                latitude: lat,
                longitude: lng,
                name: String(selectedWhatsappTemplate?.headerLocationName || '').trim() || undefined,
                address: String(selectedWhatsappTemplate?.headerLocationAddress || '').trim() || undefined,
              },
            }],
          });
        }
        if (buttonVariables.length) {
          const buttonValueMap: Record<string, string> = {
            project_slug: String(selectedProject?.websiteSlug || '').trim(),
            project_brochure_url: selectedProjectBrochureUrl,
            project_name: String(selectedProject?.name || '').trim(),
            custom: '',
          };
          buttonVariables.forEach((entry: any) => {
            const sourceKey = String(entry?.source || entry?.key || '').trim();
            const text = formatWhatsappVariableValue(
              sourceKey,
              buttonValueMap[sourceKey] || String(entry?.defaultValue || '').trim() || '-'
            );
            templateComponents.push({
              type: 'button',
              sub_type: 'url',
              index: String(entry?.index ?? '0'),
              parameters: [{ type: 'text', text }],
            });
          });
        }
        if (templateVariables.length) {
          templateComponents.push({
            type: 'body',
            parameters: templateVariables.map((entry: any) => {
              const sourceKey = String(entry?.source || entry?.key || '').trim();
              return {
                type: 'text',
                text: formatWhatsappVariableValue(
                  sourceKey,
                  String(whatsappTestTemplateValues[String(entry?.key || '').trim()] || '').trim() || '-'
                ),
              };
            }),
          });
        }
      }
      let resolvedText: string | undefined;
      if (whatsappTestMode === 'template') {
        let body = String(selectedWhatsappTemplate?.bodyText || '').trim();
        if (body && templateVariables.length) {
          templateVariables.forEach((entry: any, i: number) => {
            const sourceKey = String(entry?.source || entry?.key || '').trim();
            const val = formatWhatsappVariableValue(
              sourceKey,
              String(whatsappTestTemplateValues[String(entry?.key || '').trim()] || '').trim() || '-'
            );
            body = body.replace(`{{${i + 1}}}`, val);
          });
        }
        if (body) resolvedText = body;
      }
      await callable({
        senderId: whatsappTestSenderId || undefined,
        to: toValue,
        ...(whatsappTestMode === 'text'
          ? { text: String(whatsappTestText || '').trim() }
          : {
            templateName: String(whatsappTestTemplateName || '').trim(),
            templateLanguage: String(whatsappTestTemplateLanguage || 'en').trim() || 'en',
            templateComponents,
            resolvedText,
          }),
      });
      setWhatsappTestSuccess('Message queued successfully.');
      if (whatsappTestMode === 'text') setWhatsappTestText('');
    } catch (err: any) {
      console.error('WhatsApp send test failed', err);
      setWhatsappTestError(err?.message || 'Failed to send WhatsApp message.');
    } finally {
      setWhatsappTestSending(false);
    }
  };

  const launchWhatsappCoexistenceSignup = async () => {
    clearWhatsappCoexTimers();
    const runId = Date.now();
    whatsappCoexRunIdRef.current = runId;
    const appId = String(whatsappCoexAppId || '').trim();
    const configId = String(whatsappCoexConfigId || '').trim();
    if (!appId) {
      setWhatsappCoexError('Facebook App ID is required.');
      whatsappCoexRunIdRef.current = 0;
      return;
    }
    if (!configId) {
      setWhatsappCoexError('Embedded Signup Config ID is required.');
      whatsappCoexRunIdRef.current = 0;
      return;
    }
    const FB = (window as any)?.FB;
    if (!FB || typeof FB.login !== 'function') {
      setWhatsappCoexError('Facebook SDK not ready yet. Please wait and try again.');
      whatsappCoexRunIdRef.current = 0;
      return;
    }
    setWhatsappCoexLaunching(true);
    setWhatsappCoexError(null);
    setWhatsappCoexResult(null);
    setWhatsappCoexEvent(null);
    setWhatsappCoexCompletionResult(null);
    setWhatsappCoexRedirectCopySuccess(false);
    whatsappCoexEventSeenRef.current = false;
    whatsappCoexEventRef.current = null;
    whatsappCoexCompletionSuccessRef.current = false;
    whatsappCoexCompletionStartedRef.current = false;
    const redirectUri = window.location.href.split('#')[0];
    try {
      FB.init({
        appId,
        cookie: true,
        xfbml: false,
        version: 'v23.0',
      });
      await new Promise<void>((resolve) => {
        let callbackFired = false;
        whatsappCoexPopupTimeoutRef.current = window.setTimeout(() => {
          if (runId !== whatsappCoexRunIdRef.current) {
            resolve();
            return;
          }
          if (callbackFired) return;
          setWhatsappCoexError(
            'No popup response received. Popup may be blocked by browser/IAB. Open this app in Chrome/Safari and allow popups for this site.'
          );
          resolve();
        }, 8000);
        FB.login(
          (response: any) => {
            if (runId !== whatsappCoexRunIdRef.current) {
              resolve();
              return;
            }
            callbackFired = true;
            clearWhatsappCoexTimers();
            setWhatsappCoexResult(response || null);
            if (!response?.authResponse) {
              setWhatsappCoexError('Login cancelled, blocked, or not completed.');
              whatsappCoexRunIdRef.current = 0;
            } else {
              const code = String(response?.authResponse?.code || response?.code || '').trim();
              if (!code) {
                setWhatsappCoexError(
                  'Login succeeded but no authorization code was returned. Ensure this config is a System User token config and response_type=code.'
                );
                whatsappCoexRunIdRef.current = 0;
                resolve();
                return;
              }
              setWhatsappCoexCompletionLoading(true);
              whatsappCoexCompletionStartedRef.current = true;
              const runCompletion = async () => {
                for (let i = 0; i < 20 && !whatsappCoexEventRef.current; i += 1) {
                  await new Promise((r) => window.setTimeout(r, 300));
                }
                const functions = getFunctions();
                const complete = httpsCallable(functions, 'whatsappCompleteEmbeddedSignup');
                return complete({
                  code,
                  appId,
                  configId,
                  redirectUri,
                  event: whatsappCoexEventRef.current || null,
                });
              };
              runCompletion()
                .then((result: any) => {
                  if (runId !== whatsappCoexRunIdRef.current) return;
                  setWhatsappCoexCompletionResult(result?.data || null);
                  whatsappCoexCompletionSuccessRef.current = true;
                  setWhatsappCoexError(null);
                })
                .catch((err: any) => {
                  if (runId !== whatsappCoexRunIdRef.current) return;
                  whatsappCoexCompletionSuccessRef.current = false;
                  setWhatsappCoexError(err?.message || 'Failed to complete embedded signup on server.');
                })
                .finally(() => {
                  if (runId !== whatsappCoexRunIdRef.current) return;
                  setWhatsappCoexCompletionLoading(false);
                });
              whatsappCoexNoEventTimeoutRef.current = window.setTimeout(() => {
                if (runId !== whatsappCoexRunIdRef.current) return;
                if (
                  !whatsappCoexEventSeenRef.current &&
                  !whatsappCoexCompletionStartedRef.current &&
                  !whatsappCoexCompletionSuccessRef.current &&
                  !whatsappCoexCompletionLoading
                ) {
                  setWhatsappCoexError(
                    'Facebook login succeeded, but no WhatsApp Embedded Signup event arrived. Most likely the Config ID is not a valid WhatsApp Embedded Signup configuration for this App, or popup/cookies were blocked.'
                  );
                }
              }, 12000);
            }
            resolve();
          },
          {
            scope: 'whatsapp_business_management,whatsapp_business_messaging',
            config_id: configId,
            redirect_uri: redirectUri,
            display: 'popup',
            response_type: 'code',
            override_default_response_type: true,
            extras: {
              featureType: 'whatsapp_business_app_onboarding',
              sessionInfoVersion: '3',
            },
          }
        );
      });
    } catch (err: any) {
      if (runId !== whatsappCoexRunIdRef.current) return;
      setWhatsappCoexError(err?.message || 'Failed to launch Embedded Signup.');
      whatsappCoexRunIdRef.current = 0;
    } finally {
      if (runId === whatsappCoexRunIdRef.current) setWhatsappCoexLaunching(false);
    }
  };

  const migrateLegacyBookingDocuments = async () => {
    if (bookingDocsMigrationRunning) return;
    const confirmed = window.confirm(
      'Run one-time migration?\n\nLegacy booking docs will be copied into new structure:\n- uploaded\n- draft\n\nExisting documents in new structure will be kept as-is.'
    );
    if (!confirmed) return;

    setBookingDocsMigrationRunning(true);
    setBookingDocsMigrationResult(null);
    try {
      const functions = getFunctions(getApp(), 'us-central1');
      const migrateLegacyBookingDocs = httpsCallable(
        functions,
        'migrateLegacyBookingDocs'
      );
      const result = await migrateLegacyBookingDocs({});
      const payload = (result?.data || {}) as {
        projectsScanned?: number;
        unitsScanned?: number;
        bookingsScanned?: number;
        migratedUploaded?: number;
        migratedDraft?: number;
        skippedExisting?: number;
      };
      setBookingDocsMigrationResult(
        `Migration complete. Projects: ${Number(payload.projectsScanned || 0)}, Units: ${Number(payload.unitsScanned || 0)}, Bookings: ${Number(payload.bookingsScanned || 0)}, Uploaded migrated: ${Number(payload.migratedUploaded || 0)}, Draft migrated: ${Number(payload.migratedDraft || 0)}, Skipped(existing): ${Number(payload.skippedExisting || 0)}.`
      );
    } catch (err: any) {
      console.error('Legacy booking docs migration failed', err);
      setBookingDocsMigrationResult(err?.message || 'Migration failed.');
    } finally {
      setBookingDocsMigrationRunning(false);
    }
  };

  const resetStatusForm = () => {
    setStatusLabel('');
    setStatusOrder((statuses[statuses.length - 1]?.order || statuses.length) + 1);
    setStatusColor('');
    setStatusAllowedNext([]);
    setStatusPrompts([]);
    setPromptLabel('');
    setPromptType('text');
    setPromptOptionsInput('');
    setEditingStatusId(null);
    setStatusError(null);
  };

  const refreshMetaSecretStatus = async () => {
    try {
      const functions = getFunctions();
      const getMetaLeadStatus = httpsCallable(functions, 'getMetaLeadStatus');
      const res: any = await getMetaLeadStatus({});
      setMetaSecretsStatus({
        hasVerifyToken: !!res?.data?.hasVerifyToken,
        hasPageToken: !!res?.data?.hasPageToken,
        hasSystemUserToken: !!res?.data?.hasSystemUserToken,
        hasAppSecret: !!res?.data?.hasAppSecret,
      });
    } catch (err) {
      console.warn('getMetaLeadStatus failed', err);
      setMetaSecretsStatus(null);
    }
  };

  const saveMetaConfig = async () => {
    setMetaConfigError(null);
    setMetaConfigSaving(true);
    try {
      const cfgRef = doc(db, 'integrations', 'metaLeadAds');
      await setDoc(
        cfgRef,
        {
          enabled: metaEnabled,
          defaultSource: metaDefaultSource.trim() || 'Social Ad',
          defaultStatus: metaDefaultStatus.trim() || 'New',
          updatedAt: serverTimestamp(),
        },
        { merge: true }
      );
      await refreshMetaSecretStatus();
    } catch (err: any) {
      console.error('Save Meta Lead config failed', err);
      setMetaConfigError(err?.message || 'Failed to save Meta Lead settings.');
    } finally {
      setMetaConfigSaving(false);
    }
  };

  const openNewMetaFormDrawer = () => {
    setEditingMetaFormId(null);
    setMetaFormId('');
    setMetaFormProjectId('');
    setMetaFormActive(true);
    setMetaFormDrawerOpen(true);
  };

  const openNewMetaPageDrawer = () => {
    setEditingMetaPageId(null);
    setMetaPageId('');
    setMetaPageLabel('');
    setMetaPageActive(true);
    setMetaPageType('own');
    setMetaPageDrawerOpen(true);
  };

  const openEditMetaPageDrawer = (row: any) => {
    setEditingMetaPageId(row.id);
    setMetaPageId(row.id);
    setMetaPageLabel(row.label || '');
    setMetaPageActive(row.active !== false);
    setMetaPageType(row.type === 'agency' ? 'agency' : 'own');
    setMetaPageDrawerOpen(true);
  };

  const saveMetaPage = async (e: React.FormEvent) => {
    e.preventDefault();
    setMetaPagesError(null);
    const pageId = metaPageId.trim();
    if (!pageId) {
      setMetaPagesError('Page ID is required.');
      return;
    }
    try {
      const ref = doc(db, 'metaPages', pageId);
      await setDoc(
        ref,
        {
          label: metaPageLabel.trim() || null,
          type: metaPageType,
          active: !!metaPageActive,
          updatedAt: serverTimestamp(),
          ...(editingMetaPageId ? {} : { createdAt: serverTimestamp() }),
        },
        { merge: true }
      );
      setMetaPageDrawerOpen(false);
    } catch (err: any) {
      console.error('Save Meta page failed', err);
      setMetaPagesError(err?.message || 'Failed to save page.');
    }
  };

  const deleteMetaPage = async (id: string) => {
    try {
      await deleteDoc(doc(db, 'metaPages', id));
    } catch (err: any) {
      console.error('Delete Meta page failed', err);
      setMetaPagesError(err?.message || 'Failed to delete page.');
    }
  };

  const openEditMetaFormDrawer = (row: any) => {
    setEditingMetaFormId(row.id);
    setMetaFormId(row.id);
    setMetaFormProjectId(row.projectId || '');
    setMetaFormActive(row.active !== false);
    setMetaFormDrawerOpen(true);
  };

  const saveMetaForm = async (e: React.FormEvent) => {
    e.preventDefault();
    setMetaFormsError(null);
    const formId = metaFormId.trim();
    if (!formId) {
      setMetaFormsError('Form ID is required.');
      return;
    }
    try {
      const ref = doc(db, 'metaLeadForms', formId);
      await setDoc(
        ref,
        {
          projectId: metaFormProjectId || null,
          active: !!metaFormActive,
          updatedAt: serverTimestamp(),
          ...(editingMetaFormId ? {} : { createdAt: serverTimestamp() }),
        },
        { merge: true }
      );
      setMetaFormDrawerOpen(false);
    } catch (err: any) {
      console.error('Save Meta form mapping failed', err);
      setMetaFormsError(err?.message || 'Failed to save form mapping.');
    }
  };

  const deleteMetaForm = async (id: string) => {
    try {
      await deleteDoc(doc(db, 'metaLeadForms', id));
    } catch (err: any) {
      console.error('Delete Meta form mapping failed', err);
      setMetaFormsError(err?.message || 'Failed to delete mapping.');
    }
  };

  const parseDelimitedLine = (line: string, delimiter: string): string[] => {
    const out: string[] = [];
    let cur = '';
    let inQuotes = false;
    for (let i = 0; i < line.length; i += 1) {
      const ch = line[i];
      if (ch === '"') {
        const next = line[i + 1];
        if (inQuotes && next === '"') {
          cur += '"';
          i += 1;
        } else {
          inQuotes = !inQuotes;
        }
        continue;
      }
      if (!inQuotes && ch === delimiter) {
        out.push(cur);
        cur = '';
        continue;
      }
      cur += ch;
    }
    out.push(cur);
    return out.map((x) => x.trim());
  };

  const detectDelimiter = (headerLine: string): string => {
    const candidates = [';', '\t', ','];
    let best = ',';
    let bestCount = -1;
    candidates.forEach((d) => {
      const c = headerLine.split(d).length - 1;
      if (c > bestCount) {
        bestCount = c;
        best = d;
      }
    });
    return best;
  };

  const normalizeHeader = (h: string) =>
    String(h || '')
      .replace(/\uFEFF/g, '')
      .trim()
      .toUpperCase()
      .replace(/\s+/g, ' ');

  const headerToUnitFieldKey = (header: string) =>
    String(header || '')
      .replace(/\uFEFF/g, '')
      .trim()
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '_')
      .replace(/^_+|_+$/g, '');

  const parseNumber = (raw: any): number | null => {
    const s = String(raw || '').trim();
    if (!s) return null;
    const cleaned = s.replace(/[, ]+/g, '');
    const n = Number(cleaned);
    return Number.isFinite(n) ? n : null;
  };

  const parseDateValue = (raw: any): any | null => {
    const s = String(raw || '').trim();
    if (!s) return null;
    // Support DD/MM/YYYY or DD-MM-YYYY
    const m = s.match(/^(\d{1,2})[/-](\d{1,2})[/-](\d{2,4})$/);
    if (m) {
      const dd = Number(m[1]);
      const mm = Number(m[2]);
      const yyyy = Number(m[3].length === 2 ? `20${m[3]}` : m[3]);
      const d = new Date(yyyy, mm - 1, dd);
      if (!Number.isNaN(d.getTime())) return d;
    }
    const d = new Date(s);
    if (!Number.isNaN(d.getTime())) return d;
    return null;
  };

  const sanitizeId = (v: string) =>
    String(v || '')
      .trim()
      .toLowerCase()
      .replace(/[^a-z0-9_-]/g, '_')
      .slice(0, 200);

  const parseUnitsFile = async (file: File) => {
    setUnitsParseError(null);
    setUnitsImportSuccess(null);
    setUnitsParseWarnings([]);
    setUnitsExtraColumns([]);
    setUnitsParsedHeaders([]);
    setUnitsParsed([]);
    if (!file) return;
    const text = await file.text();
    const lines = text
      .split(/\r?\n/)
      .map((l) => l.trimEnd())
      .filter((l) => l.trim().length > 0);
    if (lines.length < 2) {
      setUnitsParseError('CSV file is empty or missing data rows.');
      return;
    }
    const delimiter = detectDelimiter(lines[0]);
    const rawHeaders = parseDelimitedLine(lines[0], delimiter);
    const headers = rawHeaders.map(normalizeHeader);
    setUnitsParsedHeaders(headers);
    const idx = (name: string) => headers.indexOf(normalizeHeader(name));

    const knownHeaders = new Set([
      'FLAT/BUNGLOW/OFFICE',
      'BLOCK TYPE',
      'USAGE',
      'CARPET AREA',
      'AREA OF EXCLUSIVE BALCONY',
      'AREA OF EXCLUSIVE WASH',
      'AREA OF EXCLUSIVE OPEN TERRACE',
      'STATUS',
      'UNIT AMOUNT',
      'RECEIVED AMOUNT',
      'DATE OF AGREEMENT OF SALE',
      'ENCUMBRANCE STATUS',
      'ALLOTEE NAME',
      'TYPE OF KYC',
      'KYC ID',
      'MOBILE NUMBER',
      'REDEVELOPMENT',
    ]);
    const extraCols = rawHeaders
      .map((raw, i) => ({ raw: String(raw || '').trim(), normalized: headers[i], index: i }))
      .filter((h) => h.raw && !knownHeaders.has(h.normalized));

    const requiredHeaders = ['FLAT/BUNGLOW/OFFICE'];
    const missing = requiredHeaders.filter((h) => idx(h) < 0);
    if (missing.length) {
      setUnitsParseError(`Missing required column(s): ${missing.join(', ')}`);
      return;
    }

    const warnings: string[] = [];
    const rows: any[] = [];
    const seen = new Map<string, number>();

    for (let i = 1; i < lines.length; i += 1) {
      const parts = parseDelimitedLine(lines[i], delimiter);
      const get = (header: string) => {
        const j = idx(header);
        return j >= 0 ? (parts[j] ?? '') : '';
      };

      const unitNumber = String(get('FLAT/BUNGLOW/OFFICE') || '').trim();
      if (!unitNumber) {
        warnings.push(`Row ${i + 1}: missing unit number (FLAT/BUNGLOW/OFFICE).`);
        continue;
      }
      const unitNumberLower = unitNumber.toLowerCase();
      seen.set(unitNumberLower, (seen.get(unitNumberLower) || 0) + 1);

      const customFields: Record<string, any> = {};
      extraCols.forEach(({ raw, index }) => {
        const rawCell = parts[index];
        const value = String(rawCell ?? '').trim();
        if (value === '') return;
        const fieldKey = headerToUnitFieldKey(raw);
        if (!fieldKey || fieldKey === 'id') return;
        const asNumber = parseNumber(value);
        customFields[fieldKey] = asNumber != null ? asNumber : value;
      });

      rows.push({
        unitNumber,
        unitNumberLower,
        blockType: String(get('BLOCK TYPE') || '').trim() || null,
        usage: String(get('USAGE') || '').trim() || null,
        carpetArea: parseNumber(get('CARPET AREA')),
        exclusiveBalconyArea: parseNumber(get('AREA OF EXCLUSIVE BALCONY')),
        exclusiveWashArea: parseNumber(get('AREA OF EXCLUSIVE WASH')),
        openTerraceArea: parseNumber(get('AREA OF EXCLUSIVE OPEN TERRACE')),
        status: String(get('STATUS') || '').trim() || null,
        unitAmount: parseNumber(get('UNIT AMOUNT')),
        receivedAmount: parseNumber(get('RECEIVED AMOUNT')),
        agreementDate: parseDateValue(get('DATE OF AGREEMENT OF SALE')),
        encumbranceStatus: String(get('ENCUMBRANCE STATUS') || '').trim() || null,
        allotteeName: String(get('ALLOTEE NAME') || '').trim() || null,
        kycType: String(get('TYPE OF KYC') || '').trim() || null,
        kycId: String(get('KYC ID') || '').trim() || null,
        mobileNumber: String(get('MOBILE NUMBER') || '').trim() || null,
        redevelopment: String(get('REDEVELOPMENT') || '').trim() || null,
        customFields,
      });
    }

    const dups = Array.from(seen.entries()).filter(([, c]) => c > 1);
    if (dups.length) {
      warnings.push(`Duplicate unit numbers in file (keeping last): ${dups.slice(0, 10).map(([u, c]) => `${u}×${c}`).join(', ')}${dups.length > 10 ? '…' : ''}`);
    }

    // Deduplicate by unitNumberLower (keep last)
    const dedup = new Map<string, any>();
    rows.forEach((r) => dedup.set(r.unitNumberLower, r));
    const finalRows = Array.from(dedup.values());

    setUnitsExtraColumns(extraCols.map((c) => c.raw));
    setUnitsParseWarnings(warnings.slice(0, 25));
    setUnitsParsed(finalRows);
  };

  const buildUnitPayloadForImport = (
    row: any,
    opts: { headerSet?: Set<string>; includeAllKnown: boolean }
  ) => {
    const now = serverTimestamp();
    const payload: any = {
      projectId: editingProjectId,
      unitNumber: row.unitNumber,
      unitNumberLower: row.unitNumberLower,
      isActive: true,
      importedAt: now,
      updatedAt: now,
    };
    const headerSet = opts.headerSet || new Set<string>();
    const includeField = (csvHeader: string) =>
      opts.includeAllKnown || headerSet.has(normalizeHeader(csvHeader));

    if (includeField('BLOCK TYPE')) payload.blockType = row.blockType ?? null;
    if (includeField('USAGE')) payload.usage = row.usage ?? null;
    if (includeField('CARPET AREA')) payload.carpetArea = row.carpetArea ?? null;
    if (includeField('AREA OF EXCLUSIVE BALCONY')) payload.exclusiveBalconyArea = row.exclusiveBalconyArea ?? null;
    if (includeField('AREA OF EXCLUSIVE WASH')) payload.exclusiveWashArea = row.exclusiveWashArea ?? null;
    if (includeField('AREA OF EXCLUSIVE OPEN TERRACE')) payload.openTerraceArea = row.openTerraceArea ?? null;
    if (includeField('STATUS')) payload.status = row.status ?? null;
    if (includeField('UNIT AMOUNT')) payload.unitAmount = row.unitAmount ?? null;
    if (includeField('RECEIVED AMOUNT')) payload.receivedAmount = row.receivedAmount ?? null;
    if (includeField('DATE OF AGREEMENT OF SALE')) {
      payload.agreementDate = row.agreementDate ? (row.agreementDate instanceof Date ? row.agreementDate : null) : null;
    }
    if (includeField('ENCUMBRANCE STATUS')) payload.encumbranceStatus = row.encumbranceStatus ?? null;
    if (includeField('ALLOTEE NAME')) payload.allotteeName = row.allotteeName ?? null;
    if (includeField('TYPE OF KYC')) payload.kycType = row.kycType ?? null;
    if (includeField('KYC ID')) payload.kycId = row.kycId ?? null;
    if (includeField('MOBILE NUMBER')) payload.mobileNumber = row.mobileNumber ?? null;
    if (includeField('REDEVELOPMENT')) payload.redevelopment = row.redevelopment ?? null;

    if (unitsIncludeExtraColumns && row.customFields && typeof row.customFields === 'object') {
      Object.entries(row.customFields).forEach(([k, v]) => {
        if (!k) return;
        if (
          [
            'projectId',
            'unitNumber',
            'unitNumberLower',
            'isActive',
            'importedAt',
            'updatedAt',
            'createdAt',
          ].includes(k)
        ) {
          return;
        }
        payload[k] = v;
      });
    }
    return payload;
  };

  const handleUpdateExistingUnitsOnly = async () => {
    if (!editingProjectId) {
      setUnitsParseError('Save the project first, then import units.');
      return;
    }
    if (!unitsParsed.length) {
      setUnitsParseError('No units parsed. Please choose a valid CSV file.');
      return;
    }

    setUnitsImporting(true);
    setUnitsParseError(null);
    setUnitsImportSuccess(null);
    try {
      const projectId = editingProjectId;
      const unitsCol = collection(db, 'projects', projectId, 'units');
      const existingIds = new Set<string>();
      let lastDoc: any = null;
      // eslint-disable-next-line no-constant-condition
      while (true) {
        const q = lastDoc
          ? query(unitsCol, orderBy(documentId()), startAfter(lastDoc), limit(500))
          : query(unitsCol, orderBy(documentId()), limit(500));
        const snap = await getDocs(q);
        if (snap.empty) break;
        snap.docs.forEach((d) => existingIds.add(String(d.id)));
        lastDoc = snap.docs[snap.docs.length - 1];
      }

      const headerSet = new Set(unitsParsedHeaders || []);
      let updatedCount = 0;
      let skippedCount = 0;

      for (let i = 0; i < unitsParsed.length; i += 400) {
        const chunk = unitsParsed.slice(i, i + 400);
        const batch = writeBatch(db);
        for (const r of chunk) {
          const id = sanitizeId(r.unitNumberLower || r.unitNumber);
          if (!existingIds.has(id)) {
            skippedCount += 1;
            continue;
          }
          const ref = doc(unitsCol, id);
          const payload = buildUnitPayloadForImport(r, {
            headerSet,
            includeAllKnown: false,
          });
          batch.set(ref, payload, { merge: true });
          updatedCount += 1;
        }
        await batch.commit();
      }

      await updateDoc(doc(db, 'projects', projectId), {
        unitsLastImportedAt: serverTimestamp(),
      });
      setProjectUnitsLastImportedAt(new Date());
      setUnitsImportSuccess(
        `Updated ${updatedCount} existing units.${skippedCount ? ` Skipped ${skippedCount} rows (unit not found).` : ''}`
      );
    } catch (err: any) {
      console.error('Update existing units failed', err);
      setUnitsParseError(err?.message || 'Failed to update existing units.');
    } finally {
      setUnitsImporting(false);
    }
  };

	  const handleReplaceAllUnits = async () => {
	    if (!editingProjectId) {
	      setUnitsParseError('Save the project first, then import units.');
	      return;
	    }
	    if (!unitsParsed.length) {
	      setUnitsParseError('No units parsed. Please choose a valid CSV file.');
	      return;
	    }
	    const ok = window.confirm(
	      `This will REPLACE the unit master for this project using the CSV (${unitsParsed.length} units).\n\nExisting units not present in the CSV will be marked inactive (not deleted), so any future booking/paperwork data stays safe.\n\nContinue?`
	    );
	    if (!ok) return;

    setUnitsImporting(true);
    setUnitsParseError(null);
    setUnitsImportSuccess(null);
	    try {
	      const projectId = editingProjectId;
	      const unitsCol = collection(db, 'projects', projectId, 'units');

	      // 1) Upsert imported units (batched). Keep any extra fields (booking/paperwork pointers) by merging.
	      const now = serverTimestamp();
	      const importedIds = new Set<string>();
	      for (let i = 0; i < unitsParsed.length; i += 400) {
	        const chunk = unitsParsed.slice(i, i + 400);
	        const batch = writeBatch(db);
	        chunk.forEach((r) => {
	          const id = sanitizeId(r.unitNumberLower || r.unitNumber);
	          importedIds.add(id);
	          const ref = doc(unitsCol, id);
	          const payload = buildUnitPayloadForImport(r, { includeAllKnown: true });
	          batch.set(ref, payload, { merge: true });
	        });
	        await batch.commit();
	      }

	      // 2) Mark units missing from CSV as inactive (paged)
	      let lastDoc: any = null;
	      // eslint-disable-next-line no-constant-condition
	      while (true) {
	        const q = lastDoc
	          ? query(unitsCol, orderBy(documentId()), startAfter(lastDoc), limit(400))
	          : query(unitsCol, orderBy(documentId()), limit(400));
	        const snap = await getDocs(q);
	        if (snap.empty) break;
	        const batch = writeBatch(db);
	        snap.docs.forEach((d) => {
	          if (importedIds.has(d.id)) return;
	          batch.set(d.ref, { isActive: false, updatedAt: now }, { merge: true });
	        });
	        await batch.commit();
	        lastDoc = snap.docs[snap.docs.length - 1];
	      }

	      // 3) Update project metadata
	      await updateDoc(doc(db, 'projects', projectId), {
	        unitsCount: unitsParsed.length,
	        unitsLastImportedAt: serverTimestamp(),
	      });

      setUnitsImportSuccess(`Imported ${unitsParsed.length} units successfully.`);
      setProjectUnitsCount(unitsParsed.length);
      setProjectUnitsLastImportedAt(new Date());
    } catch (err: any) {
      console.error('Units import failed', err);
      setUnitsParseError(err?.message || 'Units import failed.');
    } finally {
      setUnitsImporting(false);
    }
  };

  const loadUnitsPage = async (opts?: { reset?: boolean }) => {
    if (!unitsProjectId) return;
    const reset = !!opts?.reset;
    setUnitsLoadError(null);
    setUnitsLoading(true);
    try {
      const unitsCol = collection(db, 'projects', unitsProjectId, 'units');
      const base = query(unitsCol, orderBy('unitNumberLower', 'asc'), limit(100));
      const q = reset || !unitsLastDoc ? base : query(unitsCol, orderBy('unitNumberLower', 'asc'), startAfter(unitsLastDoc), limit(100));
      let snap: any;
      try {
        snap = await getDocs(q);
      } catch (err) {
        // Fallback if older docs don't have unitNumberLower.
        const base2 = query(unitsCol, orderBy(documentId()), limit(100));
        const q2 = reset || !unitsLastDoc ? base2 : query(unitsCol, orderBy(documentId()), startAfter(unitsLastDoc), limit(100));
        snap = await getDocs(q2);
      }
      const rows = snap.docs.map((d: any) => ({ id: d.id, ...(d.data() as any) }));
      setUnitsRows((prev) => (reset ? rows : [...prev, ...rows]));
      const last = snap.docs[snap.docs.length - 1] || null;
      setUnitsLastDoc(last);
      setUnitsHasMore(snap.docs.length === 100);
    } catch (err: any) {
      console.error('Load units failed', err);
      setUnitsLoadError(err?.message || 'Failed to load units.');
    } finally {
      setUnitsLoading(false);
    }
  };

  useEffect(() => {
    if (!unitsDialogOpen) return;
    setUnitsRows([]);
    setUnitsLastDoc(null);
    setUnitsHasMore(false);
    setUnitsSearch('');
    loadUnitsPage({ reset: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [unitsDialogOpen, unitsProjectId]);

  const unitDialogColumns = useMemo(() => {
    const base = [
      { key: 'unitNumber', label: 'Unit No', align: 'left' as const },
      { key: 'blockType', label: 'Block', align: 'left' as const },
      { key: 'usage', label: 'Usage', align: 'left' as const },
      { key: 'carpetArea', label: 'Carpet Area', align: 'right' as const },
      { key: 'exclusiveBalconyArea', label: 'Excl. Balcony', align: 'right' as const },
      { key: 'status', label: 'Status', align: 'left' as const },
      { key: 'unitAmount', label: 'Unit Amount', align: 'right' as const },
      { key: 'receivedAmount', label: 'Received Amount', align: 'right' as const },
      { key: 'agreementDate', label: 'Agreement Date', align: 'left' as const },
      { key: 'encumbranceStatus', label: 'Encumbrance Status', align: 'left' as const },
      { key: 'allotteeName', label: 'Allottee Name', align: 'left' as const },
      { key: 'kycType', label: 'KYC Type', align: 'left' as const },
      { key: 'kycId', label: 'KYC ID', align: 'left' as const },
      { key: 'mobileNumber', label: 'Mobile Number', align: 'left' as const },
      { key: 'redevelopment', label: 'Redevelopment', align: 'left' as const },
      { key: 'bookingStatus', label: 'Booking Status', align: 'left' as const },
      { key: 'activeBookingId', label: 'Active Booking ID', align: 'left' as const },
      { key: 'isActive', label: 'Is Active', align: 'left' as const },
    ];
    const baseKeys = new Set(base.map((c) => c.key));
    const extras = new Set<string>();
    (unitsRows || []).forEach((row: any) => {
      Object.keys(row || {}).forEach((key) => {
        if (key === 'id' || key === 'unitNumberLower') return;
        if (baseKeys.has(key)) return;
        extras.add(key);
      });
    });
    const extraCols = Array.from(extras)
      .sort((a, b) => a.localeCompare(b))
      .map((key) => ({
        key,
        label: key.replace(/([a-z])([A-Z])/g, '$1 $2').replace(/_/g, ' '),
        align: 'left' as const,
      }));
    return [...base, ...extraCols];
  }, [unitsRows]);

  const renderUnitColumnValue = useCallback((row: any, key: string) => {
    const value = row?.[key];
    if (value == null || value === '') return '—';
    if (typeof value === 'number') return Number.isFinite(value) ? value : '—';
    if (typeof value === 'boolean') return value ? 'Yes' : 'No';
    if (value?.toDate && typeof value.toDate === 'function') {
      const d = value.toDate();
      return d instanceof Date && !Number.isNaN(d.getTime()) ? d.toLocaleString() : '—';
    }
    if (value instanceof Date) {
      return !Number.isNaN(value.getTime()) ? value.toLocaleDateString() : '—';
    }
    if (Array.isArray(value)) return value.map((x) => String(x)).join(', ');
    if (typeof value === 'object') return JSON.stringify(value);
    return String(value);
  }, []);

  const downloadUnitsSampleCsv = () => {
    const headers = [
      'BLOCK TYPE',
      'FLAT/BUNGLOW/OFFICE',
      'USAGE',
      'CARPET AREA',
      'AREA OF EXCLUSIVE BALCONY',
      'STATUS',
      'UNIT AMOUNT',
      'RECEIVED AMOUNT',
      'DATE OF AGREEMENT OF SALE',
      'ENCUMBRANCE STATUS',
      'ALLOTEE NAME',
      'TYPE OF KYC',
      'KYC ID',
      'MOBILE NUMBER',
      'REDEVELOPMENT',
    ];
    const sampleRow = [
      'A',
      'A-101',
      'Residential',
      '650',
      '40',
      'Available',
      '5500000',
      '0',
      '15/01/2026',
      'Clear',
      '—',
      '—',
      '—',
      '9876543210',
      'No',
    ];
    const delimiter = ';';
    const escape = (v: string) => {
      const s = String(v ?? '');
      if (s.includes('"') || s.includes(delimiter) || s.includes('\n')) return `"${s.replace(/"/g, '""')}"`;
      return s;
    };
    const csv = `${headers.map(escape).join(delimiter)}\n${sampleRow.map(escape).join(delimiter)}\n`;
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'project-units-sample.csv';
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  };

  const exportExistingUnitsCsv = async (projectIdArg?: string | null, projectNameArg?: string | null) => {
    const projectId = String(projectIdArg || unitsProjectId || editingProjectId || '').trim();
    if (!projectId) {
      setUnitsParseError('Save/select a project first, then export units.');
      return;
    }
    setUnitsParseError(null);
    setUnitsImportSuccess(null);
    setUnitsImporting(true);
    try {
      const unitsCol = collection(db, 'projects', projectId, 'units');
      const rows: any[] = [];
      let lastDoc: any = null;

      // eslint-disable-next-line no-constant-condition
      while (true) {
        const q = lastDoc
          ? query(unitsCol, orderBy('unitNumberLower', 'asc'), startAfter(lastDoc), limit(500))
          : query(unitsCol, orderBy('unitNumberLower', 'asc'), limit(500));
        const snap = await getDocs(q);
        if (snap.empty) break;
        rows.push(...snap.docs.map((d) => ({ id: d.id, ...(d.data() as any) })));
        lastDoc = snap.docs[snap.docs.length - 1];
      }

      const activeRows = rows.filter((r) => r?.isActive !== false);
      const headers = [
        'BLOCK TYPE',
        'FLAT/BUNGLOW/OFFICE',
        'USAGE',
        'CARPET AREA',
        'AREA OF EXCLUSIVE BALCONY',
        'AREA OF EXCLUSIVE WASH',
        'AREA OF EXCLUSIVE OPEN TERRACE',
        'STATUS',
        'UNIT AMOUNT',
        'RECEIVED AMOUNT',
        'DATE OF AGREEMENT OF SALE',
        'ENCUMBRANCE STATUS',
        'ALLOTEE NAME',
        'TYPE OF KYC',
        'KYC ID',
        'MOBILE NUMBER',
        'REDEVELOPMENT',
        'BOOKING STATUS',
        'ACTIVE BOOKING ID',
        'IS ACTIVE',
      ];
      const escape = (value: any) => {
        const raw =
          value?.toDate && typeof value.toDate === 'function'
            ? value.toDate()?.toISOString?.()?.slice(0, 10) || ''
            : value instanceof Date
            ? value.toISOString().slice(0, 10)
            : typeof value === 'boolean'
            ? (value ? 'TRUE' : 'FALSE')
            : value == null
            ? ''
            : String(value);
        return `"${raw.replace(/"/g, '""')}"`;
      };

      const csvRows = activeRows.map((r) =>
        [
          r.blockType,
          r.unitNumber,
          r.usage,
          r.carpetArea,
          r.exclusiveBalconyArea,
          r.exclusiveWashArea,
          r.openTerraceArea,
          r.status,
          r.unitAmount,
          r.receivedAmount,
          r.agreementDate,
          r.encumbranceStatus,
          r.allotteeName,
          r.kycType,
          r.kycId,
          r.mobileNumber,
          r.redevelopment,
          r.bookingStatus,
          r.activeBookingId,
          r.isActive,
        ]
          .map(escape)
          .join(',')
      );
      const csv = `${headers.join(',')}\n${csvRows.join('\n')}\n`;
      const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      const safeProject = String(projectNameArg || unitsProjectName || projectName || projectId).replace(/[^\w.-]+/g, '_');
      a.download = `${safeProject || 'project'}_units_export.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      setUnitsImportSuccess(`Exported ${activeRows.length} units.`);
    } catch (err: any) {
      console.error('Export units failed', err);
      setUnitsParseError(err?.message || 'Failed to export units.');
    } finally {
      setUnitsImporting(false);
    }
  };

  const openProjectGstDialog = async (projectRow: any) => {
    const nextProjectId = String(projectRow?.id || '').trim();
    if (!nextProjectId) return;
    let usageValues: string[] = [];
    try {
      const unitsSnap = await getDocs(
        query(collection(db, 'projects', nextProjectId, 'units'), limit(3000))
      );
      usageValues = Array.from(
        new Set(
          unitsSnap.docs
            .map((d) => String((d.data() as any)?.usage || '').trim())
            .filter(Boolean)
        )
      );
    } catch {
      usageValues = [];
    }
    const nextTypes = Array.from(
      new Set(
        [
          ...usageValues,
          ...Object.keys((projectRow?.gstPercentByUsage || {}) as Record<string, any>),
          ...Object.keys((projectRow?.gstPercentByUnitType || {}) as Record<string, any>),
        ]
          .map((v) => String(v || '').trim())
          .filter(Boolean)
      )
    );
    const rawMap = ({
      ...((projectRow?.gstPercentByUnitType || {}) as Record<string, any>),
      ...((projectRow?.gstPercentByUsage || {}) as Record<string, any>),
    }) as Record<string, any>;
    const nextValues: Record<string, string> = {};
    nextTypes.forEach((type) => {
      const value = Number(rawMap[type]);
      nextValues[type] = Number.isFinite(value) && value >= 0 ? String(value) : '';
    });
    const rawDefault = Number(projectRow?.gstDefaultPercent ?? 1);
    const sbTypes = Array.from(
      new Set(
        [
          ...usageValues,
          ...Object.keys((projectRow?.sbAreaPercentByUsage || {}) as Record<string, any>),
          ...Object.keys((projectRow?.sbAreaPercentByUnitType || {}) as Record<string, any>),
        ]
          .map((v) => String(v || '').trim())
          .filter(Boolean)
      )
    );
    const rawSbMap = ({
      ...((projectRow?.sbAreaPercentByUnitType || {}) as Record<string, any>),
      ...((projectRow?.sbAreaPercentByUsage || {}) as Record<string, any>),
    }) as Record<string, any>;
    const nextSbValues: Record<string, string> = {};
    sbTypes.forEach((type) => {
      const value = Number(rawSbMap[type]);
      nextSbValues[type] = Number.isFinite(value) && value >= 0 ? String(value) : '';
    });
    const rawDefaultSb = Number(projectRow?.sbAreaPercent ?? 0);
    setProjectGstProjectId(nextProjectId);
    setProjectGstProjectName(String(projectRow?.name || ''));
    setProjectGstTypes(nextTypes);
    setProjectGstValues(nextValues);
    setProjectDefaultGstPercentInput(
      Number.isFinite(rawDefault) && rawDefault >= 0 ? String(rawDefault) : '1'
    );
    setProjectSbTypes(sbTypes);
    setProjectSbValues(nextSbValues);
    setProjectDefaultSbPercentInput(
      Number.isFinite(rawDefaultSb) && rawDefaultSb >= 0 ? String(rawDefaultSb) : '0'
    );
    setProjectGstError(null);
    setProjectGstDialogOpen(true);
  };

  const saveProjectGstConfig = async () => {
    if (!projectGstProjectId) return;
    setProjectGstError(null);
    setProjectGstSaving(true);
    try {
      const parsedDefault = Number(String(projectDefaultGstPercentInput || '').trim());
      const safeDefault = Number.isFinite(parsedDefault) && parsedDefault >= 0 ? parsedDefault : 1;
      const nextMap: Record<string, number> = {};
      projectGstTypes.forEach((type) => {
        const key = String(type || '').trim();
        if (!key) return;
        const parsed = Number(String(projectGstValues[key] || '').trim());
        if (!Number.isFinite(parsed) || parsed < 0) return;
        nextMap[key] = parsed;
      });
      const parsedDefaultSb = Number(String(projectDefaultSbPercentInput || '').trim());
      const safeDefaultSb = Number.isFinite(parsedDefaultSb) && parsedDefaultSb >= 0 ? parsedDefaultSb : 0;
      const nextSbMap: Record<string, number> = {};
      projectSbTypes.forEach((type) => {
        const key = String(type || '').trim();
        if (!key) return;
        const parsed = Number(String(projectSbValues[key] || '').trim());
        if (!Number.isFinite(parsed) || parsed < 0) return;
        nextSbMap[key] = parsed;
      });
      await updateDoc(doc(db, 'projects', projectGstProjectId), {
        gstPercentByUsage: nextMap,
        gstPercentByUnitType: nextMap,
        gstDefaultPercent: safeDefault,
        sbAreaPercentByUsage: nextSbMap,
        sbAreaPercentByUnitType: nextSbMap,
        sbAreaPercent: safeDefaultSb,
      });
      setProjectGstDialogOpen(false);
    } catch (err: any) {
      setProjectGstError(err?.message || 'Failed to save GST/SB settings.');
    } finally {
      setProjectGstSaving(false);
    }
  };

  const handleCreateProject = async (e: React.FormEvent) => {
    e.preventDefault();
    setProjectsError(null);
    setProjectFormError(null);
    setProjectSaving(true);
    try {
      const parseCurrencyInput = (value: string) => {
        const cleaned = String(value || '').replace(/,/g, '').trim();
        if (!cleaned) return 0;
        const parsed = Number(cleaned);
        return Number.isFinite(parsed) ? parsed : 0;
      };
      const trimmedName = projectName.trim();
      // prevent duplicate project names (case-insensitive)
      const exists = projects.some(
        (p) =>
          p.id !== editingProjectId &&
          typeof p.name === 'string' &&
          p.name.trim().toLowerCase() === trimmedName.toLowerCase()
      );
      if (exists) {
        setProjectFormError('A project with this name already exists.');
        return;
      }
      const selectedCompanyProfileId = String(projectCompanyProfileId || '').trim();
      if (!selectedCompanyProfileId) {
        setProjectFormError('Company profile is required.');
        return;
      }
      const selectedCompanyProfileName = companyProfileNameById.get(selectedCompanyProfileId) || selectedCompanyProfileId;
      const advocateAgreementFee = parseCurrencyInput(projectAdvocateAgreementFee);
      const advocateDeedFee = parseCurrencyInput(projectAdvocateDeedFee);
      const bucReceivedAt = projectBucReceivedAt.trim() || null;
      const bucReceivedBuildingNos = projectBucBuildings.split(',').map((value) => value.trim()).filter(Boolean);
      const parsedSbAreaPercent = Number(String(projectSbAreaPercent || '').trim());
      const sbAreaPercent = Number.isFinite(parsedSbAreaPercent) && parsedSbAreaPercent >= 0 ? parsedSbAreaPercent : 0;
      const projectsRef = collection(db, 'projects');
      if (editingProjectId) {
        const projectRef = doc(db, 'projects', editingProjectId);
        await updateDoc(projectRef, {
          name: trimmedName,
          location: projectLocation.trim(),
          latitude: projectLatitude.trim() || null,
          longitude: projectLongitude.trim() || null,
          address: projectAddress.trim() || null,
          whatsappSubtitle: projectWhatsappSubtitle.trim() || null,
          websiteSlug: projectWebsiteSlug.trim() || null,
          brochureUrl: projectBrochureUrl.trim() || null,
          backgroundImageUrl: projectBackgroundImageUrl.trim() || null,
          documentImageUrl: projectDocumentImageUrl.trim() || null,
          logoUrl: projectLogoUrl.trim() || null,
          logoBwUrl: projectLogoBwUrl.trim() || null,
          ...(projectBackgroundImageUrl.trim() ? { backgroundImagePath: null } : {}),
          ...(projectDocumentImageUrl.trim() ? { documentImagePath: null } : {}),
          ...(projectLogoUrl.trim() ? { logoPath: null } : {}),
          ...(projectLogoBwUrl.trim() ? { logoBwPath: null } : {}),
          unitTypes,
          advocateAgreementFee,
          advocateDeedFee,
          bucReceivedAt,
          bucReceivedBuildingNos,
          sbAreaPercent,
          companyProfileId: selectedCompanyProfileId,
          companyProfileName: selectedCompanyProfileName,
        });

        if (logoFile) {
          // delete old logo if exists
          if (editingLogoPath) {
            try { await deleteObject(ref(storage, editingLogoPath)); } catch (e) { /* ignore */ }
          }
          const path = `project-logos/${editingProjectId}/${logoFile.name}`;
          const storageRef = ref(storage, path);
          await uploadBytes(storageRef, logoFile);
          const url = await getDownloadURL(storageRef);
          await updateDoc(projectRef, { logoUrl: url, logoPath: path });
        }
        if (projectBackgroundImageFile) {
          if (editingBackgroundImagePath) {
            try { await deleteObject(ref(storage, editingBackgroundImagePath)); } catch (e) { /* ignore */ }
          }
          const path = `project-backgrounds/${editingProjectId}/${projectBackgroundImageFile.name}`;
          const storageRef = ref(storage, path);
          await uploadBytes(storageRef, projectBackgroundImageFile);
          const url = await getDownloadURL(storageRef);
          await updateDoc(projectRef, { backgroundImageUrl: url, backgroundImagePath: path });
        }
        if (projectDocumentImageFile) {
          if (editingDocumentImagePath) {
            try { await deleteObject(ref(storage, editingDocumentImagePath)); } catch (e) { /* ignore */ }
          }
          const path = `project-document-images/${editingProjectId}/${projectDocumentImageFile.name}`;
          const storageRef = ref(storage, path);
          await uploadBytes(storageRef, projectDocumentImageFile);
          const url = await getDownloadURL(storageRef);
          await updateDoc(projectRef, { documentImageUrl: url, documentImagePath: path });
        }
        if (projectLogoBwFile) {
          if (editingLogoBwPath) {
            try { await deleteObject(ref(storage, editingLogoBwPath)); } catch (e) { /* ignore */ }
          }
          const path = `project-logos-bw/${editingProjectId}/${projectLogoBwFile.name}`;
          const storageRef = ref(storage, path);
          await uploadBytes(storageRef, projectLogoBwFile);
          const url = await getDownloadURL(storageRef);
          await updateDoc(projectRef, { logoBwUrl: url, logoBwPath: path });
        }
      } else {
        const docRef = await addDoc(projectsRef, {
          name: trimmedName,
          location: projectLocation.trim(),
          latitude: projectLatitude.trim() || null,
          longitude: projectLongitude.trim() || null,
          address: projectAddress.trim() || null,
          whatsappSubtitle: projectWhatsappSubtitle.trim() || null,
          websiteSlug: projectWebsiteSlug.trim() || null,
          brochureUrl: projectBrochureUrl.trim() || null,
          backgroundImageUrl: projectBackgroundImageUrl.trim() || null,
          documentImageUrl: projectDocumentImageUrl.trim() || null,
          logoUrl: projectLogoUrl.trim() || null,
          logoBwUrl: projectLogoBwUrl.trim() || null,
          backgroundImagePath: null,
          documentImagePath: null,
          logoPath: null,
          logoBwPath: null,
          unitTypes,
          advocateAgreementFee,
          advocateDeedFee,
          bucReceivedAt,
          bucReceivedBuildingNos,
          sbAreaPercent,
          companyProfileId: selectedCompanyProfileId,
          companyProfileName: selectedCompanyProfileName,
          createdAt: serverTimestamp(),
        });

        if (logoFile) {
          const path = `project-logos/${docRef.id}/${logoFile.name}`;
          const storageRef = ref(storage, path);
          await uploadBytes(storageRef, logoFile);
          const url = await getDownloadURL(storageRef);
          await updateDoc(docRef, { logoUrl: url, logoPath: path });
        }
        if (projectBackgroundImageFile) {
          const path = `project-backgrounds/${docRef.id}/${projectBackgroundImageFile.name}`;
          const storageRef = ref(storage, path);
          await uploadBytes(storageRef, projectBackgroundImageFile);
          const url = await getDownloadURL(storageRef);
          await updateDoc(docRef, { backgroundImageUrl: url, backgroundImagePath: path });
        }
        if (projectDocumentImageFile) {
          const path = `project-document-images/${docRef.id}/${projectDocumentImageFile.name}`;
          const storageRef = ref(storage, path);
          await uploadBytes(storageRef, projectDocumentImageFile);
          const url = await getDownloadURL(storageRef);
          await updateDoc(docRef, { documentImageUrl: url, documentImagePath: path });
        }
        if (projectLogoBwFile) {
          const path = `project-logos-bw/${docRef.id}/${projectLogoBwFile.name}`;
          const storageRef = ref(storage, path);
          await uploadBytes(storageRef, projectLogoBwFile);
          const url = await getDownloadURL(storageRef);
          await updateDoc(docRef, { logoBwUrl: url, logoBwPath: path });
        }
      }

      resetProjectForm();
      setEditingProjectId(null);
      setEditingLogoPath(null);
      setEditingBackgroundImagePath(null);
      setEditingDocumentImagePath(null);
      setEditingLogoBwPath(null);
      setProjectDrawerOpen(false);
    } catch (err: any) {
      console.error('Create project error', err);
      setProjectsError(err.message || 'Failed to create project.');
    } finally {
      setProjectSaving(false);
    }
  };

  const applyStandardOfficeWorkflowsToProject = async () => {
    if (!editingProjectId) return;
    setOfficeWorkflowApplyError(null);
    setOfficeWorkflowApplySuccess(null);
    setOfficeWorkflowApplying(true);
    try {
      if (!currentUser?.email) throw new Error('Not signed in.');
      const by = currentUser.email;
      const projectRef = doc(db, 'projects', editingProjectId);
      await setDoc(
        doc(db, 'projects', editingProjectId, 'workflowTemplates', DEFAULT_OFFICE_MAIN_TEMPLATE_ID),
        { ...defaultOfficeMainTemplate(), updatedAt: serverTimestamp(), updatedBy: by },
        { merge: true }
      );
      await setDoc(
        doc(db, 'projects', editingProjectId, 'workflowTemplates', DEFAULT_OFFICE_LOAN_TEMPLATE_ID),
        { ...defaultOfficeLoanTemplate(), updatedAt: serverTimestamp(), updatedBy: by },
        { merge: true }
      );
      await setDoc(
        projectRef,
        {
          officeMainWorkflowTemplateId: DEFAULT_OFFICE_MAIN_TEMPLATE_ID,
          officeLoanWorkflowTemplateId: DEFAULT_OFFICE_LOAN_TEMPLATE_ID,
          updatedAt: serverTimestamp(),
          updatedBy: by,
        },
        { merge: true }
      );
      setOfficeWorkflowApplySuccess('Standard office workflows applied to this project.');
    } catch (err: any) {
      console.error('Apply office workflows failed', err);
      setOfficeWorkflowApplyError(err?.message || 'Failed to apply workflows.');
    } finally {
      setOfficeWorkflowApplying(false);
    }
  };

  const resetOfficeWorkflowForm = () => {
    setEditingOfficeWorkflowId(null);
    setOfficeWorkflowName('');
    setOfficeWorkflowType('main');
    setOfficeWorkflowVersion(1);
    setOfficeWorkflowSteps([]);
    setOfficeWorkflowProjectIds([]);
    setOfficeWorkflowSaveError(null);
    setOfficeWorkflowSaveSuccess(null);
    setOfficeWorkflowApplyToProjectsError(null);
    setOfficeWorkflowApplyToProjectsSuccess(null);
    setOfficeWorkflowSelectedStepIdx(0);
  };

  const normalizeKey = (s: string) =>
    String(s || '')
      .trim()
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '_')
      .replace(/^_+|_+$/g, '')
      .slice(0, 60);

  const addOfficeWorkflowStep = () => {
    setOfficeWorkflowSteps((prev) => {
      const nextOrder = (prev?.length || 0) + 1;
      const nextIdx = prev.length;
      setOfficeWorkflowSelectedStepIdx(nextIdx);
      return [
        ...(prev || []),
        {
          key: `step_${nextOrder}`,
          title: '',
          dependsOn: [],
          kind: 'task',
          fields: [],
          requiredDocs: [],
        },
      ];
    });
  };

  const moveOfficeWorkflowStep = (idx: number, dir: -1 | 1) => {
    setOfficeWorkflowSteps((prev) => {
      const arr = [...(prev || [])];
      const j = idx + dir;
      if (j < 0 || j >= arr.length) return arr;
      const tmp = arr[idx];
      arr[idx] = arr[j];
      arr[j] = tmp;
      return arr;
    });
    setOfficeWorkflowSelectedStepIdx((prev) => {
      const j = idx + dir;
      if (prev === idx) return j;
      if (prev === j) return idx;
      return prev;
    });
  };

  const deleteOfficeWorkflowStep = (idx: number) => {
    setOfficeWorkflowSteps((prev) => {
      const next = (prev || []).filter((_, i) => i !== idx);
      setOfficeWorkflowSelectedStepIdx((selected) => {
        if (next.length <= 0) return 0;
        if (selected === idx) return Math.min(idx, next.length - 1);
        if (selected > idx) return selected - 1;
        return Math.min(selected, next.length - 1);
      });
      return next;
    });
  };

  const openNewOfficeWorkflow = (type: 'main' | 'loan') => {
    resetOfficeWorkflowForm();
    setOfficeWorkflowType(type);
    if (type === 'main') {
      const t = defaultOfficeMainTemplate();
      setOfficeWorkflowName(t.name);
      setOfficeWorkflowVersion(t.version || 1);
      setOfficeWorkflowSteps(
        (t.steps || []).map((s: any) => ({
          ...s,
          fields: Array.isArray(s.fields) ? s.fields : [],
          requiredDocs: Array.isArray(s.requiredDocs) ? s.requiredDocs : [],
        }))
      );
    } else {
      const t = defaultOfficeLoanTemplate();
      setOfficeWorkflowName(t.name);
      setOfficeWorkflowVersion(t.version || 1);
      setOfficeWorkflowSteps(
        (t.steps || []).map((s: any) => ({
          ...s,
          fields: Array.isArray(s.fields) ? s.fields : [],
          requiredDocs: Array.isArray(s.requiredDocs) ? s.requiredDocs : [],
        }))
      );
    }
    setOfficeWorkflowSelectedStepIdx(0);
    setOfficeWorkflowDrawerOpen(true);
  };

  const openEditOfficeWorkflow = (id: string) => {
    const row = officeWorkflows.find((w) => w.id === id);
    if (!row) return;
    resetOfficeWorkflowForm();
    setEditingOfficeWorkflowId(id);
    setOfficeWorkflowName(String(row.name || ''));
    setOfficeWorkflowType((row.type === 'loan' ? 'loan' : 'main') as any);
    setOfficeWorkflowVersion(Number(row.version || 1));
    setOfficeWorkflowSteps(
      Array.isArray(row.steps)
        ? row.steps.map((s: any) => ({
            ...s,
            fields: Array.isArray(s.fields) ? s.fields : [],
            requiredDocs: Array.isArray(s.requiredDocs) ? s.requiredDocs : [],
          }))
        : []
    );
    setOfficeWorkflowProjectIds(Array.isArray(row.assignedProjectIds) ? row.assignedProjectIds : []);
    setOfficeWorkflowSelectedStepIdx(0);
    setOfficeWorkflowDrawerOpen(true);
  };

  const validateOfficeWorkflow = () => {
    const name = officeWorkflowName.trim();
    if (!name) return 'Workflow name is required.';
    const steps = Array.isArray(officeWorkflowSteps) ? officeWorkflowSteps : [];
    if (steps.length === 0) return 'Add at least one step.';
    const keys = steps.map((s: any) => String(s.key || '').trim()).filter(Boolean);
    if (keys.length !== steps.length) return 'Each step must have a key.';
    const dup = keys.find((k, i) => keys.indexOf(k) !== i);
    if (dup) return `Duplicate step key: ${dup}`;
    const emptyTitleIdx = steps.findIndex((s: any) => !String(s.title || '').trim());
    if (emptyTitleIdx >= 0) return `Step ${emptyTitleIdx + 1} title is required.`;
    return null;
  };

  const saveOfficeWorkflow = async () => {
    setOfficeWorkflowSaveError(null);
    setOfficeWorkflowSaveSuccess(null);
    const err = validateOfficeWorkflow();
    if (err) {
      setOfficeWorkflowSaveError(err);
      return;
    }
    if (!currentUser?.email) {
      setOfficeWorkflowSaveError('Not signed in.');
      return;
    }
    setOfficeWorkflowSaving(true);
    try {
      const by = currentUser.email;
      const steps = (officeWorkflowSteps || []).map((s: any, idx: number) => {
        const key = String(s.key || '').trim() || normalizeKey(String(s.title || '')) || `step_${idx + 1}`;
        return {
          key,
          title: String(s.title || '').trim(),
          order: idx + 1,
          dependsOn: Array.isArray(s.dependsOn) ? s.dependsOn.filter(Boolean) : [],
          kind: s.kind || 'task',
          paymentReceiptType:
            s.kind === 'payment'
              ? (String(s.paymentReceiptType || 'both') as 'both' | 'customer' | 'gst')
              : undefined,
          fields: Array.isArray(s.fields)
            ? s.fields.map((f: any) => ({
                key: String(f.key || '').trim(),
                label: String(f.label || '').trim(),
                type: f.type,
                required: !!f.required,
                options: f.type === 'select' ? (Array.isArray(f.options) ? f.options.filter(Boolean) : []) : undefined,
              }))
            : [],
          requiredDocs: Array.isArray(s.requiredDocs)
            ? s.requiredDocs.map((d: any) => ({
                key: String(d.key || '').trim(),
                label: String(d.label || '').trim(),
                required: !!d.required,
              }))
            : [],
        };
      });
      const payload = {
        name: officeWorkflowName.trim(),
        type: officeWorkflowType,
        version: Number(officeWorkflowVersion || 1),
        steps,
        assignedProjectIds: officeWorkflowProjectIds,
        updatedAt: serverTimestamp(),
        updatedBy: by,
      };
      if (editingOfficeWorkflowId) {
        await updateDoc(doc(db, 'officeWorkflowTemplates', editingOfficeWorkflowId), payload);
        setOfficeWorkflowSaveSuccess('Workflow saved.');
      } else {
        const newRef = doc(collection(db, 'officeWorkflowTemplates'));
        await setDoc(newRef, { ...payload, createdAt: serverTimestamp(), createdBy: by });
        setEditingOfficeWorkflowId(newRef.id);
        setOfficeWorkflowSaveSuccess('Workflow created.');
      }
    } catch (e: any) {
      console.error('Save office workflow failed', e);
      setOfficeWorkflowSaveError(e?.message || 'Failed to save workflow.');
    } finally {
      setOfficeWorkflowSaving(false);
    }
  };

  const applyOfficeWorkflowToProjects = async () => {
    setOfficeWorkflowApplyToProjectsError(null);
    setOfficeWorkflowApplyToProjectsSuccess(null);
    const err = validateOfficeWorkflow();
    if (err) {
      setOfficeWorkflowApplyToProjectsError(err);
      return;
    }
    if (!editingOfficeWorkflowId) {
      setOfficeWorkflowApplyToProjectsError('Save the workflow first.');
      return;
    }
    if (!currentUser?.email) {
      setOfficeWorkflowApplyToProjectsError('Not signed in.');
      return;
    }
    const target = (officeWorkflowProjectIds || []).filter(Boolean);
    if (target.length === 0) {
      setOfficeWorkflowApplyToProjectsError('Select at least one project.');
      return;
    }
    setOfficeWorkflowApplyingToProjects(true);
    try {
      const by = currentUser.email;
      const steps = (officeWorkflowSteps || []).map((s: any, idx: number) => ({
        key: String(s.key || '').trim(),
        title: String(s.title || '').trim(),
        order: idx + 1,
        dependsOn: Array.isArray(s.dependsOn) ? s.dependsOn.filter(Boolean) : [],
        kind: s.kind || 'task',
        paymentReceiptType:
          s.kind === 'payment'
            ? (String(s.paymentReceiptType || 'both') as 'both' | 'customer' | 'gst')
            : undefined,
        fields: Array.isArray(s.fields)
          ? s.fields.map((f: any) => ({
              key: String(f.key || '').trim(),
              label: String(f.label || '').trim(),
              type: f.type,
              required: !!f.required,
              options: f.type === 'select' ? (Array.isArray(f.options) ? f.options.filter(Boolean) : []) : undefined,
            }))
          : [],
        requiredDocs: Array.isArray(s.requiredDocs)
          ? s.requiredDocs.map((d: any) => ({
              key: String(d.key || '').trim(),
              label: String(d.label || '').trim(),
              required: !!d.required,
            }))
          : [],
      }));

      // Copy into each project and set as active template for that project.
      // Note: This overwrites the template with the same id inside the project.
      const MAX_BATCH = 400;
      let batch = writeBatch(db);
      let ops = 0;
      const commitIfNeeded = async () => {
        if (ops === 0) return;
        await batch.commit();
        batch = writeBatch(db);
        ops = 0;
      };

      for (const pid of target) {
        const tplRef = doc(db, 'projects', pid, 'workflowTemplates', editingOfficeWorkflowId);
        batch.set(tplRef, {
          name: officeWorkflowName.trim(),
          type: officeWorkflowType,
          version: Number(officeWorkflowVersion || 1),
          steps,
          updatedAt: serverTimestamp(),
          updatedBy: by,
          createdAt: serverTimestamp(),
          createdBy: by,
        });
        ops++;

        const projectRef = doc(db, 'projects', pid);
        batch.set(
          projectRef,
          {
            ...(officeWorkflowType === 'main'
              ? { officeMainWorkflowTemplateId: editingOfficeWorkflowId }
              : { officeLoanWorkflowTemplateId: editingOfficeWorkflowId }),
            updatedAt: serverTimestamp(),
            updatedBy: by,
          },
          { merge: true }
        );
        ops++;
        if (ops >= MAX_BATCH) await commitIfNeeded();
      }
      await commitIfNeeded();

      await updateDoc(doc(db, 'officeWorkflowTemplates', editingOfficeWorkflowId), {
        assignedProjectIds: target,
        updatedAt: serverTimestamp(),
        updatedBy: currentUser.email,
      });
      setOfficeWorkflowApplyToProjectsSuccess('Workflow applied to selected projects.');
    } catch (e: any) {
      console.error('Apply workflow to projects failed', e);
      setOfficeWorkflowApplyToProjectsError(e?.message || 'Failed to apply workflow.');
    } finally {
      setOfficeWorkflowApplyingToProjects(false);
    }
  };

  const resetOfficeDocumentTemplateForm = () => {
    setEditingOfficeDocumentTemplateId(null);
    setOfficeDocumentTemplateName('');
    setOfficeDocumentTemplateVersion(1);
    setOfficeDocumentTemplateGroupKey('');
    setOfficeDocumentTemplateBodyHtml('');
    setOfficeDocumentTemplateDraftFileNameTemplate('');
    setOfficeDocumentTemplateLinkToBank(false);
    setOfficeDocumentTemplateLinkedBanks([]);
    setOfficeDocumentTemplateUseProjectLogoBw(false);
    setOfficeDocEditorMode('visual');
    setOfficeDocPaperSize('A4');
    setOfficeDocVisualTitle('Allotment Letter');
    setOfficeDocVisualSubtitle('');
    setOfficeDocShowTitle(true);
    setOfficeDocShowRefNo(true);
    setOfficeDocShowDate(true);
    setOfficeDocShowPageNumbers(true);
    setOfficeDocTitleAlign('left');
    setOfficeDocFontFamily('Arial');
    setOfficeDocLetterSpacingPx(0);
    setOfficeDocUseCompanyFont(false);
    setOfficeDocRefNo(generateOfficeDocRefNo([]));
    setOfficeDocRefPrefix('GEN');
    setOfficeDocRefMode('sequence');
    setOfficeDocGeneratedOutputType('draft');
    setOfficeDocShowInSection('draft_documents');
    setOfficeDocHeaderHeightCm(3.8);
    setOfficeDocFooterHeightCm(1.5);
    setOfficeDocHeaderTopMarginCm(0);
    setOfficeDocFooterBottomMarginCm(0);
    setOfficeDocHeaderFooterSideMarginCm(0);
    setOfficeDocHeaderHorizontalOffsetCm(0);
    setOfficeDocHeaderVerticalOffsetCm(0);
    setOfficeDocFooterHorizontalOffsetCm(0);
    setOfficeDocFooterVerticalOffsetCm(0);
    setOfficeDocFooterOverlayEnabled(false);
    setOfficeDocFooterOverlayText('');
    setOfficeDocFooterOverlayFontSizePx(12);
    setOfficeDocFooterOverlayOffsetXCm(0);
    setOfficeDocFooterOverlayOffsetYCm(0);
    setOfficeDocFooterOverlayPageScope('all');
    setOfficeDocFooterOverlayPageNumbers('');
    setOfficeDocMainContentOffsetCm(0);
    setOfficeDocMarginTopCm(0);
    setOfficeDocMarginRightCm(0.9);
    setOfficeDocMarginBottomCm(0);
    setOfficeDocMarginLeftCm(1.6);
    setOfficeDocCompanyLogoHeightCm(2.8);
    setOfficeDocProjectLogoHeightCm(1.9);
    setOfficeDocCompanyLogoOffsetXCm(0);
    setOfficeDocCompanyLogoOffsetYCm(0);
    setOfficeDocProjectLogoOffsetXCm(0);
    setOfficeDocProjectLogoOffsetYCm(0);
    setOfficeDocCompanyHeaderContentType('logo');
    setOfficeDocProjectHeaderContentType('logo');
    setOfficeDocCompanyHeaderText('');
    setOfficeDocProjectHeaderText('');
    setOfficeDocCompanyHeaderTextCss('font-size:12px;line-height:1.2;color:#111;white-space:nowrap;');
    setOfficeDocProjectHeaderTextCss('font-size:12px;line-height:1.2;color:#111;white-space:nowrap;');
    setOfficeDocHeaderCenterHtml('');
    setOfficeDocShowFooterContent(true);
    setOfficeDocBackgroundImageUrl('');
    setOfficeDocBackgroundOpacity(0.08);
    setOfficeDocBackgroundSize('contain');
    setOfficeDocBackgroundScale(72);
    setOfficeDocBackgroundPosX(-44);
    setOfficeDocBackgroundPosY(109);
    setOfficeDocVisualBody('');
    setOfficeDocVisualFooter('');
    setOfficeDocActiveField('body');
    setOfficeDocumentTemplateProjectIds([]);
    setOfficeDocumentTemplateRequestTypes([]);
    setOfficeDocumentTemplateSaveError(null);
    setOfficeDocumentTemplateSaveSuccess(null);
  };

  const normalizeRequestTypes = (raw: string): string[] => {
    const seen = new Set<string>();
    return raw
      .split(/[\n,]/g)
      .map((value) => value.trim())
      .filter(Boolean)
      .filter((value) => {
        const key = value.toLowerCase();
        if (seen.has(key)) return false;
        seen.add(key);
        return true;
      });
  };

  const toUploadTypeKey = (raw: string): string =>
    normalizeUploadTypeKey(raw);

  const resetOfficeDocumentUploadTypeForm = () => {
    setEditingOfficeDocumentUploadTypeId(null);
    setOfficeDocumentUploadTypeKey('');
    setOfficeDocumentUploadTypeKeyTouched(false);
    setOfficeDocumentUploadTypeLabel('');
    setOfficeDocumentUploadTypeDisplayNameTemplate('');
    setOfficeDocumentUploadTypeDisplaySection('main_documents');
    setOfficeDocumentUploadTypeLinkedDraftTemplateId('');
    setOfficeDocumentUploadTypeAllowedExtensions('pdf,jpg,jpeg,png');
    setOfficeDocumentUploadTypeMaxSizeMb(15);
    setOfficeDocumentUploadTypeAllowMultiple(true);
    setOfficeDocumentUploadTypeProjectIds([]);
    setOfficeDocumentUploadTypeSaveError(null);
    setOfficeDocumentUploadTypeSaveSuccess(null);
  };

  const openNewOfficeDocumentUploadType = () => {
    resetOfficeDocumentUploadTypeForm();
    setOfficeDocumentUploadTypeDrawerOpen(true);
  };

  const openEditOfficeDocumentUploadType = (id: string) => {
    const row = officeDocumentUploadTypes.find((item) => item.id === id);
    if (!row) return;
    resetOfficeDocumentUploadTypeForm();
    setEditingOfficeDocumentUploadTypeId(id);
    setOfficeDocumentUploadTypeKey(String(row.key || row.id || ''));
    setOfficeDocumentUploadTypeKeyTouched(true);
    setOfficeDocumentUploadTypeLabel(String(row.label || row.key || row.id || ''));
    setOfficeDocumentUploadTypeDisplayNameTemplate(String(row.displayNameTemplate || ''));
    setOfficeDocumentUploadTypeDisplaySection(
      String(row.displaySection || '').trim().toLowerCase() === 'loan_documents'
        ? 'loan_documents'
        : String(row.displaySection || '').trim().toLowerCase() === 'draft_documents'
          ? 'draft_documents'
          : 'main_documents'
    );
    setOfficeDocumentUploadTypeLinkedDraftTemplateId(String(row.linkedDraftTemplateId || ''));
    setOfficeDocumentUploadTypeAllowedExtensions(String(row.allowedExtensions || 'pdf,jpg,jpeg,png'));
    setOfficeDocumentUploadTypeMaxSizeMb(Math.max(1, Number(row.maxSizeMb || 15) || 15));
    setOfficeDocumentUploadTypeAllowMultiple(row.allowMultiple !== false);
    setOfficeDocumentUploadTypeProjectIds(
      Array.isArray(row.assignedProjectIds)
        ? row.assignedProjectIds.map((pid: any) => String(pid || '')).filter(Boolean)
        : []
    );
    setOfficeDocumentUploadTypeDrawerOpen(true);
  };

  const saveOfficeDocumentUploadType = async () => {
    setOfficeDocumentUploadTypeSaveError(null);
    setOfficeDocumentUploadTypeSaveSuccess(null);
    const key = toUploadTypeKey(officeDocumentUploadTypeKey);
    const label = String(officeDocumentUploadTypeLabel || '').trim();
    if (!key) {
      setOfficeDocumentUploadTypeSaveError('Type key is required.');
      return;
    }
    if (!label) {
      setOfficeDocumentUploadTypeSaveError('Display label is required.');
      return;
    }
    if (!currentUser?.email) {
      setOfficeDocumentUploadTypeSaveError('Not signed in.');
      return;
    }
    setOfficeDocumentUploadTypeSaving(true);
    try {
      const payload = {
        key,
        label,
        displayNameTemplate: String(officeDocumentUploadTypeDisplayNameTemplate || '').trim() || null,
        displaySection: officeDocumentUploadTypeDisplaySection,
        linkedDraftTemplateId: String(officeDocumentUploadTypeLinkedDraftTemplateId || '').trim() || null,
        allowedExtensions: String(officeDocumentUploadTypeAllowedExtensions || '')
          .split(/[,\s]+/g)
          .map((v) => v.trim().toLowerCase().replace(/^\./, ''))
          .filter(Boolean)
          .join(','),
        maxSizeMb: Math.max(1, Number(officeDocumentUploadTypeMaxSizeMb || 15) || 15),
        allowMultiple: !!officeDocumentUploadTypeAllowMultiple,
        assignedProjectIds: officeDocumentUploadTypeProjectIds,
        updatedAt: serverTimestamp(),
        updatedBy: currentUser.email,
      };
      if (editingOfficeDocumentUploadTypeId) {
        await updateDoc(doc(db, 'officeDocumentUploadTypes', editingOfficeDocumentUploadTypeId), payload);
        setOfficeDocumentUploadTypeSaveSuccess('Document config saved.');
      } else {
        const newRef = doc(collection(db, 'officeDocumentUploadTypes'));
        await setDoc(newRef, {
          ...payload,
          createdAt: serverTimestamp(),
          createdBy: currentUser.email,
        });
        setEditingOfficeDocumentUploadTypeId(newRef.id);
        setOfficeDocumentUploadTypeSaveSuccess('Document config created.');
      }
    } catch (e: any) {
      console.error('Save document config failed', e);
      setOfficeDocumentUploadTypeSaveError(e?.message || 'Failed to save document config.');
    } finally {
      setOfficeDocumentUploadTypeSaving(false);
    }
  };

  const openNewOfficeDocumentTemplate = () => {
    resetOfficeDocumentTemplateForm();
    setOfficeDocVisualTitle('Allotment Letter');
    setOfficeDocShowTitle(true);
    setOfficeDocShowRefNo(true);
    setOfficeDocShowDate(true);
    setOfficeDocShowPageNumbers(true);
    setOfficeDocTitleAlign('center');
    setOfficeDocFontFamily('Arial');
    setOfficeDocLetterSpacingPx(0);
    setOfficeDocUseCompanyFont(false);
    setOfficeDocRefPrefix('GEN');
    setOfficeDocRefMode('sequence');
    setOfficeDocHeaderHeightCm(3.8);
    setOfficeDocFooterHeightCm(1.5);
    setOfficeDocHeaderTopMarginCm(0);
    setOfficeDocFooterBottomMarginCm(0);
    setOfficeDocHeaderFooterSideMarginCm(0);
    setOfficeDocHeaderHorizontalOffsetCm(0);
    setOfficeDocHeaderVerticalOffsetCm(0);
    setOfficeDocFooterHorizontalOffsetCm(0);
    setOfficeDocFooterVerticalOffsetCm(0);
    setOfficeDocFooterOverlayEnabled(false);
    setOfficeDocFooterOverlayText('');
    setOfficeDocFooterOverlayFontSizePx(12);
    setOfficeDocFooterOverlayOffsetXCm(0);
    setOfficeDocFooterOverlayOffsetYCm(0);
    setOfficeDocFooterOverlayPageScope('all');
    setOfficeDocFooterOverlayPageNumbers('');
    setOfficeDocMainContentOffsetCm(0);
    setOfficeDocMarginTopCm(0);
    setOfficeDocMarginRightCm(0.9);
    setOfficeDocMarginBottomCm(0);
    setOfficeDocMarginLeftCm(1.6);
    setOfficeDocCompanyLogoHeightCm(2.8);
    setOfficeDocProjectLogoHeightCm(1.9);
    setOfficeDocCompanyLogoOffsetXCm(0);
    setOfficeDocCompanyLogoOffsetYCm(0);
    setOfficeDocProjectLogoOffsetXCm(0);
    setOfficeDocProjectLogoOffsetYCm(0);
    setOfficeDocCompanyHeaderContentType('logo');
    setOfficeDocProjectHeaderContentType('logo');
    setOfficeDocCompanyHeaderText('');
    setOfficeDocProjectHeaderText('');
    setOfficeDocCompanyHeaderTextCss('font-size:12px;line-height:1.2;color:#111;white-space:nowrap;');
    setOfficeDocProjectHeaderTextCss('font-size:12px;line-height:1.2;color:#111;white-space:nowrap;');
    setOfficeDocHeaderCenterHtml('');
    setOfficeDocShowFooterContent(true);
    setOfficeDocRefNo(generateOfficeDocRefNo([]));
    setOfficeDocVisualBody('Project: {{project_name}}\nUnit: {{unit_no}}\nAllottee: {{allottee_names}}');
    setOfficeDocumentTemplateBodyHtml('<h1>Allotment Letter</h1><p>Project: {{project_name}}</p><p>Unit: {{unit_no}}</p><p>Allottee: {{allottee_names}}</p>');
    setOfficeDocumentTemplateDrawerOpen(true);
  };

  const loadPaymentDetailsTemplatePreset = () => {
    setOfficeDocumentTemplateName('Payment Details');
    setOfficeDocumentTemplateVersion(1);
    setOfficeDocumentTemplateDraftFileNameTemplate('{{unit_no}} - PAYMENT DETAILS');
    setOfficeDocEditorMode('html');
    setOfficeDocShowTitle(false);
    setOfficeDocShowRefNo(false);
    setOfficeDocShowDate(false);
    setOfficeDocVisualTitle('');
    setOfficeDocVisualSubtitle('');
    setOfficeDocumentTemplateBodyHtml(PAYMENT_DETAILS_TEMPLATE_PRESET_HTML.trim());
    setOfficeDocVisualBody(deriveVisualBodyFromHtml(PAYMENT_DETAILS_TEMPLATE_PRESET_HTML));
    setOfficeDocActiveField('body');
    setOfficeDocumentTemplateSaveError(null);
    setOfficeDocumentTemplateSaveSuccess('Payment details preset loaded. Review and click Create/Save.');
  };

  const loadPaymentReceiptVoucherA5TemplatePreset = () => {
    setOfficeDocumentTemplateName('Customer Payment Receipt Voucher');
    setOfficeDocumentTemplateVersion(1);
    setOfficeDocumentTemplateGroupKey('payment_receipt_voucher');
    setOfficeDocumentTemplateDraftFileNameTemplate('{{unit_no}} - PAYMENT RECEIPT VOUCHER');
    setOfficeDocEditorMode('html');
    setOfficeDocShowTitle(false);
    setOfficeDocShowRefNo(false);
    setOfficeDocShowDate(false);
    setOfficeDocVisualTitle('');
    setOfficeDocVisualSubtitle('');
    setOfficeDocPaperSize('A5');
    setOfficeDocumentTemplateBodyHtml(PAYMENT_RECEIPT_VOUCHER_A5_TEMPLATE_PRESET_HTML.trim());
    setOfficeDocVisualBody(deriveVisualBodyFromHtml(PAYMENT_RECEIPT_VOUCHER_A5_TEMPLATE_PRESET_HTML));
    setOfficeDocActiveField('body');
    setOfficeDocumentTemplateSaveError(null);
    setOfficeDocumentTemplateSaveSuccess('Customer payment receipt (A5) preset loaded. Review and click Create/Save.');
  };

  const loadJoiningLetterTemplatePreset = () => {
    setOfficeDocumentTemplateName('Joining Letter');
    setOfficeDocumentTemplateVersion(1);
    setOfficeDocumentTemplateGroupKey('joining_letter');
    setOfficeDocumentTemplateDraftFileNameTemplate('{{employee_name}} - JOINING LETTER');
    setOfficeDocEditorMode('html');
    setOfficeDocPaperSize('A4');
    setOfficeDocShowTitle(true);
    setOfficeDocShowRefNo(true);
    setOfficeDocShowDate(true);
    setOfficeDocShowPageNumbers(true);
    setOfficeDocTitleAlign('center');
    setOfficeDocVisualTitle('Appointment / Joining Letter');
    setOfficeDocVisualSubtitle('');
    setOfficeDocRefPrefix('HR');
    setOfficeDocRefMode('sequence');
    setOfficeDocGeneratedOutputType('draft');
    setOfficeDocShowInSection('draft_documents');
    setOfficeDocumentTemplateBodyHtml(JOINING_LETTER_TEMPLATE_PRESET_HTML.trim());
    setOfficeDocVisualBody(deriveVisualBodyFromHtml(JOINING_LETTER_TEMPLATE_PRESET_HTML));
    setOfficeDocActiveField('body');
    setOfficeDocumentTemplateSaveError(null);
    setOfficeDocumentTemplateSaveSuccess('Joining letter preset loaded. Review and click Create/Save.');
  };

  const openEditOfficeDocumentTemplate = (id: string) => {
    const row = officeDocumentTemplates.find((w) => w.id === id);
    if (!row) return;
    resetOfficeDocumentTemplateForm();
    setEditingOfficeDocumentTemplateId(id);
    setOfficeDocumentTemplateName(String(row.name || ''));
    setOfficeDocumentTemplateVersion(Number(row.version || 1));
    setOfficeDocumentTemplateGroupKey(
      String(row.documentGroupKey || row.visualModel?.documentGroupKey || '').trim()
    );
    setOfficeDocumentTemplateBodyHtml(String(row.bodyHtml || ''));
    setOfficeDocumentTemplateDraftFileNameTemplate(String(row.draftFileNameTemplate || ''));
    const hasVisual = !!row.visualModel;
    const modeFromData =
      row.editorMode === 'html'
        ? 'html'
        : row.editorMode === 'visual'
          ? 'visual'
          : hasVisual
            ? 'visual'
            : 'html';
    setOfficeDocEditorMode(modeFromData);
    const vm = row.visualModel || {};
    const loadedPaperSize = String(vm.paperSize || row.paperSize || '').toUpperCase() === 'A5' ? 'A5' : 'A4';
    setOfficeDocPaperSize(loadedPaperSize);
    setOfficeDocVisualTitle(String(vm.title || row.name || ''));
    setOfficeDocVisualSubtitle(String(vm.subtitle || ''));
    setOfficeDocShowTitle(vm.showTitle !== false);
    setOfficeDocShowRefNo(vm.showRefNo !== false);
    setOfficeDocShowDate(vm.showDate !== false);
    setOfficeDocShowPageNumbers(vm.showPageNumbers !== false);
    setOfficeDocTitleAlign((vm.titleAlign === 'center' || vm.titleAlign === 'right' || vm.titleAlign === 'left' ? vm.titleAlign : 'left') as 'left' | 'center' | 'right');
    setOfficeDocFontFamily(String(vm.fontFamily || 'Arial'));
    setOfficeDocLetterSpacingPx(Math.max(-2, Math.min(10, Number(vm.letterSpacingPx ?? 0) || 0)));
    setOfficeDocUseCompanyFont(Boolean(vm.useCompanyFont));
    const legacyRefNo = String(vm.refNo || generateOfficeDocRefNo(Array.isArray(row.assignedProjectIds) ? row.assignedProjectIds : []));
    setOfficeDocRefNo(legacyRefNo);
    const legacyPrefix = legacyRefNo.includes('/') ? legacyRefNo.split('/')[0] : '';
    setOfficeDocRefPrefix(normalizeOfficeRefPrefix(String(vm.refPrefix || legacyPrefix || 'GEN')) || 'GEN');
    setOfficeDocRefMode(
      String(vm.refMode || '').trim() === 'unit_short_booking_code' ? 'unit_short_booking_code' : 'sequence'
    );
    setOfficeDocGeneratedOutputType(String(vm.generatedOutputType || '').trim() === 'final' ? 'final' : 'draft');
    setOfficeDocShowInSection(
      (() => {
        const raw = String(vm.showInSection || '').trim();
        if (raw === 'main_documents' || raw === 'draft_documents' || raw === 'loan_documents' || raw === 'hidden') return raw;
        return String(vm.generatedOutputType || '').trim() === 'final' ? 'main_documents' : 'draft_documents';
      })()
    );
    setOfficeDocHeaderHeightCm(Math.max(0, Number(vm.headerHeightCm || 3.8) || 3.8));
    setOfficeDocFooterHeightCm(Math.max(0, Number(vm.footerHeightCm || 1.5) || 1.5));
    setOfficeDocHeaderTopMarginCm(Math.max(0, Number(vm.headerTopMarginCm ?? 0) || 0));
    setOfficeDocFooterBottomMarginCm(Math.max(0, Number(vm.footerBottomMarginCm ?? 0) || 0));
    setOfficeDocHeaderFooterSideMarginCm(Math.max(0, Number(vm.headerFooterSideMarginCm ?? 0) || 0));
    setOfficeDocHeaderHorizontalOffsetCm(Math.max(-30, Math.min(30, Number(vm.headerHorizontalOffsetCm ?? 0) || 0)));
    setOfficeDocHeaderVerticalOffsetCm(Math.max(-30, Math.min(30, Number(vm.headerVerticalOffsetCm ?? 0) || 0)));
    setOfficeDocFooterHorizontalOffsetCm(Math.max(-30, Math.min(30, Number(vm.footerHorizontalOffsetCm ?? 0) || 0)));
    setOfficeDocFooterVerticalOffsetCm(Math.max(-30, Math.min(30, Number(vm.footerVerticalOffsetCm ?? 0) || 0)));
    setOfficeDocFooterOverlayEnabled(Boolean(vm.footerOverlayEnabled));
    setOfficeDocFooterOverlayText(String(vm.footerOverlayText || ''));
    setOfficeDocFooterOverlayFontSizePx(
      Math.max(8, Math.min(72, Number(vm.footerOverlayFontSizePx ?? 12) || 12))
    );
    setOfficeDocFooterOverlayOffsetXCm(Math.max(-30, Math.min(30, Number(vm.footerOverlayOffsetXCm ?? 0) || 0)));
    setOfficeDocFooterOverlayOffsetYCm(Math.max(-30, Math.min(30, Number(vm.footerOverlayOffsetYCm ?? 0) || 0)));
    setOfficeDocFooterOverlayPageScope(
      vm.footerOverlayPageScope === 'first' ?
        'first' :
        vm.footerOverlayPageScope === 'last' ?
          'last' :
          vm.footerOverlayPageScope === 'custom' ? 'custom' : 'all'
    );
    setOfficeDocFooterOverlayPageNumbers(
      String(vm.footerOverlayPageNumbers || '')
    );
    setOfficeDocMainContentOffsetCm(
      Math.max(-10, Math.min(10, Number(vm.mainContentOffsetCm ?? vm.refDateOffsetCm ?? 0) || 0))
    );
    const legacySide = Math.max(0, Number(vm.sideMarginCm || 1.6) || 1.6);
    setOfficeDocMarginTopCm(Math.max(0, Number(vm.marginTopCm ?? 0) || 0));
    setOfficeDocMarginRightCm(Math.max(0, Number(vm.marginRightCm ?? 0.9) || 0.9));
    setOfficeDocMarginBottomCm(Math.max(0, Number(vm.marginBottomCm ?? 0) || 0));
    setOfficeDocMarginLeftCm(Math.max(0, Number(vm.marginLeftCm ?? legacySide) || legacySide));
    setOfficeDocCompanyLogoHeightCm(Math.max(0, Number(vm.companyLogoHeightCm ?? 2.8) || 2.8));
    setOfficeDocProjectLogoHeightCm(Math.max(0, Number(vm.projectLogoHeightCm ?? 1.9) || 1.9));
    setOfficeDocCompanyLogoOffsetXCm(Math.max(-30, Math.min(30, Number(vm.companyLogoOffsetXCm ?? 0) || 0)));
    setOfficeDocCompanyLogoOffsetYCm(Math.max(-30, Math.min(30, Number(vm.companyLogoOffsetYCm ?? 0) || 0)));
    setOfficeDocProjectLogoOffsetXCm(Math.max(-30, Math.min(30, Number(vm.projectLogoOffsetXCm ?? 0) || 0)));
    setOfficeDocProjectLogoOffsetYCm(Math.max(-30, Math.min(30, Number(vm.projectLogoOffsetYCm ?? 0) || 0)));
    setOfficeDocCompanyHeaderContentType(
      vm.companyHeaderContentType === 'text' || vm.companyHeaderContentType === 'none'
        ? vm.companyHeaderContentType
        : 'logo'
    );
    setOfficeDocProjectHeaderContentType(
      vm.projectHeaderContentType === 'text' || vm.projectHeaderContentType === 'none'
        ? vm.projectHeaderContentType
        : 'logo'
    );
    setOfficeDocCompanyHeaderText(String(vm.companyHeaderText || ''));
    setOfficeDocProjectHeaderText(String(vm.projectHeaderText || ''));
    setOfficeDocCompanyHeaderTextCss(
      String(vm.companyHeaderTextCss || 'font-size:12px;line-height:1.2;color:#111;white-space:nowrap;')
    );
    setOfficeDocProjectHeaderTextCss(
      String(vm.projectHeaderTextCss || 'font-size:12px;line-height:1.2;color:#111;white-space:nowrap;')
    );
    setOfficeDocHeaderCenterHtml(String(vm.headerCenterHtml || ''));
    setOfficeDocShowFooterContent(vm.showFooterContent !== false);
    setOfficeDocBackgroundImageUrl(String(vm.backgroundImageUrl || ''));
    setOfficeDocBackgroundOpacity(Math.max(0, Math.min(0.4, Number(vm.backgroundOpacity ?? 0.08) || 0.08)));
    setOfficeDocBackgroundSize(vm.backgroundSize === 'cover' ? 'cover' : 'contain');
    setOfficeDocBackgroundScale(Math.max(10, Math.min(300, Number(vm.backgroundScale ?? 72) || 72)));
    setOfficeDocBackgroundPosX(Math.max(-300, Math.min(300, Number(vm.backgroundPosX ?? -44) || -44)));
    setOfficeDocBackgroundPosY(Math.max(-300, Math.min(300, Number(vm.backgroundPosY ?? 109) || 109)));
    const fallbackBody = deriveVisualBodyFromHtml(String(row.bodyHtml || ''));
    const vmBody = String(vm.body || '');
    const fallbackHasMarkup = hasHtmlMarkup(fallbackBody);
    const vmHasMarkup = hasHtmlMarkup(vmBody);
    const preferHtmlBody = modeFromData === 'html' || (fallbackHasMarkup && !vmHasMarkup);
    setOfficeDocVisualBody(String(preferHtmlBody ? (fallbackBody || vmBody || '') : (vmBody || fallbackBody || '')));
    setOfficeDocVisualFooter(String(vm.footer || ''));
    setOfficeDocActiveField('body');
    setOfficeDocumentTemplateProjectIds(Array.isArray(row.assignedProjectIds) ? row.assignedProjectIds : []);
    setOfficeDocumentTemplateRequestTypes(
      Array.isArray(row.requestTypes)
        ? row.requestTypes.map((value: any) => String(value || '').trim()).filter(Boolean)
        : []
    );
    setOfficeDocumentTemplateLinkToBank(Boolean(row.linkToBank ?? row.visualModel?.linkToBank));
    setOfficeDocumentTemplateLinkedBanks(
      (Array.isArray(row.linkedBanks) ? row.linkedBanks : Array.isArray(row.visualModel?.linkedBanks) ? row.visualModel.linkedBanks : [])
        .map((value: any) => String(value || '').trim())
        .filter(Boolean)
    );
    setOfficeDocumentTemplateUseProjectLogoBw(
      Boolean(row.useProjectLogoBw ?? row.visualModel?.useProjectLogoBw)
    );
    setOfficeDocumentTemplateDrawerOpen(true);
  };

  const openAssignOfficeDocumentTemplateRequestTypes = (id: string) => {
    const row = officeDocumentTemplates.find((item) => item.id === id);
    if (!row) return;
    const current = Array.isArray(row.requestTypes)
      ? row.requestTypes.map((value: any) => String(value || '').trim()).filter(Boolean)
      : [];
    setOfficeDocumentTemplateAssignTemplateId(id);
    setOfficeDocumentTemplateAssignDraft(current.join('\n'));
    setOfficeDocumentTemplateAssignOpen(true);
  };

  const saveOfficeDocumentTemplateRequestTypes = async () => {
    if (!officeDocumentTemplateAssignTemplateId) return;
    setOfficeDocumentTemplateAssignSaving(true);
    try {
      const requestTypes = normalizeRequestTypes(officeDocumentTemplateAssignDraft);
      await updateDoc(doc(db, 'officeDocumentTemplates', officeDocumentTemplateAssignTemplateId), {
        requestTypes,
        updatedAt: serverTimestamp(),
        updatedBy: currentUser?.email || null,
      });
      setOfficeDocumentTemplateAssignOpen(false);
      setOfficeDocumentTemplateAssignTemplateId(null);
      setOfficeDocumentTemplateAssignDraft('');
    } catch (e: any) {
      setOfficeDocumentTemplatesError(e?.message || 'Failed to assign request types.');
    } finally {
      setOfficeDocumentTemplateAssignSaving(false);
    }
  };

  const saveOfficeDocumentTemplate = async () => {
    setOfficeDocumentTemplateSaveError(null);
    setOfficeDocumentTemplateSaveSuccess(null);
    const name = officeDocumentTemplateName.trim();
    if (!name) {
      setOfficeDocumentTemplateSaveError('Template name is required.');
      return;
    }
    const rawBodyToSave = officeDocEditorMode === 'visual' ? officeDocVisualBody : officeDocumentTemplateBodyHtml;
    const bodyHtmlToSave = toHtmlBodyFragment(rawBodyToSave);
    if (!bodyHtmlToSave.trim()) {
      setOfficeDocumentTemplateSaveError('Template content is required.');
      return;
    }
    if (!currentUser?.email) {
      setOfficeDocumentTemplateSaveError('Not signed in.');
      return;
    }
    setOfficeDocumentTemplateSaving(true);
    try {
      const by = currentUser.email;
      const version = Math.max(1, Number(officeDocumentTemplateVersion || 1) || 1);
      const templateId = makeOfficeDocumentTemplateId(name, version);
      const normalizedManualGroupKey = String(officeDocumentTemplateGroupKey || '')
        .trim()
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '_')
        .replace(/^_+|_+$/g, '');
      const resolvedDocumentGroupKey = officeDocumentTemplateLinkToBank
        ? normalizedManualGroupKey || templateId
        : templateId;
      const payload = {
        name,
        templateId,
        documentGroupKey: resolvedDocumentGroupKey,
        version,
        bodyHtml: bodyHtmlToSave,
        draftFileNameTemplate: String(officeDocumentTemplateDraftFileNameTemplate || '').trim() || null,
        linkToBank: !!officeDocumentTemplateLinkToBank,
        linkedBanks: officeDocumentTemplateLinkToBank
          ? Array.from(
              new Set(
                officeDocumentTemplateLinkedBanks
                  .map((value) => String(value || '').trim())
                  .filter(Boolean)
              )
            )
          : [],
        useProjectLogoBw: !!officeDocumentTemplateUseProjectLogoBw,
        renderHtml: officeDocVisualHtml,
        assignedProjectIds: officeDocumentTemplateProjectIds,
        requestTypes: officeDocumentTemplateRequestTypes,
        placeholders: officeDocPlaceholders,
        editorMode: officeDocEditorMode,
        visualModel: {
          paperSize: officeDocPaperSize,
          title: officeDocVisualTitle,
          subtitle: officeDocVisualSubtitle,
          showTitle: officeDocShowTitle,
          showRefNo: officeDocShowRefNo,
          showDate: officeDocShowDate,
          showPageNumbers: officeDocShowPageNumbers,
          titleAlign: officeDocTitleAlign,
          fontFamily: officeDocFontFamily,
          letterSpacingPx: officeDocLetterSpacingPx,
          useCompanyFont: officeDocUseCompanyFont,
          refNo: officeDocResolvedPreviewRefNo || officeDocRefNo,
          refPrefix: normalizeOfficeRefPrefix(officeDocRefPrefix || ''),
          refMode: officeDocRefMode,
          documentGroupKey: resolvedDocumentGroupKey,
          generatedOutputType: officeDocGeneratedOutputType,
          showInSection: officeDocShowInSection,
          linkToBank: !!officeDocumentTemplateLinkToBank,
          linkedBanks: officeDocumentTemplateLinkToBank
            ? Array.from(
                new Set(
                  officeDocumentTemplateLinkedBanks
                    .map((value) => String(value || '').trim())
                    .filter(Boolean)
                )
              )
            : [],
          useProjectLogoBw: !!officeDocumentTemplateUseProjectLogoBw,
          headerHeightCm: officeDocHeaderHeightCm,
          footerHeightCm: officeDocFooterHeightCm,
          headerTopMarginCm: officeDocHeaderTopMarginCm,
          footerBottomMarginCm: officeDocFooterBottomMarginCm,
          headerFooterSideMarginCm: officeDocHeaderFooterSideMarginCm,
          headerHorizontalOffsetCm: officeDocHeaderHorizontalOffsetCm,
          headerVerticalOffsetCm: officeDocHeaderVerticalOffsetCm,
          footerHorizontalOffsetCm: officeDocFooterHorizontalOffsetCm,
          footerVerticalOffsetCm: officeDocFooterVerticalOffsetCm,
          footerOverlayEnabled: officeDocFooterOverlayEnabled,
          footerOverlayText: officeDocFooterOverlayText,
          footerOverlayFontSizePx: officeDocFooterOverlayFontSizePx,
          footerOverlayOffsetXCm: officeDocFooterOverlayOffsetXCm,
          footerOverlayOffsetYCm: officeDocFooterOverlayOffsetYCm,
          footerOverlayPageScope: officeDocFooterOverlayPageScope,
          footerOverlayPageNumbers: officeDocFooterOverlayPageNumbers,
          mainContentOffsetCm: officeDocMainContentOffsetCm,
          marginTopCm: officeDocMarginTopCm,
          marginRightCm: officeDocMarginRightCm,
          marginBottomCm: officeDocMarginBottomCm,
          marginLeftCm: officeDocMarginLeftCm,
          companyLogoHeightCm: officeDocCompanyLogoHeightCm,
          projectLogoHeightCm: officeDocProjectLogoHeightCm,
          companyLogoOffsetXCm: officeDocCompanyLogoOffsetXCm,
          companyLogoOffsetYCm: officeDocCompanyLogoOffsetYCm,
          projectLogoOffsetXCm: officeDocProjectLogoOffsetXCm,
          projectLogoOffsetYCm: officeDocProjectLogoOffsetYCm,
          companyHeaderContentType: officeDocCompanyHeaderContentType,
          projectHeaderContentType: officeDocProjectHeaderContentType,
          companyHeaderText: officeDocCompanyHeaderText,
          projectHeaderText: officeDocProjectHeaderText,
          companyHeaderTextCss: officeDocCompanyHeaderTextCss,
          projectHeaderTextCss: officeDocProjectHeaderTextCss,
          headerCenterHtml: officeDocHeaderCenterHtml,
          showFooterContent: officeDocShowFooterContent,
          backgroundImageUrl: officeDocBackgroundImageUrl,
          backgroundOpacity: officeDocBackgroundOpacity,
          backgroundSize: officeDocBackgroundSize,
          backgroundScale: officeDocBackgroundScale,
          backgroundPosX: officeDocBackgroundPosX,
          backgroundPosY: officeDocBackgroundPosY,
          body: officeDocVisualBody,
          footer: officeDocVisualFooter,
        },
        updatedAt: serverTimestamp(),
        updatedBy: by,
      };
      if (editingOfficeDocumentTemplateId) {
        await updateDoc(doc(db, 'officeDocumentTemplates', editingOfficeDocumentTemplateId), payload);
        setOfficeDocumentTemplateSaveSuccess('Document template saved.');
      } else {
        const newRef = doc(collection(db, 'officeDocumentTemplates'));
        await setDoc(newRef, { ...payload, createdAt: serverTimestamp(), createdBy: by });
        setEditingOfficeDocumentTemplateId(newRef.id);
        setOfficeDocumentTemplateSaveSuccess('Document template created.');
      }
    } catch (e: any) {
      console.error('Save office document template failed', e);
      setOfficeDocumentTemplateSaveError(e?.message || 'Failed to save document template.');
    } finally {
      setOfficeDocumentTemplateSaving(false);
    }
  };

  const handleSaveStatus = async (e: React.FormEvent) => {
    e.preventDefault();
    setStatusError(null);
    setStatusSaving(true);
    try {
      const trimmed = statusLabel.trim();
      if (!trimmed) {
        setStatusError('Status name is required.');
        return;
      }
      const dup = statuses.some(
        (s) =>
          s.id !== editingStatusId &&
          typeof s.label === 'string' &&
          s.label.trim().toLowerCase() === trimmed.toLowerCase()
      );
      if (dup) {
        setStatusError('A status with this name already exists.');
        return;
      }

      const cleanPrompts = statusPrompts.map((p) => {
        const base: any = { id: p.id, label: p.label, type: p.type };
        if (p.type === 'select') {
          base.options = (p.options || []).filter(Boolean);
        }
        return base;
      });

      const colorTrimmed = statusColor.trim();
      const colorOk = /^#([0-9a-fA-F]{6})$/.test(colorTrimmed);
      if (colorTrimmed && !colorOk) {
        setStatusError('Color must be a hex value like #1e88e5.');
        return;
      }

      const payload = {
        label: trimmed,
        order: Number(statusOrder) || 0,
        color: colorOk ? colorTrimmed : null,
        allowedNext: statusAllowedNext,
        prompts: cleanPrompts,
        active: true,
        updatedAt: serverTimestamp(),
      };
      if (editingStatusId) {
        await updateDoc(doc(db, 'leadStatuses', editingStatusId), payload);
      } else {
        await addDoc(collection(db, 'leadStatuses'), {
          ...payload,
          createdAt: serverTimestamp(),
        });
      }
      resetStatusForm();
      setStatusDrawerOpen(false);
    } catch (err: any) {
      console.error('Save status failed', err);
      setStatusError(err.message || 'Failed to save status.');
    } finally {
      setStatusSaving(false);
    }
  };

  const handleDeleteStatus = async (id: string, label: string) => {
    const confirmed = window.confirm(`Delete status "${label}"?`);
    if (!confirmed) return;
    try {
      await deleteDoc(doc(db, 'leadStatuses', id));
    } catch (err) {
      console.error('Delete status failed', err);
      setStatusError('Failed to delete status.');
    }
  };

  const addPrompt = () => {
    const trimmed = promptLabel.trim();
    if (!trimmed) return;
    const id = trimmed.toLowerCase().replace(/\s+/g, '-');
    setStatusPrompts((prev) => [
      ...prev,
      {
        id,
        label: trimmed,
        type: promptType,
        options:
          promptType === 'select'
            ? promptOptionsInput.split(',').map((o) => o.trim()).filter(Boolean)
            : undefined,
      },
    ]);
    setPromptLabel('');
    setPromptType('text');
    setPromptOptionsInput('');
  };

  const removePrompt = (id: string) => {
    setStatusPrompts((prev) => prev.filter((p) => p.id !== id));
  };

  const makeOfficeDocumentTemplateId = (nameRaw: string, versionRaw: number | string) => {
    const base = String(nameRaw || '')
      .trim()
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '_')
      .replace(/^_+|_+$/g, '');
    const version = Math.max(1, Number(versionRaw || 1) || 1);
    return `${base || 'template'}_v${version}`;
  };

  const insertOfficeDocPlaceholder = (token: string) => {
    if (officeDocEditorMode === 'html') {
      setOfficeDocumentTemplateBodyHtml((prev) => {
        const next = `${prev}${prev ? ' ' : ''}${token}`;
        setOfficeDocVisualBody(next);
        return next;
      });
      return;
    }
    if (officeDocActiveField === 'title') {
      setOfficeDocVisualTitle((prev) => `${prev}${prev ? ' ' : ''}${token}`);
      return;
    }
    if (officeDocActiveField === 'subtitle') {
      setOfficeDocVisualSubtitle((prev) => `${prev}${prev ? ' ' : ''}${token}`);
      return;
    }
    if (officeDocActiveField === 'footer') {
      setOfficeDocVisualFooter((prev) => `${prev}${prev ? ' ' : ''}${token}`);
      return;
    }
    if (officeDocBodyEditorRef.current && document.activeElement === officeDocBodyEditorRef.current) {
      applyOfficeBodyCommand('insertText', token);
      return;
    }
    setOfficeDocVisualBody((prev) => `${prev}${prev ? ' ' : ''}${token}`);
  };

  const loadMammothBrowser = async () => {
    const w = window as any;
    if (w?.mammoth?.convertToHtml) return w.mammoth;
    await new Promise<void>((resolve, reject) => {
      const existing = document.getElementById('mammoth-browser-script') as HTMLScriptElement | null;
      if (existing) {
        if ((window as any)?.mammoth?.convertToHtml) {
          resolve();
          return;
        }
        existing.addEventListener('load', () => resolve(), { once: true });
        existing.addEventListener('error', () => reject(new Error('Failed to load mammoth script.')), { once: true });
        return;
      }
      const script = document.createElement('script');
      script.id = 'mammoth-browser-script';
      script.src = 'https://unpkg.com/mammoth@1.8.0/mammoth.browser.min.js';
      script.async = true;
      script.onload = () => resolve();
      script.onerror = () => reject(new Error('Failed to load mammoth script.'));
      document.head.appendChild(script);
    });
    if (!(window as any)?.mammoth?.convertToHtml) {
      throw new Error('Mammoth converter not available.');
    }
    return (window as any).mammoth;
  };

  const onImportDocxTemplate = async (file: File) => {
    if (!file) return;
    setOfficeDocImportError(null);
    setOfficeDocumentTemplateSaveSuccess(null);
    setOfficeDocImportingDocx(true);
    try {
      const mammoth = await loadMammothBrowser();
      const buffer = await file.arrayBuffer();
      const result = await mammoth.convertToHtml({ arrayBuffer: buffer });
      const parser = new DOMParser();
      const parsed = parser.parseFromString(String(result?.value || ''), 'text/html');
      const cleanedHtml = String(parsed.body?.innerHTML || result?.value || '').trim();
      if (!cleanedHtml) throw new Error('No readable HTML found in document.');
      const normalizedHtml = normalizeImportedDocxHtml(cleanedHtml);
      setOfficeDocumentTemplateBodyHtml(normalizedHtml);
      setOfficeDocVisualBody(deriveVisualBodyFromHtml(normalizedHtml));
      setOfficeDocEditorMode('html');
      setOfficeDocumentTemplateSaveSuccess('DOCX imported to Template HTML. Review and Save.');
    } catch (err: any) {
      setOfficeDocImportError(err?.message || 'Failed to import DOCX.');
    } finally {
      setOfficeDocImportingDocx(false);
      if (officeDocxImportInputRef.current) officeDocxImportInputRef.current.value = '';
    }
  };

  const buildOfficeTemplateSamplePdfPayload = () => {
    const htmlForPreview = officeDocEditorMode === 'html'
      ? applyTemplateTokens(String(officeDocumentTemplateBodyHtml || ''), officeDocPreviewTokenValues)
      : applyOfficeDocPreviewTokens(officeDocVisualHtml, false);
    return buildPdfPayloadFromTemplate({
      html: htmlForPreview,
      tokenValues: officeDocPreviewTokenValues,
      visualModel: {
        paperSize: officeDocPaperSize,
        title: officeDocVisualTitle,
        subtitle: officeDocVisualSubtitle,
        showTitle: officeDocShowTitle,
        showRefNo: officeDocShowRefNo,
        showDate: officeDocShowDate,
        showPageNumbers: officeDocShowPageNumbers,
        titleAlign: officeDocTitleAlign,
        fontFamily: officeDocFontFamily,
        letterSpacingPx: officeDocLetterSpacingPx,
        useCompanyFont: officeDocUseCompanyFont,
        issueDate: formatDdMmYyyy(new Date()),
        refNo: officeDocResolvedPreviewRefNo || officeDocRefNo,
        refPrefix: normalizeOfficeRefPrefix(officeDocRefPrefix || ''),
        refMode: officeDocRefMode,
        generatedOutputType: officeDocGeneratedOutputType,
        showInSection: officeDocShowInSection,
        marginTopCm: officeDocMarginTopCm,
        marginRightCm: officeDocMarginRightCm,
        marginBottomCm: officeDocMarginBottomCm,
        marginLeftCm: officeDocMarginLeftCm,
        mainContentOffsetCm: officeDocMainContentOffsetCm,
        footerHeightCm: officeDocFooterHeightCm,
        footerOverlayEnabled: officeDocFooterOverlayEnabled,
        footerOverlayText: officeDocFooterOverlayText,
        footerOverlayFontSizePx: officeDocFooterOverlayFontSizePx,
        footerOverlayOffsetXCm: officeDocFooterOverlayOffsetXCm,
        footerOverlayOffsetYCm: officeDocFooterOverlayOffsetYCm,
        footerOverlayPageScope: officeDocFooterOverlayPageScope,
        footerOverlayPageNumbers: officeDocFooterOverlayPageNumbers,
        companyLogoHeightCm: officeDocCompanyLogoHeightCm,
        projectLogoHeightCm: officeDocProjectLogoHeightCm,
        companyLogoOffsetXCm: officeDocCompanyLogoOffsetXCm,
        companyLogoOffsetYCm: officeDocCompanyLogoOffsetYCm,
        projectLogoOffsetXCm: officeDocProjectLogoOffsetXCm,
        projectLogoOffsetYCm: officeDocProjectLogoOffsetYCm,
        companyHeaderContentType: officeDocCompanyHeaderContentType,
        projectHeaderContentType: officeDocProjectHeaderContentType,
        companyHeaderText: officeDocCompanyHeaderText,
        projectHeaderText: officeDocProjectHeaderText,
        companyHeaderTextCss: officeDocCompanyHeaderTextCss,
        projectHeaderTextCss: officeDocProjectHeaderTextCss,
        headerCenterHtml: officeDocHeaderCenterHtml,
        showFooterContent: officeDocShowFooterContent,
        backgroundImageUrl: officeDocBackgroundImageUrl || String(activeDocProject?.backgroundImageUrl || ''),
        backgroundOpacity: officeDocBackgroundOpacity,
        backgroundSize: officeDocBackgroundSize,
        backgroundScale: officeDocBackgroundScale,
        backgroundPosX: officeDocBackgroundPosX,
        backgroundPosY: officeDocBackgroundPosY,
        body: officeDocVisualBody,
        footer: officeDocVisualFooter,
      },
      companyLogoUrl: String(
        resolveCompanyLogoForTemplate(activeDocCompanyProfile, Boolean(officeDocumentTemplateUseProjectLogoBw)) || ''
      ),
      projectLogoUrl: String(
        (officeDocumentTemplateUseProjectLogoBw && String(activeDocProject?.logoBwUrl || '').trim()) ||
          activeDocProject?.logoUrl ||
          ''
      ),
      companyAddress: String(activeDocCompanyProfile?.address || ''),
      companyMobile: String(officeDocPreviewTokenValues['{{company_mobile}}'] || ''),
      companyEmail: String(activeDocCompanyProfile?.email || ''),
      companyFontFamily: String(activeDocCompanyProfile?.fontFamily || ''),
      companyFontUrl: String(activeDocCompanyProfile?.fontUrl || ''),
      companyFontFormat: String(activeDocCompanyProfile?.fontFormat || ''),
      gujaratiFontFamily: String((activeDocCompanyProfile as any)?.gujaratiFontFamily || ''),
      gujaratiFontUrl: String((activeDocCompanyProfile as any)?.gujaratiFontUrl || ''),
      gujaratiFontFormat: String((activeDocCompanyProfile as any)?.gujaratiFontFormat || ''),
      paperSize: officeDocPaperSize,
      fileName: `${makeOfficeDocumentTemplateId(
        officeDocumentTemplateName || 'template',
        officeDocumentTemplateVersion || 1
      )}-sample`,
    });
  };

  const blobFromRenderTemplateResult = (resultData: unknown): {
    blob: Blob;
    fileName: string;
    mimeType: string;
  } => {
    const data = (resultData || {}) as {
      pdfBytes?: number[] | Record<string, number>;
      pdfBase64?: string | { type?: string; data?: number[] };
      mimeType?: string;
      fileName?: string;
    };
    const mimeType = data.mimeType || 'application/pdf';
    const fileName = data.fileName || 'template-sample.pdf';
    const rawBytes = Array.isArray(data.pdfBytes)
      ? data.pdfBytes
      : data.pdfBytes && typeof data.pdfBytes === 'object'
      ? Object.keys(data.pdfBytes)
          .filter((key) => /^\d+$/.test(key))
          .sort((a, b) => Number(a) - Number(b))
          .map((key) => Number((data.pdfBytes as Record<string, number>)[key] ?? 0))
      : null;
    if (rawBytes && rawBytes.length > 0) {
      return {
        blob: new Blob([new Uint8Array(rawBytes)], { type: mimeType }),
        fileName,
        mimeType,
      };
    }
    const rawBase64 =
      typeof data.pdfBase64 === 'string'
        ? data.pdfBase64
        : data.pdfBase64?.type === 'Buffer' && Array.isArray(data.pdfBase64?.data)
        ? (() => {
            const source = data.pdfBase64?.data || [];
            let binaryBuffer = '';
            const chunkSize = 0x8000;
            for (let i = 0; i < source.length; i += chunkSize) {
              const chunk = source.slice(i, i + chunkSize);
              binaryBuffer += String.fromCharCode(...chunk);
            }
            return btoa(binaryBuffer);
          })()
        : '';
    if (!rawBase64) throw new Error('PDF response was empty.');
    const normalizedBase64 = String(rawBase64)
      .replace(/^data:application\/pdf;base64,/i, '')
      .replace(/\s+/g, '')
      .replace(/-/g, '+')
      .replace(/_/g, '/');
    const paddedBase64 = normalizedBase64.padEnd(
      normalizedBase64.length + ((4 - (normalizedBase64.length % 4)) % 4),
      '='
    );
    let binary = '';
    try {
      binary = atob(paddedBase64);
    } catch {
      throw new Error('Invalid PDF payload received from server.');
    }
    const bytes = new Uint8Array(binary.length);
    for (let index = 0; index < binary.length; index += 1) {
      bytes[index] = binary.charCodeAt(index);
    }
    return {
      blob: new Blob([bytes], { type: mimeType }),
      fileName,
      mimeType,
    };
  };

  const generateOfficeTemplatePdfPreview = async () => {
    if (officeDocPdfPreviewLoading) return;
    setOfficeDocPdfPreviewLoading(true);
    setOfficeDocPdfPreviewError(null);
    try {
      const functions = getFunctions();
      const renderTemplatePdf = httpsCallable(functions, 'renderTemplatePdf');
      const payload = buildOfficeTemplateSamplePdfPayload();
      const result = await renderTemplatePdf(payload);
      const { blob } = blobFromRenderTemplateResult(result.data);
      const url = URL.createObjectURL(blob);
      setOfficeDocPdfPreviewUrl((prev) => {
        if (prev) URL.revokeObjectURL(prev);
        return url;
      });
    } catch (error: any) {
      setOfficeDocPdfPreviewError(error?.message || 'Failed to generate PDF preview.');
    } finally {
      setOfficeDocPdfPreviewLoading(false);
    }
  };

  const downloadOfficeTemplateSample = async () => {
    if (officeDocSampleDownloading) return;

    setOfficeDocSampleDownloading(true);
    setOfficeDocumentTemplateSaveError(null);
    setOfficeDocumentTemplateSaveSuccess(null);
    try {
      const functions = getFunctions();
      const renderTemplatePdf = httpsCallable(functions, 'renderTemplatePdf');
      const payload = buildOfficeTemplateSamplePdfPayload();
      const result = await renderTemplatePdf(payload);
      const { blob, fileName } = blobFromRenderTemplateResult(result.data);
      const url = URL.createObjectURL(blob);
      const anchor = document.createElement('a');
      anchor.href = url;
      anchor.download = fileName;
      document.body.appendChild(anchor);
      anchor.click();
      anchor.remove();
      URL.revokeObjectURL(url);
      setOfficeDocumentTemplateSaveSuccess('Sample PDF downloaded.');
    } catch (error: any) {
      setOfficeDocumentTemplateSaveError(error?.message || 'Failed to generate sample PDF.');
    } finally {
      setOfficeDocSampleDownloading(false);
    }
  };

  const reducePdfFile = useCallback(async () => {
    setPdfReduceError(null);
    setPdfReduceSuccess(null);
    const sourceFile = pdfReduceSourceFile;
    if (!sourceFile) {
      setPdfReduceError('Select a PDF file first.');
      return;
    }
    const maxMb = Number(String(pdfReduceMaxMb || '').trim());
    if (!Number.isFinite(maxMb) || maxMb <= 0) {
      setPdfReduceError('Enter valid max MB.');
      return;
    }

    setPdfReduceBusy(true);
    try {
      const sourceBuffer = await sourceFile.arrayBuffer();
      const targetBytes = Math.floor(maxMb * 1024 * 1024);
      if (sourceBuffer.byteLength <= targetBytes) {
        const passthroughUrl = URL.createObjectURL(new Blob([sourceBuffer], { type: 'application/pdf' }));
        const passthroughLink = document.createElement('a');
        const baseName = String(sourceFile.name || 'document').replace(/\.pdf$/i, '');
        passthroughLink.href = passthroughUrl;
        passthroughLink.download = `${baseName}_reduced.pdf`;
        document.body.appendChild(passthroughLink);
        passthroughLink.click();
        passthroughLink.remove();
        URL.revokeObjectURL(passthroughUrl);
        setPdfReduceSuccess('File is already within target size. Downloaded copy.');
        return;
      }

      const formatBytes = (bytes: number): string => {
        const value = Number(bytes || 0);
        if (!Number.isFinite(value) || value <= 0) return '0 B';
        if (value < 1024) return `${Math.round(value)} B`;
        if (value < 1024 * 1024) return `${(value / 1024).toFixed(1)} KB`;
        return `${(value / (1024 * 1024)).toFixed(2)} MB`;
      };
      const result = await optimizePdfToMaxBytes({
        file: sourceFile,
        maxBytes: targetBytes,
        label: 'PDF',
        formatBytes,
      });

      const reducedUrl = URL.createObjectURL(result.file);
      const reducedLink = document.createElement('a');
      const baseName = String(sourceFile.name || 'document').replace(/\.pdf$/i, '');
      reducedLink.href = reducedUrl;
      reducedLink.download = `${baseName}_reduced.pdf`;
      document.body.appendChild(reducedLink);
      reducedLink.click();
      reducedLink.remove();
      URL.revokeObjectURL(reducedUrl);

      const sizeMb = (Number(result.file.size || 0) / (1024 * 1024)).toFixed(2);
      if (Number(result.file.size || 0) <= targetBytes) {
        setPdfReduceSuccess(`Reduced successfully. Output size: ${sizeMb} MB.`);
      } else {
        setPdfReduceSuccess(
          result.notice && /did not reduce/i.test(result.notice)
            ? result.notice
            : `Best possible reduced output generated (${sizeMb} MB).`
        );
      }
    } catch (err: any) {
      setPdfReduceError(err?.message || 'Failed to reduce PDF.');
    } finally {
      setPdfReduceBusy(false);
    }
  }, [pdfReduceMaxMb, pdfReduceSourceFile]);

  useEffect(() => {
    if (activeTab !== 1 || projectSettingsSection !== 'googleContacts' || !isTopAdmin) return;
    setGoogleContactsSyncConfigLoading(true);
    setGoogleContactsSyncConfigError(null);
    const unsub = onSnapshot(
      doc(db, 'googleContactSyncConfig', 'default'),
      (snap) => {
        const data = (snap.data() || {}) as GoogleContactsSyncConfig;
        setGoogleContactsSyncConfig({
          enabled: Boolean(data.enabled),
          sharedAccountEmail: String(data.sharedAccountEmail || ''),
          sampleSize: Number(data.sampleSize || GOOGLE_CONTACT_SYNC_DEFAULT_SAMPLE_SIZE),
          connected: Boolean(data.connected),
          connectedEmail: String(data.connectedEmail || ''),
          connectedScopes: Array.isArray(data.connectedScopes) ? data.connectedScopes : [],
          lastAuthAt: data.lastAuthAt,
          lastSyncAt: data.lastSyncAt,
          authStatus: String(data.authStatus || ''),
          updatedAt: data.updatedAt,
          updatedByEmail: data.updatedByEmail || null,
        });
        setGoogleContactsSyncConfigLoading(false);
      },
      (err) => {
        setGoogleContactsSyncConfigError(err?.message || 'Failed to load Google Contacts sync settings.');
        setGoogleContactsSyncConfigLoading(false);
      }
    );
    return () => unsub();
  }, [activeTab, db, isTopAdmin, projectSettingsSection]);

  useEffect(() => {
    if (activeTab !== 1 || projectSettingsSection !== 'googleContacts' || !isTopAdmin) return;
    setGoogleContactsSyncRunsLoading(true);
    setGoogleContactsSyncRunsError(null);
    const unsub = onSnapshot(
      query(collection(db, 'googleContactSyncRuns'), orderBy('createdAt', 'desc'), limit(10)),
      (snap) => {
        const rows: GoogleContactsSyncRun[] = snap.docs.map((docSnap) => ({
          id: docSnap.id,
          ...(docSnap.data() as Omit<GoogleContactsSyncRun, 'id'>),
        }));
        setGoogleContactsSyncRuns(rows);
        setGoogleContactsSyncRunsLoading(false);
      },
      (err) => {
        setGoogleContactsSyncRunsError(err?.message || 'Failed to load Google Contacts sync runs.');
        setGoogleContactsSyncRunsLoading(false);
      }
    );
    return () => unsub();
  }, [activeTab, db, isTopAdmin, projectSettingsSection]);

  const saveGoogleContactsSyncConfig = useCallback(async () => {
    if (!isTopAdmin) return;
    setGoogleContactsSyncConfigSaving(true);
    setGoogleContactsSyncConfigError(null);
    setGoogleContactsSyncConfigSuccess(null);
    try {
      await setDoc(
        doc(db, 'googleContactSyncConfig', 'default'),
        {
          enabled: Boolean(googleContactsSyncConfig.enabled),
          sharedAccountEmail: String(googleContactsSyncConfig.sharedAccountEmail || '').trim(),
          sampleSize: Math.max(
            10,
            Math.min(
              250,
              Number(googleContactsSyncConfig.sampleSize || GOOGLE_CONTACT_SYNC_DEFAULT_SAMPLE_SIZE)
            )
          ),
          updatedAt: serverTimestamp(),
          updatedByUid: currentUser?.uid || null,
          updatedByEmail: currentUser?.email || null,
        },
        { merge: true }
      );
      setGoogleContactsSyncConfigSuccess('Google Contacts sync settings saved.');
    } catch (err) {
      setGoogleContactsSyncConfigError(
        errorMessage(err, 'Failed to save Google Contacts sync settings.')
      );
    } finally {
      setGoogleContactsSyncConfigSaving(false);
    }
  }, [currentUser?.email, currentUser?.uid, db, googleContactsSyncConfig, isTopAdmin]);

  const runGoogleContactsPreview = useCallback(async (mode: GoogleContactsSyncMode) => {
    if (!isTopAdmin) return;
    setGoogleContactsSyncRunLoading(mode);
    setGoogleContactsSyncRunsError(null);
    setGoogleContactsSyncRunNotice(null);
    try {
      const functions = getFunctions();
      const callable = httpsCallable(functions, 'googleContactsRunPreview');
      const result = (await callable({
        mode,
        previewLimit:
          mode === 'sample'
            ? Number(googleContactsSyncConfig.sampleSize || GOOGLE_CONTACT_SYNC_DEFAULT_SAMPLE_SIZE)
            : 100,
      })) as { data?: { notice?: unknown } };
      setGoogleContactsSyncRunNotice(
        String(result?.data?.notice || 'Google Contacts preview generated.')
      );
    } catch (err) {
      setGoogleContactsSyncRunsError(
        errorMessage(err, 'Failed to generate Google Contacts preview.')
      );
    } finally {
      setGoogleContactsSyncRunLoading(null);
    }
  }, [googleContactsSyncConfig.sampleSize, isTopAdmin]);

  const beginGoogleContactsAuth = useCallback(async () => {
    if (!isTopAdmin) return;
    setGoogleContactsConnectLoading(true);
    setGoogleContactsSyncRunsError(null);
    try {
      const functions = getFunctions();
      const callable = httpsCallable(functions, 'googleContactsBeginAuth');
      const result = (await callable({
        returnUrl:
          typeof window !== 'undefined'
            ? `${window.location.origin}/settings`
            : '',
      })) as { data?: { authUrl?: unknown } };
      const authUrl = String(result.data?.authUrl || '').trim();
      if (!authUrl) throw new Error('Google auth URL was not returned.');
      if (typeof window !== 'undefined') {
        window.open(authUrl, '_blank', 'noopener,noreferrer');
      }
      setGoogleContactsSyncRunNotice(
        'Google auth window opened. Complete the login in the new tab, then return here.'
      );
    } catch (err) {
      setGoogleContactsSyncRunsError(
        errorMessage(err, 'Failed to start Google Contacts connection.')
      );
    } finally {
      setGoogleContactsConnectLoading(false);
    }
  }, [isTopAdmin]);

  const disconnectGoogleContacts = useCallback(async () => {
    if (!isTopAdmin) return;
    setGoogleContactsDisconnectLoading(true);
    setGoogleContactsSyncRunsError(null);
    try {
      const functions = getFunctions();
      const callable = httpsCallable(functions, 'googleContactsDisconnect');
      await callable({});
      setGoogleContactsSyncRunNotice('Google Contacts account disconnected.');
    } catch (err) {
      setGoogleContactsSyncRunsError(
        errorMessage(err, 'Failed to disconnect Google Contacts.')
      );
    } finally {
      setGoogleContactsDisconnectLoading(false);
    }
  }, [isTopAdmin]);

  const runGoogleContactsSync = useCallback(async (mode: 'sample' | 'full') => {
    if (!isTopAdmin) return;
    setGoogleContactsSyncLoading(mode);
    setGoogleContactsSyncRunsError(null);
    setGoogleContactsSyncRunNotice(null);
    try {
      const functions = getFunctions();
      const callable = httpsCallable(functions, 'googleContactsRunSync');
      const result = (await callable({ mode })) as {
        data?: {
          status?: unknown;
          syncedRows?: unknown;
          pendingRows?: unknown;
          created?: unknown;
          updated?: unknown;
          duplicateMatches?: unknown;
        };
      };
      if (mode === 'full') {
        setGoogleContactsSyncRunNotice(
          [
            `Backfill batch ${String(result.data?.status || 'running')}.`,
            `Processed: ${Number(result.data?.syncedRows || 0)}`,
            `Remaining: ${Number(result.data?.pendingRows || 0)}`,
            `Created: ${Number(result.data?.created || 0)}`,
            `Updated: ${Number(result.data?.updated || 0)}`,
          ].join(' ')
        );
      } else {
        setGoogleContactsSyncRunNotice(
          [
            `Sync ${String(result.data?.status || 'completed')}.`,
            `Rows: ${Number(result.data?.syncedRows || 0)}`,
            `Created: ${Number(result.data?.created || 0)}`,
            `Updated: ${Number(result.data?.updated || 0)}`,
            `Duplicate matches: ${Number(result.data?.duplicateMatches || 0)}`,
          ].join(' ')
        );
      }
    } catch (err) {
      setGoogleContactsSyncRunsError(
        errorMessage(err, 'Failed to run Google Contacts sync.')
      );
    } finally {
      setGoogleContactsSyncLoading(null);
    }
  }, [isTopAdmin]);

  if (!canPage('settings')) {
    return <Typography variant="h5">You do not have permission to view this page.</Typography>;
  }

  return (
    <>
      {!hideTitle ? (
        <Typography variant="h4" gutterBottom>
          Settings
        </Typography>
      ) : null}
      <Paper elevation={plain ? 0 : 1} sx={plain ? { p: 0, bgcolor: 'transparent', boxShadow: 'none' } : { p: 0 }}>
        {!hideTabs && (
          <Tabs
            value={tab}
            onChange={(_, v) => {
              if (typeof forceTab === 'number') return;
              setTab(v);
            }}
            variant="scrollable"
            scrollButtons="auto"
          >
            <Tab label="User Management" />
            <Tab label="Projects" />
            <Tab label="Lead Status Management" />
            <Tab label="Meta Leads" />
            <Tab label="Group Profile" />
          </Tabs>
        )}

        {/* User Management */}
        {tab === 0 && (
          <Box sx={{ p: 2 }}>
            <UserList onAddUser={() => { setAddUserTab('profile'); setError(''); setSuccess(''); setUserDrawerOpen(true); }} />
          </Box>
        )}

        {/* Projects */}
        {tab === 1 && (
          <Box sx={{ p: 2 }}>
            {!hideProjectSettingsSections ? (
              // Section tabs, styled like the new office screens (Geist type, saffron underline).
              <Box
                component="nav"
                aria-label="Settings sections"
                sx={{
                  position: 'sticky',
                  top: 0,
                  zIndex: 2,
                  bgcolor: plain ? 'background.paper' : '#fbfbf8',
                  mx: -2,
                  mt: -2,
                  mb: 2.5,
                  px: 2,
                  pt: 1.5,
                  borderBottom: '1px solid #e1e4dc',
                  display: 'flex',
                  alignItems: 'flex-end',
                  gap: 2,
                  overflowX: 'auto',
                  scrollbarWidth: 'thin',
                  fontFamily: '"Geist", ui-sans-serif, system-ui, sans-serif',
                }}
              >
                <Box component="span" sx={{ fontSize: 20, fontWeight: 600, letterSpacing: '-0.02em', color: '#1d2a26', pb: 1.5, pr: 1, flexShrink: 0 }}>Settings</Box>
                {(() => {
                  // Most used sections stay visible; the rest sit under More (the open one also shows as a tab).
                  const primary: Array<[typeof projectSettingsSection, string]> = [
                    ['projects', 'Projects'],
                    ['workflows', 'Workflows'],
                    ['officeUsers', 'Office users'],
                    ['documentTemplates', 'Documents'],
                    ['emailTemplates', 'Email'],
                    ['whatsapp', 'WhatsApp'],
                  ];
                  const more: Array<[typeof projectSettingsSection, string]> = [
                    ['companyProfiles', 'Company profile'],
                    ['bankAccounts', 'Bank accounts'],
                    ['pdfTools', 'PDF tools'],
                    ['documentConfig', 'Document config'],
                    ['emailConfigs', 'Email config'],
                    ['contactsBook', 'Contacts book'],
                    ...(isTopAdmin ? ([['googleContacts', 'Google Contacts sync']] as Array<[typeof projectSettingsSection, string]>) : []),
                  ];
                  const openExtra = more.find(([k]) => k === projectSettingsSection);
                  const tabSx = (on: boolean) => ({
                    font: 'inherit',
                    fontSize: 13.5,
                    whiteSpace: 'nowrap' as const,
                    cursor: 'pointer',
                    background: 'none',
                    border: 0,
                    px: 1.5,
                    pt: 1.25,
                    pb: 1.5,
                    color: on ? '#1d2a26' : '#56625d',
                    fontWeight: on ? 600 : 400,
                    borderBottom: on ? '2px solid #e46d2b' : '2px solid transparent',
                    '&:hover': { color: '#1d2a26' },
                    '&:focus-visible': { outline: '2px solid #e46d2b', outlineOffset: -2, borderRadius: 1 },
                  });
                  return (
                    <Box sx={{ display: 'flex', gap: 0.25, flexShrink: 0, alignItems: 'flex-end' }}>
                      {[...primary, ...(openExtra ? [openExtra] : [])].map(([key, label]) => (
                        <Box key={key} component="button" type="button" aria-current={projectSettingsSection === key ? 'page' : undefined} onClick={() => setProjectSettingsSection(key)} sx={tabSx(projectSettingsSection === key)}>
                          {label}
                        </Box>
                      ))}
                      <Box component="button" type="button" aria-haspopup="menu" aria-expanded={!!settingsMoreAnchor} onClick={(e: React.MouseEvent<HTMLElement>) => setSettingsMoreAnchor(e.currentTarget)} sx={{ ...tabSx(false), display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
                        More
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden="true"><path d="m6 9 6 6 6-6" /></svg>
                      </Box>
                      <Menu anchorEl={settingsMoreAnchor} open={!!settingsMoreAnchor} onClose={() => setSettingsMoreAnchor(null)} anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }} transformOrigin={{ vertical: 'top', horizontal: 'left' }} slotProps={{ paper: { sx: { borderRadius: 3, mt: 0.5, minWidth: 220 } } }}>
                        {more.map(([key, label]) => (
                          <MenuItem key={key} selected={projectSettingsSection === key} onClick={() => { setProjectSettingsSection(key); setSettingsMoreAnchor(null); }} sx={{ fontSize: 14 }}>
                            {label}
                          </MenuItem>
                        ))}
                      </Menu>
                    </Box>
                  );
                })()}
              </Box>
            ) : null}
            {projectSettingsSection === 'projects' ? (
            <>
            <Stack id="settings-projects" direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
              <Typography variant="h6">Projects</Typography>
              <Stack direction="row" spacing={1}>
                <Button
                  variant="outlined"
                  onClick={migrateLegacyBookingDocuments}
                  disabled={bookingDocsMigrationRunning || !canAction('settings','addProject')}
                >
                  {bookingDocsMigrationRunning ? 'Migrating…' : 'Migrate Legacy Booking Docs'}
                </Button>
                <Button
                  startIcon={<AddIcon />}
                  variant="contained"
                  onClick={() => {
                    resetProjectForm();
                    setProjectDrawerOpen(true);
                  }}
                  disabled={!canAction('settings','addProject')}
                >
                  Add Project
                </Button>
              </Stack>
            </Stack>
            {bookingDocsMigrationResult ? (
              <Alert
                severity={bookingDocsMigrationResult.toLowerCase().includes('failed') ? 'error' : 'success'}
                sx={{ mb: 2 }}
              >
                {bookingDocsMigrationResult}
              </Alert>
            ) : null}
            {projectsLoading && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                <CircularProgress size={20} />
                <Typography variant="body2">Loading projects…</Typography>
              </Box>
            )}
            {projectsError && <Alert severity="error" sx={{ mb: 2 }}>{projectsError}</Alert>}
	            <TableContainer component={Paper} variant="outlined">
	              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Name</TableCell>
                    <TableCell>Location</TableCell>
                    <TableCell>Company Profile</TableCell>
                    <TableCell>Unit Types</TableCell>
                    <TableCell>Units</TableCell>
                    <TableCell>Logo</TableCell>
                    {canAction('settings','addProject') && <TableCell align="right">Actions</TableCell>}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {projects.length === 0 && !projectsLoading && (
                    <TableRow>
                      <TableCell colSpan={canAction('settings','addProject') ? 7 : 6} align="center">
                        No projects yet.
                      </TableCell>
                    </TableRow>
                  )}
                  {projects.map((p) => (
                    <TableRow key={p.id}>
                      <TableCell>{p.name}</TableCell>
                      <TableCell>{p.location}</TableCell>
                      <TableCell>{p.companyProfileName || companyProfileNameById.get(String(p.companyProfileId || '')) || '—'}</TableCell>
                      <TableCell>
                        <Stack direction="row" gap={0.5} flexWrap="wrap">
                          {(p.unitTypes || []).map((u: string) => (
                            <Chip key={u} label={u} size="small" />
                          ))}
                        </Stack>
                      </TableCell>
                      <TableCell>
                        <Stack direction="row" alignItems="center" spacing={1}>
                          <Chip
                            size="small"
                            label={Number(p.unitsCount || 0) || 0}
                            variant="outlined"
                            sx={{ height: 22, '& .MuiChip-label': { fontWeight: 800 } }}
                          />
                          <Button
                            size="small"
                            variant="outlined"
                            startIcon={<ListAltOutlinedIcon fontSize="small" />}
                            onClick={() => {
                              setUnitsProjectId(p.id);
                              setUnitsProjectName(p.name || '');
                              setProjectUnitsCount(Number(p.unitsCount || 0) || 0);
                              setProjectUnitsLastImportedAt(p.unitsLastImportedAt || null);
                              setUnitsDialogOpen(true);
                            }}
                          >
                            View
                          </Button>
                          <Button
                            size="small"
                            variant="outlined"
                            onClick={() => openProjectGstDialog(p)}
                          >
                            %
                          </Button>
                        </Stack>
                      </TableCell>
                      <TableCell>
                        {p.logoUrl ? (
                          <img src={p.logoUrl} alt={p.name} style={{ width: 48, height: 48, objectFit: 'contain' }} />
                        ) : (
                          '—'
                        )}
                      </TableCell>
                      {canAction('settings','addProject') && (
                        <TableCell align="right">
                          <Tooltip title="Edit">
                            <IconButton size="small" onClick={() => {
                              setEditingProjectId(p.id);
                              setEditingLogoPath(p.logoPath || null);
                              setEditingLogoBwPath(p.logoBwPath || null);
                              setEditingBackgroundImagePath(p.backgroundImagePath || null);
                              setEditingDocumentImagePath(p.documentImagePath || null);
                              setProjectName(p.name || '');
                              setProjectLocation(p.location || '');
                              setProjectLatitude(String(p.latitude || ''));
                              setProjectLongitude(String(p.longitude || ''));
                              setProjectAddress(String(p.address || ''));
                              setProjectWhatsappSubtitle(String(p.whatsappSubtitle || ''));
                              setProjectWebsiteSlug(String(p.websiteSlug || ''));
                              setProjectBrochureUrl(String(p.brochureUrl || ''));
                              setProjectBackgroundImageUrl(String(p.backgroundImageUrl || ''));
                              setProjectDocumentImageUrl(String(p.documentImageUrl || ''));
                              setProjectLogoUrl(String(p.logoUrl || ''));
                              setProjectLogoBwUrl(String(p.logoBwUrl || ''));
                              setProjectAdvocateAgreementFee(
                                Number(p.advocateAgreementFee || 0)
                                  ? String(Number(p.advocateAgreementFee || 0))
                                  : ''
                              );
                              setProjectAdvocateDeedFee(
                                Number(p.advocateDeedFee || 0)
                                  ? String(Number(p.advocateDeedFee || 0))
                                  : ''
                              );
                              setProjectBucReceivedAt(String(p.bucReceivedAt || ''));
                              setProjectBucBuildings(Array.isArray(p.bucReceivedBuildingNos) ? p.bucReceivedBuildingNos.join(', ') : '');
                              setProjectSbAreaPercent(
                                Number(p.sbAreaPercent || 0)
                                  ? String(Number(p.sbAreaPercent || 0))
                                  : ''
                              );
                              setProjectCompanyProfileId(String(p.companyProfileId || ''));
                              setUnitTypes(p.unitTypes || []);
                              setLogoFile(null);
                              setProjectLogoBwFile(null);
                              setProjectBackgroundImageFile(null);
                              setProjectDocumentImageFile(null);
                              setProjectUnitsCount(Number(p.unitsCount || 0) || 0);
                              setProjectUnitsLastImportedAt(p.unitsLastImportedAt || null);
                              setProjectDrawerOpen(true);
                            }}>
                              <EditIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Delete">
                            <IconButton size="small" color="error" onClick={async () => {
                              const confirmed = window.confirm(`Delete project ${p.name}?`);
                              if (!confirmed) return;
                              try {
                                await deleteDoc(doc(db, 'projects', p.id));
                                if (p.logoPath) {
                                  try { await deleteObject(ref(storage, p.logoPath)); } catch (e) { /* ignore */ }
                                }
                                if (p.logoBwPath) {
                                  try { await deleteObject(ref(storage, p.logoBwPath)); } catch (e) { /* ignore */ }
                                }
                                if (p.backgroundImagePath) {
                                  try { await deleteObject(ref(storage, p.backgroundImagePath)); } catch (e) { /* ignore */ }
                                }
                              } catch (err) {
                                console.error('Delete project failed', err);
                                setProjectsError('Failed to delete project.');
                              }
                            }}>
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </TableCell>
                      )}
                    </TableRow>
                  ))}
	                </TableBody>
	              </Table>
	            </TableContainer>
            </>
            ) : null}

              {projectSettingsSection === 'companyProfiles' ? (
              <Box id="settings-company-profile" sx={{ mt: 3 }}>
                <Stack
                  direction="row"
                  justifyContent="space-between"
                  alignItems={{ xs: 'flex-start', sm: 'center' }}
                  flexWrap="wrap"
                  rowGap={1}
                  sx={{ mb: 1.5 }}
                >
                  <Box>
                    <Typography variant="h6">Company Profiles</Typography>
                    <Typography variant="body2" color="text.secondary">
                      Create reusable company profiles and link each project to one profile.
                    </Typography>
                  </Box>
                  <Button
                    startIcon={<AddIcon />}
                    variant="outlined"
                    size="small"
                    onClick={openNewCompanyProfile}
                    disabled={!canAction('settings', 'addProject')}
                  >
                    Add Company Profile
                  </Button>
                </Stack>

                {companyProfilesLoading ? (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                    <CircularProgress size={20} />
                    <Typography variant="body2">Loading company profiles…</Typography>
                  </Box>
                ) : null}
                {companyProfilesError ? <Alert severity="error" sx={{ mb: 2 }}>{companyProfilesError}</Alert> : null}

                <TableContainer component={Paper} variant="outlined">
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Name</TableCell>
                        <TableCell>Logo</TableCell>
                        <TableCell>Contact</TableCell>
                        <TableCell align="right">Actions</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {companyProfiles.length === 0 ? (
                        <TableRow>
                          <TableCell colSpan={4} align="center">
                            No company profiles yet.
                          </TableCell>
                        </TableRow>
                      ) : (
                        companyProfiles.map((c: any) => (
                          <TableRow key={String(c.id)} hover>
                            <TableCell sx={{ fontWeight: 800 }}>{String(c.name || c.id)}</TableCell>
                            <TableCell>
                              {c.logoUrl ? <img src={String(c.logoUrl)} alt={String(c.name || 'Company')} style={{ width: 42, height: 42, objectFit: 'contain' }} /> : '—'}
                            </TableCell>
                            <TableCell>
                              {[
                                String(c.mobile || '').trim(),
                                String(c.email || '').trim(),
                                c.fontFamily ? `Font: ${String(c.fontFamily)}` : '',
                              ].filter(Boolean).join(' • ') || '—'}
                            </TableCell>
                            <TableCell align="right">
                              <Button size="small" variant="outlined" onClick={() => openEditCompanyProfile(String(c.id))}>Edit</Button>
                              <Button size="small" color="error" sx={{ ml: 1 }} onClick={() => deleteCompanyProfile(String(c.id), String(c.name || c.id))}>Delete</Button>
                            </TableCell>
                          </TableRow>
                        ))
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Box>
              ) : null}

              {projectSettingsSection === 'bankAccounts' ? (
              <Box id="settings-bank-accounts" sx={{ mt: 3 }}>
                <Stack
                  direction="row"
                  justifyContent="space-between"
                  alignItems={{ xs: 'flex-start', sm: 'center' }}
                  flexWrap="wrap"
                  rowGap={1}
                  sx={{ mb: 1.5 }}
                >
                  <Box>
                    <Typography variant="h6">Bank Accounts</Typography>
                    <Typography variant="body2" color="text.secondary">
                      Add bank accounts once and assign them to one or more projects.
                    </Typography>
                  </Box>
                  <Button
                    startIcon={<AddIcon />}
                    variant="outlined"
                    size="small"
                    onClick={() => openNewBankDrawer(null)}
                    disabled={!canAction('settings', 'addProject')}
                  >
                    Add Bank
                  </Button>
                </Stack>

                {bankAccountsLoading ? (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                    <CircularProgress size={20} />
                    <Typography variant="body2">Loading banks…</Typography>
                  </Box>
                ) : null}
                {bankAccountsError ? <Alert severity="error" sx={{ mb: 2 }}>{bankAccountsError}</Alert> : null}

                <TableContainer component={Paper} variant="outlined">
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Label</TableCell>
                        <TableCell>Details</TableCell>
                        <TableCell>Assigned Projects</TableCell>
                        <TableCell align="right">Actions</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {bankAccountsAll.length === 0 && !bankAccountsLoading ? (
                        <TableRow>
                          <TableCell colSpan={4} align="center">
                            No banks yet.
                          </TableCell>
                        </TableRow>
                      ) : (
                        bankAccountsAll.map((b: any) => {
                          const assignedIds = Array.isArray(b.projectIds)
                            ? b.projectIds.filter(Boolean).map((x: any) => String(x))
                            : [];
                          const detail = [
                            b.bankName ? String(b.bankName) : null,
                            b.accountLast4 ? `****${String(b.accountLast4)}` : null,
                            b.ifsc ? String(b.ifsc) : null,
                          ]
                            .filter(Boolean)
                            .join(' • ');
                          const roleRaw = String(b.accountRole || '').toLowerCase();
                          const roleLabel =
                            roleRaw === 'collection'
                              ? 'Collection'
                              : roleRaw === 'operating30'
                                ? 'Operating 30%'
                                : roleRaw === 'rera70'
                                  ? 'RERA 70%'
                                  : roleRaw === 'dummy_advocate'
                                    ? 'Advocate Fee (Dummy)'
                                    : 'Other';

                          const allowCustomer = typeof b.allowCustomerReceipts === 'boolean' ? b.allowCustomerReceipts : roleRaw === 'collection';
                          const allowGst =
                            typeof b.allowGstReceipts === 'boolean'
                              ? b.allowGstReceipts
                              : roleRaw !== 'rera70' && roleRaw !== 'collection' && roleRaw !== 'dummy_advocate';
                          const allowPay = typeof b.allowPayments === 'boolean' ? b.allowPayments : roleRaw === 'operating30' || roleRaw === 'other' || !roleRaw;
                          const restricted = typeof b.restrictedOutflow === 'boolean' ? b.restrictedOutflow : roleRaw === 'rera70';

                          return (
                            <TableRow key={b.id} hover>
                              <TableCell sx={{ minWidth: 220 }}>
                                <Typography variant="body2" sx={{ fontWeight: 900 }} noWrap>
                                  {String(b.name || 'Bank')}
                                </Typography>
                                <Stack direction="row" gap={0.5} flexWrap="wrap" sx={{ mt: 0.5 }}>
                                  <Chip size="small" label={roleLabel} variant="outlined" />
                                  {allowCustomer ? <Chip size="small" label="Cust Receipt" color="success" variant="outlined" /> : null}
                                  {allowGst ? <Chip size="small" label="GST Receipt" variant="outlined" /> : null}
                                  {allowPay ? <Chip size="small" label="Payments" color="warning" variant="outlined" /> : null}
                                  {restricted ? <Chip size="small" label="Restricted" color="error" variant="outlined" /> : null}
                                </Stack>
                              </TableCell>
                              <TableCell>{detail || '—'}</TableCell>
                              <TableCell>
                                {assignedIds.length === 0 ? (
                                  <Chip size="small" label="Unassigned" variant="outlined" />
                                ) : (
                                  <Stack direction="row" gap={0.5} flexWrap="wrap">
                                    {assignedIds.slice(0, 4).map((pid: string) => (
                                      <Chip
                                        key={pid}
                                        size="small"
                                        label={projectNameById.get(pid) || pid}
                                        variant="outlined"
                                      />
                                    ))}
                                    {assignedIds.length > 4 ? (
                                      <Chip size="small" label={`+${assignedIds.length - 4}`} variant="outlined" />
                                    ) : null}
                                  </Stack>
                                )}
                              </TableCell>
                              <TableCell align="right">
                                <Button size="small" variant="outlined" onClick={() => openEditBankDrawer(String(b.id))}>
                                  Edit / Assign
                                </Button>
                              </TableCell>
                            </TableRow>
                          );
                        })
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Box>
              ) : null}
	
            {projectSettingsSection === 'workflows' ? (
            <>
            <WorkflowStudio
              db={db}
              currentUser={currentUser}
              projects={projects}
              canEdit={canAction('settings', 'addProject')}
              uploadDocKeyOptions={Array.from(new Set(
                officeDocumentUploadTypes
                  .map((row: any) => String(row?.key || '').trim())
                  .filter(Boolean)
              ))}
              docTemplateOptions={Array.from(new Set(
                [
                  ...DEFAULT_DOCUMENT_GROUP_KEYS,
                  ...officeDocumentTemplates
                    .map((row: any) =>
                      String(
                        row?.documentGroupKey ||
                        row?.visualModel?.documentGroupKey ||
                        row?.templateId ||
                        row?.id ||
                        ''
                      ).trim()
                    )
                    .filter(Boolean),
                ]
              ))}
              emailTemplateOptions={Array.from(new Set(
                officeEmailTemplates
                  .map((row: any) => String(row?.templateKey || row?.id || '').trim())
                  .filter(Boolean)
              ))}
            />
            <Accordion sx={{ mt: 1.5 }}>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography variant="subtitle2" sx={{ fontWeight: 800 }}>Legacy Workflow Builder (Old)</Typography>
              </AccordionSummary>
              <AccordionDetails>
                <Stack spacing={1}>
                  <Typography variant="body2" color="text.secondary">
                    Old builder is kept only for reference. Use the new Workflow Builder above for all new setup.
                  </Typography>
                  {officeWorkflowsLoading ? (
                    <Typography variant="caption" color="text.secondary">Loading legacy workflows…</Typography>
                  ) : null}
                  {officeWorkflowsError ? (
                    <Alert severity="error" sx={{ py: 0 }}>{officeWorkflowsError}</Alert>
                  ) : null}
                  {!officeWorkflowsLoading && !officeWorkflowsError && officeWorkflows.length > 0 ? (
                    <Stack spacing={0.5}>
                      {officeWorkflows.slice(0, 4).map((w) => (
                        <Stack key={w.id} direction="row" spacing={1} alignItems="center" justifyContent="space-between">
                          <Typography variant="caption" sx={{ fontWeight: 700 }} noWrap>
                            {String(w.name || w.id)} · v{Number(w.version || 1)}
                          </Typography>
                          <Button size="small" variant="text" onClick={() => openEditOfficeWorkflow(w.id)}>
                            Edit
                          </Button>
                        </Stack>
                      ))}
                    </Stack>
                  ) : null}
                  <Stack direction="row" spacing={1} flexWrap="wrap">
                    <Button
                      size="small"
                      variant="outlined"
                      onClick={() => openNewOfficeWorkflow('main')}
                      disabled={!canAction('settings', 'addProject')}
                    >
                      Open Old Main Builder
                    </Button>
                    <Button
                      size="small"
                      variant="outlined"
                      onClick={() => openNewOfficeWorkflow('loan')}
                      disabled={!canAction('settings', 'addProject')}
                    >
                      Open Old Loan Builder
                    </Button>
                  </Stack>
                </Stack>
              </AccordionDetails>
            </Accordion>

            </>
            ) : null}

            {projectSettingsSection === 'emailConfigs' ? (
            <>
            <Stack direction="row" justifyContent="space-between" alignItems={{ xs: 'flex-start', sm: 'center' }} flexWrap="wrap" rowGap={1} sx={{ mb: 1.5 }}>
              <Box>
                <Typography variant="h6">Email Configuration</Typography>
                <Typography variant="body2" color="text.secondary">
                  Configure sender email accounts (SMTP) and assign them to projects.
                </Typography>
              </Box>
              <Button
                size="small"
                variant="outlined"
                onClick={openNewOfficeEmailConfig}
                disabled={!canAction('settings', 'addProject')}
              >
                New Email Config
              </Button>
            </Stack>

            {officeEmailConfigsLoading ? (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                <CircularProgress size={20} />
                <Typography variant="body2">Loading email configurations…</Typography>
              </Box>
            ) : null}
            {officeEmailConfigsError ? <Alert severity="error" sx={{ mb: 2 }}>{officeEmailConfigsError}</Alert> : null}

            <TableContainer component={Paper} variant="outlined">
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Name</TableCell>
                    <TableCell>From</TableCell>
                    <TableCell>SMTP</TableCell>
                    <TableCell>Projects</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell align="right">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {officeEmailConfigs.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={6} align="center">No email configurations yet.</TableCell>
                    </TableRow>
                  ) : officeEmailConfigs.map((row: any) => (
                    <TableRow key={row.id} hover>
                      <TableCell sx={{ fontWeight: 800 }}>{String(row.name || row.id)}</TableCell>
                      <TableCell>{String(row.fromName || '—')} {String(row.fromEmail || '') ? `<${String(row.fromEmail)}>` : ''}</TableCell>
                      <TableCell>{String(row.smtpHost || '—')}:{String(row.smtpPort || '—')}</TableCell>
                      <TableCell>{Array.isArray(row.assignedProjectIds) ? row.assignedProjectIds.length : 0}</TableCell>
                      <TableCell>
                        <Chip
                          size="small"
                          color={row.active === false ? 'default' : 'success'}
                          label={row.active === false ? 'Inactive' : 'Active'}
                          variant="outlined"
                        />
                      </TableCell>
                      <TableCell align="right">
                        <Stack direction="row" spacing={1} justifyContent="flex-end">
                          <Button size="small" variant="outlined" onClick={() => openEditOfficeEmailConfig(String(row.id))}>Edit</Button>
                          <Button size="small" color="error" variant="outlined" onClick={() => deleteOfficeEmailConfig(String(row.id), String(row.name || row.id))}>Delete</Button>
                        </Stack>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>

            <Drawer
              anchor="right"
              open={officeEmailConfigDrawerOpen}
              onClose={() => setOfficeEmailConfigDrawerOpen(false)}
              PaperProps={{
                sx: {
                  top: 'var(--office-appbar-height, 64px)',
                  height: 'calc(100% - var(--office-appbar-height, 64px))',
                },
              }}
            >
              <Box sx={{ width: 460, p: 3 }}>
                <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
                  <Typography variant="h6">{editingOfficeEmailConfigId ? 'Edit Email Config' : 'New Email Config'}</Typography>
                  <IconButton onClick={() => setOfficeEmailConfigDrawerOpen(false)}><CloseIcon /></IconButton>
                </Stack>
                {officeEmailConfigSaveError ? <Alert severity="error" sx={{ mb: 1 }}>{officeEmailConfigSaveError}</Alert> : null}
                <Stack spacing={1.2}>
                  <TextField label="Configuration Name" value={officeEmailConfigName} onChange={(e) => setOfficeEmailConfigName(e.target.value)} fullWidth />
                  <FormControl fullWidth size="small">
                    <InputLabel id="email-provider-label">Provider</InputLabel>
                    <Select labelId="email-provider-label" label="Provider" value={officeEmailConfigProvider} onChange={(e) => setOfficeEmailConfigProvider(String(e.target.value || 'gmail') === 'custom' ? 'custom' : 'gmail')}>
                      <MenuItem value="gmail">Gmail</MenuItem>
                      <MenuItem value="custom">Custom SMTP</MenuItem>
                    </Select>
                  </FormControl>
                  <TextField label="From Name" value={officeEmailConfigFromName} onChange={(e) => setOfficeEmailConfigFromName(e.target.value)} fullWidth />
                  <TextField label="From Email" value={officeEmailConfigFromEmail} onChange={(e) => setOfficeEmailConfigFromEmail(e.target.value)} fullWidth />
                  <TextField label="Reply-To (optional)" value={officeEmailConfigReplyTo} onChange={(e) => setOfficeEmailConfigReplyTo(e.target.value)} fullWidth />
                  <TextField label="SMTP Host" value={officeEmailConfigSmtpHost} onChange={(e) => setOfficeEmailConfigSmtpHost(e.target.value)} fullWidth />
                  <Stack direction="row" spacing={1}>
                    <TextField label="SMTP Port" value={officeEmailConfigSmtpPort} onChange={(e) => setOfficeEmailConfigSmtpPort(e.target.value)} fullWidth />
                    <FormControlLabel control={<Switch checked={officeEmailConfigSecure} onChange={(e) => setOfficeEmailConfigSecure(e.target.checked)} />} label="SSL/TLS" />
                  </Stack>
                  <TextField label="SMTP Username" value={officeEmailConfigSmtpUser} onChange={(e) => setOfficeEmailConfigSmtpUser(e.target.value)} fullWidth />
                  <TextField label="SMTP Password / App Password" type="password" value={officeEmailConfigSmtpPass} onChange={(e) => setOfficeEmailConfigSmtpPass(e.target.value)} fullWidth />
                  <FormControl fullWidth size="small">
                    <InputLabel id="email-config-projects-label">Assign Projects</InputLabel>
                    <Select
                      labelId="email-config-projects-label"
                      label="Assign Projects"
                      multiple
                      value={officeEmailConfigProjectIds}
                      onChange={(e) => setOfficeEmailConfigProjectIds(e.target.value as string[])}
                      renderValue={(selected) => (selected as string[]).map((id) => projectNameById.get(id) || id).join(', ')}
                    >
                      {(projects || []).map((p: any) => (
                        <MenuItem key={p.id} value={p.id}>
                          <Checkbox checked={officeEmailConfigProjectIds.indexOf(p.id) > -1} />
                          <ListItemText primary={p.name || p.id} />
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  <FormControlLabel control={<Switch checked={officeEmailConfigIsActive} onChange={(e) => setOfficeEmailConfigIsActive(e.target.checked)} />} label="Active" />
                  <Stack direction="row" spacing={1} justifyContent="flex-end">
                    <Button onClick={() => setOfficeEmailConfigDrawerOpen(false)}>Cancel</Button>
                    <Button variant="contained" onClick={saveOfficeEmailConfig} disabled={officeEmailConfigSaving}>
                      {officeEmailConfigSaving ? 'Saving…' : 'Save'}
                    </Button>
                  </Stack>
                </Stack>
              </Box>
            </Drawer>
            </>
            ) : null}

            {projectSettingsSection === 'emailTemplates' ? (
            <>
            <Stack direction="row" justifyContent="space-between" alignItems={{ xs: 'flex-start', sm: 'center' }} flexWrap="wrap" rowGap={1} sx={{ mb: 1.5 }}>
              <Box>
                <Typography variant="h6">Email Templates</Typography>
                <Typography variant="body2" color="text.secondary">
                  Create reusable email templates with subject, body, recipients and optional booking document attachments.
                </Typography>
              </Box>
              <Button
                size="small"
                variant="outlined"
                onClick={openNewOfficeEmailTemplate}
                disabled={!canAction('settings', 'addProject')}
              >
                New Email Template
              </Button>
            </Stack>
            {officeEmailTemplatesLoading ? (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                <CircularProgress size={20} />
                <Typography variant="body2">Loading email templates…</Typography>
              </Box>
            ) : null}
            {officeEmailTemplatesError ? <Alert severity="error" sx={{ mb: 2 }}>{officeEmailTemplatesError}</Alert> : null}
            <TableContainer component={Paper} variant="outlined">
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Name</TableCell>
                    <TableCell>Template Key</TableCell>
                    <TableCell>Type</TableCell>
                    <TableCell>Email Config</TableCell>
                    <TableCell>To</TableCell>
                    <TableCell>Attachments</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell align="right">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {officeEmailTemplates.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={8} align="center">No email templates yet.</TableCell>
                    </TableRow>
                  ) : officeEmailTemplates.map((row: any) => (
                    <TableRow key={row.id} hover>
                      <TableCell sx={{ fontWeight: 800 }}>{String(row.name || row.id)}</TableCell>
                      <TableCell>{String(row.templateKey || row.id)}</TableCell>
                      <TableCell>{String(row.templateType || 'unit')}</TableCell>
                      <TableCell>{String(officeEmailConfigs.find((cfg: any) => String(cfg.id) === String(row.emailConfigId))?.name || row.emailConfigId || '—')}</TableCell>
                      <TableCell>{String(row.toMode || 'primary_customer')}</TableCell>
                      <TableCell>{Array.isArray(row.attachmentDocKeys) ? row.attachmentDocKeys.length : 0}</TableCell>
                      <TableCell>
                        <Chip size="small" color={row.active === false ? 'default' : 'success'} label={row.active === false ? 'Inactive' : 'Active'} variant="outlined" />
                      </TableCell>
                      <TableCell align="right">
                        <Stack direction="row" spacing={1} justifyContent="flex-end">
                          <Button size="small" variant="outlined" onClick={() => openEditOfficeEmailTemplate(String(row.id))}>Edit</Button>
                          <Button size="small" color="error" variant="outlined" onClick={() => deleteOfficeEmailTemplate(String(row.id), String(row.name || row.id))}>Delete</Button>
                        </Stack>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>

            <ResizableRightDrawer
              storageKey="office-email-template-drawer-width"
              open={officeEmailTemplateDrawerOpen}
              onClose={() => setOfficeEmailTemplateDrawerOpen(false)}
              defaultWidth={920}
              minWidth={620}
              maxWidth={1500}
              PaperProps={{
                sx: {
                  top: 'var(--office-appbar-height, 64px)',
                  height: 'calc(100% - var(--office-appbar-height, 64px))',
                },
              }}
            >
              <Box sx={{ width: '100%', p: 3, height: '100%', overflow: 'auto' }}>
                <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
                  <Typography variant="h6">{editingOfficeEmailTemplateId ? 'Edit Email Template' : 'New Email Template'}</Typography>
                  <IconButton onClick={() => setOfficeEmailTemplateDrawerOpen(false)}><CloseIcon /></IconButton>
                </Stack>
                {officeEmailTemplateSaveError ? <Alert severity="error" sx={{ mb: 1 }}>{officeEmailTemplateSaveError}</Alert> : null}
                <Stack spacing={1.2}>
                  <TextField
                    label="Template Name"
                    value={officeEmailTemplateName}
                    onChange={(e) => {
                      const name = String(e.target.value || '');
                      setOfficeEmailTemplateName(name);
                      setOfficeEmailTemplateKey(normalizeEmailTemplateKey(name));
                    }}
                    fullWidth
                  />
                  <TextField
                    label="Template Key"
                    value={officeEmailTemplateKey}
                    helperText="Auto-generated from Template Name."
                    fullWidth
                    InputProps={{ readOnly: true }}
                  />
                  <FormControl fullWidth size="small">
                    <InputLabel id="email-template-type-label">Template Type</InputLabel>
                    <Select
                      labelId="email-template-type-label"
                      label="Template Type"
                      value={officeEmailTemplateType}
                      onChange={(e) => setOfficeEmailTemplateType(String(e.target.value || 'unit') === 'general' ? 'general' : 'unit')}
                    >
                      <MenuItem value="unit">Unit Related</MenuItem>
                      <MenuItem value="general">General (Non-unit)</MenuItem>
                    </Select>
                  </FormControl>
                  <FormControl fullWidth size="small">
                    <InputLabel id="email-template-config-label">Email Config</InputLabel>
                    <Select labelId="email-template-config-label" label="Email Config" value={officeEmailTemplateConfigId} onChange={(e) => setOfficeEmailTemplateConfigId(String(e.target.value || ''))}>
                      {officeEmailConfigs.map((cfg: any) => (
                        <MenuItem key={cfg.id} value={cfg.id}>{String(cfg.name || cfg.id)}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  <FormControl fullWidth size="small">
                    <InputLabel id="email-template-projects-label">Assign Projects</InputLabel>
                    <Select
                      labelId="email-template-projects-label"
                      label="Assign Projects"
                      multiple
                      value={officeEmailTemplateProjectIds}
                      onChange={(e) => setOfficeEmailTemplateProjectIds(e.target.value as string[])}
                      renderValue={(selected) => (selected as string[]).map((id) => projectNameById.get(id) || id).join(', ')}
                    >
                      {(projects || []).map((p: any) => (
                        <MenuItem key={p.id} value={p.id}>
                          <Checkbox checked={officeEmailTemplateProjectIds.indexOf(p.id) > -1} />
                          <ListItemText primary={p.name || p.id} />
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  <FormControl fullWidth size="small">
                    <InputLabel id="email-template-to-label">Recipient Mode</InputLabel>
                    <Select labelId="email-template-to-label" label="Recipient Mode" value={officeEmailTemplateToMode} onChange={(e) => setOfficeEmailTemplateToMode((String(e.target.value || 'primary_customer') as any))}>
                      <MenuItem value="primary_customer">Primary Customer</MenuItem>
                      <MenuItem value="all_customers">All Customers</MenuItem>
                      <MenuItem value="custom">Custom Emails</MenuItem>
                    </Select>
                  </FormControl>
                  {officeEmailTemplateToMode === 'custom' ? (
                    <TextField label="Custom Emails" value={officeEmailTemplateCustomTo} onChange={(e) => setOfficeEmailTemplateCustomTo(e.target.value)} helperText="Comma-separated emails" fullWidth />
                  ) : null}
                  <FormControl fullWidth size="small">
                    <InputLabel id="email-template-attachments-label">Attachment Document Keys</InputLabel>
                    <Select
                      labelId="email-template-attachments-label"
                      label="Attachment Document Keys"
                      multiple
                      value={officeEmailTemplateAttachmentDocKeys}
                      onChange={(e) => setOfficeEmailTemplateAttachmentDocKeys(e.target.value as string[])}
                      renderValue={(selected) => (selected as string[]).join(', ')}
                    >
                      {Array.from(
                        new Set(
                          [
                            ...officeDocumentTemplates.map((row: any) =>
                              String(
                                row?.documentGroupKey ||
                                row?.visualModel?.documentGroupKey ||
                                row?.templateId ||
                                row?.id ||
                                ''
                              ).trim()
                            ),
                            ...officeDocumentUploadTypes.map((row: any) =>
                              String(row?.key || row?.id || '').trim()
                            ),
                            'allotment_letter_v1',
                            'booking_form_v1',
                            'payment_details_v1',
                            'sanction_letter',
                            'upload_sanction_letter',
                          ].filter(Boolean)
                        )
                      ).map((key) => (
                        <MenuItem key={key} value={key}>
                          <Checkbox checked={officeEmailTemplateAttachmentDocKeys.indexOf(key) > -1} />
                          <ListItemText primary={key} />
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  <TextField label="Subject Template" value={officeEmailTemplateSubject} onChange={(e) => setOfficeEmailTemplateSubject(e.target.value)} helperText="Supports placeholders like {{unit_no}}, {{allottee_primary_name}}, {{project_name}}." fullWidth />
                  <Box sx={{ px: 0.5 }}>
                    <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 0.5 }}>
                      <Typography variant="caption" color="text.secondary">Body / Preview Split</Typography>
                      <Typography variant="caption" color="text.secondary">{officeEmailTemplateSplitPct}% / {100 - officeEmailTemplateSplitPct}%</Typography>
                    </Stack>
                    <Slider
                      size="small"
                      min={35}
                      max={75}
                      value={officeEmailTemplateSplitPct}
                      onChange={(_e, value) => setOfficeEmailTemplateSplitPct(Number(value))}
                    />
                  </Box>
                  <Stack
                    direction={{ xs: 'column', md: 'row' }}
                    spacing={1.2}
                    sx={{ alignItems: 'stretch' }}
                  >
                    <Box sx={{ flex: { xs: '1 1 auto', md: `0 0 ${officeEmailTemplateSplitPct}%` }, minWidth: 0 }}>
                      <TextField
                        label="Body HTML"
                        value={officeEmailTemplateBodyHtml}
                        onChange={(e) => setOfficeEmailTemplateBodyHtml(e.target.value)}
                        multiline
                        minRows={12}
                        fullWidth
                      />
                    </Box>
                    <Box sx={{ flex: { xs: '1 1 auto', md: `0 0 ${100 - officeEmailTemplateSplitPct}%` }, minWidth: 0 }}>
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                        Live Preview
                      </Typography>
                      <Box
                        sx={{
                          border: '1px solid',
                          borderColor: 'divider',
                          borderRadius: 1,
                          overflow: 'hidden',
                          height: { xs: 530, md: 715 },
                          bgcolor: '#fff',
                        }}
                      >
                        <iframe
                          title="Inline Email Preview"
                          srcDoc={officeEmailPreviewBodyHtml}
                          style={{ width: '100%', height: '100%', border: 0 }}
                        />
                      </Box>
                    </Box>
                  </Stack>
                  <FormControlLabel control={<Switch checked={officeEmailTemplateIsActive} onChange={(e) => setOfficeEmailTemplateIsActive(e.target.checked)} />} label="Active" />
                  <Stack direction="row" spacing={1} justifyContent="flex-end">
                    <Button onClick={() => setOfficeEmailTemplateDrawerOpen(false)}>Cancel</Button>
                    <Button
                      variant="outlined"
                      onClick={() => setOfficeEmailTemplatePreviewOpen(true)}
                      disabled={!String(officeEmailTemplateBodyHtml || '').trim()}
                    >
                      Preview
                    </Button>
                    <Button
                      variant="outlined"
                      onClick={openOfficeEmailTemplateTestDialog}
                      disabled={!editingOfficeEmailTemplateId || officeEmailTemplateType !== 'unit'}
                    >
                      Send Test
                    </Button>
                    <Button variant="contained" onClick={saveOfficeEmailTemplate} disabled={officeEmailTemplateSaving}>
                      {officeEmailTemplateSaving ? 'Saving…' : 'Save'}
                    </Button>
                  </Stack>
                </Stack>
              </Box>
            </ResizableRightDrawer>

            <Dialog
              open={officeEmailTemplateTestDialogOpen}
              onClose={() => {
                if (officeEmailTemplateTestSending) return;
                setOfficeEmailTemplateTestDialogOpen(false);
              }}
              fullWidth
              maxWidth="sm"
            >
              <DialogTitle>Send Test Email</DialogTitle>
              <DialogContent dividers>
                <Stack spacing={1.5} sx={{ pt: 0.5 }}>
                  {officeEmailTemplateTestError ? (
                    <Alert severity="error">{officeEmailTemplateTestError}</Alert>
                  ) : null}
                  {officeEmailTemplateTestSuccess ? (
                    <Alert severity="success">{officeEmailTemplateTestSuccess}</Alert>
                  ) : null}
                  <FormControl fullWidth size="small">
                    <InputLabel id="email-test-project-label">Project</InputLabel>
                    <Select
                      labelId="email-test-project-label"
                      label="Project"
                      value={officeEmailTemplateTestProjectId}
                      onChange={(e) => {
                        setOfficeEmailTemplateTestProjectId(String(e.target.value || ''));
                        setOfficeEmailTemplateTestUnitId('');
                      }}
                    >
                      {(projects || [])
                        .filter((p: any) => {
                          if (!Array.isArray(officeEmailTemplateProjectIds) || officeEmailTemplateProjectIds.length === 0) return true;
                          return officeEmailTemplateProjectIds.includes(String(p?.id || ''));
                        })
                        .map((p: any) => (
                          <MenuItem key={p.id} value={p.id}>{p.name || p.id}</MenuItem>
                        ))}
                    </Select>
                  </FormControl>
                  <Autocomplete
                    options={officeEmailTemplateTestUnits}
                    loading={officeEmailTemplateTestUnitsLoading}
                    value={officeEmailTemplateTestUnits.find((u: any) => String(u?.id || '') === officeEmailTemplateTestUnitId) || null}
                    onChange={(_e, value: any) => setOfficeEmailTemplateTestUnitId(String(value?.id || ''))}
                    getOptionLabel={(option: any) => {
                      const unitNo = String(option?.unitNumber || option?.id || '').trim();
                      const block = String(option?.blockType || '').trim();
                      return block ? `${unitNo} (${block})` : unitNo;
                    }}
                    isOptionEqualToValue={(option: any, value: any) => String(option?.id || '') === String(value?.id || '')}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Unit for Test"
                        placeholder="Select unit"
                        helperText="Test email will render this unit’s data and documents."
                      />
                    )}
                  />
                </Stack>
              </DialogContent>
              <DialogActions>
                <Button
                  onClick={() => setOfficeEmailTemplateTestDialogOpen(false)}
                  disabled={officeEmailTemplateTestSending}
                >
                  Cancel
                </Button>
                <Button
                  variant="contained"
                  onClick={sendOfficeEmailTemplateTest}
                  disabled={officeEmailTemplateTestSending}
                >
                  {officeEmailTemplateTestSending ? 'Sending…' : 'Send Test'}
                </Button>
              </DialogActions>
            </Dialog>

            <Dialog
              open={officeEmailTemplatePreviewOpen}
              onClose={() => setOfficeEmailTemplatePreviewOpen(false)}
              fullWidth
              maxWidth="md"
            >
              <DialogTitle>Email Preview</DialogTitle>
              <DialogContent dividers>
                <Stack spacing={1.2}>
                  <TextField
                    label="Preview Subject"
                    value={officeEmailPreviewSubject}
                    fullWidth
                    InputProps={{ readOnly: true }}
                  />
                  <Typography variant="caption" color="text.secondary">
                    Preview uses sample values (for exact data use Send Test with unit selection).
                  </Typography>
                  <Box
                    sx={{
                      border: '1px solid',
                      borderColor: 'divider',
                      borderRadius: 1,
                      overflow: 'hidden',
                      minHeight: 420,
                      bgcolor: '#fff',
                    }}
                  >
                    <iframe
                      title="Email Body Preview"
                      srcDoc={officeEmailPreviewBodyHtml}
                      style={{ width: '100%', height: '420px', border: 0 }}
                    />
                  </Box>
                </Stack>
              </DialogContent>
              <DialogActions>
                <Button onClick={() => setOfficeEmailTemplatePreviewOpen(false)}>Close</Button>
              </DialogActions>
            </Dialog>
            </>
            ) : null}

            {projectSettingsSection === 'documentTemplates' ? (
            <>
            <Stack id="settings-document-templates" direction="row" justifyContent="space-between" alignItems={{ xs: 'flex-start', sm: 'center' }} flexWrap="wrap" rowGap={1} sx={{ mb: 1.5 }}>
              <Box>
                <Typography variant="h6">Document Templates</Typography>
                <Typography variant="body2" color="text.secondary">
                  Create reusable letter templates and assign them to projects for workflow use.
                </Typography>
              </Box>
              <Button
                size="small"
                variant="outlined"
                onClick={openNewOfficeDocumentTemplate}
                disabled={!canAction('settings', 'addProject')}
              >
                New Template
              </Button>
            </Stack>

            {officeDocumentTemplatesLoading ? (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                <CircularProgress size={20} />
                <Typography variant="body2">Loading document templates…</Typography>
              </Box>
            ) : null}
            {officeDocumentTemplatesError ? <Alert severity="error" sx={{ mb: 2 }}>{officeDocumentTemplatesError}</Alert> : null}

            <TableContainer component={Paper} variant="outlined">
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Name</TableCell>
                    <TableCell>Type</TableCell>
                    <TableCell>Version</TableCell>
                    <TableCell>Assigned Projects</TableCell>
                    <TableCell>Request Types</TableCell>
                    <TableCell align="right">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {officeDocumentTemplates.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={6} align="center">
                        No document templates yet.
                      </TableCell>
                    </TableRow>
                  ) : (
                    officeDocumentTemplates.map((t) => (
                      <TableRow key={t.id} hover>
                        <TableCell sx={{ fontWeight: 800 }}>{t.name || t.id}</TableCell>
                        <TableCell>{String(t.templateId || makeOfficeDocumentTemplateId(String(t.name || ''), Number(t.version || 1)))}</TableCell>
                        <TableCell>{Number(t.version || 1) || 1}</TableCell>
                        <TableCell>{Array.isArray(t.assignedProjectIds) ? t.assignedProjectIds.length : 0}</TableCell>
                        <TableCell>{Array.isArray(t.requestTypes) ? t.requestTypes.length : 0}</TableCell>
                        <TableCell align="right">
                          <Stack direction="row" spacing={1} justifyContent="flex-end">
                            <Button size="small" variant="outlined" onClick={() => openEditOfficeDocumentTemplate(t.id)}>
                              Edit
                            </Button>
                            <Button size="small" variant="outlined" onClick={() => openAssignOfficeDocumentTemplateRequestTypes(t.id)}>
                              Assign
                            </Button>
                          </Stack>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>

            <Dialog
              open={officeDocumentTemplateAssignOpen}
              onClose={() => {
                if (officeDocumentTemplateAssignSaving) return;
                setOfficeDocumentTemplateAssignOpen(false);
              }}
              fullWidth
              maxWidth="sm"
            >
              <DialogTitle>Assign Request Types</DialogTitle>
              <DialogContent>
                <TextField
                  sx={{ mt: 1 }}
                  label="Request Types"
                  value={officeDocumentTemplateAssignDraft}
                  onChange={(e) => setOfficeDocumentTemplateAssignDraft(e.target.value)}
                  fullWidth
                  multiline
                  minRows={6}
                  helperText="Type one per line (or use commas)."
                />
              </DialogContent>
              <DialogActions>
                <Button
                  onClick={() => setOfficeDocumentTemplateAssignOpen(false)}
                  disabled={officeDocumentTemplateAssignSaving}
                >
                  Cancel
                </Button>
                <Button
                  variant="contained"
                  onClick={saveOfficeDocumentTemplateRequestTypes}
                  disabled={officeDocumentTemplateAssignSaving}
                >
                  {officeDocumentTemplateAssignSaving ? 'Saving…' : 'Save'}
                </Button>
              </DialogActions>
            </Dialog>
            </>
            ) : null}

            {projectSettingsSection === 'documentConfig' ? (
            <>
            <Stack id="settings-document-config" direction="row" justifyContent="space-between" alignItems={{ xs: 'flex-start', sm: 'center' }} flexWrap="wrap" rowGap={1} sx={{ mb: 1.5 }}>
              <Box>
                <Typography variant="h6">Document Config</Typography>
                <Typography variant="body2" color="text.secondary">
                  Configure upload document types, display names, and limits used in booking uploads.
                </Typography>
              </Box>
              <Button
                size="small"
                variant="outlined"
                onClick={openNewOfficeDocumentUploadType}
                disabled={!canAction('settings', 'addProject')}
              >
                Add Upload Type
              </Button>
            </Stack>

            {officeDocumentUploadTypesLoading ? (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                <CircularProgress size={20} />
                <Typography variant="body2">Loading document config…</Typography>
              </Box>
            ) : null}
            {officeDocumentUploadTypesError ? <Alert severity="error" sx={{ mb: 2 }}>{officeDocumentUploadTypesError}</Alert> : null}

            <TableContainer component={Paper} variant="outlined">
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Type Key</TableCell>
                    <TableCell>Display Label</TableCell>
                    <TableCell>Default Display Name</TableCell>
                    <TableCell>Show In</TableCell>
                    <TableCell>Linked Draft</TableCell>
                    <TableCell>Rules</TableCell>
                    <TableCell>Assigned Projects</TableCell>
                    <TableCell align="right">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {officeDocumentUploadTypes.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={8} align="center">
                        No upload types yet.
                      </TableCell>
                    </TableRow>
                  ) : (
                    officeDocumentUploadTypes.map((row) => (
                      <TableRow key={String(row.id)} hover>
                        <TableCell sx={{ fontWeight: 800 }}>{String(row.key || row.id || '')}</TableCell>
                        <TableCell>{String(row.label || row.key || row.id || '—')}</TableCell>
                        <TableCell>{String(row.displayNameTemplate || '—')}</TableCell>
                        <TableCell>
                          {String(row.displaySection || 'main_documents')
                            .replace(/_/g, ' ')
                            .replace(/\b\w/g, (m) => m.toUpperCase())}
                        </TableCell>
                        <TableCell>{String(row.linkedDraftTemplateId || '—')}</TableCell>
                        <TableCell>
                          <Stack direction="row" gap={0.5} flexWrap="wrap">
                            <Chip size="small" variant="outlined" label={`${Number(row.maxSizeMb || 15) || 15} MB`} />
                            <Chip size="small" variant="outlined" label={String(row.allowedExtensions || 'pdf,jpg,jpeg,png')} />
                            <Chip size="small" variant="outlined" color={row.allowMultiple === false ? 'default' : 'success'} label={row.allowMultiple === false ? 'Single' : 'Multi'} />
                          </Stack>
                        </TableCell>
                        <TableCell>{Array.isArray(row.assignedProjectIds) ? row.assignedProjectIds.length : 0}</TableCell>
                        <TableCell align="right">
                          <Stack direction="row" spacing={1} justifyContent="flex-end">
                            <Button size="small" variant="outlined" onClick={() => openEditOfficeDocumentUploadType(String(row.id))}>
                              Edit
                            </Button>
                          </Stack>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
            <Dialog
              open={officeDocumentUploadTypeDrawerOpen}
              onClose={() => {
                if (officeDocumentUploadTypeSaving) return;
                setOfficeDocumentUploadTypeDrawerOpen(false);
              }}
              fullWidth
              maxWidth="lg"
            >
              <DialogTitle>
                {editingOfficeDocumentUploadTypeId ? 'Edit Upload Type' : 'New Upload Type'}
              </DialogTitle>
              <DialogContent>
                <Stack spacing={1.5} sx={{ mt: 1 }}>
                  <Autocomplete
                    freeSolo
                    options={officeDocumentUploadTypeKeySuggestionOptions}
                    inputValue={officeDocumentUploadTypeKey}
                    onInputChange={(_event, value) => {
                      setOfficeDocumentUploadTypeKey(toUploadTypeKey(value));
                      setOfficeDocumentUploadTypeKeyTouched(String(value || '').trim().length > 0);
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Type Key"
                        helperText="Search existing unused keys or type new key."
                        fullWidth
                      />
                    )}
                  />
                  <TextField
                    label="Display Label"
                    value={officeDocumentUploadTypeLabel}
                    onChange={(e) => {
                      const nextLabel = e.target.value;
                      setOfficeDocumentUploadTypeLabel(nextLabel);
                      if (!editingOfficeDocumentUploadTypeId && !officeDocumentUploadTypeKeyTouched) {
                        setOfficeDocumentUploadTypeKey(toUploadTypeKey(nextLabel));
                      }
                    }}
                    helperText="Shown in Booking Drawer documents list."
                    fullWidth
                  />
                  <TextField
                    label="Default Display Name Template"
                    value={officeDocumentUploadTypeDisplayNameTemplate}
                    onChange={(e) => setOfficeDocumentUploadTypeDisplayNameTemplate(e.target.value)}
                    helperText="Optional, for example: {{unit_no}} - BOOKING FORM SIGNED"
                    fullWidth
                  />
                  <FormControl fullWidth>
                    <InputLabel id="doc-upload-type-display-section-label">Show In</InputLabel>
                    <Select
                      labelId="doc-upload-type-display-section-label"
                      label="Show In"
                      value={officeDocumentUploadTypeDisplaySection}
                      onChange={(e) =>
                        setOfficeDocumentUploadTypeDisplaySection(
                          String(e.target.value || 'main_documents') as
                            | 'main_documents'
                            | 'loan_documents'
                            | 'draft_documents'
                        )
                      }
                    >
                      <MenuItem value="main_documents">Documents</MenuItem>
                      <MenuItem value="loan_documents">Loan Documents</MenuItem>
                      <MenuItem value="draft_documents">Draft Documents</MenuItem>
                    </Select>
                  </FormControl>
                  <TextField
                    label="Linked Draft Template ID"
                    value={officeDocumentUploadTypeLinkedDraftTemplateId}
                    onChange={(e) => setOfficeDocumentUploadTypeLinkedDraftTemplateId(e.target.value)}
                    helperText="Optional. If this uploaded doc exists, same draft key is hidden from Draft Documents."
                    fullWidth
                  />
                  <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
                    <TextField
                      label="Allowed Extensions"
                      value={officeDocumentUploadTypeAllowedExtensions}
                      onChange={(e) => setOfficeDocumentUploadTypeAllowedExtensions(e.target.value)}
                      helperText="Comma-separated. Example: pdf,jpg,jpeg,png"
                      fullWidth
                    />
                    <TextField
                      label="Max Size (MB)"
                      type="number"
                      value={officeDocumentUploadTypeMaxSizeMb}
                      onChange={(e) => setOfficeDocumentUploadTypeMaxSizeMb(Number(e.target.value || 15))}
                      inputProps={{ min: 1, max: 1000, step: 1 }}
                      fullWidth
                    />
                  </Stack>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={officeDocumentUploadTypeAllowMultiple}
                        onChange={(e) => setOfficeDocumentUploadTypeAllowMultiple(e.target.checked)}
                      />
                    }
                    label="Allow multiple files"
                  />
                  <FormControl fullWidth>
                    <InputLabel id="doc-upload-type-projects-label">Assign Projects</InputLabel>
                    <Select
                      labelId="doc-upload-type-projects-label"
                      label="Assign Projects"
                      multiple
                      value={officeDocumentUploadTypeProjectIds}
                      onChange={(e) => setOfficeDocumentUploadTypeProjectIds((e.target.value as string[]).map((x) => String(x)))}
                      renderValue={(selected) => (
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                          {(selected as string[]).map((pid) => (
                            <Chip key={pid} size="small" label={projectNameById.get(pid) || pid} />
                          ))}
                        </Box>
                      )}
                    >
                      {projects.map((p: any) => (
                        <MenuItem key={String(p.id)} value={String(p.id)}>
                          <Checkbox checked={officeDocumentUploadTypeProjectIds.includes(String(p.id))} />
                          <ListItemText primary={String(p.name || p.id)} />
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  {officeDocumentUploadTypeSaveError ? <Alert severity="error">{officeDocumentUploadTypeSaveError}</Alert> : null}
                  {officeDocumentUploadTypeSaveSuccess ? <Alert severity="success">{officeDocumentUploadTypeSaveSuccess}</Alert> : null}
                </Stack>
              </DialogContent>
              <DialogActions>
                <Button onClick={() => setOfficeDocumentUploadTypeDrawerOpen(false)} disabled={officeDocumentUploadTypeSaving}>
                  Cancel
                </Button>
                <Button variant="contained" onClick={saveOfficeDocumentUploadType} disabled={officeDocumentUploadTypeSaving}>
                  {officeDocumentUploadTypeSaving ? 'Saving…' : 'Save'}
                </Button>
              </DialogActions>
            </Dialog>
            </>
            ) : null}

            {projectSettingsSection === 'pdfTools' ? (
            <Box id="settings-pdf-tools" sx={{ mt: 1 }}>
              <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1.5 }}>
                <Box>
                  <Typography variant="h6">PDF Tools</Typography>
                  <Typography variant="body2" color="text.secondary">
                    Upload a PDF, set max size in MB, and download reduced file.
                  </Typography>
                </Box>
              </Stack>
              <Paper variant="outlined" sx={{ p: 2, maxWidth: 760 }}>
                <Stack spacing={1.25}>
                  <Button
                    variant="outlined"
                    component="label"
                    sx={{ textTransform: 'none', width: 'fit-content' }}
                  >
                    Upload PDF
                    <input
                      hidden
                      type="file"
                      accept="application/pdf,.pdf"
                      onChange={(e) => {
                        const file = (e.target.files || [])[0] || null;
                        const input = e.target as HTMLInputElement;
                        if (input) input.value = '';
                        setPdfReduceSourceFile(file);
                        setPdfReduceError(null);
                        setPdfReduceSuccess(null);
                      }}
                    />
                  </Button>
                  <Typography variant="body2" color="text.secondary">
                    {pdfReduceSourceFile
                      ? `${pdfReduceSourceFile.name} • ${(pdfReduceSourceFile.size / (1024 * 1024)).toFixed(2)} MB`
                      : 'No file selected.'}
                  </Typography>
                  <TextField
                    label="Max Size (MB)"
                    value={pdfReduceMaxMb}
                    onChange={(e) => setPdfReduceMaxMb(e.target.value)}
                    type="number"
                    inputProps={{ min: 0.1, step: 0.1 }}
                    sx={{ maxWidth: 220 }}
                  />
                  {pdfReduceError ? <Alert severity="error">{pdfReduceError}</Alert> : null}
                  {pdfReduceSuccess ? <Alert severity="success">{pdfReduceSuccess}</Alert> : null}
                  <Box>
                    <Button
                      variant="contained"
                      onClick={reducePdfFile}
                      disabled={!pdfReduceSourceFile || pdfReduceBusy}
                    >
                      {pdfReduceBusy ? 'Reducing…' : 'Reduce & Download'}
                    </Button>
                  </Box>
                </Stack>
              </Paper>
            </Box>
            ) : null}

            {projectSettingsSection === 'contactsBook' ? (
              <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
                <Stack spacing={2}>
                  <Stack
                    direction={{ xs: 'column', sm: 'row' }}
                    justifyContent="space-between"
                    alignItems={{ xs: 'flex-start', sm: 'center' }}
                    spacing={1}
                  >
                    <Box>
                      <Typography variant="h6">Contacts Book</Typography>
                      <Typography variant="body2" color="text.secondary">
                        Store non-CRM contacts such as staff family, vendors, bankers, advocates, consultants, and friends.
                      </Typography>
                    </Box>
                    <Button variant="contained" startIcon={<AddIcon />} onClick={openNewContactsBook}>
                      Add Contact
                    </Button>
                  </Stack>
                  {contactsBookError ? <Alert severity="error">{contactsBookError}</Alert> : null}
                  {contactsBookLoading ? (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 2 }}>
                      <CircularProgress size={18} />
                      <Typography variant="body2">Loading contacts…</Typography>
                    </Box>
                  ) : null}
                  <TableContainer>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>
                            <TableSortLabel
                              active={contactsBookSortBy === 'name'}
                              direction={contactsBookSortBy === 'name' ? contactsBookSortDirection : 'asc'}
                              onClick={() => toggleContactsBookSort('name')}
                            >
                              Name
                            </TableSortLabel>
                          </TableCell>
                          <TableCell>Phone</TableCell>
                          <TableCell>
                            <TableSortLabel
                              active={contactsBookSortBy === 'type'}
                              direction={contactsBookSortBy === 'type' ? contactsBookSortDirection : 'asc'}
                              onClick={() => toggleContactsBookSort('type')}
                            >
                              Type
                            </TableSortLabel>
                          </TableCell>
                          <TableCell>
                            <TableSortLabel
                              active={contactsBookSortBy === 'dob'}
                              direction={contactsBookSortBy === 'dob' ? contactsBookSortDirection : 'asc'}
                              onClick={() => toggleContactsBookSort('dob')}
                            >
                              DOB
                            </TableSortLabel>
                          </TableCell>
                          <TableCell>
                            <TableSortLabel
                              active={contactsBookSortBy === 'anniversary'}
                              direction={contactsBookSortBy === 'anniversary' ? contactsBookSortDirection : 'asc'}
                              onClick={() => toggleContactsBookSort('anniversary')}
                            >
                              Anniversary
                            </TableSortLabel>
                          </TableCell>
                          <TableCell>
                            <TableSortLabel
                              active={contactsBookSortBy === 'status'}
                              direction={contactsBookSortBy === 'status' ? contactsBookSortDirection : 'asc'}
                              onClick={() => toggleContactsBookSort('status')}
                            >
                              Status
                            </TableSortLabel>
                          </TableCell>
                          <TableCell align="right">Actions</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {contactsBookRows.length === 0 && !contactsBookLoading ? (
                          <TableRow>
                            <TableCell colSpan={7} align="center">No contacts added.</TableCell>
                          </TableRow>
                        ) : (
                          sortedContactsBookRows.map((row: any) => (
                            <TableRow key={String(row.id)} hover>
                              <TableCell>
                                <Typography variant="body2" sx={{ fontWeight: 700 }}>
                                  {String(row.name || row.displayName || '—')}
                                </Typography>
                                {row.organization ? (
                                  <Typography variant="caption" color="text.secondary">
                                    {String(row.organization)}
                                  </Typography>
                                ) : null}
                              </TableCell>
                              <TableCell>{String(row.phone || row.phoneE164 || row.whatsappNumber || '—')}</TableCell>
                              <TableCell>{String(row.type || row.contactType || 'Other')}</TableCell>
                              <TableCell>{formatContactsBookDate(row.dateOfBirth || row.dob)}</TableCell>
                              <TableCell>{formatContactsBookDate(row.anniversaryDate)}</TableCell>
                              <TableCell>
                                <Chip
                                  size="small"
                                  label={row.active === false ? 'Inactive' : 'Active'}
                                  color={row.active === false ? 'default' : 'success'}
                                  variant="outlined"
                                />
                              </TableCell>
                              <TableCell align="right">
                                <IconButton size="small" onClick={() => openEditContactsBook(row)}>
                                  <EditIcon fontSize="small" />
                                </IconButton>
                                <IconButton size="small" color="error" onClick={() => deleteContactsBook(row)}>
                                  <DeleteIcon fontSize="small" />
                                </IconButton>
                              </TableCell>
                            </TableRow>
                          ))
                        )}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Stack>
              </Paper>
            ) : null}

            {projectSettingsSection === 'officeUsers' ? (
              <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
                <OfficeUserManagementPage embedded />
              </Paper>
            ) : null}

            {projectSettingsSection === 'googleContacts' ? (
            <Box id="settings-google-contacts-sync" sx={{ mt: 1 }}>
              <Stack
                direction={{ xs: 'column', md: 'row' }}
                justifyContent="space-between"
                alignItems={{ xs: 'flex-start', md: 'center' }}
                spacing={1.5}
                sx={{ mb: 2 }}
              >
                <Box>
                  <Typography variant="h6">Google Contacts Sync</Typography>
                  <Typography variant="body2" color="text.secondary">
                    Preview the merged CRM + Office contact list before connecting the shared Google account.
                  </Typography>
                </Box>
                <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                  <Button
                    variant="outlined"
                    color={googleContactsSyncConfig.connected ? 'success' : 'primary'}
                    onClick={beginGoogleContactsAuth}
                    disabled={googleContactsConnectLoading}
                  >
                    {googleContactsConnectLoading
                      ? 'Opening…'
                      : googleContactsSyncConfig.connected
                        ? 'Reconnect Google'
                        : 'Connect Google'}
                  </Button>
                  <Button
                    variant="outlined"
                    color="inherit"
                    onClick={disconnectGoogleContacts}
                    disabled={
                      googleContactsDisconnectLoading ||
                      !googleContactsSyncConfig.connected
                    }
                  >
                    {googleContactsDisconnectLoading ? 'Disconnecting…' : 'Disconnect'}
                  </Button>
                  <Button
                    variant="outlined"
                    onClick={() => runGoogleContactsPreview('dry-run')}
                    disabled={
                      googleContactsSyncRunLoading !== null ||
                      googleContactsSyncLoading !== null
                    }
                  >
                    {googleContactsSyncRunLoading === 'dry-run' ? 'Running…' : 'Dry Run'}
                  </Button>
                  <Button
                    variant="outlined"
                    onClick={() => runGoogleContactsPreview('sample')}
                    disabled={
                      googleContactsSyncRunLoading !== null ||
                      googleContactsSyncLoading !== null
                    }
                  >
                    {googleContactsSyncRunLoading === 'sample' ? 'Running…' : 'Sample Preview'}
                  </Button>
                  <Button
                    variant="outlined"
                    onClick={() => runGoogleContactsPreview('full')}
                    disabled={
                      googleContactsSyncRunLoading !== null ||
                      googleContactsSyncLoading !== null
                    }
                  >
                    {googleContactsSyncRunLoading === 'full' ? 'Running…' : 'Full Preview'}
                  </Button>
                  <Button
                    variant="contained"
                    onClick={() => runGoogleContactsSync('sample')}
                    disabled={
                      googleContactsSyncLoading !== null ||
                      !googleContactsSyncConfig.connected
                    }
                  >
                    {googleContactsSyncLoading === 'sample' ? 'Syncing…' : 'Sample Sync'}
                  </Button>
                  <Button
                    variant="contained"
                    color="success"
                    onClick={() => runGoogleContactsSync('full')}
                    disabled={
                      googleContactsSyncLoading !== null ||
                      !googleContactsSyncConfig.connected
                    }
                  >
                    {googleContactsSyncLoading === 'full' ? 'Processing…' : 'Run Backfill Batch'}
                  </Button>
                </Stack>
              </Stack>

              {!isTopAdmin ? (
                <Alert severity="error">Only admin users can access Google Contacts Sync.</Alert>
              ) : (
                <Stack spacing={2}>
                  <Alert severity="info">
                    Sample Sync writes a small test set. Run Backfill Batch processes the full list in resumable batches and skips rows already synced with the same data.
                  </Alert>

                  <Paper variant="outlined" sx={{ p: 2 }}>
                    <Stack spacing={2}>
                      <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                        Sync Settings
                      </Typography>
                      {googleContactsSyncConfigLoading ? (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <CircularProgress size={18} />
                          <Typography variant="body2">Loading sync settings…</Typography>
                        </Box>
                      ) : null}
                      <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5}>
                        <TextField
                          label="Shared Google Account Email"
                          value={googleContactsSyncConfig.sharedAccountEmail || ''}
                          onChange={(e) =>
                            setGoogleContactsSyncConfig((prev) => ({
                              ...prev,
                              sharedAccountEmail: e.target.value,
                            }))
                          }
                          fullWidth
                        />
                        <TextField
                          label="Sample Size"
                          type="number"
                          value={googleContactsSyncConfig.sampleSize || GOOGLE_CONTACT_SYNC_DEFAULT_SAMPLE_SIZE}
                          onChange={(e) =>
                            setGoogleContactsSyncConfig((prev) => ({
                              ...prev,
                              sampleSize: Number(e.target.value || GOOGLE_CONTACT_SYNC_DEFAULT_SAMPLE_SIZE),
                            }))
                          }
                          inputProps={{ min: 10, max: 250, step: 1 }}
                          sx={{ width: { xs: '100%', md: 180 } }}
                        />
                      </Stack>
                      <Stack
                        direction={{ xs: 'column', md: 'row' }}
                        spacing={1.5}
                        alignItems={{ xs: 'flex-start', md: 'center' }}
                      >
                        <Chip
                          color={googleContactsSyncConfig.connected ? 'success' : 'default'}
                          label={googleContactsSyncConfig.connected ? 'Connected' : 'Not Connected'}
                        />
                        {googleContactsSyncConfig.connectedEmail ? (
                          <Typography variant="body2">
                            {googleContactsSyncConfig.connectedEmail}
                          </Typography>
                        ) : null}
                        {googleContactsSyncConfig.authStatus ? (
                          <Typography variant="caption" color="text.secondary">
                            Status: {googleContactsSyncConfig.authStatus}
                          </Typography>
                        ) : null}
                      </Stack>
                      <FormControlLabel
                        control={
                          <Switch
                            checked={Boolean(googleContactsSyncConfig.enabled)}
                            onChange={(e) =>
                              setGoogleContactsSyncConfig((prev) => ({
                                ...prev,
                                enabled: e.target.checked,
                              }))
                            }
                          />
                        }
                        label="Enable sync once the Google account is connected"
                      />
                      {googleContactsSyncConfig.updatedAt ? (
                        <Typography variant="caption" color="text.secondary">
                          Last updated {formatDateTimeLabel(googleContactsSyncConfig.updatedAt)}
                          {googleContactsSyncConfig.updatedByEmail ? ` by ${googleContactsSyncConfig.updatedByEmail}` : ''}
                        </Typography>
                      ) : null}
                      {googleContactsSyncConfig.lastAuthAt ? (
                        <Typography variant="caption" color="text.secondary">
                          Last connected {formatDateTimeLabel(googleContactsSyncConfig.lastAuthAt)}
                        </Typography>
                      ) : null}
                      {googleContactsSyncConfig.lastSyncAt ? (
                        <Typography variant="caption" color="text.secondary">
                          Last sync {formatDateTimeLabel(googleContactsSyncConfig.lastSyncAt)}
                        </Typography>
                      ) : null}
                      {googleContactsSyncConfigError ? (
                        <Alert severity="error">{googleContactsSyncConfigError}</Alert>
                      ) : null}
                      {googleContactsSyncConfigSuccess ? (
                        <Alert severity="success">{googleContactsSyncConfigSuccess}</Alert>
                      ) : null}
                      <Box>
                        <Button
                          variant="contained"
                          onClick={saveGoogleContactsSyncConfig}
                          disabled={googleContactsSyncConfigSaving}
                        >
                          {googleContactsSyncConfigSaving ? 'Saving…' : 'Save Settings'}
                        </Button>
                      </Box>
                    </Stack>
                  </Paper>

                  {googleContactsSyncRunNotice ? (
                    <Alert severity="success">{googleContactsSyncRunNotice}</Alert>
                  ) : null}
                  {googleContactsSyncRunsError ? (
                    <Alert severity="error">{googleContactsSyncRunsError}</Alert>
                  ) : null}

                  <Paper variant="outlined" sx={{ p: 2 }}>
                    <Stack spacing={1.5}>
                      <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                        Recent Sync Runs
                      </Typography>
                      {googleContactsSyncRunsLoading ? (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <CircularProgress size={18} />
                          <Typography variant="body2">Loading preview runs…</Typography>
                        </Box>
                      ) : null}
                      <TableContainer component={Paper} variant="outlined">
                        <Table size="small">
                          <TableHead>
                            <TableRow>
                              <TableCell>Run</TableCell>
                              <TableCell>Created</TableCell>
                              <TableCell>Status</TableCell>
                              <TableCell align="right">Contacts</TableCell>
                              <TableCell align="right">Office</TableCell>
                              <TableCell align="right">CRM</TableCell>
                              <TableCell align="right">Closed</TableCell>
                              <TableCell align="right">Processed</TableCell>
                              <TableCell align="right">Pending</TableCell>
                              <TableCell align="right">Errors</TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {googleContactsSyncRuns.length === 0 && !googleContactsSyncRunsLoading ? (
                              <TableRow>
                                <TableCell colSpan={10} align="center">
                                  No sync runs yet.
                                </TableCell>
                              </TableRow>
                            ) : (
                              googleContactsSyncRuns.map((run) => (
                                <TableRow key={run.id} hover>
                                  <TableCell sx={{ textTransform: 'capitalize', fontWeight: 700 }}>
                                    {String(run.mode || 'dry-run').replace(/-/g, ' ')}
                                  </TableCell>
                                  <TableCell>{formatDateTimeLabel(run.createdAt)}</TableCell>
                                  <TableCell>{run.status || '—'}</TableCell>
                                  <TableCell align="right">{Number(run.summary?.contactsPlanned || 0)}</TableCell>
                                  <TableCell align="right">{Number(run.summary?.officeCustomers || 0)}</TableCell>
                                  <TableCell align="right">{Number(run.summary?.crmLeads || 0)}</TableCell>
                                  <TableCell align="right">{Number(run.summary?.closedLeadContacts || 0)}</TableCell>
                                  <TableCell align="right">
                                    {Number(run.summary?.processedRows || run.summary?.syncedRows || 0)}
                                  </TableCell>
                                  <TableCell align="right">{Number(run.summary?.pendingRows || 0)}</TableCell>
                                  <TableCell align="right">{Number(run.summary?.errors || 0)}</TableCell>
                                </TableRow>
                              ))
                            )}
                          </TableBody>
                        </Table>
                      </TableContainer>
                    </Stack>
                  </Paper>

                  <Paper variant="outlined" sx={{ p: 2 }}>
                    <Stack spacing={1.5}>
                      <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                        Latest Preview Rows
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Highest-priority merged contact rows. Customer records win over CRM leads on duplicate phone numbers.
                      </Typography>
                      <TableContainer component={Paper} variant="outlined">
                        <Table size="small">
                          <TableHead>
                            <TableRow>
                              <TableCell>Name</TableCell>
                              <TableCell>Phone</TableCell>
                              <TableCell>Source</TableCell>
                              <TableCell>Groups</TableCell>
                              <TableCell>Organization</TableCell>
                              <TableCell>Updated</TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {((googleContactsSyncRuns[0]?.previewRows || []) as GoogleContactsSyncPreviewRow[]).length === 0 ? (
                              <TableRow>
                                <TableCell colSpan={6} align="center">
                                  Run a preview to inspect merged contacts.
                                </TableCell>
                              </TableRow>
                            ) : (
                              (googleContactsSyncRuns[0]?.previewRows || []).map((row, index) => (
                                <TableRow key={`${row.phone10 || 'row'}-${index}`} hover>
                                  <TableCell>
                                    <Stack spacing={0.35}>
                                      <Typography variant="body2" sx={{ fontWeight: 700 }}>
                                        {row.displayName || '—'}
                                      </Typography>
                                      {row.note ? (
                                        <Typography
                                          variant="caption"
                                          color="text.secondary"
                                          sx={{ whiteSpace: 'pre-line' }}
                                        >
                                          {row.note}
                                        </Typography>
                                      ) : null}
                                    </Stack>
                                  </TableCell>
                                  <TableCell>{formatPhoneForPreview(String(row.phone10 || ''))}</TableCell>
                                  <TableCell sx={{ textTransform: 'capitalize' }}>
                                    {String(row.sourceType || '—').replace(/-/g, ' ')}
                                  </TableCell>
                                  <TableCell>
                                    <Stack direction="row" gap={0.5} flexWrap="wrap">
                                      {(row.groupNames || []).length > 0 ? (
                                        (row.groupNames || []).map((group) => (
                                          <Chip key={`${row.phone10 || 'row'}-${group}`} size="small" variant="outlined" label={group} />
                                        ))
                                      ) : (
                                        <Typography variant="body2">—</Typography>
                                      )}
                                    </Stack>
                                  </TableCell>
                                  <TableCell>{row.organization || '—'}</TableCell>
                                  <TableCell>{formatDateTimeLabel(row.updatedAtIso)}</TableCell>
                                </TableRow>
                              ))
                            )}
                          </TableBody>
                        </Table>
                      </TableContainer>
                    </Stack>
                  </Paper>
                </Stack>
              )}
            </Box>
            ) : null}

            {projectSettingsSection === 'whatsapp' ? (
            <>
            <Stack id="settings-whatsapp" direction="row" justifyContent="space-between" alignItems={{ xs: 'flex-start', sm: 'center' }} flexWrap="wrap" rowGap={1} sx={{ mb: 1.5 }}>
              <Box>
                <Typography variant="h6">WhatsApp</Typography>
                <Typography variant="body2" color="text.secondary">
                  Configure senders, templates, quick replies, journey automation, and test sends.
                </Typography>
              </Box>
              <Stack direction="row" spacing={1}>
                <Button size="small" variant="outlined" onClick={runWhatsappStatusCheck} disabled={whatsappStatusLoading}>
                  {whatsappStatusLoading ? 'Checking…' : 'Check Status'}
                </Button>
                {whatsappSettingsSubSection === 'templates' ? (
                <Button size="small" variant="outlined" onClick={openNewWhatsappTemplate}>
                  Add Template
                </Button>
                ) : null}
                {whatsappSettingsSubSection === 'senders' ? (
                <Button size="small" startIcon={<AddIcon />} variant="outlined" onClick={openNewWhatsappSender}>
                  Add Sender
                </Button>
                ) : null}
              </Stack>
            </Stack>

            {whatsappStatusError ? <Alert severity="error" sx={{ mb: 1.5 }}>{whatsappStatusError}</Alert> : null}
            {whatsappStatusData ? (
              <Stack direction="row" spacing={1} sx={{ mb: 1.5 }} flexWrap="wrap" useFlexGap>
                <Chip size="small" color={whatsappStatusData?.hasVerifyToken ? 'success' : 'default'} label={`Verify Token: ${whatsappStatusData?.hasVerifyToken ? 'Yes' : 'No'}`} />
                <Chip size="small" color={whatsappStatusData?.hasFallbackAccessToken ? 'success' : 'default'} label={`Fallback Token: ${whatsappStatusData?.hasFallbackAccessToken ? 'Yes' : 'No'}`} />
                <Chip size="small" color="primary" label={`Active Senders: ${Number(whatsappStatusData?.activeSenders || 0)}`} />
              </Stack>
            ) : null}

            <Paper variant="outlined" sx={{ mb: 1.5 }}>
              <Tabs
                value={whatsappSettingsSubSection}
                onChange={(_event, value) => setWhatsappSettingsSubSection(value)}
                variant="scrollable"
                allowScrollButtonsMobile
              >
                <Tab value="senders" label={`Senders (${whatsappSenders.length})`} />
                <Tab value="templates" label={`Templates (${whatsappTemplates.length})`} />
                <Tab value="quickReplies" label={`Quick Replies (${whatsappQuickReplies.length})`} />
                <Tab value="automation" label={`Journey Automation (${whatsappAutomationRules.length})`} />
                <Tab value="conversationFlows" label="Conversation Flows" />
                <Tab value="birthdayWishes" label="Birthday Wishes" />
                <Tab value="testMessage" label="Test Message" />
              </Tabs>
            </Paper>

            <Box sx={{ mb: 2 }}>
              <Paper variant="outlined" sx={{ p: 1.25, width: '100%' }}>
                {whatsappSettingsSubSection === 'senders' ? (
                <>
                <Typography variant="subtitle1" sx={{ fontWeight: 800, mb: 1 }}>Senders</Typography>
                {whatsappSendersLoading ? (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 2 }}>
                    <CircularProgress size={18} />
                    <Typography variant="body2">Loading senders…</Typography>
                  </Box>
                ) : null}
                {whatsappSendersError ? <Alert severity="error" sx={{ mb: 1 }}>{whatsappSendersError}</Alert> : null}
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Name</TableCell>
                        <TableCell>Phone Number ID</TableCell>
                        <TableCell>Status</TableCell>
                        <TableCell align="right">Actions</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {whatsappSenders.length === 0 && !whatsappSendersLoading ? (
                        <TableRow>
                          <TableCell colSpan={4} align="center">No senders configured.</TableCell>
                        </TableRow>
                      ) : (
                        whatsappSenders.map((sender: any) => (
                          <TableRow key={String(sender.id)} hover>
                            <TableCell sx={{ fontWeight: 700 }}>{String(sender.name || sender.id)}</TableCell>
                            <TableCell>{String(sender.phoneNumberId || '—')}</TableCell>
                            <TableCell>
                              <Stack direction="row" spacing={0.5}>
                                <Chip size="small" color={sender?.active === false ? 'default' : 'success'} label={sender?.active === false ? 'Inactive' : 'Active'} />
                                {sender?.isDefault ? <Chip size="small" color="primary" label="Default" /> : null}
                              </Stack>
                            </TableCell>
                            <TableCell align="right">
                              <Button size="small" variant="outlined" onClick={() => openEditWhatsappSender(String(sender.id))}>Edit</Button>
                              <Button size="small" color="error" sx={{ ml: 1 }} onClick={() => deleteWhatsappSender(String(sender.id), String(sender.name || sender.id))}>Delete</Button>
                            </TableCell>
                          </TableRow>
                        ))
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
                <Divider sx={{ my: 2 }} />
                <Stack
                  direction={{ xs: 'column', sm: 'row' }}
                  justifyContent="space-between"
                  alignItems={{ xs: 'flex-start', sm: 'center' }}
                  spacing={1}
                  sx={{ mb: 1 }}
                >
                  <Box>
                    <Typography variant="subtitle1" sx={{ fontWeight: 800 }}>
                      Recent Automation Dispatches
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Latest queued, sent, skipped, and failed journey sends.
                    </Typography>
                  </Box>
                  <Chip
                    size="small"
                    label={whatsappAutomationDispatchesLoading ? 'Loading…' : `${whatsappAutomationDispatches.length} shown`}
                  />
                </Stack>
                {whatsappAutomationDispatchesError ? (
                  <Alert severity="error" sx={{ mb: 1 }}>
                    {whatsappAutomationDispatchesError}
                  </Alert>
                ) : null}
                <TableContainer component={Paper} variant="outlined">
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Status</TableCell>
                        <TableCell>Rule</TableCell>
                        <TableCell>Trigger</TableCell>
                        <TableCell>Lead / Activity</TableCell>
                        <TableCell>Due / Result Time</TableCell>
                        <TableCell>Details</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {whatsappAutomationDispatches.length === 0 && !whatsappAutomationDispatchesLoading ? (
                        <TableRow>
                          <TableCell colSpan={6} align="center">
                            No automation dispatches found yet.
                          </TableCell>
                        </TableRow>
                      ) : (
                        whatsappAutomationDispatches.map((dispatch: any) => {
                          const status = String(dispatch.status || 'pending').trim();
                          const statusColor =
                            status === 'sent' ? 'success' :
                              status === 'failed' ? 'error' :
                                status === 'skipped' ? 'warning' :
                                  status === 'processing' ? 'info' :
                                    'default';
                          const detailText =
                            status === 'skipped' ? String(dispatch.skipReason || 'Skipped') :
                              status === 'failed' ? String(dispatch.lastError || 'Failed') :
                                status === 'sent' ? [
                                  dispatch.waMessageId ? `WA: ${String(dispatch.waMessageId)}` : '',
                                  dispatch.messageId ? `CRM: ${String(dispatch.messageId)}` : '',
                                ].filter(Boolean).join(' • ') :
                                  dispatch.postponedForDnd ? 'Postponed for DND' :
                                    '';
                          const resultTime =
                            dispatch.sentAt ||
                            dispatch.failedAt ||
                            dispatch.processingAt ||
                            dispatch.updatedAt ||
                            null;
                          return (
                            <TableRow key={String(dispatch.id)} hover>
                              <TableCell>
                                <Chip size="small" color={statusColor as any} label={status} />
                              </TableCell>
                              <TableCell>
                                <Typography sx={{ fontWeight: 700, fontSize: 13 }}>
                                  {String(dispatch.ruleName || dispatch.ruleId || '—')}
                                </Typography>
                                <Typography variant="caption" color="text.secondary">
                                  {String(dispatch.ruleId || '')}
                                </Typography>
                              </TableCell>
                              <TableCell>{String(dispatch.trigger || '—')}</TableCell>
                              <TableCell>
                                <Typography variant="body2">
                                  {String(dispatch.leadId || '—')}
                                </Typography>
                                {dispatch.activityId ? (
                                  <Typography variant="caption" color="text.secondary">
                                    {String(dispatch.activityId)}
                                  </Typography>
                                ) : null}
                              </TableCell>
                              <TableCell>
                                <Typography variant="body2">
                                  Due: {formatWhatsappTs(dispatch.dueAt)}
                                </Typography>
                                <Typography variant="caption" color="text.secondary">
                                  Result: {formatWhatsappTs(resultTime)}
                                </Typography>
                              </TableCell>
                              <TableCell>
                                <Typography variant="caption" color={status === 'failed' ? 'error' : 'text.secondary'}>
                                  {detailText || '—'}
                                </Typography>
                              </TableCell>
                            </TableRow>
                          );
                        })
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
                </>
                ) : null}
                {whatsappSettingsSubSection === 'templates' ? (
                <>
                <Typography variant="subtitle1" sx={{ fontWeight: 800, mb: 1 }}>Templates</Typography>
                {whatsappTemplatesLoading ? (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 1 }}>
                    <CircularProgress size={16} />
                    <Typography variant="body2">Loading templates…</Typography>
                  </Box>
                ) : null}
                {whatsappTemplatesError ? <Alert severity="error" sx={{ mb: 1 }}>{whatsappTemplatesError}</Alert> : null}
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Label</TableCell>
                        <TableCell>Meta Name</TableCell>
                        <TableCell>Lang</TableCell>
                        <TableCell>Vars</TableCell>
                        <TableCell>Lead Chat</TableCell>
                        <TableCell align="right">Actions</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {whatsappTemplates.length === 0 && !whatsappTemplatesLoading ? (
                        <TableRow>
                          <TableCell colSpan={6} align="center">No templates configured.</TableCell>
                        </TableRow>
                      ) : (
                        whatsappTemplates.map((template: any) => (
                          <TableRow key={String(template.id)} hover>
                            <TableCell sx={{ fontWeight: 700 }}>{String(template.label || template.name || template.id)}</TableCell>
                            <TableCell>{String(template.name || '—')}</TableCell>
                            <TableCell>{String(template.language || 'en')}</TableCell>
                            <TableCell>{Array.isArray(template.variables) ? template.variables.length : 0}</TableCell>
                            <TableCell>
                              <Chip
                                size="small"
                                color={template?.showInLeadChat ? 'success' : 'default'}
                                label={template?.showInLeadChat ? 'Shown' : 'Hidden'}
                              />
                            </TableCell>
                            <TableCell align="right">
                              <Button size="small" variant="outlined" onClick={() => openEditWhatsappTemplate(String(template.id))}>Edit</Button>
                              <Button size="small" color="error" sx={{ ml: 1 }} onClick={() => deleteWhatsappTemplate(String(template.id), String(template.label || template.name || template.id))}>Delete</Button>
                            </TableCell>
                          </TableRow>
                        ))
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
                </>
                ) : null}
                {whatsappSettingsSubSection === 'automation' ? (
                <>
                <Stack
                  direction={{ xs: 'column', sm: 'row' }}
                  justifyContent="space-between"
                  alignItems={{ xs: 'flex-start', sm: 'center' }}
                  spacing={1}
                  sx={{ mb: 1 }}
                >
                  <Box>
                    <Typography variant="subtitle1" sx={{ fontWeight: 800 }}>
                      Journey Automation
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Matching active steps are queued, delayed, and deduped per lead or event.
                    </Typography>
                  </Box>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <FormControlLabel
                      control={
                        <Switch
                          checked={whatsappAutomationEnabled}
                          onChange={(e) => saveWhatsappAutomationEnabled(e.target.checked)}
                          disabled={whatsappAutomationSaving}
                        />
                      }
                      label="Journey Active"
                    />
                    <Button
                      variant="outlined"
                      startIcon={<AddIcon />}
                      onClick={openNewWhatsappAutomationRule}
                    >
                      Add Step
                    </Button>
                  </Stack>
                </Stack>
                {whatsappAutomationError ? (
                  <Alert severity="error" sx={{ mb: 1 }}>
                    {whatsappAutomationError}
                  </Alert>
                ) : null}
                {!whatsappAutomationEnabled ? (
                  <Alert severity="warning" sx={{ mb: 1 }}>
                    WhatsApp automation is currently off. Rules will not send until you enable it.
                  </Alert>
                ) : null}
                {whatsappAutomationEnabled && whatsappAutomationProjectScope !== 'all' && whatsappAutomationProjectIds.length === 0 ? (
                  <Alert severity="info" sx={{ mb: 1 }}>
                    Select at least one journey project or switch project scope to All Projects. No automated WhatsApp messages are sent while selected projects is empty.
                  </Alert>
                ) : null}
                {whatsappAutomationRules.some((rule: any) => rule?.active !== false && String(rule?.mode || '') === 'test') ? (
                  <Alert severity="warning" sx={{ mb: 1 }}>
                    One or more steps are in Test mode. Those steps only run for {whatsappAutomationTestPhone || 'the configured test number'}.
                  </Alert>
                ) : null}
                <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.25} sx={{ mb: 1.25 }}>
                  <FormControl sx={{ minWidth: { md: 220 } }}>
                    <InputLabel id="wa-journey-project-scope-label">Project Scope</InputLabel>
                    <Select
                      labelId="wa-journey-project-scope-label"
                      value={whatsappAutomationProjectScope}
                      onChange={(e) => {
                        const next = String(e.target.value || 'selected') === 'all' ? 'all' : 'selected';
                        setWhatsappAutomationProjectScope(next);
                        saveWhatsappAutomationConfig({ projectScope: next });
                      }}
                      label="Project Scope"
                    >
                      <MenuItem value="all">All Projects</MenuItem>
                      <MenuItem value="selected">Selected Projects</MenuItem>
                    </Select>
                  </FormControl>
                  <FormControl fullWidth>
                    <InputLabel id="wa-journey-projects-label">Journey Projects</InputLabel>
                    <Select
                      labelId="wa-journey-projects-label"
                      multiple
                      value={whatsappAutomationProjectIds}
                      disabled={whatsappAutomationProjectScope === 'all'}
                      onChange={(e) => {
                        const next = (e.target.value as string[]).map((value) => String(value || '')).filter(Boolean);
                        setWhatsappAutomationProjectIds(next);
                        saveWhatsappAutomationConfig({ projectIds: next });
                      }}
                      label="Journey Projects"
                      renderValue={(selected) =>
                        whatsappAutomationProjectScope === 'all'
                          ? 'All current and future projects'
                          : (selected as string[])
                          .map((id) => projectNameById.get(String(id || '')) || id)
                          .join(', ')
                      }
                    >
                      {projects.map((project: any) => (
                        <MenuItem key={String(project.id)} value={String(project.id)}>
                          <Checkbox checked={whatsappAutomationProjectIds.includes(String(project.id))} />
                          <ListItemText primary={String(project.name || project.id)} />
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  <FormControl fullWidth>
                    <InputLabel id="wa-journey-stop-status-label">Stop Statuses</InputLabel>
                    <Select
                      labelId="wa-journey-stop-status-label"
                      multiple
                      value={whatsappAutomationStopStatusIds}
                      onChange={(e) => {
                        const next = (e.target.value as string[]).map((value) => String(value || '')).filter(Boolean);
                        setWhatsappAutomationStopStatusIds(next);
                        saveWhatsappAutomationConfig({ stopStatusIds: next });
                      }}
                      label="Stop Statuses"
                      renderValue={(selected) =>
                        (selected as string[])
                          .map((id) => statuses.find((s: any) => String(s.id) === id)?.label || id)
                          .join(', ')
                      }
                    >
                      {statuses.map((status: any) => (
                        <MenuItem key={String(status.id)} value={String(status.id)}>
                          <Checkbox checked={whatsappAutomationStopStatusIds.includes(String(status.id))} />
                          <ListItemText primary={String(status.label || status.id)} />
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  <FormControlLabel
                    sx={{ minWidth: { md: 250 } }}
                    control={
                      <Switch
                        checked={whatsappAutomationNewLeadDndBypass}
                        onChange={(e) => {
                          setWhatsappAutomationNewLeadDndBypass(e.target.checked);
                          saveWhatsappAutomationConfig({ newLeadDndBypass: e.target.checked });
                        }}
                      />
                    }
                    label="New lead bypasses DND"
                  />
                </Stack>
                <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.25} sx={{ mb: 1.25 }}>
                  <TextField
                    label="Test Phone Number"
                    placeholder="+91 98765 43210"
                    value={whatsappAutomationTestPhone}
                    onChange={(e) => setWhatsappAutomationTestPhone(e.target.value)}
                    onBlur={() => saveWhatsappAutomationConfig({ testPhone: whatsappAutomationTestPhone.trim() })}
                    helperText="Steps set to Test only run for leads matching this phone."
                    fullWidth
                  />
                  <Button
                    variant="outlined"
                    onClick={() => saveWhatsappAutomationConfig({ testPhone: whatsappAutomationTestPhone.trim() })}
                    disabled={whatsappAutomationSaving}
                    sx={{ minWidth: 120, alignSelf: { xs: 'stretch', md: 'flex-start' } }}
                  >
                    Save Test
                  </Button>
                </Stack>
                {whatsappAutomationRulesLoading ? (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 1 }}>
                    <CircularProgress size={16} />
                    <Typography variant="body2">Loading automation rules…</Typography>
                  </Box>
                ) : null}
                {whatsappAutomationRulesError ? (
                  <Alert severity="error" sx={{ mb: 1 }}>
                    {whatsappAutomationRulesError}
                  </Alert>
                ) : null}
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Rule</TableCell>
                        <TableCell>Trigger</TableCell>
                        <TableCell>Template</TableCell>
                        <TableCell>Conditions</TableCell>
                        <TableCell>Status</TableCell>
                        <TableCell align="right">Actions</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {whatsappAutomationRules.length === 0 && !whatsappAutomationRulesLoading ? (
                        <TableRow>
                          <TableCell colSpan={6} align="center">
                            No automation rules configured.
                          </TableCell>
                        </TableRow>
                      ) : (
                        whatsappAutomationRules.map((rule: any) => {
                          const template = whatsappTemplates.find(
                            (row: any) => String(row?.id || '') === String(rule?.templateId || '')
                          );
                          const conditionParts = [
                            Array.isArray(rule?.conditions?.sourceValues) && rule.conditions.sourceValues.length
                              ? `Source: ${rule.conditions.sourceValues.join(', ')}`
                              : '',
                            Array.isArray(rule?.conditions?.statusValues) && rule.conditions.statusValues.length
                              ? `Status: ${rule.conditions.statusValues.map((id: string) => statuses.find((s: any) => String(s.id) === String(id))?.label || id).join(', ')}`
                              : '',
                            Array.isArray(rule?.conditions?.appointmentTypes) && rule.conditions.appointmentTypes.length
                              ? `Appointment: ${rule.conditions.appointmentTypes.join(', ')}`
                              : '',
                            Array.isArray(rule?.conditions?.receiptTypes) && rule.conditions.receiptTypes.length
                              ? `Receipt: ${rule.conditions.receiptTypes.map((value: string) => value === 'customer' ? 'Customer Receipt' : value).join(', ')}`
                              : '',
                            String(rule?.conditions?.siteVisitState || 'any') !== 'any'
                              ? `Site visit: ${String(rule.conditions.siteVisitState) === 'yes' ? 'yes' : 'no'}`
                              : '',
                            String(rule?.conditions?.upcomingAppointmentState || 'any') !== 'any'
                              ? `Upcoming appointment: ${String(rule.conditions.upcomingAppointmentState) === 'yes' ? 'yes' : 'no'}`
                              : '',
                            Number(rule?.conditions?.cooldownAmount || 0) > 0
                              ? `Cooldown: ${Number(rule.conditions.cooldownAmount)} ${String(rule.conditions.cooldownUnit || 'hours')}`
                              : '',
                            `${Number(rule.delayAmount || 0)} ${String(rule.delayUnit || 'minutes')} ${String(rule.delayDirection || 'after')} ${String(rule.scheduleAnchor || 'event')}`,
                            String(rule.sendWindow || 'any') === 'working_hours'
                              ? 'Window: working hours'
                              : String(rule.sendWindow || 'any') === 'after_hours'
                                ? 'Window: after hours'
                                : 'Window: any time',
                            String(rule.repeatMode || 'once_per_lead') === 'once_per_event' ? 'Repeat: per event' : 'Repeat: once per lead',
                            String(rule.dndMode || 'respect') === 'bypass' ? 'DND bypass' : 'DND respected',
                          ].filter(Boolean);
                          const ruleMode = rule?.active === false ? 'inactive' :
                            String(rule?.mode || 'active') === 'test' ? 'test' :
                              'active';
                          return (
                            <TableRow key={String(rule.id)} hover>
                              <TableCell>
                                <Typography sx={{ fontWeight: 700, fontSize: 13 }}>
                                  {String(rule.name || rule.id)}
                                </Typography>
                                <Typography variant="caption" color="text.secondary">
                                  Priority {Number(rule.priority || 0)}
                                </Typography>
                              </TableCell>
                              <TableCell>{String(rule.trigger || '—')}</TableCell>
                              <TableCell>
                                {String(template?.label || template?.name || rule?.templateId || '—')}
                              </TableCell>
                              <TableCell>
                                <Typography variant="caption" color="text.secondary">
                                  {conditionParts.join(' • ') || 'Applies to all leads for this trigger'}
                                </Typography>
                              </TableCell>
                              <TableCell>
                                <FormControl size="small" sx={{ minWidth: 125 }}>
                                  <Select
                                    value={ruleMode}
                                    onChange={(e) => {
                                      const nextMode = String(e.target.value || 'inactive') as 'inactive' | 'test' | 'active';
                                      setDoc(
                                        doc(db, 'whatsappAutomationRules', String(rule.id)),
                                        {
                                          active: nextMode !== 'inactive',
                                          mode: nextMode,
                                          updatedAt: serverTimestamp(),
                                          updatedBy: String(currentUser?.email || currentUser?.uid || 'unknown'),
                                        },
                                        { merge: true }
                                      ).catch((err: any) => {
                                        setWhatsappAutomationRulesError(err?.message || 'Failed to update step mode.');
                                      });
                                    }}
                                  >
                                    <MenuItem value="inactive">Inactive</MenuItem>
                                    <MenuItem value="test">Test</MenuItem>
                                    <MenuItem value="active">Active</MenuItem>
                                  </Select>
                                </FormControl>
                              </TableCell>
                              <TableCell align="right">
                                <Button
                                  size="small"
                                  variant="outlined"
                                  onClick={() => openEditWhatsappAutomationRule(String(rule.id))}
                                >
                                  Edit
                                </Button>
                                <Button
                                  size="small"
                                  color="error"
                                  sx={{ ml: 1 }}
                                  onClick={() => deleteWhatsappAutomationRule(String(rule.id), String(rule.name || rule.id))}
                                >
                                  Delete
                                </Button>
                              </TableCell>
                            </TableRow>
                          );
                        })
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
                </>
                ) : null}
                {whatsappSettingsSubSection === 'conversationFlows' ? (
                <>
                <Stack
                  direction={{ xs: 'column', sm: 'row' }}
                  justifyContent="space-between"
                  alignItems={{ xs: 'flex-start', sm: 'center' }}
                  spacing={1}
                  sx={{ mb: 1.5 }}
                >
                  <Box>
                    <Typography variant="subtitle1" sx={{ fontWeight: 800 }}>
                      Conversation Flows
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Create multiple project-selection flows with different keywords, projects, replies, and priority.
                    </Typography>
                  </Box>
                  <Button
                    variant="outlined"
                    startIcon={<AddIcon />}
                    onClick={createWhatsappConversationFlow}
                    disabled={whatsappConversationFlowSaving}
                  >
                    New Flow
                  </Button>
                </Stack>
                <Stack spacing={1.5}>
                  <Alert severity="info" sx={{ py: 0.75 }}>
                    Active flows are matched by start keyword/button payload. If more than one matches, the lowest priority number wins.
                  </Alert>
                  <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.25} alignItems={{ xs: 'stretch', md: 'center' }}>
                    <FormControl fullWidth>
                      <InputLabel id="wa-conversation-flow-select-label">Flow</InputLabel>
                      <Select
                        labelId="wa-conversation-flow-select-label"
                        label="Flow"
                        value={selectedWhatsappConversationFlowId}
                        onChange={(e) => setSelectedWhatsappConversationFlowId(String(e.target.value || 'project_selection'))}
                      >
                        {whatsappConversationFlows.length ? (
                          whatsappConversationFlows.map((flow) => (
                            <MenuItem key={flow.id} value={flow.id}>
                              {String(flow.name || flow.id)} {flow.active === true ? '(Active)' : '(Inactive)'}
                            </MenuItem>
                          ))
                        ) : (
                          <MenuItem value="project_selection">Project Selection Flow</MenuItem>
                        )}
                      </Select>
                    </FormControl>
                    <Button
                      variant="outlined"
                      color="error"
                      onClick={deleteWhatsappConversationFlow}
                      disabled={whatsappConversationFlowSaving || selectedWhatsappConversationFlowId === 'project_selection'}
                      sx={{ whiteSpace: 'nowrap' }}
                    >
                      Delete Flow
                    </Button>
                  </Stack>
                  <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.25} alignItems={{ xs: 'stretch', md: 'center' }}>
                    <TextField
                      label="Flow Name"
                      value={whatsappConversationFlowName}
                      onChange={(e) => setWhatsappConversationFlowName(e.target.value)}
                      fullWidth
                    />
                    <TextField
                      label="Priority"
                      type="number"
                      value={whatsappConversationFlowPriority}
                      onChange={(e) => setWhatsappConversationFlowPriority(e.target.value)}
                      helperText="Lower runs first"
                      sx={{ minWidth: { md: 180 } }}
                    />
                    <FormControlLabel
                      control={
                        <Switch
                          checked={whatsappConversationFlowActive}
                          onChange={(e) => setWhatsappConversationFlowActive(e.target.checked)}
                        />
                      }
                      label="Flow Active"
                      sx={{ minWidth: { md: 160 } }}
                    />
                  </Stack>
                  <Autocomplete
                    multiple
                    options={projects}
                    value={projects.filter((project: any) => whatsappConversationFlowProjectIds.includes(String(project.id)))}
                    onChange={(_event, nextValue) => setWhatsappConversationFlowProjectIds(nextValue.map((project: any) => String(project.id || '')).filter(Boolean))}
                    getOptionLabel={(option: any) => String(option?.name || option?.title || option?.id || '')}
                    isOptionEqualToValue={(option: any, value: any) => String(option?.id || '') === String(value?.id || '')}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Projects to Show"
                        helperText="WhatsApp list messages are capped; keep this to your main active projects."
                      />
                    )}
                  />
                  <TextField
                    label="Start Keywords / Button Payloads"
                    value={whatsappConversationFlowStartKeywords}
                    onChange={(e) => setWhatsappConversationFlowStartKeywords(e.target.value)}
                    helperText="Comma separated. Example: Select Project, select_project"
                    fullWidth
                  />
                  <TextField
                    label="Project List Message"
                    value={whatsappConversationFlowProjectPrompt}
                    onChange={(e) => setWhatsappConversationFlowProjectPrompt(e.target.value)}
                    multiline
                    minRows={2}
                    fullWidth
                  />
                  <TextField
                    label="Action Menu Message"
                    value={whatsappConversationFlowActionPrompt}
                    onChange={(e) => setWhatsappConversationFlowActionPrompt(e.target.value)}
                    multiline
                    minRows={2}
                    fullWidth
                  />
                  <FormControl fullWidth>
                    <InputLabel id="wa-conversation-actions-label">Project Action Options</InputLabel>
                    <Select
                      labelId="wa-conversation-actions-label"
                      label="Project Action Options"
                      multiple
                      value={whatsappConversationFlowActionKeys}
                      onChange={(e) => setWhatsappConversationFlowActionKeys((e.target.value as string[]).map((value) => String(value || '')).filter(Boolean))}
                      renderValue={(selected) => (selected as string[])
                        .map((value) => WHATSAPP_CONVERSATION_ACTION_LABELS[value] || value)
                        .join(', ')}
                    >
                      {WHATSAPP_CONVERSATION_ACTION_OPTIONS.map((action) => (
                        <MenuItem key={action.key} value={action.key}>
                          <Checkbox checked={whatsappConversationFlowActionKeys.includes(action.key)} />
                          <ListItemText primary={action.label} />
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  <Paper variant="outlined" sx={{ p: 1.5, bgcolor: '#fafafa' }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 800, mb: 1 }}>
                      Flow Chart
                    </Typography>
                    <Stack
                      direction={{ xs: 'column', md: 'row' }}
                      spacing={1}
                      alignItems={{ xs: 'stretch', md: 'center' }}
                    >
                      {[
                        'Customer sends Select Project',
                        'CRM sends project list',
                        'Customer selects project',
                        'CRM sends action menu',
                        'CRM sends custom reply + selected buttons',
                      ].map((label, index, rows) => (
                        <React.Fragment key={label}>
                          <Box
                            sx={{
                              border: '1px solid',
                              borderColor: 'divider',
                              borderRadius: 1,
                              px: 1.5,
                              py: 1,
                              bgcolor: 'background.paper',
                              minWidth: { md: 150 },
                              flex: 1,
                            }}
                          >
                            <Typography variant="caption" color="text.secondary">
                              Step {index + 1}
                            </Typography>
                            <Typography variant="body2" sx={{ fontWeight: 700 }}>
                              {label}
                            </Typography>
                          </Box>
                          {index < rows.length - 1 ? (
                            <Typography
                              variant="body2"
                              color="text.secondary"
                              sx={{ textAlign: 'center', display: { xs: 'none', md: 'block' } }}
                            >
                              -&gt;
                            </Typography>
                          ) : null}
                        </React.Fragment>
                      ))}
                    </Stack>
                  </Paper>
                  <Box>
                    <Typography variant="subtitle2" sx={{ fontWeight: 800, mb: 0.5 }}>
                      Custom Replies Per Project
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Optional. Blank messages use the project default, like brochure PDF, location, website, or call text.
                    </Typography>
                    <Stack spacing={1} sx={{ mt: 1 }}>
                      {projects
                        .filter((project: any) => whatsappConversationFlowProjectIds.includes(String(project.id || '')))
                        .map((project: any) => {
                          const projectId = String(project.id || '');
                          const projectName = String(project.name || project.title || projectId);
                          return (
                            <Accordion key={projectId} disableGutters>
                              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                                <Box>
                                  <Typography variant="subtitle2" sx={{ fontWeight: 800 }}>
                                    {projectName}
                                  </Typography>
                                  <Typography variant="caption" color="text.secondary">
                                    Configure reply text and follow-up buttons for this project.
                                  </Typography>
                                </Box>
                              </AccordionSummary>
                              <AccordionDetails>
                                <Stack spacing={1.5}>
                                  {whatsappConversationFlowActionKeys.map((actionKey) => {
                                    const config = whatsappConversationFlowActionReplies[projectId]?.[actionKey] || {
                                      messageText: '',
                                      buttonActionKeys: [],
                                      responseType: 'default',
                                      urlButtonText: '',
                                      urlSource: 'auto',
                                      customUrl: '',
                                    };
                                    const responseType = String(config.responseType || 'default');
                                    const urlSource = String(config.urlSource || 'auto');
                                    const buttonOptions = WHATSAPP_CONVERSATION_ACTION_OPTIONS
                                      .filter((action) => action.key !== actionKey && whatsappConversationFlowActionKeys.includes(action.key));
                                    return (
                                      <Paper key={actionKey} variant="outlined" sx={{ p: 1.5 }}>
                                        <Stack spacing={1.25}>
                                          <Typography variant="subtitle2" sx={{ fontWeight: 800 }}>
                                            {WHATSAPP_CONVERSATION_ACTION_LABELS[actionKey] || actionKey}
                                          </Typography>
                                          <TextField
                                            label="Custom Reply Message"
                                            value={config.messageText}
                                            onChange={(e) => updateWhatsappConversationActionReply(projectId, actionKey, { messageText: e.target.value })}
                                            helperText="Available tokens: {{project_name}}, {{website_url}}, {{brochure_url}}, {{phone}}, {{location}}"
                                            multiline
                                            minRows={2}
                                            fullWidth
                                          />
                                          <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.25}>
                                            <FormControl fullWidth size="small">
                                              <InputLabel id={`wa-flow-response-type-${projectId}-${actionKey}`}>Response Type</InputLabel>
                                              <Select
                                                labelId={`wa-flow-response-type-${projectId}-${actionKey}`}
                                                label="Response Type"
                                                value={responseType}
                                                onChange={(e) => updateWhatsappConversationActionReply(projectId, actionKey, { responseType: String(e.target.value || 'default') })}
                                              >
                                                {WHATSAPP_CONVERSATION_RESPONSE_TYPES.map((type) => (
                                                  <MenuItem key={type.key} value={type.key}>
                                                    {type.label}
                                                  </MenuItem>
                                                ))}
                                              </Select>
                                            </FormControl>
                                            <FormControl fullWidth size="small">
                                              <InputLabel id={`wa-flow-url-source-${projectId}-${actionKey}`}>URL Source</InputLabel>
                                              <Select
                                                labelId={`wa-flow-url-source-${projectId}-${actionKey}`}
                                                label="URL Source"
                                                value={urlSource}
                                                onChange={(e) => updateWhatsappConversationActionReply(projectId, actionKey, { urlSource: String(e.target.value || 'auto') })}
                                              >
                                                {WHATSAPP_CONVERSATION_URL_SOURCES.map((source) => (
                                                  <MenuItem key={source.key} value={source.key}>
                                                    {source.label}
                                                  </MenuItem>
                                                ))}
                                              </Select>
                                            </FormControl>
                                          </Stack>
                                          {(responseType === 'url_button' || responseType === 'url_button_and_replies') ? (
                                            <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.25}>
                                              <TextField
                                                label="URL Button Text"
                                                value={config.urlButtonText || ''}
                                                onChange={(e) => updateWhatsappConversationActionReply(projectId, actionKey, { urlButtonText: e.target.value })}
                                                placeholder={
                                                  actionKey === 'brochure' ? 'Open Brochure' :
                                                    actionKey === 'location' ? 'Open Location' :
                                                      actionKey === 'website' ? 'View Details' :
                                                        'Open Link'
                                                }
                                                size="small"
                                                fullWidth
                                              />
                                              {urlSource === 'custom' ? (
                                                <TextField
                                                  label="Custom URL"
                                                  value={config.customUrl || ''}
                                                  onChange={(e) => updateWhatsappConversationActionReply(projectId, actionKey, { customUrl: e.target.value })}
                                                  placeholder="https://..."
                                                  size="small"
                                                  fullWidth
                                                />
                                              ) : null}
                                            </Stack>
                                          ) : null}
                                          <FormControl fullWidth size="small">
                                            <InputLabel id={`wa-flow-buttons-${projectId}-${actionKey}`}>Buttons After Reply</InputLabel>
                                            <Select
                                              labelId={`wa-flow-buttons-${projectId}-${actionKey}`}
                                              label="Buttons After Reply"
                                              multiple
                                              value={config.buttonActionKeys || []}
                                              onChange={(e) => updateWhatsappConversationActionReply(projectId, actionKey, {
                                                buttonActionKeys: (e.target.value as string[]).map((value) => String(value || '')).filter(Boolean).slice(0, 3),
                                              })}
                                              renderValue={(selected) => (selected as string[])
                                                .map((value) => WHATSAPP_CONVERSATION_ACTION_LABELS[value] || value)
                                                .join(', ')}
                                            >
                                              {buttonOptions.map((action) => (
                                                <MenuItem key={action.key} value={action.key}>
                                                  <Checkbox checked={(config.buttonActionKeys || []).includes(action.key)} />
                                                  <ListItemText primary={action.label} />
                                                </MenuItem>
                                              ))}
                                            </Select>
                                          </FormControl>
                                        </Stack>
                                      </Paper>
                                    );
                                  })}
                                </Stack>
                              </AccordionDetails>
                            </Accordion>
                          );
                        })}
                      {!whatsappConversationFlowProjectIds.length ? (
                        <Alert severity="info" sx={{ py: 0.75 }}>
                          Select projects above to configure project-specific replies.
                        </Alert>
                      ) : null}
                    </Stack>
                  </Box>
                  {whatsappConversationFlowError ? <Alert severity="error">{whatsappConversationFlowError}</Alert> : null}
                  {whatsappConversationFlowSuccess ? <Alert severity="success">{whatsappConversationFlowSuccess}</Alert> : null}
                  <Stack direction="row" justifyContent="flex-end">
                    <Button
                      variant="contained"
                      onClick={saveWhatsappConversationFlow}
                      disabled={whatsappConversationFlowSaving}
                    >
                      {whatsappConversationFlowSaving ? 'Saving…' : 'Save Flow'}
                    </Button>
                  </Stack>
                </Stack>
                </>
                ) : null}
                {whatsappSettingsSubSection === 'birthdayWishes' ? (
                <>
                <Stack spacing={1.5}>
                  <Stack
                    direction={{ xs: 'column', sm: 'row' }}
                    justifyContent="space-between"
                    alignItems={{ xs: 'flex-start', sm: 'center' }}
                    spacing={1}
                  >
                    <Box>
                      <Typography variant="subtitle1" sx={{ fontWeight: 800 }}>
                        Birthday Wishes
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Sends WhatsApp birthday wishes daily at 9:30 AM IST. Each person is sent only once per year.
                      </Typography>
                    </Box>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={birthdayWishesEnabled}
                          onChange={(e) => setBirthdayWishesEnabled(e.target.checked)}
                        />
                      }
                      label="Birthday Wishes Active"
                    />
                  </Stack>
                  <Alert severity="info" sx={{ py: 0.75 }}>
                    Supports booked project customers, brokers, staff users, and Contacts Book entries with DOB saved in CRM.
                  </Alert>
                  {birthdayUpcomingLoading ? (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 1 }}>
                      <CircularProgress size={16} />
                      <Typography variant="body2">Loading upcoming birthdays…</Typography>
                    </Box>
                  ) : null}
                  {birthdayUpcomingError ? <Alert severity="error">{birthdayUpcomingError}</Alert> : null}
                  <Stack spacing={1}>
                    <Accordion defaultExpanded disableGutters>
                      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                        <Box>
                          <Typography variant="subtitle2" sx={{ fontWeight: 800 }}>
                            Upcoming Customer Birthdays
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            Next 5 booked project customers with DOB saved.
                          </Typography>
                        </Box>
                      </AccordionSummary>
                      <AccordionDetails>
                        <UpcomingBirthdayTable
                          rows={birthdayUpcomingCustomers}
                          emptyText="No upcoming customer birthdays found."
                        />
                      </AccordionDetails>
                    </Accordion>
                    <Accordion disableGutters>
                      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                        <Box>
                          <Typography variant="subtitle2" sx={{ fontWeight: 800 }}>
                            Upcoming Broker Birthdays
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            Next 5 brokers or channel partners with DOB saved.
                          </Typography>
                        </Box>
                      </AccordionSummary>
                      <AccordionDetails>
                        <UpcomingBirthdayTable
                          rows={birthdayUpcomingBrokers}
                          emptyText="No upcoming broker birthdays found."
                        />
                      </AccordionDetails>
                    </Accordion>
                    <Accordion disableGutters>
                      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                        <Box>
                          <Typography variant="subtitle2" sx={{ fontWeight: 800 }}>
                            Upcoming Staff Birthdays
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            Next 5 staff users with DOB saved.
                          </Typography>
                        </Box>
                      </AccordionSummary>
                      <AccordionDetails>
                        <UpcomingBirthdayTable
                          rows={birthdayUpcomingStaff}
                          emptyText="No upcoming staff birthdays found."
                        />
                      </AccordionDetails>
                    </Accordion>
                    <Accordion disableGutters>
                      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                        <Box>
                          <Typography variant="subtitle2" sx={{ fontWeight: 800 }}>
                            Upcoming Contacts Book Birthdays
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            Next 5 Contacts Book entries with DOB saved.
                          </Typography>
                        </Box>
                      </AccordionSummary>
                      <AccordionDetails>
                        <UpcomingBirthdayTable
                          rows={birthdayUpcomingContacts}
                          emptyText="No upcoming Contacts Book birthdays found."
                        />
                      </AccordionDetails>
                    </Accordion>
                    <Accordion disableGutters>
                      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                        <Box>
                          <Typography variant="subtitle2" sx={{ fontWeight: 800 }}>
                            All Upcoming Birthdays
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            Combined next 5 across customers, brokers, staff, and Contacts Book.
                          </Typography>
                        </Box>
                      </AccordionSummary>
                      <AccordionDetails>
                        <UpcomingBirthdayTable
                          rows={birthdayUpcomingAll}
                          emptyText="No upcoming birthdays found."
                          showType
                        />
                      </AccordionDetails>
                    </Accordion>
                  </Stack>
                  <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.25}>
                    <FormControlLabel
                      sx={{ minWidth: { md: 260 } }}
                      control={
                        <Switch
                          checked={birthdayWishesIncludeProjectCustomers}
                          onChange={(e) => setBirthdayWishesIncludeProjectCustomers(e.target.checked)}
                        />
                      }
                      label="Project customers"
                    />
                    <FormControl fullWidth>
                      <InputLabel id="birthday-customer-template-label">Customer Template</InputLabel>
                      <Select
                        labelId="birthday-customer-template-label"
                        label="Customer Template"
                        value={birthdayWishesCustomerTemplateId}
                        onChange={(e) => setBirthdayWishesCustomerTemplateId(String(e.target.value || ''))}
                      >
                        <MenuItem value="">None</MenuItem>
                        {whatsappTemplates.map((template: any) => (
                          <MenuItem key={String(template.id)} value={String(template.id)}>
                            {String(template.label || template.name || template.id)}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Stack>
                  <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.25}>
                    <FormControlLabel
                      sx={{ minWidth: { md: 260 } }}
                      control={
                        <Switch
                          checked={birthdayWishesIncludeBrokers}
                          onChange={(e) => setBirthdayWishesIncludeBrokers(e.target.checked)}
                        />
                      }
                      label="Brokers / channel partners"
                    />
                    <FormControl fullWidth>
                      <InputLabel id="birthday-broker-template-label">Broker Template</InputLabel>
                      <Select
                        labelId="birthday-broker-template-label"
                        label="Broker Template"
                        value={birthdayWishesBrokerTemplateId}
                        onChange={(e) => setBirthdayWishesBrokerTemplateId(String(e.target.value || ''))}
                      >
                        <MenuItem value="">None</MenuItem>
                        {whatsappTemplates.map((template: any) => (
                          <MenuItem key={String(template.id)} value={String(template.id)}>
                            {String(template.label || template.name || template.id)}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Stack>
                  <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.25}>
                    <FormControlLabel
                      sx={{ minWidth: { md: 260 } }}
                      control={
                        <Switch
                          checked={birthdayWishesIncludeStaff}
                          onChange={(e) => setBirthdayWishesIncludeStaff(e.target.checked)}
                        />
                      }
                      label="Staff users"
                    />
                    <FormControl fullWidth>
                      <InputLabel id="birthday-staff-template-label">Staff Template</InputLabel>
                      <Select
                        labelId="birthday-staff-template-label"
                        label="Staff Template"
                        value={birthdayWishesStaffTemplateId}
                        onChange={(e) => setBirthdayWishesStaffTemplateId(String(e.target.value || ''))}
                      >
                        <MenuItem value="">None</MenuItem>
                        {whatsappTemplates.map((template: any) => (
                          <MenuItem key={String(template.id)} value={String(template.id)}>
                            {String(template.label || template.name || template.id)}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Stack>
                  <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.25}>
                    <FormControlLabel
                      sx={{ minWidth: { md: 260 } }}
                      control={
                        <Switch
                          checked={birthdayWishesIncludeContactsBook}
                          onChange={(e) => setBirthdayWishesIncludeContactsBook(e.target.checked)}
                        />
                      }
                      label="Contacts Book"
                    />
                    <FormControl fullWidth>
                      <InputLabel id="birthday-contact-template-label">Contact Template</InputLabel>
                      <Select
                        labelId="birthday-contact-template-label"
                        label="Contact Template"
                        value={birthdayWishesContactTemplateId}
                        onChange={(e) => setBirthdayWishesContactTemplateId(String(e.target.value || ''))}
                      >
                        <MenuItem value="">None</MenuItem>
                        {whatsappTemplates.map((template: any) => (
                          <MenuItem key={String(template.id)} value={String(template.id)}>
                            {String(template.label || template.name || template.id)}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Stack>
                  {birthdayWishesError ? <Alert severity="error">{birthdayWishesError}</Alert> : null}
                  {birthdayWishesSuccess ? <Alert severity="success">{birthdayWishesSuccess}</Alert> : null}
                  <Stack direction="row" justifyContent="flex-end">
                    <Button
                      variant="contained"
                      onClick={saveBirthdayWishesConfig}
                      disabled={birthdayWishesSaving}
                    >
                      {birthdayWishesSaving ? 'Saving…' : 'Save Birthday Settings'}
                    </Button>
                  </Stack>
                </Stack>
                </>
                ) : null}
                {whatsappSettingsSubSection === 'quickReplies' ? (
                <>
                <Stack
                  direction={{ xs: 'column', sm: 'row' }}
                  justifyContent="space-between"
                  alignItems={{ xs: 'flex-start', sm: 'center' }}
                  spacing={1}
                  sx={{ mb: 1 }}
                >
                  <Typography variant="subtitle1" sx={{ fontWeight: 800 }}>
                    Quick Replies
                  </Typography>
                  <Button
                    variant="outlined"
                    size="small"
                    startIcon={<AddIcon />}
                    onClick={openNewWhatsappQuickReply}
                  >
                    Add Quick Reply
                  </Button>
                </Stack>
                {whatsappQuickRepliesLoading ? (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 1 }}>
                    <CircularProgress size={16} />
                    <Typography variant="body2">Loading quick replies…</Typography>
                  </Box>
                ) : null}
                {whatsappQuickRepliesError ? <Alert severity="error" sx={{ mb: 1 }}>{whatsappQuickRepliesError}</Alert> : null}
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Label</TableCell>
                        <TableCell>Category</TableCell>
                        <TableCell>Scope</TableCell>
                        <TableCell>Status</TableCell>
                        <TableCell align="right">Actions</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {whatsappQuickReplies.length === 0 && !whatsappQuickRepliesLoading ? (
                        <TableRow>
                          <TableCell colSpan={5} align="center">No quick replies configured.</TableCell>
                        </TableRow>
                      ) : (
                        whatsappQuickReplies.map((reply: any) => (
                          <TableRow key={String(reply.id)} hover>
                            <TableCell>
                              <Typography sx={{ fontWeight: 700, fontSize: 13 }}>
                                {String(reply.label || reply.id)}
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                {String(reply.text || '').slice(0, 72) || '—'}
                              </Typography>
                            </TableCell>
                            <TableCell>{String(reply.category || 'General')}</TableCell>
                            <TableCell>
                              {String(reply.scope || 'global') === 'project'
                                ? `${projectNameById.get(String(reply.projectId || '')) || 'Project'}`
                                : 'Global'}
                            </TableCell>
                            <TableCell>
                              <Chip
                                size="small"
                                color={reply?.active === false ? 'default' : 'success'}
                                label={reply?.active === false ? 'Inactive' : 'Active'}
                              />
                            </TableCell>
                            <TableCell align="right">
                              <Button size="small" variant="outlined" onClick={() => openEditWhatsappQuickReply(String(reply.id))}>Edit</Button>
                              <Button size="small" color="error" sx={{ ml: 1 }} onClick={() => deleteWhatsappQuickReply(String(reply.id), String(reply.label || reply.id))}>Delete</Button>
                            </TableCell>
                          </TableRow>
                        ))
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
                </>
                ) : null}

                {whatsappSettingsSubSection === 'testMessage' ? (
                <>
                <Typography variant="subtitle1" sx={{ fontWeight: 800, mb: 1 }}>Send Test Message</Typography>
                <Stack spacing={1.25}>
                  <FormControl fullWidth size="small">
                    <InputLabel id="wa-test-sender-label">Sender</InputLabel>
                    <Select
                      labelId="wa-test-sender-label"
                      label="Sender"
                      value={whatsappTestSenderId}
                      onChange={(e) => setWhatsappTestSenderId(String(e.target.value || ''))}
                    >
                      {whatsappSenders.map((sender: any) => (
                        <MenuItem key={String(sender.id)} value={String(sender.id)}>
                          {String(sender.name || sender.id)}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  <TextField
                    size="small"
                    label="To (10-digit India or E164 digits)"
                    value={whatsappTestTo}
                    onChange={(e) => setWhatsappTestTo(e.target.value)}
                    fullWidth
                  />
                  <Paper variant="outlined" sx={{ p: 1 }}>
                    <Typography variant="caption" sx={{ display: 'block', fontWeight: 700, mb: 0.75 }}>
                      Auto Fill from Customer Receipt
                    </Typography>
                    <Stack spacing={1}>
                      <FormControl fullWidth size="small">
                        <InputLabel id="wa-prefill-project-label">Project</InputLabel>
                        <Select
                          labelId="wa-prefill-project-label"
                          label="Project"
                          value={whatsappPrefillProjectId}
                          onChange={(e) => setWhatsappPrefillProjectId(String(e.target.value || ''))}
                        >
                          {projects.map((project: any) => (
                            <MenuItem key={String(project.id)} value={String(project.id)}>
                              {String(project.name || project.id)}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                      <TextField
                        size="small"
                        label="Unit No"
                        value={whatsappPrefillUnitNo}
                        onChange={(e) => setWhatsappPrefillUnitNo(e.target.value)}
                        fullWidth
                      />
                      <FormControl fullWidth size="small">
                        <InputLabel id="wa-prefill-txn-label">Receipt Transaction</InputLabel>
                        <Select
                          labelId="wa-prefill-txn-label"
                          label="Receipt Transaction"
                          value={whatsappPrefillTxnId}
                          onChange={(e) => setWhatsappPrefillTxnId(String(e.target.value || ''))}
                        >
                          {whatsappPrefillTxns.map((tx: any) => {
                            const dt = tx?.txnDate?.toDate && typeof tx.txnDate.toDate === 'function'
                              ? tx.txnDate.toDate().toLocaleDateString('en-GB')
                              : '';
                            return (
                              <MenuItem key={String(tx.id)} value={String(tx.id)}>
                                {`₹${Number(tx?.amount || 0).toLocaleString('en-IN')} • ${String(tx?.partyName || '—')} • ${dt || '—'}`}
                              </MenuItem>
                            );
                          })}
                        </Select>
                      </FormControl>
                      {whatsappPrefillTxnsLoading ? <Typography variant="caption">Loading receipts…</Typography> : null}
                      {whatsappPrefillTxnsError ? <Alert severity="error">{whatsappPrefillTxnsError}</Alert> : null}
                      <Button variant="outlined" onClick={autoFillWhatsappTemplateValues} disabled={!selectedWhatsappTemplate || !whatsappPrefillTxnId}>
                        Auto Fill Variables
                      </Button>
                    </Stack>
                  </Paper>
                  <FormControl fullWidth size="small">
                    <InputLabel id="wa-test-mode-label">Mode</InputLabel>
                    <Select
                      labelId="wa-test-mode-label"
                      label="Mode"
                      value={whatsappTestMode}
                      onChange={(e) => setWhatsappTestMode(String(e.target.value || 'text') as 'text' | 'template')}
                    >
                      <MenuItem value="text">Text</MenuItem>
                      <MenuItem value="template">Template</MenuItem>
                    </Select>
                  </FormControl>
                  {whatsappTestMode === 'text' ? (
                    <TextField
                      size="small"
                      label="Message"
                      value={whatsappTestText}
                      onChange={(e) => setWhatsappTestText(e.target.value)}
                      fullWidth
                      multiline
                      minRows={3}
                    />
                  ) : (
                    <>
                      <FormControl fullWidth size="small">
                        <InputLabel id="wa-test-template-label">Template</InputLabel>
                        <Select
                          labelId="wa-test-template-label"
                          label="Template"
                          value={whatsappTestTemplateId}
                          onChange={(e) => setWhatsappTestTemplateId(String(e.target.value || ''))}
                        >
                          {whatsappTemplates.map((template: any) => (
                            <MenuItem key={String(template.id)} value={String(template.id)}>
                              {String(template.label || template.name || template.id)}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                      <Stack direction="row" spacing={1}>
                        <TextField
                          size="small"
                          label="Meta Template Name"
                          value={whatsappTestTemplateName}
                          onChange={(e) => setWhatsappTestTemplateName(e.target.value)}
                          fullWidth
                        />
                        <TextField
                          size="small"
                          label="Language"
                          value={whatsappTestTemplateLanguage}
                          onChange={(e) => setWhatsappTestTemplateLanguage(e.target.value)}
                          sx={{ width: 120 }}
                        />
                      </Stack>
                      {Array.isArray(selectedWhatsappTemplate?.variables) && selectedWhatsappTemplate.variables.length ? (
                        <Paper variant="outlined" sx={{ p: 1 }}>
                          <Typography variant="caption" sx={{ display: 'block', fontWeight: 700, mb: 0.75 }}>
                            Variable Mapping
                          </Typography>
                          <Stack spacing={1}>
                            {selectedWhatsappTemplate.variables.map((entry: any, idx: number) => {
                              const key = String(entry?.key || '').trim();
                              if (!key) return null;
                              const label = String(entry?.label || key);
                              return (
                                <TextField
                                  key={`${key}_${idx}`}
                                  size="small"
                                  label={`${idx + 1}. ${label} (${key})`}
                                  value={String(whatsappTestTemplateValues[key] || '')}
                                  onChange={(e) => setWhatsappTestTemplateValues((prev) => ({ ...prev, [key]: e.target.value }))}
                                  fullWidth
                                />
                              );
                            })}
                          </Stack>
                        </Paper>
                      ) : null}
                    </>
                  )}
                  {whatsappTestError ? <Alert severity="error">{whatsappTestError}</Alert> : null}
                  {whatsappTestSuccess ? <Alert severity="success">{whatsappTestSuccess}</Alert> : null}
                  <Button variant="contained" onClick={sendWhatsappTestMessage} disabled={whatsappTestSending}>
                    {whatsappTestSending ? 'Sending…' : 'Send Test'}
                  </Button>
                </Stack>
                </>
                ) : null}
              </Paper>
            </Box>

            <Dialog
              open={whatsappSenderDialogOpen}
              onClose={() => {
                if (whatsappSenderSaving) return;
                setWhatsappSenderDialogOpen(false);
              }}
              fullWidth
              maxWidth="sm"
            >
              <DialogTitle>{editingWhatsappSenderId ? 'Edit WhatsApp Sender' : 'New WhatsApp Sender'}</DialogTitle>
              <DialogContent>
                <Stack spacing={1.5} sx={{ mt: 1 }}>
                  <TextField label="Sender Name" value={whatsappSenderName} onChange={(e) => setWhatsappSenderName(e.target.value)} fullWidth />
                  <TextField label="Phone Number ID" value={whatsappSenderPhoneNumberId} onChange={(e) => setWhatsappSenderPhoneNumberId(e.target.value)} fullWidth />
                  <TextField
                    label="WABA ID (WhatsApp Business Account ID)"
                    value={whatsappSenderBusinessAccountId}
                    onChange={(e) => setWhatsappSenderBusinessAccountId(e.target.value)}
                    helperText="Find in Meta Business Settings → WhatsApp Accounts."
                    fullWidth
                  />
                  <TextField
                    label="Access Token (optional)"
                    value={whatsappSenderAccessToken}
                    onChange={(e) => setWhatsappSenderAccessToken(e.target.value)}
                    helperText="Leave blank to use WHATSAPP_ACCESS_TOKEN secret."
                    fullWidth
                  />
                  <FormControlLabel control={<Switch checked={whatsappSenderActive} onChange={(e) => setWhatsappSenderActive(e.target.checked)} />} label="Active" />
                  <FormControlLabel control={<Switch checked={whatsappSenderDefault} onChange={(e) => setWhatsappSenderDefault(e.target.checked)} />} label="Default Sender" />
                  {whatsappSenderSaveError ? <Alert severity="error">{whatsappSenderSaveError}</Alert> : null}
                  {whatsappSenderSaveSuccess ? <Alert severity="success">{whatsappSenderSaveSuccess}</Alert> : null}
                </Stack>
              </DialogContent>
              <DialogActions>
                <Button onClick={() => setWhatsappSenderDialogOpen(false)} disabled={whatsappSenderSaving}>Cancel</Button>
                <Button variant="contained" onClick={saveWhatsappSender} disabled={whatsappSenderSaving}>
                  {whatsappSenderSaving ? 'Saving…' : 'Save'}
                </Button>
              </DialogActions>
            </Dialog>

            <Dialog
              open={whatsappTemplateDialogOpen}
              onClose={() => {
                if (whatsappTemplateSaving) return;
                setWhatsappTemplateDialogOpen(false);
              }}
              fullWidth
              maxWidth="lg"
            >
              <DialogTitle>{editingWhatsappTemplateId ? 'Edit WhatsApp Template' : 'New WhatsApp Template'}</DialogTitle>
              <DialogContent>
                <Stack spacing={1.5} sx={{ mt: 1 }}>
                  <Paper variant="outlined" sx={{ p: 1.25 }}>
                    <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: whatsappMetaTemplatesFetched ? 1 : 0 }}>
                      <Typography variant="caption" sx={{ fontWeight: 700 }}>
                        Import from Meta
                      </Typography>
                      <Button
                        size="small"
                        variant="outlined"
                        onClick={fetchMetaTemplates}
                        disabled={whatsappMetaTemplatesLoading}
                      >
                        {whatsappMetaTemplatesLoading ? 'Fetching…' : 'Fetch Templates'}
                      </Button>
                    </Stack>
                    {whatsappMetaTemplatesFetched && (
                      <Stack spacing={0.75}>
                        {whatsappMetaTemplates.length === 0 ? (
                          <Typography variant="body2" color="text.secondary">No templates found in Meta account.</Typography>
                        ) : (
                          whatsappMetaTemplates.map((mt: any) => {
                            const existingTemplate = findWhatsappTemplateByMeta(
                              String(mt.name || ''),
                              String(mt.language || 'en')
                            );
                            return (
                              <Stack key={`${mt.name}_${mt.language}`} direction="row" alignItems="center" spacing={1}>
                                <Typography variant="body2" sx={{ flex: 1, fontWeight: 500 }}>
                                  {String(mt.name || '')}
                                </Typography>
                                <Chip size="small" label={String(mt.language || '')} />
                                <Chip
                                  size="small"
                                  color={String(mt.status || '').toUpperCase() === 'APPROVED' ? 'success' : 'default'}
                                  label={String(mt.status || '').toLowerCase()}
                                />
                                {existingTemplate ? (
                                  <Chip size="small" color="info" label="Already added" />
                                ) : null}
                                <Button size="small" variant="text" onClick={() => applyMetaTemplate(mt)}>
                                  {existingTemplate ? 'Update' : 'Use'}
                                </Button>
                              </Stack>
                            );
                          })
                        )}
                      </Stack>
                    )}
                  </Paper>
                  <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.25}>
                    <TextField
                      label="Template Label"
                      value={whatsappTemplateLabel}
                      onChange={(e) => setWhatsappTemplateLabel(e.target.value)}
                      fullWidth
                    />
                    <TextField
                      label="Meta Template Name"
                      value={whatsappTemplateName}
                      onChange={(e) => setWhatsappTemplateName(e.target.value)}
                      fullWidth
                    />
                    <TextField
                      label="Language"
                      value={whatsappTemplateLanguage}
                      onChange={(e) => setWhatsappTemplateLanguage(e.target.value)}
                      sx={{ width: { xs: '100%', sm: 140 } }}
                    />
                  </Stack>
                  <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.25}>
                    <FormControl sx={{ width: { xs: '100%', sm: 220 } }}>
                      <InputLabel id="wa-template-header-format-label">Header Format</InputLabel>
                      <Select
                        labelId="wa-template-header-format-label"
                        value={whatsappTemplateHeaderFormat}
                        label="Header Format"
                        onChange={(e) => setWhatsappTemplateHeaderFormat(String(e.target.value || 'none') as 'none' | 'image' | 'document' | 'location')}
                      >
                        <MenuItem value="none">None</MenuItem>
                        <MenuItem value="image">Image</MenuItem>
                        <MenuItem value="document">Document</MenuItem>
                        <MenuItem value="location">Location</MenuItem>
                      </Select>
                    </FormControl>
                    {whatsappTemplateHeaderFormat === 'image' ? (
                      <>
                        <FormControl sx={{ width: { xs: '100%', sm: 220 } }}>
                          <InputLabel id="wa-template-header-image-source-label">Image Source</InputLabel>
                          <Select
                            labelId="wa-template-header-image-source-label"
                            value={whatsappTemplateHeaderImageSource}
                            label="Image Source"
                            onChange={(e) => setWhatsappTemplateHeaderImageSource(String(e.target.value || 'project_logo') as WhatsappTemplateHeaderImageSource)}
                          >
                            <MenuItem value="project_logo">Project Logo URL</MenuItem>
                            <MenuItem value="voucher_or_project_logo">Chq / Voucher Photo (fallback Project Logo)</MenuItem>
                            <MenuItem value="upload">Upload Image</MenuItem>
                            <MenuItem value="custom">Custom URL</MenuItem>
                          </Select>
                        </FormControl>
                        {whatsappTemplateHeaderImageSource === 'upload' ? (
                          <Stack spacing={0.5} sx={{ flex: 1, minWidth: { xs: '100%', sm: 320 } }}>
                            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ xs: 'stretch', sm: 'center' }}>
                              <Button variant="outlined" component="label">
                                Upload Image
                                <input
                                  type="file"
                                  accept="image/*"
                                  hidden
                                  onChange={(e) => {
                                    const file = e.target.files?.[0] || null;
                                    setWhatsappTemplateHeaderImageFile(file);
                                    if (file) setWhatsappTemplateHeaderImageUrl('');
                                  }}
                                />
                              </Button>
                              <Typography variant="body2" color="text.secondary">
                                {whatsappTemplateHeaderImageFile?.name ||
                                  (whatsappTemplateHeaderImageUrl ? 'Uploaded image saved' : 'No image selected')}
                              </Typography>
                              {whatsappTemplateHeaderImageUrl ? (
                                <Button
                                  size="small"
                                  href={whatsappTemplateHeaderImageUrl}
                                  target="_blank"
                                  rel="noreferrer"
                                >
                                  Open
                                </Button>
                              ) : null}
                            </Stack>
                            <Typography variant="caption" color="text.secondary">
                              Use JPG or PNG. The image is uploaded to Firebase Storage and saved as this template&apos;s header URL.
                            </Typography>
                          </Stack>
                        ) : null}
                        {whatsappTemplateHeaderImageSource === 'custom' ? (
                          <TextField
                            label="Header Image URL"
                            value={whatsappTemplateHeaderImageUrl}
                            onChange={(e) => setWhatsappTemplateHeaderImageUrl(e.target.value)}
                            fullWidth
                          />
                        ) : null}
                      </>
                    ) : null}
                    {whatsappTemplateHeaderFormat === 'document' ? (
                      <>
                        <FormControl sx={{ width: { xs: '100%', sm: 240 } }}>
                          <InputLabel id="wa-template-header-document-source-label">Document Source</InputLabel>
                          <Select
                            labelId="wa-template-header-document-source-label"
                            value={whatsappTemplateHeaderDocumentSource}
                            label="Document Source"
                            onChange={(e) => setWhatsappTemplateHeaderDocumentSource(String(e.target.value || 'project_brochure') as 'project_brochure' | 'lead_report' | 'custom')}
                          >
                            <MenuItem value="project_brochure">Project Brochure URL</MenuItem>
                            <MenuItem value="lead_report">Generated Lead Report PDF</MenuItem>
                            <MenuItem value="custom">Custom Document URL</MenuItem>
                          </Select>
                        </FormControl>
                        {whatsappTemplateHeaderDocumentSource === 'lead_report' ? (
                          <Alert severity="info" sx={{ py: 0.5, flex: 1 }}>
                            The generated PDF is attached automatically from Reports &gt; Bulk Send Broker Reports.
                          </Alert>
                        ) : null}
                        {whatsappTemplateHeaderDocumentSource === 'custom' ? (
                          <TextField
                            label="Header Document URL"
                            value={whatsappTemplateHeaderDocumentUrl}
                            onChange={(e) => setWhatsappTemplateHeaderDocumentUrl(e.target.value)}
                            fullWidth
                          />
                        ) : null}
                      </>
                    ) : null}
                    {whatsappTemplateHeaderFormat === 'location' ? (
                      <FormControl sx={{ minWidth: 280 }}>
                        <InputLabel>Location Source</InputLabel>
                        <Select
                          value={whatsappTemplateHeaderLocationSource}
                          label="Location Source"
                          onChange={(e) => setWhatsappTemplateHeaderLocationSource(e.target.value as any)}
                        >
                          <MenuItem value="auto">Auto (Project / Office)</MenuItem>
                          <MenuItem value="project">Project Location</MenuItem>
                          <MenuItem value="office">Office Location</MenuItem>
                          <MenuItem value="custom">Custom Coordinates</MenuItem>
                        </Select>
                      </FormControl>
                    ) : null}
                  </Stack>
                  {whatsappTemplateHeaderFormat === 'location' && whatsappTemplateHeaderLocationSource !== 'custom' && (
                    <Alert severity="info" sx={{ py: 0.5 }}>
                      {whatsappTemplateHeaderLocationSource === 'auto'
                        ? 'Location auto-resolves: Project coordinates for Site Visit, Office coordinates for Office Meeting.'
                        : whatsappTemplateHeaderLocationSource === 'project'
                          ? 'Will use the project\'s latitude, longitude & address from Project Settings.'
                          : 'Will use the head office coordinates from Group Profile.'}
                    </Alert>
                  )}
                  {whatsappTemplateHeaderFormat === 'location' && whatsappTemplateHeaderLocationSource === 'custom' && (
                    <>
                      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.25}>
                        <TextField
                          label="Latitude"
                          value={whatsappTemplateHeaderLatitude}
                          onChange={(e) => setWhatsappTemplateHeaderLatitude(e.target.value)}
                          placeholder="e.g. 22.7220"
                          fullWidth
                        />
                        <TextField
                          label="Longitude"
                          value={whatsappTemplateHeaderLongitude}
                          onChange={(e) => setWhatsappTemplateHeaderLongitude(e.target.value)}
                          placeholder="e.g. 72.1461"
                          fullWidth
                        />
                      </Stack>
                      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.25}>
                        <TextField
                          label="Location Name"
                          value={whatsappTemplateHeaderLocationName}
                          onChange={(e) => setWhatsappTemplateHeaderLocationName(e.target.value)}
                          placeholder="e.g. Swarna Avsar"
                          fullWidth
                        />
                        <TextField
                          label="Address"
                          value={whatsappTemplateHeaderLocationAddress}
                          onChange={(e) => setWhatsappTemplateHeaderLocationAddress(e.target.value)}
                          placeholder="e.g. Dholera, Gujarat"
                          fullWidth
                        />
                      </Stack>
                    </>
                  )}
                  <Stack direction="row" justifyContent="space-between" alignItems="center">
                    <Typography variant="subtitle2">Variables</Typography>
                    <Button
                      size="small"
                      variant="outlined"
                      startIcon={<AddIcon />}
                      onClick={() => setWhatsappTemplateVariables((prev) => [...prev, { key: `var${prev.length + 1}`, label: '', defaultValue: '', source: 'custom' }])}
                    >
                      Add Variable
                    </Button>
                  </Stack>
                  {whatsappTemplateVariables.length ? (
                    <Stack spacing={1}>
                      {whatsappTemplateVariables.map((entry, idx) => (
                        <Stack key={`wa_var_${idx}`} direction={{ xs: 'column', md: 'row' }} spacing={1} alignItems="center">
                          <Typography variant="body2" sx={{ fontWeight: 600, minWidth: 40, color: 'text.secondary' }}>{`{{${idx + 1}}}`}</Typography>
                          <FormControl size="small" sx={{ minWidth: 180 }}>
                            <InputLabel>Data Source</InputLabel>
                            <Select
                              value={entry.source || 'custom'}
                              label="Data Source"
                              onChange={(e) => {
                                const src = e.target.value;
                                const preset = whatsappVariableSources.find((p) => p.key === src);
                                setWhatsappTemplateVariables((prev) =>
                                  prev.map((row, rowIdx) => rowIdx === idx ? {
                                    ...row,
                                    source: src,
                                    key: src === 'custom' ? row.key : src,
                                    label: preset?.label || row.label,
                                  } : row)
                                );
                              }}
                            >
                              {whatsappVariableSourceGroups.flatMap((group) => [
                                <ListSubheader key={`wa_var_group_${group.label}`}>{group.label}</ListSubheader>,
                                ...group.sources.map((s) => (
                                  <MenuItem key={s.key} value={s.key}>{s.label}</MenuItem>
                                )),
                              ])}
                            </Select>
                          </FormControl>
                          <TextField
                            size="small"
                            label="Label"
                            value={entry.label}
                            onChange={(e) => setWhatsappTemplateVariables((prev) => prev.map((row, rowIdx) => rowIdx === idx ? { ...row, label: e.target.value } : row))}
                            sx={{ flex: 1 }}
                            fullWidth
                          />
                          <TextField
                            size="small"
                            label="Fallback Value"
                            value={entry.defaultValue}
                            onChange={(e) => setWhatsappTemplateVariables((prev) => prev.map((row, rowIdx) => rowIdx === idx ? { ...row, defaultValue: e.target.value } : row))}
                            helperText="Used when selected data source is empty."
                            sx={{ flex: 1 }}
                            fullWidth
                          />
                          <IconButton
                            size="small"
                            color="error"
                            onClick={() => setWhatsappTemplateVariables((prev) => prev.filter((_, rowIdx) => rowIdx !== idx))}
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Stack>
                      ))}
                      <Box sx={{ mt: 1.5, p: 2, bgcolor: '#e8f5e9', borderRadius: 2, border: '1px solid #c8e6c9' }}>
                        <Typography variant="caption" sx={{ fontWeight: 700, color: '#2e7d32', mb: 1, display: 'block' }}>Message Preview</Typography>
                        {whatsappTemplateBodyText ? (
                          <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', color: '#1b5e20', lineHeight: 1.7 }}>
                            {renderWhatsAppFormattedText(
                              whatsappTemplateBodyText.replace(/\{\{(\d+)\}\}/g, (_, num) => {
                                const idx = parseInt(num, 10) - 1;
                                const v = whatsappTemplateVariables[idx];
                                if (!v) return `{{${num}}}`;
                                const sampleValues: Record<string, string> = {
                                  customer_name: 'Rahul Sharma',
                                  customer_first_name: 'Rahul',
                                  staff_name: 'Priya Patel',
                                  contact_name: 'Amit Shah',
                                  broker_name: 'Devangbhai',
                                  agency_name: 'R Group',
                                  project_name: 'Swarna Heights',
                                  date: 'Monday, 30 June 2026',
                                  time: '11:00 AM',
                                  event_date: 'Monday, 30 June 2026',
                                  event_time: '11:00 AM',
                                  visit_date: 'Monday, 30 June 2026',
                                  visit_time: '11:00 AM',
                                  appointment_type: 'Site Visit',
                                  location: 'Sector 12, Noida',
                                  unit_type: '3 BHK',
                                  visit_with: 'Sales Manager',
                                  amount: '₹25,00,000',
                                  ref_no: 'TXN123456',
                                  payment_type: 'Cheque',
                                  unit_no: 'A-1201',
                                };
                                const sample = formatWhatsappVariableValue(
                                  v.source,
                                  sampleValues[v.source] || v.defaultValue || v.label || `{{${num}}}`
                                );
                                return sample;
                              }),
                            )}
                          </Typography>
                        ) : (
                          <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                            Import template from Meta to see message preview
                          </Typography>
                        )}
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1.5, borderTop: '1px solid #c8e6c9', pt: 1 }}>
                          {whatsappTemplateVariables.map((v, i) => {
                            const src = whatsappVariableSources.find((s) => s.key === v.source);
                            return `{{${i + 1}}} → ${src?.label || v.label || v.key}`;
                          }).join('   ·   ')}
                        </Typography>
                      </Box>
                    </Stack>
                  ) : (
                    <Alert severity="info">No variables configured. Template can still be sent without components.</Alert>
                  )}
                  <Stack direction="row" justifyContent="space-between" alignItems="center">
                    <Typography variant="subtitle2">Button Variables</Typography>
                    <Button
                      size="small"
                      variant="outlined"
                      startIcon={<AddIcon />}
                      onClick={() => setWhatsappTemplateButtonVariables((prev) => [...prev, { index: String(prev.length), key: `button${prev.length + 1}`, label: '', defaultValue: '', source: 'project_slug' }])}
                    >
                      Add Button Variable
                    </Button>
                  </Stack>
                  {whatsappTemplateButtonVariables.length ? (
                    <Stack spacing={1}>
                      {whatsappTemplateButtonVariables.map((entry, idx) => (
                        <Stack key={`wa_button_var_${idx}`} direction={{ xs: 'column', md: 'row' }} spacing={1} alignItems="center">
                          <TextField
                            size="small"
                            label="Button Index"
                            value={entry.index}
                            onChange={(e) => setWhatsappTemplateButtonVariables((prev) => prev.map((row, rowIdx) => rowIdx === idx ? { ...row, index: e.target.value } : row))}
                            sx={{ width: { xs: '100%', md: 120 } }}
                            helperText="0 for first button"
                          />
                          <FormControl size="small" sx={{ minWidth: 220 }}>
                            <InputLabel>Data Source</InputLabel>
                            <Select
                              value={entry.source || 'project_slug'}
                              label="Data Source"
                              onChange={(e) => {
                                const src = e.target.value;
                                const preset = whatsappVariableSources.find((p) => p.key === src);
                                setWhatsappTemplateButtonVariables((prev) =>
                                  prev.map((row, rowIdx) => rowIdx === idx ? {
                                    ...row,
                                    source: src,
                                    key: src === 'custom' ? row.key : src,
                                    label: preset?.label || row.label,
                                  } : row)
                                );
                              }}
                            >
                              {whatsappVariableSourceGroups.flatMap((group) => [
                                <ListSubheader key={`wa_button_var_group_${group.label}`}>{group.label}</ListSubheader>,
                                ...group.sources.map((s) => (
                                  <MenuItem key={s.key} value={s.key}>{s.label}</MenuItem>
                                )),
                              ])}
                            </Select>
                          </FormControl>
                          <TextField
                            size="small"
                            label="Label"
                            value={entry.label}
                            onChange={(e) => setWhatsappTemplateButtonVariables((prev) => prev.map((row, rowIdx) => rowIdx === idx ? { ...row, label: e.target.value } : row))}
                            sx={{ flex: 1 }}
                            fullWidth
                          />
                          <TextField
                            size="small"
                            label="Fallback Value"
                            value={entry.defaultValue}
                            onChange={(e) => setWhatsappTemplateButtonVariables((prev) => prev.map((row, rowIdx) => rowIdx === idx ? { ...row, defaultValue: e.target.value } : row))}
                            sx={{ flex: 1 }}
                            fullWidth
                          />
                          <IconButton
                            size="small"
                            color="error"
                            onClick={() => setWhatsappTemplateButtonVariables((prev) => prev.filter((_, rowIdx) => rowIdx !== idx))}
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Stack>
                      ))}
                    </Stack>
                  ) : (
                    <Alert severity="info">No dynamic URL button variables configured.</Alert>
                  )}
                  <Stack direction="row" alignItems="center" spacing={2} sx={{ mt: 1 }}>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={whatsappTemplateShowInLeadChat}
                          onChange={(e) => setWhatsappTemplateShowInLeadChat(e.target.checked)}
                        />
                      }
                      label="Show in Lead Chat"
                    />
                  </Stack>
                  <Typography variant="caption" color="text.secondary">
                    Only templates with this enabled will appear in the expired 24h
                    WhatsApp picker inside the lead drawer.
                  </Typography>
                  {whatsappTemplateSaveError ? <Alert severity="error">{whatsappTemplateSaveError}</Alert> : null}
                </Stack>
              </DialogContent>
              <DialogActions>
                <Button onClick={() => setWhatsappTemplateDialogOpen(false)} disabled={whatsappTemplateSaving}>Cancel</Button>
                <Button variant="contained" onClick={saveWhatsappTemplate} disabled={whatsappTemplateSaving}>
                  {whatsappTemplateSaving ? 'Saving…' : 'Save'}
                </Button>
              </DialogActions>
            </Dialog>

            <Dialog
              open={whatsappAutomationRuleDialogOpen}
              onClose={() => {
                if (whatsappAutomationRuleSaving) return;
                setWhatsappAutomationRuleDialogOpen(false);
              }}
              fullWidth
              maxWidth="lg"
            >
              <DialogTitle>
                {editingWhatsappAutomationRuleId ? 'Edit Journey Step' : 'New Journey Step'}
              </DialogTitle>
              <DialogContent>
                <Stack spacing={1.75} sx={{ mt: 1 }}>
                  <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'minmax(0, 1fr) 160px 180px' }, gap: 1.25, alignItems: 'center' }}>
                    <TextField
                      label="Step Name"
                      value={whatsappAutomationRuleName}
                      onChange={(e) => setWhatsappAutomationRuleName(e.target.value)}
                      fullWidth
                    />
                    <TextField
                      label="Priority"
                      value={whatsappAutomationRulePriority}
                      onChange={(e) => setWhatsappAutomationRulePriority(e.target.value)}
                      fullWidth
                    />
                    <FormControl fullWidth>
                      <InputLabel id="wa-automation-mode-label">Step Mode</InputLabel>
                      <Select
                        labelId="wa-automation-mode-label"
                        label="Step Mode"
                        value={whatsappAutomationRuleMode}
                        onChange={(e) => setWhatsappAutomationRuleMode(String(e.target.value || 'active') as 'inactive' | 'test' | 'active')}
                      >
                        <MenuItem value="inactive">Inactive</MenuItem>
                        <MenuItem value="test">Test number only</MenuItem>
                        <MenuItem value="active">Active for matching leads</MenuItem>
                      </Select>
                    </FormControl>
                  </Box>
                  <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr 1fr' }, gap: 1.25 }}>
                    <FormControl fullWidth>
                      <InputLabel id="wa-automation-trigger-label">Trigger</InputLabel>
                      <Select
                        labelId="wa-automation-trigger-label"
                        label="Trigger"
                        value={whatsappAutomationRuleTrigger}
                        onChange={(e) => {
                          const nextTrigger = e.target.value as WhatsappAutomationRuleTrigger;
                          setWhatsappAutomationRuleTrigger(nextTrigger);
                          if (nextTrigger !== 'appointment-scheduled' && nextTrigger !== 'visit-completed') {
                            setWhatsappAutomationRuleAppointmentTypes([]);
                          }
                          if (nextTrigger === 'customer-payment-receipt') {
                            setWhatsappAutomationRuleReceiptTypes(['customer']);
                            setWhatsappAutomationRuleRepeatMode('once_per_event');
                            setWhatsappAutomationRuleDndMode('bypass');
                            setWhatsappAutomationRuleDelayAmount('0');
                            setWhatsappAutomationRuleDelayUnit('minutes');
                            setWhatsappAutomationRuleDelayDirection('after');
                            setWhatsappAutomationRuleScheduleAnchor('event');
                          }
                          if (nextTrigger === 'inbound-whatsapp-message') {
                            setWhatsappAutomationRuleRecipient('customer');
                            setWhatsappAutomationRuleRepeatMode('once_per_event');
                            setWhatsappAutomationRuleDndMode('bypass');
                            setWhatsappAutomationRuleSendWindow('after_hours');
                            setWhatsappAutomationRuleCooldownAmount('24');
                            setWhatsappAutomationRuleCooldownUnit('hours');
                            setWhatsappAutomationRuleDelayAmount('0');
                            setWhatsappAutomationRuleDelayUnit('minutes');
                            setWhatsappAutomationRuleDelayDirection('after');
                            setWhatsappAutomationRuleScheduleAnchor('event');
                          }
                        }}
                      >
                        <MenuItem value="lead-created">Lead Created</MenuItem>
                        <MenuItem value="status-changed">Status Changed</MenuItem>
                        <MenuItem value="lead-reopened">Lead Reopened</MenuItem>
                        <MenuItem value="appointment-scheduled">Appointment Scheduled</MenuItem>
                        <MenuItem value="visit-completed">Visit Completed</MenuItem>
                        <MenuItem value="existing-lead-reenquiry">Existing Lead Re-Enquiry</MenuItem>
                        <MenuItem value="unknown-whatsapp-message">Unknown WhatsApp Message</MenuItem>
                        <MenuItem value="inbound-whatsapp-message">Inbound WhatsApp Message</MenuItem>
                        <MenuItem value="customer-payment-receipt">Customer Payment Receipt</MenuItem>
                      </Select>
                    </FormControl>
                    <FormControl fullWidth>
                      <InputLabel id="wa-automation-recipient-label">Recipient</InputLabel>
                      <Select
                        labelId="wa-automation-recipient-label"
                        label="Recipient"
                        value={whatsappAutomationRuleRecipient}
                        onChange={(e) => setWhatsappAutomationRuleRecipient(String(e.target.value || 'customer') === 'broker' ? 'broker' : 'customer')}
                      >
                        <MenuItem value="customer">Customer / Lead</MenuItem>
                        <MenuItem value="broker">Broker / Referrer</MenuItem>
                      </Select>
                    </FormControl>
                    <FormControl fullWidth>
                      <InputLabel id="wa-automation-template-label">Template</InputLabel>
                      <Select
                        labelId="wa-automation-template-label"
                        label="Template"
                        value={whatsappAutomationRuleTemplateId}
                        onChange={(e) => setWhatsappAutomationRuleTemplateId(String(e.target.value || ''))}
                      >
                        {whatsappTemplates.map((template: any) => (
                          <MenuItem key={String(template.id)} value={String(template.id)}>
                            {String(template.label || template.name || template.id)}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Box>
                  <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '160px 1fr 1fr 1fr' }, gap: 1.25 }}>
                    <TextField
                      label="Delay Amount"
                      value={whatsappAutomationRuleDelayAmount}
                      onChange={(e) => setWhatsappAutomationRuleDelayAmount(e.target.value)}
                      fullWidth
                    />
                    <FormControl fullWidth>
                      <InputLabel id="wa-automation-delay-unit-label">Delay Unit</InputLabel>
                      <Select
                        labelId="wa-automation-delay-unit-label"
                        label="Delay Unit"
                        value={whatsappAutomationRuleDelayUnit}
                        onChange={(e) => setWhatsappAutomationRuleDelayUnit(String(e.target.value || 'minutes') as 'minutes' | 'hours' | 'days')}
                      >
                        <MenuItem value="minutes">Minutes</MenuItem>
                        <MenuItem value="hours">Hours</MenuItem>
                        <MenuItem value="days">Days</MenuItem>
                      </Select>
                    </FormControl>
                    <FormControl fullWidth>
                      <InputLabel id="wa-automation-delay-direction-label">Direction</InputLabel>
                      <Select
                        labelId="wa-automation-delay-direction-label"
                        label="Direction"
                        value={whatsappAutomationRuleDelayDirection}
                        onChange={(e) => setWhatsappAutomationRuleDelayDirection(String(e.target.value || 'after') as 'after' | 'before')}
                      >
                        <MenuItem value="after">After anchor</MenuItem>
                        <MenuItem value="before">Before anchor</MenuItem>
                      </Select>
                    </FormControl>
                    <FormControl fullWidth>
                      <InputLabel id="wa-automation-anchor-label">Anchor</InputLabel>
                      <Select
                        labelId="wa-automation-anchor-label"
                        label="Anchor"
                        value={whatsappAutomationRuleScheduleAnchor}
                        onChange={(e) => setWhatsappAutomationRuleScheduleAnchor(String(e.target.value || 'event') as 'event' | 'leadCreated' | 'appointment')}
                      >
                        <MenuItem value="event">Trigger event time</MenuItem>
                        <MenuItem value="leadCreated">Lead created time</MenuItem>
                        <MenuItem value="appointment">Appointment time</MenuItem>
                      </Select>
                    </FormControl>
                  </Box>
                  <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr 1fr' }, gap: 1.25 }}>
                    <FormControl fullWidth>
                      <InputLabel id="wa-automation-repeat-label">Repeat</InputLabel>
                      <Select
                        labelId="wa-automation-repeat-label"
                        label="Repeat"
                        value={whatsappAutomationRuleRepeatMode}
                        onChange={(e) => setWhatsappAutomationRuleRepeatMode(String(e.target.value || 'once_per_lead') as 'once_per_lead' | 'once_per_event')}
                      >
                        <MenuItem value="once_per_lead">Send once per lead</MenuItem>
                        <MenuItem value="once_per_event">Send once per matching event</MenuItem>
                      </Select>
                    </FormControl>
                    <FormControl fullWidth>
                      <InputLabel id="wa-automation-send-window-label">Send Window</InputLabel>
                      <Select
                        labelId="wa-automation-send-window-label"
                        label="Send Window"
                        value={whatsappAutomationRuleSendWindow}
                        onChange={(e) => setWhatsappAutomationRuleSendWindow(String(e.target.value || 'any') as 'any' | 'working_hours' | 'after_hours')}
                      >
                        <MenuItem value="any">Any time</MenuItem>
                        <MenuItem value="working_hours">Working hours only (9:30 AM - 7:30 PM)</MenuItem>
                        <MenuItem value="after_hours">After hours only</MenuItem>
                      </Select>
                    </FormControl>
                    <FormControl fullWidth>
                      <InputLabel id="wa-automation-dnd-label">DND</InputLabel>
                      <Select
                        labelId="wa-automation-dnd-label"
                        label="DND"
                        value={whatsappAutomationRuleDndMode}
                        onChange={(e) => setWhatsappAutomationRuleDndMode(String(e.target.value || 'respect') as 'respect' | 'bypass')}
                      >
                        <MenuItem value="respect">Respect 9 PM to 8 AM</MenuItem>
                        <MenuItem value="bypass">Bypass DND</MenuItem>
                      </Select>
                    </FormControl>
                  </Box>
                  <Autocomplete
                    multiple
                    freeSolo
                    options={whatsappAutomationSourceOptions}
                    value={whatsappAutomationRuleSourceValues}
                    onChange={(_event, nextValue) => setWhatsappAutomationRuleSourceValues(nextValue.map((value) => String(value || '').trim()).filter(Boolean))}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Source Filter"
                        helperText="Leave empty to allow all sources."
                      />
                    )}
                  />
                  <FormControl fullWidth>
                    <InputLabel id="wa-automation-status-filter-label">Status Filter</InputLabel>
                    <Select
                      labelId="wa-automation-status-filter-label"
                      multiple
                      value={whatsappAutomationRuleStatusValues}
                      onChange={(e) => setWhatsappAutomationRuleStatusValues((e.target.value as string[]).map((value) => String(value || '')).filter(Boolean))}
                      label="Status Filter"
                      renderValue={(selected) =>
                        (selected as string[])
                          .map((id) => statuses.find((s: any) => String(s.id) === id)?.label || id)
                          .join(', ')
                      }
                    >
                      {statuses.map((status: any) => (
                        <MenuItem key={String(status.id)} value={String(status.id)}>
                          <Checkbox checked={whatsappAutomationRuleStatusValues.includes(String(status.id))} />
                          <ListItemText primary={String(status.label || status.id)} />
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  <TextField
                    label="Enquiry Types"
                    value={whatsappAutomationRuleEnquiryTypes}
                    onChange={(e) => setWhatsappAutomationRuleEnquiryTypes(e.target.value)}
                    helperText="Optional. Comma separated values."
                    fullWidth
                  />
                  <Accordion variant="outlined" disableGutters>
                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                      <Stack direction="row" spacing={1} alignItems="center">
                        <Typography variant="subtitle2">Advanced Conditions</Typography>
                        {whatsappAutomationRuleAdvancedConditions.length ? (
                          <Chip size="small" label={`${whatsappAutomationRuleAdvancedConditions.length} active`} />
                        ) : null}
                      </Stack>
                    </AccordionSummary>
                    <AccordionDetails>
                      <Stack spacing={1}>
                        {whatsappAutomationRuleAdvancedConditions.length === 0 ? (
                          <Alert severity="info" sx={{ py: 0.5 }}>
                            Add optional conditions for special automation cases. Existing filters above still apply.
                          </Alert>
                        ) : null}
                        {whatsappAutomationRuleAdvancedConditions.map((condition, idx) => (
                          <Box
                            key={`wa_advanced_condition_${idx}`}
                            sx={{
                              display: 'grid',
                              gridTemplateColumns: {
                                xs: '1fr',
                                md: condition.field === 'lead_match' ? '1fr 1fr 44px' : '1fr 1fr 120px 120px 44px',
                              },
                              gap: 1,
                              alignItems: 'center',
                            }}
                          >
                            <FormControl fullWidth size="small">
                              <InputLabel>Field</InputLabel>
                              <Select
                                label="Field"
                                value={condition.field}
                                onChange={(e) => {
                                  const field = String(e.target.value || 'lead_created_at') as WhatsappAutomationAdvancedCondition['field'];
                                  setWhatsappAutomationRuleAdvancedConditions((prev) =>
                                    prev.map((row, rowIdx) => {
                                      if (rowIdx !== idx) return row;
                                      return field === 'lead_match'
                                        ? { field, operator: 'any', value: '', unit: 'none' }
                                        : { field, operator: 'within_last', value: row.value || '45', unit: 'days' };
                                    })
                                  );
                                }}
                              >
                                <MenuItem value="lead_created_at">Lead Created Date</MenuItem>
                                <MenuItem value="lead_match">Lead Match</MenuItem>
                              </Select>
                            </FormControl>
                            <FormControl fullWidth size="small">
                              <InputLabel>Operator</InputLabel>
                              <Select
                                label="Operator"
                                value={condition.operator}
                                onChange={(e) => {
                                  const operator = String(
                                    e.target.value || (condition.field === 'lead_match' ? 'any' : 'within_last')
                                  ) as WhatsappAutomationAdvancedCondition['operator'];
                                  setWhatsappAutomationRuleAdvancedConditions((prev) =>
                                    prev.map((row, rowIdx) => rowIdx === idx ? { ...row, operator } : row)
                                  );
                                }}
                              >
                                {condition.field === 'lead_match' ? [
                                  <MenuItem key="any" value="any">Any</MenuItem>,
                                  <MenuItem key="known" value="known">Known lead only</MenuItem>,
                                  <MenuItem key="unknown" value="unknown">Unknown number only</MenuItem>,
                                ] : [
                                  <MenuItem key="within_last" value="within_last">Within last</MenuItem>,
                                  <MenuItem key="older_than" value="older_than">Older than</MenuItem>,
                                ]}
                              </Select>
                            </FormControl>
                            {condition.field === 'lead_created_at' ? (
                              <>
                                <TextField
                                  size="small"
                                  label="Value"
                                  value={condition.value}
                                  onChange={(e) => {
                                    const value = e.target.value.replace(/\D/g, '').slice(0, 4);
                                    setWhatsappAutomationRuleAdvancedConditions((prev) =>
                                      prev.map((row, rowIdx) => rowIdx === idx ? { ...row, value } : row)
                                    );
                                  }}
                                  inputProps={{ inputMode: 'numeric' }}
                                />
                                <FormControl fullWidth size="small">
                                  <InputLabel>Unit</InputLabel>
                                  <Select label="Unit" value={condition.unit} disabled>
                                    <MenuItem value="days">Days</MenuItem>
                                  </Select>
                                </FormControl>
                              </>
                            ) : null}
                            <IconButton
                              size="small"
                              color="error"
                              onClick={() => setWhatsappAutomationRuleAdvancedConditions((prev) => prev.filter((_, rowIdx) => rowIdx !== idx))}
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </Box>
                        ))}
                        <Button
                          size="small"
                          variant="outlined"
                          startIcon={<AddIcon />}
                          onClick={() => setWhatsappAutomationRuleAdvancedConditions((prev) => [
                            ...prev,
                            { field: 'lead_created_at', operator: 'within_last', value: '45', unit: 'days' },
                          ])}
                          sx={{ alignSelf: 'flex-start' }}
                        >
                          Add Condition
                        </Button>
                      </Stack>
                    </AccordionDetails>
                  </Accordion>
                  {whatsappAutomationRuleTrigger === 'appointment-scheduled' || whatsappAutomationRuleTrigger === 'visit-completed' ? (
                    <FormControl fullWidth>
                      <InputLabel id="wa-automation-appointment-types-label">Appointment Types</InputLabel>
                      <Select
                        labelId="wa-automation-appointment-types-label"
                        multiple
                        value={whatsappAutomationRuleAppointmentTypes}
                        onChange={(e) => setWhatsappAutomationRuleAppointmentTypes((e.target.value as string[]).map((value) => String(value || '')))}
                        label="Appointment Types"
                        renderValue={(selected) => (selected as string[]).join(', ')}
                      >
                        {['site-visit', 'office-meeting'].map((option) => (
                          <MenuItem key={option} value={option}>
                            <Checkbox checked={whatsappAutomationRuleAppointmentTypes.includes(option)} />
                            <ListItemText primary={option} />
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  ) : null}
                  {whatsappAutomationRuleTrigger === 'customer-payment-receipt' ? (
                    <FormControl fullWidth>
                      <InputLabel id="wa-automation-receipt-types-label">Receipt Type</InputLabel>
                      <Select
                        labelId="wa-automation-receipt-types-label"
                        multiple
                        value={whatsappAutomationRuleReceiptTypes}
                        onChange={(e) => setWhatsappAutomationRuleReceiptTypes((e.target.value as string[]).map((value) => String(value || '')).filter(Boolean))}
                        label="Receipt Type"
                        renderValue={(selected) => (selected as string[]).map((value) => value === 'customer' ? 'Property Payment' : value === 'gst' ? 'GST Payment' : value).join(', ')}
                      >
                        <MenuItem value="customer">
                          <Checkbox checked={whatsappAutomationRuleReceiptTypes.includes('customer')} />
                          <ListItemText primary="Property Payment" />
                        </MenuItem>
                        <MenuItem value="gst">
                          <Checkbox checked={whatsappAutomationRuleReceiptTypes.includes('gst')} />
                          <ListItemText primary="GST Payment" />
                        </MenuItem>
                      </Select>
                    </FormControl>
                  ) : null}
                  {whatsappAutomationRuleTrigger === 'existing-lead-reenquiry' || whatsappAutomationRuleTrigger === 'unknown-whatsapp-message' || whatsappAutomationRuleTrigger === 'inbound-whatsapp-message' ? (
                    <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: whatsappAutomationRuleTrigger === 'existing-lead-reenquiry' ? '1fr 1fr 150px 1fr' : '150px 1fr' }, gap: 1.25 }}>
                      {whatsappAutomationRuleTrigger === 'existing-lead-reenquiry' ? (
                        <>
                          <FormControl fullWidth>
                            <InputLabel id="wa-automation-site-visit-state-label">Has Site Visit</InputLabel>
                            <Select
                              labelId="wa-automation-site-visit-state-label"
                              label="Has Site Visit"
                              value={whatsappAutomationRuleSiteVisitState}
                              onChange={(e) => setWhatsappAutomationRuleSiteVisitState(String(e.target.value || 'any') as 'any' | 'yes' | 'no')}
                            >
                              <MenuItem value="any">Any</MenuItem>
                              <MenuItem value="yes">Yes, already visited</MenuItem>
                              <MenuItem value="no">No visit yet</MenuItem>
                            </Select>
                          </FormControl>
                          <FormControl fullWidth>
                            <InputLabel id="wa-automation-upcoming-appointment-state-label">Upcoming Appointment</InputLabel>
                            <Select
                              labelId="wa-automation-upcoming-appointment-state-label"
                              label="Upcoming Appointment"
                              value={whatsappAutomationRuleUpcomingAppointmentState}
                              onChange={(e) => setWhatsappAutomationRuleUpcomingAppointmentState(String(e.target.value || 'any') as 'any' | 'yes' | 'no')}
                            >
                              <MenuItem value="any">Any</MenuItem>
                              <MenuItem value="yes">Yes, scheduled</MenuItem>
                              <MenuItem value="no">No upcoming appointment</MenuItem>
                            </Select>
                          </FormControl>
                        </>
                      ) : null}
                      <TextField
                        label="Cooldown"
                        value={whatsappAutomationRuleCooldownAmount}
                        onChange={(e) => setWhatsappAutomationRuleCooldownAmount(e.target.value)}
                        fullWidth
                      />
                      <FormControl fullWidth>
                        <InputLabel id="wa-automation-cooldown-unit-label">Cooldown Unit</InputLabel>
                        <Select
                          labelId="wa-automation-cooldown-unit-label"
                          label="Cooldown Unit"
                          value={whatsappAutomationRuleCooldownUnit}
                          onChange={(e) => setWhatsappAutomationRuleCooldownUnit(String(e.target.value || 'hours') as 'minutes' | 'hours' | 'days')}
                        >
                          <MenuItem value="minutes">Minutes</MenuItem>
                          <MenuItem value="hours">Hours</MenuItem>
                          <MenuItem value="days">Days</MenuItem>
                        </Select>
                      </FormControl>
                    </Box>
                  ) : null}
                  <Alert severity="info" sx={{ py: 0.5 }}>
                    Active matching steps are queued independently. Use lower priority numbers only for display/order.
                  </Alert>
                  {whatsappAutomationRuleSaveError ? (
                    <Alert severity="error">{whatsappAutomationRuleSaveError}</Alert>
                  ) : null}
                </Stack>
              </DialogContent>
              <DialogActions>
                <Button
                  onClick={() => setWhatsappAutomationRuleDialogOpen(false)}
                  disabled={whatsappAutomationRuleSaving}
                >
                  Cancel
                </Button>
                <Button
                  variant="contained"
                  onClick={saveWhatsappAutomationRule}
                  disabled={whatsappAutomationRuleSaving}
                >
                  {whatsappAutomationRuleSaving ? 'Saving…' : 'Save'}
                </Button>
              </DialogActions>
            </Dialog>

            <Dialog
              open={whatsappQuickReplyDialogOpen}
              onClose={() => {
                if (whatsappQuickReplySaving) return;
                setWhatsappQuickReplyDialogOpen(false);
              }}
              fullWidth
              maxWidth="sm"
            >
              <DialogTitle>{editingWhatsappQuickReplyId ? 'Edit Quick Reply' : 'New Quick Reply'}</DialogTitle>
              <DialogContent>
                <Stack spacing={1.5} sx={{ mt: 1 }}>
                  <TextField
                    label="Reply Label"
                    value={whatsappQuickReplyLabel}
                    onChange={(e) => setWhatsappQuickReplyLabel(e.target.value)}
                    fullWidth
                  />
                  <TextField
                    label="Message Text"
                    value={whatsappQuickReplyText}
                    onChange={(e) => setWhatsappQuickReplyText(e.target.value)}
                    fullWidth
                    multiline
                    minRows={4}
                  />
                  <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.25}>
                    <TextField
                      label="Category"
                      value={whatsappQuickReplyCategory}
                      onChange={(e) => setWhatsappQuickReplyCategory(e.target.value)}
                      fullWidth
                      placeholder="General, Appointment, Pricing..."
                    />
                    <TextField
                      label="Sort Order"
                      type="number"
                      value={whatsappQuickReplySortOrder}
                      onChange={(e) => setWhatsappQuickReplySortOrder(e.target.value)}
                      sx={{ width: { xs: '100%', sm: 140 } }}
                    />
                  </Stack>
                  <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.25}>
                    <FormControl fullWidth>
                      <InputLabel id="wa-quick-reply-scope-label">Scope</InputLabel>
                      <Select
                        labelId="wa-quick-reply-scope-label"
                        value={whatsappQuickReplyScope}
                        label="Scope"
                        onChange={(e) => setWhatsappQuickReplyScope(String(e.target.value || 'global') as 'global' | 'project')}
                      >
                        <MenuItem value="global">Global</MenuItem>
                        <MenuItem value="project">Project-specific</MenuItem>
                      </Select>
                    </FormControl>
                    {whatsappQuickReplyScope === 'project' ? (
                      <FormControl fullWidth>
                        <InputLabel id="wa-quick-reply-project-label">Project</InputLabel>
                        <Select
                          labelId="wa-quick-reply-project-label"
                          value={whatsappQuickReplyProjectId}
                          label="Project"
                          onChange={(e) => setWhatsappQuickReplyProjectId(String(e.target.value || ''))}
                        >
                          {projects
                            .slice()
                            .sort((a: any, b: any) => String(a?.name || '').localeCompare(String(b?.name || '')))
                            .map((project: any) => (
                              <MenuItem key={String(project.id)} value={String(project.id)}>
                                {String(project.name || project.id)}
                              </MenuItem>
                            ))}
                        </Select>
                      </FormControl>
                    ) : null}
                  </Stack>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={whatsappQuickReplyActive}
                        onChange={(e) => setWhatsappQuickReplyActive(e.target.checked)}
                      />
                    }
                    label="Active"
                  />
                  {whatsappQuickReplySaveError ? <Alert severity="error">{whatsappQuickReplySaveError}</Alert> : null}
                </Stack>
              </DialogContent>
              <DialogActions>
                <Button onClick={() => setWhatsappQuickReplyDialogOpen(false)} disabled={whatsappQuickReplySaving}>Cancel</Button>
                <Button variant="contained" onClick={saveWhatsappQuickReply} disabled={whatsappQuickReplySaving}>
                  {whatsappQuickReplySaving ? 'Saving…' : 'Save'}
                </Button>
              </DialogActions>
            </Dialog>
            </>
            ) : null}

          </Box>
        )}

        {/* Lead Status Management */}
        {tab === 2 && (
          <Box sx={{ p: 2 }}>
            <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
              <Typography variant="h6">Lead Statuses</Typography>
              <Button
                startIcon={<AddIcon />}
                variant="contained"
                onClick={() => {
                  resetStatusForm();
                  setStatusDrawerOpen(true);
                }}
                disabled={!canAction('settings','addProject')}
              >
                Add Status
              </Button>
            </Stack>
            {statusError && <Alert severity="error" sx={{ mb: 2 }}>{statusError}</Alert>}
            <TableContainer component={Paper} variant="outlined">
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Order</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Allowed Next</TableCell>
                    <TableCell>Prompts</TableCell>
                    {canAction('settings','addProject') && <TableCell align="right">Actions</TableCell>}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {statuses.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={5} align="center">No statuses yet.</TableCell>
                    </TableRow>
                  )}
                  {statuses.map((s) => (
                    <TableRow key={s.id}>
                      <TableCell>{s.order}</TableCell>
                      <TableCell>
                        <Stack direction="row" spacing={1} alignItems="center">
                          <Box
                            sx={{
                              width: 12,
                              height: 12,
                              borderRadius: '50%',
                              bgcolor: typeof s.color === 'string' && /^#([0-9a-fA-F]{6})$/.test(s.color) ? s.color : 'transparent',
                              border: '1px solid',
                              borderColor: 'divider',
                            }}
                          />
                          <Typography variant="body2">{s.label}</Typography>
                        </Stack>
                      </TableCell>
                      <TableCell>
                        <Stack direction="row" gap={0.5} flexWrap="wrap">
                          {(s.allowedNext || []).map((n: string) => (
                            <Chip key={n} label={statuses.find((x) => x.id === n)?.label || n} size="small" />
                          ))}
                        </Stack>
                      </TableCell>
                      <TableCell>
                        <Stack direction="row" gap={0.5} flexWrap="wrap">
                          {(s.prompts || []).map((p: any) => (
                            <Chip key={p.id} label={`${p.label} (${p.type})`} size="small" />
                          ))}
                        </Stack>
                      </TableCell>
                      {canAction('settings','addProject') && (
                        <TableCell align="right">
                          <Tooltip title="Edit">
                            <IconButton size="small" onClick={() => {
                              setEditingStatusId(s.id);
                              setStatusLabel(s.label || '');
                              setStatusOrder(s.order ?? 0);
                              setStatusColor(typeof s.color === 'string' ? s.color : '');
                              setStatusAllowedNext(s.allowedNext || []);
                              setStatusPrompts(s.prompts || []);
                              setStatusDrawerOpen(true);
                            }}>
                              <EditIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Delete">
                            <IconButton size="small" color="error" onClick={() => handleDeleteStatus(s.id, s.label)}>
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </TableCell>
                      )}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
        )}

        {/* Meta Leads */}
        {tab === 3 && (
          <Box sx={{ p: 2 }}>
            <Stack
              direction="row"
              spacing={1}
              flexWrap="wrap"
              useFlexGap
              sx={{
                position: 'sticky',
                top: 8,
                zIndex: 2,
                bgcolor: 'background.paper',
                py: 1,
                mb: 2,
                borderBottom: '1px solid',
                borderColor: 'divider',
              }}
            >
              <Chip label="Meta Config" clickable onClick={() => setMetaSettingsSection('config')} color={metaSettingsSection === 'config' ? 'primary' : 'default'} variant={metaSettingsSection === 'config' ? 'filled' : 'outlined'} />
              <Chip label="Meta Pages" clickable onClick={() => setMetaSettingsSection('pages')} color={metaSettingsSection === 'pages' ? 'primary' : 'default'} variant={metaSettingsSection === 'pages' ? 'filled' : 'outlined'} />
              <Chip label="Form Mapping" clickable onClick={() => setMetaSettingsSection('mapping')} color={metaSettingsSection === 'mapping' ? 'primary' : 'default'} variant={metaSettingsSection === 'mapping' ? 'filled' : 'outlined'} />
            </Stack>
            {metaSettingsSection === 'config' ? (
            <>
            <Stack id="settings-meta-config" direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
              <Typography variant="h6">Meta Lead Ads</Typography>
              <Stack direction="row" spacing={1}>
                <Button variant="outlined" onClick={refreshMetaSecretStatus}>
                  Refresh Status
                </Button>
                <Button variant="contained" onClick={saveMetaConfig} disabled={metaConfigSaving}>
                  {metaConfigSaving ? 'Saving...' : 'Save'}
                </Button>
              </Stack>
            </Stack>

            {metaConfigError && <Alert severity="error" sx={{ mb: 2 }}>{metaConfigError}</Alert>}

            <Paper id="settings-meta-pages" variant="outlined" sx={{ p: 2, mb: 2 }}>
              <Stack spacing={2}>
                <FormControlLabel
                  control={<Switch checked={metaEnabled} onChange={(e) => setMetaEnabled(e.target.checked)} />}
                  label="Enable Meta Lead import (Facebook/Instagram instant forms)"
                />
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                  <TextField
                    label="Default Source"
                    value={metaDefaultSource}
                    onChange={(e) => setMetaDefaultSource(e.target.value)}
                    fullWidth
                  />
                  <TextField
                    label="Default Status"
                    value={metaDefaultStatus}
                    onChange={(e) => setMetaDefaultStatus(e.target.value)}
                    fullWidth
                  />
                </Stack>

                <Box>
                  <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 0.75, fontWeight: 700 }}>
                    Webhook URL
                  </Typography>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <TextField value={metaWebhookUrl || '—'} fullWidth InputProps={{ readOnly: true }} />
                    <IconButton
                      onClick={() => metaWebhookUrl && navigator.clipboard?.writeText(metaWebhookUrl)}
                      disabled={!metaWebhookUrl}
                      aria-label="Copy Webhook URL"
                    >
                      <ContentCopyOutlinedIcon />
                    </IconButton>
                  </Stack>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                    Secrets are stored in Firebase Functions Secrets (not Firestore). Use CLI to set them, then “Refresh Status”.
                  </Typography>
                </Box>

                <Box>
                  <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 0.75, fontWeight: 700 }}>
                    Secrets Status
                  </Typography>
                  {metaSecretsStatus ? (
                    <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                      <Chip label={`Verify Token: ${metaSecretsStatus.hasVerifyToken ? 'Set' : 'Missing'}`} color={metaSecretsStatus.hasVerifyToken ? 'success' : 'default'} />
                      <Chip label={`Page Token: ${metaSecretsStatus.hasPageToken ? 'Set' : 'Missing'}`} color={metaSecretsStatus.hasPageToken ? 'success' : 'default'} />
                      <Chip label={`System Token: ${metaSecretsStatus.hasSystemUserToken ? 'Set' : 'Missing'}`} color={metaSecretsStatus.hasSystemUserToken ? 'success' : 'default'} />
                      <Chip label={`App Secret: ${metaSecretsStatus.hasAppSecret ? 'Set' : 'Missing'}`} color={metaSecretsStatus.hasAppSecret ? 'success' : 'default'} />
                    </Stack>
                  ) : (
                    <Typography variant="body2" color="text.secondary">
                      Unknown (click “Refresh Status”).
                    </Typography>
                  )}
                </Box>
              </Stack>
            </Paper>
            </>
            ) : null}

            {metaSettingsSection === 'pages' ? (
            <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
              <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>Meta Pages (allowlist)</Typography>
                <Button startIcon={<AddIcon />} variant="contained" onClick={openNewMetaPageDrawer}>
                  Add Page
                </Button>
              </Stack>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                For multiple Pages, set a long-lived System User token via Firebase secret `META_SYSTEM_USER_TOKEN`.
                Add each Page ID here (active) so the webhook can accept and process it.
              </Typography>
              {metaPagesError && <Alert severity="error" sx={{ mb: 2 }}>{metaPagesError}</Alert>}
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Page ID</TableCell>
                      <TableCell>Label</TableCell>
                      <TableCell>Type</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell align="right">Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {metaPages.length === 0 && (
                      <TableRow>
                        <TableCell colSpan={5} align="center">No pages yet.</TableCell>
                      </TableRow>
                    )}
                    {metaPages.map((row) => (
                      <TableRow key={row.id}>
                        <TableCell sx={{ fontFamily: 'monospace' }}>{row.id}</TableCell>
                        <TableCell>{row.label || '—'}</TableCell>
                        <TableCell>
                          <Chip size="small" label={row.type === 'agency' ? 'Agency' : 'Own'} />
                        </TableCell>
                        <TableCell>
                          <Chip size="small" label={row.active === false ? 'Disabled' : 'Active'} />
                        </TableCell>
                        <TableCell align="right">
                          <Tooltip title="Edit">
                            <IconButton size="small" onClick={() => openEditMetaPageDrawer(row)}>
                              <EditIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Delete">
                            <IconButton size="small" color="error" onClick={() => deleteMetaPage(row.id)}>
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Paper>
            ) : null}

            {metaSettingsSection === 'mapping' ? (
            <Paper id="settings-meta-mapping" variant="outlined" sx={{ p: 2 }}>
              <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>Form → Project Mapping</Typography>
                <Button startIcon={<AddIcon />} variant="contained" onClick={openNewMetaFormDrawer}>
                  Add Mapping
                </Button>
              </Stack>
              {metaFormsError && <Alert severity="error" sx={{ mb: 2 }}>{metaFormsError}</Alert>}
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Form ID</TableCell>
                      <TableCell>Project</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell align="right">Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {metaForms.length === 0 && (
                      <TableRow>
                        <TableCell colSpan={4} align="center">No form mappings yet.</TableCell>
                      </TableRow>
                    )}
                    {metaForms.map((row) => (
                      <TableRow key={row.id}>
                        <TableCell sx={{ fontFamily: 'monospace' }}>{row.id}</TableCell>
                        <TableCell>
                          {row.projectId
                            ? (projects.find((p: any) => p.id === row.projectId)?.name || row.projectId)
                            : '—'}
                        </TableCell>
                        <TableCell>
                          <Chip size="small" label={row.active === false ? 'Disabled' : 'Active'} />
                        </TableCell>
                        <TableCell align="right">
                          <Tooltip title="Edit">
                            <IconButton size="small" onClick={() => openEditMetaFormDrawer(row)}>
                              <EditIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Delete">
                            <IconButton size="small" color="error" onClick={() => deleteMetaForm(row.id)}>
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Paper>
            ) : null}
          </Box>
        )}

        {/* Group Profile */}
        {tab === 4 && (
          <Box sx={{ p: 3, maxWidth: 640 }}>
            <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 3 }}>
              <Box>
                <Typography variant="h6" sx={{ fontWeight: 700 }}>Group Profile</Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.25 }}>
                  Branding shown on the CRM login page
                </Typography>
              </Box>
              <Button
                variant="contained"
                onClick={saveGroupProfile}
                disabled={groupProfileSaving}
              >
                {groupProfileSaving ? 'Saving…' : 'Save'}
              </Button>
            </Stack>

            {groupProfileError && (
              <Alert severity="error" sx={{ mb: 2 }} onClose={() => setGroupProfileError(null)}>
                {groupProfileError}
              </Alert>
            )}
            {groupProfileSuccess && (
              <Alert severity="success" sx={{ mb: 2 }}>
                Group profile saved successfully.
              </Alert>
            )}

            {/* Group Logo */}
            <Paper variant="outlined" sx={{ p: 2.5, mb: 2.5 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1.5 }}>
                Group Logo
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Appears in the top-left of the login page (right panel) and on the branding panel. PNG with transparent background recommended.
              </Typography>
              <Stack spacing={1.5}>
                <TextField
                  label="Logo URL (optional — upload below instead)"
                  value={groupLogoUrl}
                  onChange={(e) => setGroupLogoUrl(e.target.value)}
                  fullWidth
                  size="small"
                />
                <Stack direction="row" spacing={1.5} alignItems="center">
                  <Button
                    variant="outlined"
                    size="small"
                    component="label"
                  >
                    {groupLogoFile ? groupLogoFile.name : 'Choose file…'}
                    <input
                      type="file"
                      hidden
                      accept="image/*"
                      onChange={(e) => {
                        const f = e.target.files?.[0] ?? null;
                        setGroupLogoFile(f);
                        if (f) setGroupLogoUrl(URL.createObjectURL(f));
                      }}
                    />
                  </Button>
                  {groupLogoFile && (
                    <Typography variant="caption" color="text.secondary">
                      Will upload on Save
                    </Typography>
                  )}
                </Stack>
                {groupLogoUrl && (
                  <Box
                    component="img"
                    src={groupLogoUrl}
                    alt="Logo preview"
                    sx={{
                      maxHeight: 80,
                      maxWidth: 200,
                      objectFit: 'contain',
                      border: '1px solid',
                      borderColor: 'divider',
                      borderRadius: 1,
                      p: 1,
                      bgcolor: '#f5f5f5',
                    }}
                  />
                )}
              </Stack>
            </Paper>

            {/* Head Office Details */}
            <Paper variant="outlined" sx={{ p: 2.5, mb: 2.5 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1.5 }}>
                Head Office Details
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Used for WhatsApp location sharing in office meeting appointments.
              </Typography>
              <Stack spacing={1.5}>
                <TextField
                  label="Office Address"
                  value={groupAddress}
                  onChange={(e) => setGroupAddress(e.target.value)}
                  fullWidth
                  size="small"
                  multiline
                  rows={2}
                />
                <Stack direction="row" spacing={1.5}>
                  <TextField
                    label="Latitude"
                    value={groupLatitude}
                    onChange={(e) => setGroupLatitude(e.target.value)}
                    fullWidth
                    size="small"
                  />
                  <TextField
                    label="Longitude"
                    value={groupLongitude}
                    onChange={(e) => setGroupLongitude(e.target.value)}
                    fullWidth
                    size="small"
                  />
                </Stack>
                <Stack direction="row" spacing={1.5}>
                  <TextField
                    label="Phone"
                    value={groupPhone}
                    onChange={(e) => setGroupPhone(e.target.value)}
                    fullWidth
                    size="small"
                  />
                  <TextField
                    label="Email"
                    value={groupEmail}
                    onChange={(e) => setGroupEmail(e.target.value)}
                    fullWidth
                    size="small"
                  />
                </Stack>
              </Stack>
            </Paper>

            {/* Login Background Image */}
            <Paper variant="outlined" sx={{ p: 2.5 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1.5 }}>
                Login Background Image
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Fills the dark left panel of the login page. Landscape image works best (min 1200 × 800 px).
              </Typography>
              <Stack spacing={1.5}>
                <TextField
                  label="Background Image URL (optional — upload below instead)"
                  value={groupLoginBgUrl}
                  onChange={(e) => setGroupLoginBgUrl(e.target.value)}
                  fullWidth
                  size="small"
                />
                <Stack direction="row" spacing={1.5} alignItems="center">
                  <Button
                    variant="outlined"
                    size="small"
                    component="label"
                  >
                    {groupLoginBgFile ? groupLoginBgFile.name : 'Choose file…'}
                    <input
                      type="file"
                      hidden
                      accept="image/*"
                      onChange={(e) => {
                        const f = e.target.files?.[0] ?? null;
                        setGroupLoginBgFile(f);
                        if (f) setGroupLoginBgUrl(URL.createObjectURL(f));
                      }}
                    />
                  </Button>
                  {groupLoginBgFile && (
                    <Typography variant="caption" color="text.secondary">
                      Will upload on Save
                    </Typography>
                  )}
                </Stack>
                {groupLoginBgUrl && (
                  <Box
                    component="img"
                    src={groupLoginBgUrl}
                    alt="Background preview"
                    sx={{
                      width: '100%',
                      maxHeight: 180,
                      objectFit: 'cover',
                      objectPosition: 'center',
                      border: '1px solid',
                      borderColor: 'divider',
                      borderRadius: 1,
                    }}
                  />
                )}
              </Stack>
            </Paper>
          </Box>
        )}
      </Paper>

      <Dialog
        open={contactsBookDialogOpen}
        onClose={() => !contactsBookSaving && setContactsBookDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>{editingContactsBookId ? 'Edit Contact' : 'Add Contact'}</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField
              label="Name"
              value={contactsBookName}
              onChange={(e) => setContactsBookName(e.target.value)}
              required
              fullWidth
            />
            <TextField
              label="WhatsApp / Phone"
              value={contactsBookPhone}
              onChange={(e) => setContactsBookPhone(e.target.value)}
              fullWidth
            />
            <FormControl fullWidth>
              <InputLabel id="contacts-book-type-label">Contact Type</InputLabel>
              <Select
                labelId="contacts-book-type-label"
                label="Contact Type"
                value={contactsBookType}
                onChange={(e) => setContactsBookType(String(e.target.value || 'Other'))}
              >
                {CONTACTS_BOOK_TYPES.map((type) => (
                  <MenuItem key={type} value={type}>{type}</MenuItem>
                ))}
              </Select>
            </FormControl>
            <TextField
              label="Date of Birth"
              type="date"
              value={contactsBookDateOfBirth}
              onChange={(e) => setContactsBookDateOfBirth(e.target.value)}
              InputLabelProps={{ shrink: true }}
              fullWidth
            />
            <TextField
              label="Anniversary Date"
              type="date"
              value={contactsBookAnniversaryDate}
              onChange={(e) => setContactsBookAnniversaryDate(e.target.value)}
              InputLabelProps={{ shrink: true }}
              fullWidth
            />
            <TextField
              label="Organization / Company"
              value={contactsBookOrganization}
              onChange={(e) => setContactsBookOrganization(e.target.value)}
              fullWidth
            />
            <FormControl fullWidth>
              <InputLabel id="contacts-book-project-label">Project (optional)</InputLabel>
              <Select labelId="contacts-book-project-label" label="Project (optional)"
                value={contactsBookProjectId}
                onChange={(e) => setContactsBookProjectId(String(e.target.value || ''))}>
                <MenuItem value="">No project</MenuItem>
                {projects.map((project: any) => <MenuItem key={project.id} value={project.id}>
                  {project.name || project.title || project.id}
                </MenuItem>)}
              </Select>
            </FormControl>
            <TextField
              label="Notes"
              value={contactsBookNotes}
              onChange={(e) => setContactsBookNotes(e.target.value)}
              multiline
              minRows={3}
              fullWidth
            />
            <FormControlLabel
              control={
                <Switch
                  checked={contactsBookActive}
                  onChange={(e) => setContactsBookActive(e.target.checked)}
                />
              }
              label="Active"
            />
            {contactsBookSaveError ? <Alert severity="error">{contactsBookSaveError}</Alert> : null}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setContactsBookDialogOpen(false)} disabled={contactsBookSaving}>
            Cancel
          </Button>
          <Button onClick={saveContactsBook} variant="contained" disabled={contactsBookSaving}>
            {contactsBookSaving ? 'Saving…' : 'Save Contact'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Drawer: Add User */}
      <Drawer anchor="right" open={userDrawerOpen} onClose={() => !loading && setUserDrawerOpen(false)} PaperProps={{ sx: { width: { xs: '100%', sm: 600 }, maxWidth: '100vw', top: { xs: 56, sm: 64 }, height: { xs: 'calc(100% - 56px)', sm: 'calc(100% - 64px)' } } }}>
        <Box component="form" onSubmit={handleAddUser} noValidate sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
          <Box sx={{ px: { xs: 2, sm: 3 }, pt: 2.5, borderBottom: '1px solid #e8e4dd' }}>
            <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
              <Box><Typography variant="overline" sx={{ color: 'primary.main', fontWeight: 700, letterSpacing: '0.1em' }}>User management</Typography><Typography sx={{ fontFamily: 'Georgia, serif', fontSize: 30, lineHeight: 1.2 }}>Add team member</Typography><Typography variant="body2" color="text.secondary" sx={{ mt: 0.75 }}>Create an account and choose its reporting line and access.</Typography></Box>
              <IconButton aria-label="Close add user" onClick={() => setUserDrawerOpen(false)}><CloseIcon /></IconButton>
            </Stack>
            <Stack direction="row" spacing={2.5} sx={{ mt: 2 }}>
              <Button type="button" onClick={() => setAddUserTab('profile')} sx={{ borderRadius: 0, minHeight: 44, px: 0, borderBottom: addUserTab === 'profile' ? '3px solid' : '3px solid transparent', fontWeight: 700, color: addUserTab === 'profile' ? 'primary.main' : 'text.secondary' }}>Profile</Button>
              <Button type="button" onClick={() => setAddUserTab('access')} sx={{ borderRadius: 0, minHeight: 44, px: 0, borderBottom: addUserTab === 'access' ? '3px solid' : '3px solid transparent', fontWeight: 700, color: addUserTab === 'access' ? 'primary.main' : 'text.secondary' }}>Access & permissions</Button>
            </Stack>
          </Box>
          <Box sx={{ px: { xs: 2, sm: 3 }, py: 2.5, overflowY: 'auto', flex: 1, bgcolor: '#fcfbf9' }}>
            <Box sx={{ display: addUserTab === 'profile' ? 'block' : 'none' }}>
            <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}><Typography fontWeight={700}>Personal details</Typography><Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>Enter details for the new account.</Typography>
            <TextField margin="normal" required fullWidth label="Full Name" value={name} onChange={(e) => setName(e.target.value)} />
            <TextField margin="normal" required fullWidth label="Email Address" type="email" value={email} onChange={(e) => setEmail(e.target.value)} />
            <TextField margin="normal" required fullWidth label="Phone Number" value={phone} onChange={(e) => setPhone(e.target.value)} />
            <TextField
              margin="normal"
              fullWidth
              label="Date of Birth"
              type="date"
              value={userDateOfBirth}
              onChange={(e) => setUserDateOfBirth(e.target.value)}
              InputLabelProps={{ shrink: true }}
            />
            <TextField margin="normal" required fullWidth label="Password" type="password" value={password} onChange={(e) => setPassword(e.target.value)} />
            </Paper>
            </Box>
            <Box sx={{ display: addUserTab === 'access' ? 'block' : 'none' }}>
            <Stack spacing={2}>
            <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}><Typography fontWeight={700}>Role</Typography><Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>Choose this person’s main responsibility.</Typography>
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 1, mt: 2 }}>{roles.map((item) => <Box key={item.id} component="button" type="button" aria-pressed={role === item.id} onClick={() => setRole(item.id)} sx={{ textAlign: 'left', border: '1px solid', borderColor: role === item.id ? '#cbb48e' : '#e8e4dd', borderRadius: 1.5, bgcolor: role === item.id ? '#fbf7f0' : '#fff', minHeight: 46, px: 1.5, cursor: 'pointer', fontWeight: role === item.id ? 700 : 500, color: 'text.primary', '&:focus-visible': { outline: '3px solid #c29453', outlineOffset: 2 } }}>{role === item.id ? '◉' : '○'} &nbsp;{item.label}</Box>)}</Box>
            </Paper>

            {role === 'sales-team' && <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}><Typography fontWeight={700}>Reports to</Typography><Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>Choose the Sales Manager responsible for this person.</Typography><FormControl fullWidth><InputLabel id="new-reports-to-label">Reporting manager</InputLabel><Select labelId="new-reports-to-label" label="Reporting manager" value={newUserReportsToId} onChange={(event) => setNewUserReportsToId(String(event.target.value))}><MenuItem value="">No manager assigned</MenuItem>{reportingUsers.filter((user) => (user.roleId || user.role) === 'sales-manager').sort((a, b) => a.name.localeCompare(b.name)).map((manager) => <MenuItem key={manager.id} value={manager.id}>{manager.name} · {manager.email}</MenuItem>)}</Select></FormControl></Paper>}
            {role === 'sales-manager' && <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}><Typography fontWeight={700}>Reporting team</Typography><Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>Select Sales Team members who will report to this manager.</Typography><Stack spacing={1}>{reportingUsers.filter((user) => (user.roleId || user.role) === 'sales-team').sort((a, b) => a.name.localeCompare(b.name)).map((member) => <Box component="label" key={member.id} sx={{ display: 'flex', alignItems: 'center', border: '1px solid #e8e4dd', borderRadius: 1.5, p: 1, cursor: 'pointer' }}><Checkbox checked={newManagerDirectReportIds.includes(member.id)} onChange={(event) => setNewManagerDirectReportIds((previous) => event.target.checked ? [...previous, member.id] : previous.filter((id) => id !== member.id))} /><Box><Typography variant="body2" fontWeight={700}>{member.name}</Typography><Typography variant="caption" color="text.secondary">{member.email}</Typography></Box></Box>)}{!reportingUsers.some((user) => (user.roleId || user.role) === 'sales-team') && <Typography variant="body2" color="text.secondary">No Sales Team members yet.</Typography>}</Stack></Paper>}

            <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}><Typography fontWeight={700}>Projects</Typography><Typography variant="body2" color="text.secondary">Choose which projects this person can work on.</Typography>
            <FormControl fullWidth margin="normal">
              <InputLabel id="project-access-label">Projects</InputLabel>
              <Select
                labelId="project-access-label"
                multiple
                value={selectedProjectIds}
                label="Projects"
                onChange={(e) => setSelectedProjectIds(e.target.value as string[])}
                renderValue={(selected) =>
                  (selected as string[])
                    .map((id) => projects.find((p: any) => p.id === id)?.name || id)
                    .join(', ')
                }
              >
                {projects.length === 0 && (
                  <MenuItem disabled>
                    <ListItemText primary="No projects yet. Add projects from the Projects tab." />
                  </MenuItem>
                )}
                {projects
                  .slice()
                  .sort((a: any, b: any) => String(a?.name || '').localeCompare(String(b?.name || '')))
                  .map((p: any) => (
                    <MenuItem key={p.id} value={p.id}>
                      <Checkbox checked={selectedProjectIds.indexOf(p.id) > -1} />
                      <ListItemText primary={p.name} secondary={p.location || undefined} />
                    </MenuItem>
                  ))}
              </Select>
            </FormControl>
            </Paper>

            <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}><Typography fontWeight={700}>Pages & actions</Typography><Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>Select a page, then choose its actions in the same place.</Typography><Stack spacing={1}>{allPages.filter((page) => page.key !== 'settings').map((page) => { const selected = selectedPages.includes(page.key); const actions = pageActions[page.key] || []; return <Box key={page.key} sx={{ border: '1px solid', borderColor: selected ? '#cbb48e' : '#e8e4dd', borderRadius: 1.5, bgcolor: selected ? '#fffdfa' : '#fff', overflow: 'hidden' }}><Box component="label" sx={{ display: 'flex', alignItems: 'center', px: 1.25, minHeight: 58, cursor: 'pointer' }}><Checkbox checked={selected} onChange={(event) => { const next = event.target.checked ? [...selectedPages, page.key] : selectedPages.filter((key) => key !== page.key); setSelectedPages(next); const allowed = new Set(next.flatMap((key) => (pageActions[key] || []).map((action) => toDomainAction(key, action.key)))); setSelectedActions((previous) => previous.filter((value) => allowed.has(value))); }} size="small" /><Box sx={{ flex: 1 }}><Typography variant="body2" fontWeight={700}>{page.label}</Typography><Typography variant="caption" color="text.secondary">{actions.length ? `${actions.length} available actions` : 'View access'}</Typography></Box>{selected && <Chip label={actions.length ? `${actions.filter((action) => selectedActions.includes(toDomainAction(page.key, action.key))).length} selected` : 'View'} size="small" sx={{ bgcolor: '#f6f0e7', color: '#805421' }} />}</Box>{selected && actions.length > 0 && <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 0.5, px: 2, pb: 1.5, pt: 0.75, borderTop: '1px solid #e8e4dd' }}>{actions.map((action) => { const value = toDomainAction(page.key, action.key); return <Box component="label" key={`${page.key}-${action.key}`} sx={{ display: 'flex', alignItems: 'center', minHeight: 38, cursor: 'pointer' }}><Checkbox size="small" checked={selectedActions.includes(value)} onChange={(event) => setSelectedActions((previous) => event.target.checked ? Array.from(new Set([...previous, value])) : previous.filter((item) => item !== value))} /><Typography variant="body2">{action.label === 'Assign Change' ? 'Change assignment' : action.label}</Typography></Box>; })}</Box>}</Box>; })}</Stack></Paper>
            </Stack>
            </Box>

            {error && <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>}
            {success && <Alert severity="success" sx={{ mt: 2 }}>{success}</Alert>}

          </Box>
          <Stack direction="row" justifyContent="flex-end" spacing={1} sx={{ px: { xs: 2, sm: 3 }, py: 1.5, borderTop: '1px solid #e8e4dd', bgcolor: '#fff' }}><Button type="button" onClick={() => setUserDrawerOpen(false)} disabled={loading}>Cancel</Button><Button type="submit" variant="contained" disabled={loading}>{loading ? 'Adding user…' : 'Add user'}</Button></Stack>
        </Box>
      </Drawer>

      {/* Dialog: Add Project */}
      <Dialog
        open={projectDrawerOpen}
        onClose={() => setProjectDrawerOpen(false)}
        fullWidth
        maxWidth="lg"
        fullScreen={isProjectDialogMobile}
      >
        <DialogTitle sx={{ pr: 7 }}>
          {editingProjectId ? 'Edit Project' : 'Add Project'}
          <IconButton
            onClick={() => setProjectDrawerOpen(false)}
            sx={{ position: 'absolute', right: 8, top: 8 }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers sx={{ p: { xs: 1.5, md: 2.5 } }}>
          <Box component="form" id="project-form" onSubmit={handleCreateProject} noValidate>
            <Paper variant="outlined" sx={{ p: { xs: 1.25, md: 1.5 }, borderRadius: 1.5, mb: 2 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 800, mb: 1 }}>
                Basic Details
              </Typography>
              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
                  gap: 1.25,
                }}
              >
                <TextField
                  required
                  fullWidth
                  label="Project Name"
                  value={projectName}
                  onChange={(e) => setProjectName(e.target.value)}
                />
                <TextField
                  required
                  fullWidth
                  label="Location"
                  value={projectLocation}
                  onChange={(e) => setProjectLocation(e.target.value)}
                />
                <TextField
                  fullWidth
                  label="WhatsApp Subtitle"
                  value={projectWhatsappSubtitle}
                  onChange={(e) => setProjectWhatsappSubtitle(e.target.value)}
                  placeholder="Ready Possession"
                  helperText="Shown under the project name in WhatsApp project selection."
                />
                <TextField
                  fullWidth
                  label="Address"
                  value={projectAddress}
                  onChange={(e) => setProjectAddress(e.target.value)}
                  helperText="Full address for WhatsApp location sharing"
                />
                <TextField
                  fullWidth
                  label="Website Slug"
                  value={projectWebsiteSlug}
                  onChange={(e) => setProjectWebsiteSlug(e.target.value)}
                  placeholder="swarna-lagom"
                  helperText="Used for WhatsApp project detail button redirects."
                />
                <TextField
                  fullWidth
                  label="Brochure PDF URL"
                  value={projectBrochureUrl}
                  onChange={(e) => setProjectBrochureUrl(e.target.value)}
                  helperText="Used for WhatsApp document-header brochure templates."
                />
                <Stack direction="row" spacing={1.25}>
                  <TextField
                    fullWidth
                    label="Latitude"
                    value={projectLatitude}
                    onChange={(e) => setProjectLatitude(e.target.value)}
                  />
                  <TextField
                    fullWidth
                    label="Longitude"
                    value={projectLongitude}
                    onChange={(e) => setProjectLongitude(e.target.value)}
                  />
                </Stack>
              </Box>
            </Paper>

            <Paper variant="outlined" sx={{ p: { xs: 1.25, md: 1.5 }, borderRadius: 1.5, mb: 2 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 800, mb: 1 }}>
                Branding & Images
              </Typography>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.25} alignItems={{ sm: 'flex-start' }}>
              <TextField
                margin="normal"
                fullWidth
                label="Background Image URL"
                value={projectBackgroundImageUrl}
                onChange={(e) => setProjectBackgroundImageUrl(e.target.value)}
                helperText="This brand image is used as default document background for this project."
              />
              <Button variant="outlined" component="label" sx={{ mt: { xs: 0, sm: 2 }, minWidth: { sm: 220 }, whiteSpace: 'nowrap' }}>
                {projectBackgroundImageFile ? `Background: ${projectBackgroundImageFile.name}` : 'Upload Background Image'}
                <input
                  hidden
                  type="file"
                  accept="image/*"
                  onChange={(e) => {
                    const file = e.target.files?.[0];
                    setProjectBackgroundImageFile(file || null);
                  }}
                />
              </Button>
            </Stack>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.25} alignItems={{ sm: 'flex-start' }}>
              <TextField
                margin="normal"
                fullWidth
                label="Document Image URL (Project-wise)"
                value={projectDocumentImageUrl}
                onChange={(e) => setProjectDocumentImageUrl(e.target.value)}
                helperText="Use in template HTML with {{project_document_image_url}}."
              />
              <Button variant="outlined" component="label" sx={{ mt: { xs: 0, sm: 2 }, minWidth: { sm: 220 }, whiteSpace: 'nowrap' }}>
                {projectDocumentImageFile ? `Document Image: ${projectDocumentImageFile.name}` : 'Upload Document Image'}
                <input
                  hidden
                  type="file"
                  accept="image/*"
                  onChange={(e) => {
                    const file = e.target.files?.[0];
                    setProjectDocumentImageFile(file || null);
                  }}
                />
              </Button>
            </Stack>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.25} alignItems={{ sm: 'flex-start' }}>
              <TextField
                margin="normal"
                fullWidth
                label="Logo URL (Project-wise)"
                value={projectLogoUrl}
                onChange={(e) => setProjectLogoUrl(e.target.value)}
                helperText="Used in template token {{project_logo_url}}."
              />
              <Button variant="outlined" component="label" sx={{ mt: { xs: 0, sm: 2 }, minWidth: { sm: 220 }, whiteSpace: 'nowrap' }}>
                {logoFile ? `Logo: ${logoFile.name}` : 'Upload Logo'}
                <input
                  hidden
                  type="file"
                  accept="image/*"
                  onChange={(e) => {
                    const file = e.target.files?.[0];
                    setLogoFile(file || null);
                  }}
                />
              </Button>
            </Stack>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.25} alignItems={{ sm: 'flex-start' }}>
              <TextField
                margin="normal"
                fullWidth
                label="B/W Logo URL (Project-wise)"
                value={projectLogoBwUrl}
                onChange={(e) => setProjectLogoBwUrl(e.target.value)}
                helperText="Used when a document template enables B/W logo."
              />
              <Button variant="outlined" component="label" sx={{ mt: { xs: 0, sm: 2 }, minWidth: { sm: 220 }, whiteSpace: 'nowrap' }}>
                {projectLogoBwFile ? `B/W Logo: ${projectLogoBwFile.name}` : 'Upload B/W Logo'}
                <input
                  hidden
                  type="file"
                  accept="image/*"
                  onChange={(e) => {
                    const file = e.target.files?.[0];
                    setProjectLogoBwFile(file || null);
                  }}
                />
              </Button>
            </Stack>
            </Paper>

            <Paper variant="outlined" sx={{ p: { xs: 1.25, md: 1.5 }, borderRadius: 1.5, mb: 2 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 800, mb: 1 }}>
              Financial Defaults
            </Typography>
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: { xs: '1fr', md: '1fr 1fr 1fr' },
                gap: 1.25,
              }}
            >
              <TextField
                margin="normal"
                fullWidth
                label="Advocate Agreement Fee"
                type="number"
                inputProps={{ min: 0, step: '0.01' }}
                value={projectAdvocateAgreementFee}
                onChange={(e) => setProjectAdvocateAgreementFee(e.target.value)}
                helperText="Project-level fee for agreement."
              />
              <TextField
                margin="normal"
                fullWidth
                label="Advocate Deed Fee"
                type="number"
                inputProps={{ min: 0, step: '0.01' }}
                value={projectAdvocateDeedFee}
                onChange={(e) => setProjectAdvocateDeedFee(e.target.value)}
                helperText="Project-level fee for deed."
              />
              <TextField
                margin="normal"
                fullWidth
                label="SB Area %"
                type="number"
                inputProps={{ min: 0, step: '0.01' }}
                value={projectSbAreaPercent}
                onChange={(e) => setProjectSbAreaPercent(e.target.value)}
                helperText="Default SB %. Use SB % button for usage-wise mapping."
              />
            </Box>
            </Paper>

            <Paper variant="outlined" sx={{ p: { xs: 1.25, md: 1.5 }, borderRadius: 1.5, mb: 2 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 800, mb: 1 }}>
              Configuration
            </Typography>
            <TextField
              margin="normal"
              fullWidth
              label="BUC received date"
              type="date"
              InputLabelProps={{ shrink: true }}
              value={projectBucReceivedAt}
              onChange={(e) => setProjectBucReceivedAt(e.target.value)}
              helperText="Leave blank until SMC Building Use Certificate is received."
            />
            <TextField
              margin="normal"
              fullWidth
              label="BUC covered building numbers"
              value={projectBucBuildings}
              onChange={(e) => setProjectBucBuildings(e.target.value)}
              helperText="Comma-separated building numbers; leave blank if the BUC covers the entire project."
            />
            <FormControl fullWidth margin="normal" required>
              <InputLabel id="project-company-profile-label">Company Profile</InputLabel>
              <Select
                labelId="project-company-profile-label"
                label="Company Profile"
                value={projectCompanyProfileId}
                onChange={(e) => setProjectCompanyProfileId(String(e.target.value || ''))}
              >
                {companyProfiles.length === 0 ? (
                  <MenuItem disabled value="">
                    No company profiles. Create one in Company Profiles section.
                  </MenuItem>
                ) : (
                  companyProfiles.map((c: any) => (
                    <MenuItem key={String(c.id)} value={String(c.id)}>
                      {String(c.name || c.id)}
                    </MenuItem>
                  ))
                )}
              </Select>
            </FormControl>
            <Autocomplete
              multiple
              freeSolo
              options={availableUnitTypes}
              value={unitTypes}
              filterSelectedOptions
              onChange={(_, next) => {
                const cleaned = Array.from(
                  new Set((next as string[]).map((value) => String(value || '').trim()).filter(Boolean))
                );
                setUnitTypes(cleaned);
              }}
              renderTags={(value: readonly string[], getTagProps) =>
                value.map((option: string, index: number) => <Chip label={option} {...getTagProps({ index })} />)
              }
              renderInput={(params) => (
                <TextField
                  {...params}
                  margin="normal"
                  fullWidth
                  label="Unit Types"
                  placeholder="Search or add unit type"
                  helperText="Type and press Enter to add new, or select from existing list."
                />
              )}
            />
            </Paper>

            {editingProjectId && (
              <Box sx={{ mt: 3 }}>
                <Divider sx={{ mb: 2 }} />
                <Typography variant="subtitle2" sx={{ fontWeight: 800, mb: 1 }}>
                  Project Operations
                </Typography>
                <Typography variant="subtitle1" sx={{ fontWeight: 800 }}>
                  Office Workflow
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                  Applies the standard unit booking + loan workflow templates to this project. You can later customize steps per project.
                </Typography>
                <Button
                  variant="outlined"
                  size="small"
                  sx={{ mt: 1, alignSelf: 'flex-start' }}
                  onClick={applyStandardOfficeWorkflowsToProject}
                  disabled={officeWorkflowApplying}
                >
                  {officeWorkflowApplying ? 'Applying…' : 'Apply Standard Workflows'}
                </Button>
                {officeWorkflowApplyError ? (
                  <Alert severity="error" sx={{ mt: 1 }}>
                    {officeWorkflowApplyError}
                  </Alert>
                ) : null}
                {officeWorkflowApplySuccess ? (
                  <Alert severity="success" sx={{ mt: 1 }}>
                    {officeWorkflowApplySuccess}
                  </Alert>
                ) : null}

                <Divider sx={{ mb: 2 }} />
                <Typography variant="subtitle1" sx={{ fontWeight: 800 }}>
                  Import Units (CSV)
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                  This will replace all units for this project. Unit Number is taken from column{' '}
                  <strong>FLAT/BUNGLOW/OFFICE</strong>.
                </Typography>

                <Button
                  variant="text"
                  size="small"
                  startIcon={<ContentCopyOutlinedIcon />}
                  sx={{ mt: 0.5, alignSelf: 'flex-start' }}
                  onClick={downloadUnitsSampleCsv}
                >
                  Download sample CSV
                </Button>
                <Button
                  variant="text"
                  size="small"
                  sx={{ mt: 0.5, alignSelf: 'flex-start' }}
                  onClick={() => exportExistingUnitsCsv()}
                  disabled={unitsImporting || !editingProjectId}
                >
                  Export Existing Units CSV
                </Button>

                {(projectUnitsCount > 0 || !!unitsImportSuccess) && (
                  <Button
                    variant="outlined"
                    size="small"
                    sx={{ mt: 1, alignSelf: 'flex-start' }}
                    onClick={() => {
                      setUnitsProjectId(editingProjectId);
                      setUnitsProjectName(projectName.trim() || '');
                      setUnitsDialogOpen(true);
                    }}
                    disabled={unitsImporting}
                  >
                    View Unit List{projectUnitsCount > 0 ? ` (${projectUnitsCount})` : ''}
                  </Button>
                )}

                <Button
                  variant="outlined"
                  fullWidth
                  component="label"
                  sx={{ mt: 1.5 }}
                  disabled={unitsImporting}
                >
                  {unitsCsvFile ? `CSV: ${unitsCsvFile.name}` : 'Choose Units CSV'}
                  <input
                    hidden
                    type="file"
                    accept=".csv,.txt"
                    onChange={async (e) => {
                      const f = e.target.files?.[0] || null;
                      setUnitsCsvFile(f);
                      if (f) await parseUnitsFile(f);
                    }}
                  />
                </Button>

                <FormControlLabel
                  sx={{ mt: 1 }}
                  control={
                    <Switch
                      checked={unitsIncludeExtraColumns}
                      onChange={(e) => setUnitsIncludeExtraColumns(e.target.checked)}
                    />
                  }
                  label="Import unknown CSV columns as custom unit fields"
                />

                {unitsExtraColumns.length > 0 ? (
                  <Alert severity="info" sx={{ mt: 1 }}>
                    <Typography sx={{ fontWeight: 800, mb: 0.5 }}>Detected extra columns</Typography>
                    <Typography variant="body2">
                      {unitsExtraColumns.join(', ')}
                    </Typography>
                  </Alert>
                ) : null}

                {unitsParseWarnings.length > 0 && (
                  <Alert severity="warning" sx={{ mt: 2 }}>
                    <Typography sx={{ fontWeight: 800, mb: 0.5 }}>Warnings</Typography>
                    {unitsParseWarnings.slice(0, 6).map((w) => (
                      <Typography key={w} variant="body2">
                        {w}
                      </Typography>
                    ))}
                  </Alert>
                )}

                {unitsParsed.length > 0 && (
                  <Box sx={{ mt: 2 }}>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                      Parsed units: <strong>{unitsParsed.length}</strong>
                    </Typography>
	                    <TableContainer component={Paper} variant="outlined">
	                      <Table size="small">
	                        <TableHead>
	                          <TableRow>
	                            <TableCell>Unit No</TableCell>
	                            <TableCell>Block</TableCell>
	                            <TableCell align="right">Amount</TableCell>
	                            <TableCell align="right">Carpet Area</TableCell>
	                            <TableCell align="right">Excl. Balcony</TableCell>
	                            <TableCell>Usage</TableCell>
	                          </TableRow>
	                        </TableHead>
	                        <TableBody>
	                          {unitsParsed.slice(0, 10).map((r) => (
	                            <TableRow key={r.unitNumberLower}>
	                              <TableCell>{r.unitNumber}</TableCell>
	                              <TableCell>{r.blockType || '—'}</TableCell>
	                              <TableCell align="right">{r.unitAmount ?? '—'}</TableCell>
	                              <TableCell align="right">{r.carpetArea ?? '—'}</TableCell>
	                              <TableCell align="right">{r.exclusiveBalconyArea ?? '—'}</TableCell>
	                              <TableCell>{r.usage || '—'}</TableCell>
	                            </TableRow>
	                          ))}
	                        </TableBody>
	                      </Table>
	                    </TableContainer>
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.75 }}>
                      Preview shows first 10 rows.
                    </Typography>
                  </Box>
                )}

                {unitsImportSuccess && <Alert severity="success" sx={{ mt: 2 }}>{unitsImportSuccess}</Alert>}
                {unitsParseError && <Alert severity="error" sx={{ mt: 2 }}>{unitsParseError}</Alert>}

                <Button
                  variant="contained"
                  fullWidth
                  sx={{ mt: 2 }}
                  disabled={unitsImporting || unitsParsed.length === 0}
                  onClick={handleUpdateExistingUnitsOnly}
                >
                  {unitsImporting ? 'Importing…' : 'Update Existing Units (No Replace)'}
                </Button>

                <Button
                  variant="contained"
                  color="error"
                  fullWidth
                  sx={{ mt: 2 }}
                  disabled={unitsImporting || unitsParsed.length === 0}
                  onClick={handleReplaceAllUnits}
                >
                  {unitsImporting ? 'Importing…' : 'Replace All Units'}
                </Button>
	              </Box>
	            )}

		            <Box sx={{ mt: 3 }}>
		              <Divider sx={{ mb: 2 }} />
                  <Typography variant="subtitle2" sx={{ fontWeight: 800, mb: 1 }}>
                    Bank Assignment
                  </Typography>
		              <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
		                <Typography variant="subtitle1" sx={{ fontWeight: 800 }}>
		                  Banks
		                </Typography>
		                  <Button size="small" startIcon={<AddIcon />} onClick={() => openNewBankDrawer(editingProjectId)}>
		                    New
		                  </Button>
		                </Stack>

		              {!editingProjectId ? (
		                <Alert severity="info" sx={{ mt: 1 }}>
		                  Save this project first, then you can assign banks to it.
		                </Alert>
		              ) : null}

		              {editingProjectId ? (
		                <>
		                  {bankAccountsLoading ? (
		                    <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 1 }}>
		                      <CircularProgress size={18} />
		                      <Typography variant="body2">Loading banks…</Typography>
		                    </Stack>
		                  ) : null}
		                  {bankAccountsError ? <Alert severity="error" sx={{ mt: 1 }}>{bankAccountsError}</Alert> : null}

		                  {bankAccountsAssigned.length === 0 && !bankAccountsLoading ? (
		                    <Alert severity="info" sx={{ mt: 1 }}>
		                      No banks assigned to this project yet.
		                    </Alert>
		                  ) : (
		                    <Stack spacing={1} sx={{ mt: 1 }}>
		                      {bankAccountsAssigned.map((b: any) => (
		                        <Paper key={b.id} variant="outlined" sx={{ p: 1.25 }}>
		                          <Stack direction="row" spacing={1} alignItems="center" justifyContent="space-between">
		                            <Box sx={{ minWidth: 0 }}>
		                              <Typography variant="body2" sx={{ fontWeight: 900 }} noWrap>
		                                {String(b.name || 'Bank')}
		                              </Typography>
		                              <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }} noWrap>
		                                {[
		                                  b.bankName ? String(b.bankName) : null,
		                                  b.accountLast4 ? `****${String(b.accountLast4)}` : null,
		                                  b.ifsc ? String(b.ifsc) : null,
		                                ]
		                                  .filter(Boolean)
		                                  .join(' • ') || '—'}
		                              </Typography>
		                            </Box>
		                            <Stack direction="row" spacing={0.5} alignItems="center">
		                              <Tooltip title="Edit">
		                                <IconButton size="small" onClick={() => openEditBankDrawer(String(b.id))}>
		                                  <EditIcon fontSize="small" />
		                                </IconButton>
		                              </Tooltip>
		                              <Tooltip title="Remove from this project">
		                                <IconButton size="small" onClick={() => handleRemoveBankFromProject(String(b.id))}>
		                                  <DeleteIcon fontSize="small" />
		                                </IconButton>
		                              </Tooltip>
		                            </Stack>
		                          </Stack>
		                        </Paper>
		                      ))}
		                    </Stack>
		                  )}

		                  <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ sm: 'center' }} sx={{ mt: 2 }}>
		                    <FormControl size="small" fullWidth>
		                      <InputLabel id="assign-existing-bank-label">Assign existing bank</InputLabel>
		                      <Select
		                        labelId="assign-existing-bank-label"
		                        label="Assign existing bank"
		                        value={assignExistingBankId}
		                        onChange={(e) => setAssignExistingBankId(String(e.target.value || ''))}
		                      >
		                        <MenuItem value="">
		                          <em>Select…</em>
		                        </MenuItem>
		                        {bankAccountsAll
		                          .filter((b: any) => !(Array.isArray(b.projectIds) && b.projectIds.includes(editingProjectId)))
		                          .map((b: any) => (
		                            <MenuItem key={b.id} value={b.id}>
		                              {String(b.name || b.bankName || b.id)}
		                            </MenuItem>
		                          ))}
		                      </Select>
		                    </FormControl>
		                    <Button
		                      variant="outlined"
		                      onClick={handleAssignExistingBank}
		                      disabled={!assignExistingBankId}
		                      sx={{ whiteSpace: 'nowrap' }}
		                    >
		                      Add
		                    </Button>
		                  </Stack>
		                </>
		              ) : null}
		            </Box>

	            {projectFormError && <Alert severity="error" sx={{ mt: 2 }}>{projectFormError}</Alert>}
	            {projectsError && <Alert severity="error" sx={{ mt: 2 }}>{projectsError}</Alert>}
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={() => setProjectDrawerOpen(false)} disabled={projectSaving}>
            Cancel
          </Button>
          <Button
            type="submit"
            form="project-form"
            variant="contained"
            disabled={projectSaving}
          >
            {projectSaving ? 'Saving...' : 'Save Project'}
          </Button>
        </DialogActions>
      </Dialog>

        {/* Drawer: Add/Edit Company Profile */}
        <Drawer anchor="right" open={companyProfileDrawerOpen} onClose={() => setCompanyProfileDrawerOpen(false)}>
          <Box sx={{ width: 420, p: 3 }}>
            <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
              <Typography variant="h6">{editingCompanyProfileId ? 'Edit Company Profile' : 'Add Company Profile'}</Typography>
              <IconButton onClick={() => setCompanyProfileDrawerOpen(false)}><CloseIcon /></IconButton>
            </Stack>
            <Box component="form" onSubmit={(e) => { e.preventDefault(); saveCompanyProfile(); }} noValidate>
              <TextField
                margin="normal"
                required
                fullWidth
                label="Profile Name"
                value={companyProfileName}
                onChange={(e) => setCompanyProfileName(e.target.value)}
              />
              <TextField
                margin="normal"
                fullWidth
                label="Company Logo URL"
                value={companyProfileLogoUrl}
                onChange={(e) => setCompanyProfileLogoUrl(e.target.value)}
              />
              <Button variant="outlined" fullWidth component="label" sx={{ mt: 1.5 }}>
                {companyProfileLogoFile ? `Logo: ${companyProfileLogoFile.name}` : 'Upload Logo'}
                <input
                  hidden
                  type="file"
                  accept="image/*"
                  onChange={(e) => setCompanyProfileLogoFile(e.target.files?.[0] || null)}
                />
              </Button>
              <TextField
                margin="normal"
                fullWidth
                label="Company B/W Logo URL"
                value={companyProfileLogoBwUrl}
                onChange={(e) => setCompanyProfileLogoBwUrl(e.target.value)}
                helperText="Used when document template enables B/W Project Logo."
              />
              <Button variant="outlined" fullWidth component="label" sx={{ mt: 1.5 }}>
                {companyProfileLogoBwFile ? `B/W Logo: ${companyProfileLogoBwFile.name}` : 'Upload B/W Logo'}
                <input
                  hidden
                  type="file"
                  accept="image/*"
                  onChange={(e) => setCompanyProfileLogoBwFile(e.target.files?.[0] || null)}
                />
              </Button>
              <TextField
                margin="normal"
                fullWidth
                label="Address"
                value={companyProfileAddress}
                onChange={(e) => setCompanyProfileAddress(e.target.value)}
                multiline
                minRows={2}
              />
              <TextField
                margin="normal"
                fullWidth
                label="Mobile"
                value={companyProfileMobile}
                onChange={(e) => setCompanyProfileMobile(e.target.value)}
              />
              <TextField
                margin="normal"
                fullWidth
                label="Email"
                value={companyProfileEmail}
                onChange={(e) => setCompanyProfileEmail(e.target.value)}
              />
              <TextField
                margin="normal"
                fullWidth
                label="Custom Font Family (Optional)"
                placeholder="Example: Gotham"
                value={companyProfileFontFamily}
                onChange={(e) => setCompanyProfileFontFamily(e.target.value)}
              />
              <TextField
                margin="normal"
                fullWidth
                label="Custom Font URL (Optional)"
                value={companyProfileFontUrl}
                onChange={(e) => setCompanyProfileFontUrl(e.target.value)}
                helperText="Supports .woff2, .woff, .ttf, .otf"
              />
              <TextField
                margin="normal"
                fullWidth
                select
                label="Font Format"
                value={companyProfileFontFormat}
                onChange={(e) =>
                  setCompanyProfileFontFormat(
                    (String(e.target.value || 'woff2').toLowerCase() as 'woff2' | 'woff' | 'ttf' | 'otf')
                  )
                }
              >
                <MenuItem value="woff2">WOFF2</MenuItem>
                <MenuItem value="woff">WOFF</MenuItem>
                <MenuItem value="ttf">TTF</MenuItem>
                <MenuItem value="otf">OTF</MenuItem>
              </TextField>
              <Button variant="outlined" fullWidth component="label" sx={{ mt: 1.5 }}>
                {companyProfileFontFile ? `Font: ${companyProfileFontFile.name}` : 'Upload Font'}
                <input
                  hidden
                  type="file"
                  accept=".woff2,.woff,.ttf,.otf,font/woff2,font/woff,font/ttf,font/otf,application/font-woff,application/font-woff2,application/x-font-ttf,application/x-font-opentype"
                  onChange={(e) => setCompanyProfileFontFile(e.target.files?.[0] || null)}
                />
              </Button>
              <Divider sx={{ my: 2 }} />
              <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                Gujarati Font (Optional)
              </Typography>
              <TextField
                margin="normal"
                fullWidth
                label="Gujarati Font Family (Optional)"
                placeholder="Example: Noto Sans Gujarati"
                value={companyProfileGujaratiFontFamily}
                onChange={(e) => setCompanyProfileGujaratiFontFamily(e.target.value)}
              />
              <TextField
                margin="normal"
                fullWidth
                label="Gujarati Font URL (Optional)"
                value={companyProfileGujaratiFontUrl}
                onChange={(e) => setCompanyProfileGujaratiFontUrl(e.target.value)}
                helperText="Supports .woff2, .woff, .ttf, .otf"
              />
              <TextField
                margin="normal"
                fullWidth
                select
                label="Gujarati Font Format"
                value={companyProfileGujaratiFontFormat}
                onChange={(e) =>
                  setCompanyProfileGujaratiFontFormat(
                    (String(e.target.value || 'woff2').toLowerCase() as 'woff2' | 'woff' | 'ttf' | 'otf')
                  )
                }
              >
                <MenuItem value="woff2">WOFF2</MenuItem>
                <MenuItem value="woff">WOFF</MenuItem>
                <MenuItem value="ttf">TTF</MenuItem>
                <MenuItem value="otf">OTF</MenuItem>
              </TextField>
              <Button variant="outlined" fullWidth component="label" sx={{ mt: 1.5 }}>
                {companyProfileGujaratiFontFile ? `Gujarati Font: ${companyProfileGujaratiFontFile.name}` : 'Upload Gujarati Font'}
                <input
                  hidden
                  type="file"
                  accept=".woff2,.woff,.ttf,.otf,font/woff2,font/woff,font/ttf,font/otf,application/font-woff,application/font-woff2,application/x-font-ttf,application/x-font-opentype"
                  onChange={(e) => setCompanyProfileGujaratiFontFile(e.target.files?.[0] || null)}
                />
              </Button>
              {companyProfileFormError ? <Alert severity="error" sx={{ mt: 2 }}>{companyProfileFormError}</Alert> : null}
              <Button
                type="submit"
                fullWidth
                variant="contained"
                sx={{ mt: 3 }}
                disabled={companyProfileSaving}
              >
                {companyProfileSaving ? 'Saving…' : 'Save Company Profile'}
              </Button>
            </Box>
          </Box>
        </Drawer>

	      {/* Drawer: Add/Edit Bank Account */}
	      <Drawer anchor="right" open={bankDrawerOpen} onClose={() => setBankDrawerOpen(false)}>
	        <Box sx={{ width: 420, p: 3 }}>
	          <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
	            <Typography variant="h6">{editingBankId ? 'Edit Bank' : 'Add Bank'}</Typography>
	            <IconButton onClick={() => setBankDrawerOpen(false)}><CloseIcon /></IconButton>
	          </Stack>
	          <Box component="form" onSubmit={(e) => { e.preventDefault(); handleSaveBankAccount(); }} noValidate>
	            <TextField
	              margin="normal"
	              required
	              fullWidth
	              label="Bank Label (display name)"
	              value={bankLabel}
	              onChange={(e) => setBankLabel(e.target.value)}
	            />
	            <TextField
	              margin="normal"
	              fullWidth
	              label="Bank Name"
	              value={bankName}
	              onChange={(e) => setBankName(e.target.value)}
	            />
	            <TextField
	              margin="normal"
	              fullWidth
	              label="Account Last 4 Digits"
	              value={bankAccountLast4}
	              onChange={(e) => setBankAccountLast4(e.target.value)}
	              inputProps={{ inputMode: 'numeric' }}
	            />
	            <TextField
	              margin="normal"
	              fullWidth
	              label="IFSC (optional)"
	              value={bankIfsc}
	              onChange={(e) => setBankIfsc(e.target.value)}
	            />
			            <TextField
			              margin="normal"
			              fullWidth
			              label="Account Holder Name (optional)"
			              value={bankHolderName}
			              onChange={(e) => setBankHolderName(e.target.value)}
			            />

                  <FormControl fullWidth margin="normal">
                    <InputLabel id="bank-role-label">Account Role</InputLabel>
                    <Select
                      labelId="bank-role-label"
                      label="Account Role"
                      value={bankAccountRole}
                      onChange={(e) => {
                        const next = e.target.value as any;
                        setBankAccountRole(next);
                        // Apply sensible defaults when role changes (still editable below).
                        if (next === 'collection') {
                          setBankAllowCustomerReceipts(true);
                          setBankAllowGstReceipts(false);
                          setBankAllowPayments(false);
                          setBankRestrictedOutflow(false);
                        } else if (next === 'operating30') {
                          setBankAllowCustomerReceipts(false);
                          setBankAllowGstReceipts(true);
                          setBankAllowPayments(true);
                          setBankRestrictedOutflow(false);
                        } else if (next === 'rera70') {
                          setBankAllowCustomerReceipts(false);
                          setBankAllowGstReceipts(false);
                          setBankAllowPayments(false);
                          setBankRestrictedOutflow(true);
                        } else if (next === 'dummy_advocate') {
                          setBankAllowCustomerReceipts(false);
                          setBankAllowGstReceipts(false);
                          setBankAllowPayments(false);
                          setBankRestrictedOutflow(false);
                        } else {
                          setBankAllowCustomerReceipts(false);
                          setBankAllowGstReceipts(true);
                          setBankAllowPayments(true);
                          setBankRestrictedOutflow(false);
                        }
                      }}
                    >
                      <MenuItem value="collection">Customer Collection (receipt-only)</MenuItem>
                      <MenuItem value="operating30">Operating 30% (payments allowed)</MenuItem>
                      <MenuItem value="rera70">RERA 70% (restricted)</MenuItem>
                      <MenuItem value="dummy_advocate">Dummy (Advocate Fee Tracking)</MenuItem>
                      <MenuItem value="other">Other (manual)</MenuItem>
                    </Select>
                  </FormControl>

                  <Stack direction="row" spacing={1} sx={{ mt: 0.5, flexWrap: 'wrap' }}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={bankAllowCustomerReceipts}
                          onChange={(e) => setBankAllowCustomerReceipts(e.target.checked)}
                        />
                      }
                      label="Customer Receipts"
                    />
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={bankAllowGstReceipts}
                          onChange={(e) => setBankAllowGstReceipts(e.target.checked)}
                        />
                      }
                      label="GST Receipts"
                    />
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={bankAllowPayments}
                          onChange={(e) => setBankAllowPayments(e.target.checked)}
                        />
                      }
                      label="Payments"
                    />
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={bankRestrictedOutflow}
                          onChange={(e) => setBankRestrictedOutflow(e.target.checked)}
                        />
                      }
                      label="Restricted Outflow"
                    />
                  </Stack>

                <FormControl fullWidth margin="normal">
                  <InputLabel id="bank-projects-label">Assign Projects</InputLabel>
                  <Select
                    labelId="bank-projects-label"
                    label="Assign Projects"
                    multiple
                    value={bankProjectIds}
                    onChange={(e) => setBankProjectIds(e.target.value as string[])}
                    renderValue={(selected) => (
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                        {(selected as string[]).map((pid) => (
                          <Chip key={pid} size="small" label={projectNameById.get(pid) || pid} />
                        ))}
                      </Box>
                    )}
                  >
                    {projects.map((p: any) => (
                      <MenuItem key={p.id} value={String(p.id)}>
                        {String(p.name || p.id)}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
		            {bankFormError ? <Alert severity="error" sx={{ mt: 2 }}>{bankFormError}</Alert> : null}
	            <Button
	              type="submit"
	              fullWidth
	              variant="contained"
	              sx={{ mt: 3 }}
	              disabled={bankSaving}
	            >
	              {bankSaving ? 'Saving…' : 'Save Bank'}
	            </Button>
	          </Box>
	        </Box>
	      </Drawer>

	      {/* Drawer: Add/Edit Office Document Template */}
	      <ResizableRightDrawer
          open={officeDocumentTemplateDrawerOpen}
          onClose={() => {
            setOfficeDocumentTemplateDrawerOpen(false);
            setOfficeDocumentTemplateSaveError(null);
            setOfficeDocumentTemplateSaveSuccess(null);
          }}
          storageKey="drawerWidth.officeDocumentTemplateEditor"
          defaultWidth={920}
          minWidth={520}
          maxWidth={1400}
        >
	        <Box sx={{ height: '100%', overflow: 'auto', p: 3, pt: { xs: 2, sm: 2 } }}>
	          <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
              <Box>
                <Typography variant="h6">
                  {editingOfficeDocumentTemplateId ? 'Edit Document Template' : 'New Document Template'}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Use placeholders like {'{{project_name}}'}, {'{{unit_number}}'}, {'{{allottee_name}}'}.
                </Typography>
              </Box>
	            <IconButton
                onClick={() => {
                  setOfficeDocumentTemplateDrawerOpen(false);
                  setOfficeDocumentTemplateSaveError(null);
                  setOfficeDocumentTemplateSaveSuccess(null);
                }}
              >
                <CloseIcon />
              </IconButton>
	          </Stack>

	          <Stack spacing={1.5}>
              <Box
                sx={{
                  position: 'sticky',
                  top: { xs: 64, sm: 64 },
                  zIndex: 6,
                  bgcolor: 'background.paper',
                  borderBottom: '1px solid',
                  borderColor: 'divider',
                }}
              >
                <Accordion
                  disableGutters
                  elevation={0}
                  expanded={officeDocInsertFieldsOpen}
                  onChange={(_e, expanded) => setOfficeDocInsertFieldsOpen(expanded)}
                  sx={{ bgcolor: 'transparent', '&:before': { display: 'none' } }}
                >
                  <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 600 }}>
                      Insert Field
                    </Typography>
                  </AccordionSummary>
                  <AccordionDetails sx={{ pt: 0 }}>
                    <Stack direction="row" alignItems="center" spacing={1} flexWrap="wrap" useFlexGap>
                      {officeDocPlaceholders.map((token) => (
                        <Chip key={token} size="small" label={token} onClick={() => insertOfficeDocPlaceholder(token)} clickable />
                      ))}
                      <Button
                        size="small"
                        variant="outlined"
                        onClick={downloadOfficeTemplateSample}
                        disabled={officeDocSampleDownloading}
                      >
                        {officeDocSampleDownloading ? 'Generating PDF…' : 'Download Sample PDF'}
                      </Button>
                      <Button
                        size="small"
                        variant="outlined"
                        onClick={() => officeDocxImportInputRef.current?.click()}
                        disabled={officeDocImportingDocx}
                      >
                        {officeDocImportingDocx ? 'Importing…' : 'Import DOCX'}
                      </Button>
                      <Button
                        size="small"
                        variant="outlined"
                        onClick={loadPaymentDetailsTemplatePreset}
                      >
                        Load Payment Details Preset
                      </Button>
                      <Button
                        size="small"
                        variant="outlined"
                        onClick={loadPaymentReceiptVoucherA5TemplatePreset}
                      >
                        Load Payment Receipt (A5) Preset
                      </Button>
                      <Button
                        size="small"
                        variant="outlined"
                        onClick={loadJoiningLetterTemplatePreset}
                      >
                        Load Joining Letter Preset
                      </Button>
                      <Typography variant="caption" color="text.secondary">
                        Use calc: {'{{calc: unit_amount + gst + stamp_duty}}'}
                      </Typography>
                    </Stack>
                  </AccordionDetails>
                </Accordion>
                <input
                  ref={officeDocxImportInputRef}
                  type="file"
                  accept=".docx,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
                  style={{ display: 'none' }}
                  onChange={(e) => {
                    const f = e.target.files?.[0];
                    if (f) onImportDocxTemplate(f);
                  }}
                />
              </Box>
              <TextField
                label="Template Name"
                value={officeDocumentTemplateName}
                onChange={(e) => setOfficeDocumentTemplateName(e.target.value)}
                fullWidth
              />
              <TextField
                label="Template ID"
                value={makeOfficeDocumentTemplateId(officeDocumentTemplateName, officeDocumentTemplateVersion)}
                fullWidth
                InputProps={{ readOnly: true }}
              />
              <TextField
                label="Version"
                type="number"
                value={officeDocumentTemplateVersion}
                onChange={(e) => setOfficeDocumentTemplateVersion(Number(e.target.value || 1))}
                fullWidth
              />
              <FormControl fullWidth>
                <InputLabel id="office-doc-paper-size-label">Paper Size</InputLabel>
                <Select
                  labelId="office-doc-paper-size-label"
                  label="Paper Size"
                  value={officeDocPaperSize}
                  onChange={(e) => {
                    const next = String(e.target.value || 'A4').toUpperCase();
                    setOfficeDocPaperSize(next === 'A5' ? 'A5' : 'A4');
                  }}
                >
                  <MenuItem value="A4">A4 (Default)</MenuItem>
                  <MenuItem value="A5">A5</MenuItem>
                </Select>
              </FormControl>
              {officeDocImportError ? <Alert severity="error">{officeDocImportError}</Alert> : null}

              {
                <Box
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: { xs: '1fr', lg: 'minmax(0, 1fr) minmax(420px, 0.95fr)' },
                    gap: 2,
                    alignItems: 'start',
                  }}
                >
                  <Stack spacing={1.25}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                      Basic Fields
                    </Typography>
                    <FormControl fullWidth>
                      <InputLabel id="doc-template-projects-label-visual">Assign Projects</InputLabel>
                      <Select
                        labelId="doc-template-projects-label-visual"
                        label="Assign Projects"
                        multiple
                        value={officeDocumentTemplateProjectIds}
                        onChange={(e) => {
                          const nextIds = e.target.value as string[];
                          setOfficeDocumentTemplateProjectIds(nextIds);
                        }}
                        renderValue={(selected) => (
                          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                            {(selected as string[]).map((pid) => (
                              <Chip key={pid} size="small" label={projectNameById.get(pid) || pid} />
                            ))}
                          </Box>
                        )}
                      >
                        {projects.length === 0 ? (
                          <MenuItem disabled>No projects yet.</MenuItem>
                        ) : (
                          projects.map((p: any) => (
                            <MenuItem key={p.id} value={String(p.id)}>
                              <Checkbox checked={officeDocumentTemplateProjectIds.indexOf(String(p.id)) > -1} />
                              <ListItemText primary={String(p.name || p.id)} />
                            </MenuItem>
                          ))
                        )}
                      </Select>
                    </FormControl>
                    <TextField
                      label="Request Types (one per line or comma separated)"
                      value={officeDocumentTemplateRequestTypes.join('\n')}
                      onChange={(e) => setOfficeDocumentTemplateRequestTypes(normalizeRequestTypes(e.target.value))}
                      fullWidth
                      multiline
                      minRows={2}
                      helperText="These will appear in Office → Request Forms → New Request."
                    />
                    <Box sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1.5, p: 1.25 }}>
                      <FormControlLabel
                        control={
                          <Switch
                            checked={officeDocumentTemplateLinkToBank}
                            onChange={(e) => setOfficeDocumentTemplateLinkToBank(e.target.checked)}
                          />
                        }
                        label="Link With Bank"
                      />
                      <FormControlLabel
                        control={
                          <Switch
                            checked={officeDocumentTemplateUseProjectLogoBw}
                            onChange={(e) => setOfficeDocumentTemplateUseProjectLogoBw(e.target.checked)}
                          />
                        }
                        label="Use B/W Project Logo"
                      />
                      {officeDocumentTemplateLinkToBank ? (
                        <Stack spacing={1}>
                          <Autocomplete
                            freeSolo
                            options={officeDocumentTemplateGroupKeyOptions}
                            value={officeDocumentTemplateGroupKey || ''}
                            onChange={(_, value) => {
                              setOfficeDocumentTemplateGroupKey(String(value || '').trim());
                            }}
                            inputValue={officeDocumentTemplateGroupKey || ''}
                            onInputChange={(_, value) => {
                              setOfficeDocumentTemplateGroupKey(String(value || '').trim());
                            }}
                            renderInput={(params) => (
                              <TextField
                                {...params}
                                label="Document Group Key"
                                placeholder="Select or add group key"
                                helperText="Same key for all bank variants (example: builder_noc)."
                              />
                            )}
                          />
                          <Autocomplete
                            multiple
                            freeSolo
                            options={officeDocumentTemplateBankOptions}
                            value={officeDocumentTemplateLinkedBanks}
                            onChange={(_, value) => {
                              const next = Array.from(
                                new Set(
                                  (Array.isArray(value) ? value : [])
                                    .map((v) => String(v || '').trim())
                                    .filter(Boolean)
                                )
                              );
                              setOfficeDocumentTemplateLinkedBanks(next);
                            }}
                            renderTags={(value, getTagProps) =>
                              value.map((option, index) => (
                                <Chip {...getTagProps({ index })} key={`${option}-${index}`} size="small" label={option} />
                              ))
                            }
                            renderInput={(params) => (
                              <TextField
                                {...params}
                                label="Linked Bank Names"
                                placeholder="Search or type bank name"
                                helperText={
                                  officeDocumentTemplateExternalBankLoading
                                    ? 'Loading bank names from transactions and sanction forms…'
                                    : 'Search from payer bank/sanction banks or add new.'
                                }
                              />
                            )}
                          />
                        </Stack>
                      ) : null}
                    </Box>
                    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.25}>
                      <FormControl fullWidth>
                        <InputLabel id="doc-generated-output-type-label">Generated Output</InputLabel>
                        <Select
                          labelId="doc-generated-output-type-label"
                          label="Generated Output"
                          value={officeDocGeneratedOutputType}
                          onChange={(e) => {
                            const nextOutputType =
                              String(e.target.value || 'draft') === 'final' ? 'final' : 'draft';
                            setOfficeDocGeneratedOutputType(nextOutputType);
                            if (nextOutputType === 'draft') {
                              setOfficeDocShowInSection('draft_documents');
                            }
                          }}
                        >
                          <MenuItem value="draft">Draft Documents</MenuItem>
                          <MenuItem value="final">Documents</MenuItem>
                        </Select>
                      </FormControl>
                      <FormControl fullWidth>
                        <InputLabel id="doc-show-in-section-label">Show In Section</InputLabel>
                        <Select
                          labelId="doc-show-in-section-label"
                          label="Show In Section"
                          value={officeDocShowInSection}
                          disabled={officeDocGeneratedOutputType === 'draft'}
                          onChange={(e) => {
                            const raw = String(e.target.value || 'draft_documents');
                            setOfficeDocShowInSection(
                              raw === 'main_documents' || raw === 'draft_documents' || raw === 'loan_documents' || raw === 'hidden'
                                ? raw
                                : 'draft_documents'
                            );
                          }}
                        >
                          <MenuItem value="main_documents">Documents</MenuItem>
                          <MenuItem value="draft_documents">Draft Documents</MenuItem>
                          <MenuItem value="loan_documents">Loan Documents</MenuItem>
                          <MenuItem value="hidden">Hidden</MenuItem>
                        </Select>
                      </FormControl>
                    </Stack>
                    {officeDocGeneratedOutputType === 'draft' ? (
                      <TextField
                        label="Draft File Name Template"
                        value={officeDocumentTemplateDraftFileNameTemplate}
                        onChange={(e) => setOfficeDocumentTemplateDraftFileNameTemplate(e.target.value)}
                        fullWidth
                        helperText="Optional. Example: {{unit_no}} - Booking Form Draft"
                      />
                    ) : null}
                    <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                      Header Logos
                    </Typography>
                    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.25}>
                      <FormControl fullWidth>
                        <InputLabel id="doc-header-company-content-type-label">Left Header Slot</InputLabel>
                        <Select
                          labelId="doc-header-company-content-type-label"
                          label="Left Header Slot"
                          value={officeDocCompanyHeaderContentType}
                          onChange={(e) =>
                            setOfficeDocCompanyHeaderContentType(
                              (e.target.value as 'logo' | 'text' | 'none') || 'logo'
                            )
                          }
                        >
                          <MenuItem value="logo">Company Logo</MenuItem>
                          <MenuItem value="text">Custom Text</MenuItem>
                          <MenuItem value="none">Empty</MenuItem>
                        </Select>
                      </FormControl>
                      <TextField
                        label="Left Header Text"
                        value={officeDocCompanyHeaderText}
                        onChange={(e) => setOfficeDocCompanyHeaderText(e.target.value)}
                        fullWidth
                        disabled={officeDocCompanyHeaderContentType !== 'text'}
                      />
                    </Stack>
                    <TextField
                      label="Left Header Text CSS"
                      value={officeDocCompanyHeaderTextCss}
                      onChange={(e) => setOfficeDocCompanyHeaderTextCss(e.target.value)}
                      fullWidth
                      disabled={officeDocCompanyHeaderContentType !== 'text'}
                      helperText="Example: font-size:16px;font-weight:700;font-style:italic;color:#333;"
                    />
                    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.25}>
                      <FormControl fullWidth>
                        <InputLabel id="doc-header-project-content-type-label">Right Header Slot</InputLabel>
                        <Select
                          labelId="doc-header-project-content-type-label"
                          label="Right Header Slot"
                          value={officeDocProjectHeaderContentType}
                          onChange={(e) =>
                            setOfficeDocProjectHeaderContentType(
                              (e.target.value as 'logo' | 'text' | 'none') || 'logo'
                            )
                          }
                        >
                          <MenuItem value="logo">Project Logo</MenuItem>
                          <MenuItem value="text">Custom Text</MenuItem>
                          <MenuItem value="none">Empty</MenuItem>
                        </Select>
                      </FormControl>
                      <TextField
                        label="Right Header Text"
                        value={officeDocProjectHeaderText}
                        onChange={(e) => setOfficeDocProjectHeaderText(e.target.value)}
                        fullWidth
                        disabled={officeDocProjectHeaderContentType !== 'text'}
                      />
                    </Stack>
                    <TextField
                      label="Right Header Text CSS"
                      value={officeDocProjectHeaderTextCss}
                      onChange={(e) => setOfficeDocProjectHeaderTextCss(e.target.value)}
                      fullWidth
                      disabled={officeDocProjectHeaderContentType !== 'text'}
                      helperText="Example: font-size:16px;font-weight:700;font-style:italic;color:#333;"
                    />
                    <TextField
                      label="Center Header HTML / Text"
                      value={officeDocHeaderCenterHtml}
                      onChange={(e) => setOfficeDocHeaderCenterHtml(e.target.value)}
                      fullWidth
                      multiline
                      minRows={2}
                      helperText="Optional center slot in header. Supports HTML and placeholders."
                    />
                    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.25}>
                      <TextField
                        label="Company Logo Height (cm)"
                        type="number"
                        inputProps={{ step: 0.1, min: 0 }}
                        value={officeDocCompanyLogoHeightCm}
                        onChange={(e) => setOfficeDocCompanyLogoHeightCm(Math.max(0, Number(e.target.value || 0) || 0))}
                        fullWidth
                      />
                      <TextField
                        label="Project Logo Height (cm)"
                        type="number"
                        inputProps={{ step: 0.1, min: 0 }}
                        value={officeDocProjectLogoHeightCm}
                        onChange={(e) => setOfficeDocProjectLogoHeightCm(Math.max(0, Number(e.target.value || 0) || 0))}
                        fullWidth
                      />
                    </Stack>
                    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.25}>
                      <TextField
                        label="Company Logo X Offset (cm)"
                        type="number"
                        inputProps={{ step: 0.1, min: -30, max: 30 }}
                        value={officeDocCompanyLogoOffsetXCm}
                        onChange={(e) =>
                          setOfficeDocCompanyLogoOffsetXCm(
                            Math.max(-30, Math.min(30, Number(e.target.value || 0) || 0))
                          )
                        }
                        fullWidth
                      />
                      <TextField
                        label="Company Logo Y Offset (cm)"
                        type="number"
                        inputProps={{ step: 0.1, min: -30, max: 30 }}
                        value={officeDocCompanyLogoOffsetYCm}
                        onChange={(e) =>
                          setOfficeDocCompanyLogoOffsetYCm(
                            Math.max(-30, Math.min(30, Number(e.target.value || 0) || 0))
                          )
                        }
                        fullWidth
                      />
                    </Stack>
                    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.25}>
                      <TextField
                        label="Project Logo X Offset (cm)"
                        type="number"
                        inputProps={{ step: 0.1, min: -30, max: 30 }}
                        value={officeDocProjectLogoOffsetXCm}
                        onChange={(e) =>
                          setOfficeDocProjectLogoOffsetXCm(
                            Math.max(-30, Math.min(30, Number(e.target.value || 0) || 0))
                          )
                        }
                        fullWidth
                      />
                      <TextField
                        label="Project Logo Y Offset (cm)"
                        type="number"
                        inputProps={{ step: 0.1, min: -30, max: 30 }}
                        value={officeDocProjectLogoOffsetYCm}
                        onChange={(e) =>
                          setOfficeDocProjectLogoOffsetYCm(
                            Math.max(-30, Math.min(30, Number(e.target.value || 0) || 0))
                          )
                        }
                        fullWidth
                      />
                    </Stack>
                    <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                      Footer Overlay Text
                    </Typography>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={officeDocFooterOverlayEnabled}
                          onChange={(e) => setOfficeDocFooterOverlayEnabled(e.target.checked)}
                        />
                      }
                      label="Enable floating footer overlay text"
                    />
                    <TextField
                      label="Overlay Text / HTML (supports placeholders)"
                      value={officeDocFooterOverlayText}
                      onChange={(e) => setOfficeDocFooterOverlayText(e.target.value)}
                      fullWidth
                      multiline
                      minRows={2}
                      disabled={!officeDocFooterOverlayEnabled}
                    />
                    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.25}>
                      <TextField
                        label="Overlay Font Size (px)"
                        type="number"
                        inputProps={{ step: 1, min: 8, max: 72 }}
                        value={officeDocFooterOverlayFontSizePx}
                        onChange={(e) =>
                          setOfficeDocFooterOverlayFontSizePx(
                            Math.max(8, Math.min(72, Number(e.target.value || 12) || 12))
                          )
                        }
                        fullWidth
                        disabled={!officeDocFooterOverlayEnabled}
                      />
                    </Stack>
                    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.25}>
                      <TextField
                        label="Overlay X Offset (cm)"
                        type="number"
                        inputProps={{ step: 0.1, min: -30, max: 30 }}
                        value={officeDocFooterOverlayOffsetXCm}
                        onChange={(e) =>
                          setOfficeDocFooterOverlayOffsetXCm(
                            Math.max(-30, Math.min(30, Number(e.target.value || 0) || 0))
                          )
                        }
                        fullWidth
                        disabled={!officeDocFooterOverlayEnabled}
                      />
                      <TextField
                        label="Overlay Y Offset (cm)"
                        type="number"
                        inputProps={{ step: 0.1, min: -30, max: 30 }}
                        value={officeDocFooterOverlayOffsetYCm}
                        onChange={(e) =>
                          setOfficeDocFooterOverlayOffsetYCm(
                            Math.max(-30, Math.min(30, Number(e.target.value || 0) || 0))
                          )
                        }
                        fullWidth
                        disabled={!officeDocFooterOverlayEnabled}
                      />
                    </Stack>
                    <FormControl fullWidth disabled={!officeDocFooterOverlayEnabled}>
                      <InputLabel id="doc-footer-overlay-page-scope-label">Show Overlay On</InputLabel>
                      <Select
                        labelId="doc-footer-overlay-page-scope-label"
                        label="Show Overlay On"
                        value={officeDocFooterOverlayPageScope}
                        onChange={(e) =>
                          setOfficeDocFooterOverlayPageScope(
                            (e.target.value as 'all' | 'first' | 'last' | 'custom') || 'all'
                          )
                        }
                      >
                        <MenuItem value="all">All Pages</MenuItem>
                        <MenuItem value="first">First Page</MenuItem>
                        <MenuItem value="last">Last Page</MenuItem>
                        <MenuItem value="custom">Custom Pages</MenuItem>
                      </Select>
                    </FormControl>
                    {officeDocFooterOverlayPageScope === 'custom' ? (
                      <TextField
                        label="Custom Page Numbers"
                        value={officeDocFooterOverlayPageNumbers}
                        onChange={(e) => setOfficeDocFooterOverlayPageNumbers(e.target.value)}
                        fullWidth
                        disabled={!officeDocFooterOverlayEnabled}
                        helperText="Examples: 1,3,5 or 2-4"
                      />
                    ) : null}
                    <Typography variant="caption" color="text.secondary">
                      Floating text is rendered above footer content.
                    </Typography>
                    <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                      Background Settings
                    </Typography>
                    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.25}>
                      <FormControl fullWidth>
                        <InputLabel id="doc-bg-size-label">Background Size</InputLabel>
                        <Select
                          labelId="doc-bg-size-label"
                          label="Background Size"
                          value={officeDocBackgroundSize}
                          onChange={(e) => setOfficeDocBackgroundSize((e.target.value as 'cover' | 'contain') || 'contain')}
                        >
                          <MenuItem value="contain">Contain</MenuItem>
                          <MenuItem value="cover">Cover</MenuItem>
                        </Select>
                      </FormControl>
                      <TextField
                        label="Background Opacity"
                        type="number"
                        inputProps={{ step: 0.01, min: 0, max: 0.4 }}
                        value={officeDocBackgroundOpacity}
                        onChange={(e) => setOfficeDocBackgroundOpacity(Math.max(0, Math.min(0.4, Number(e.target.value || 0) || 0)))}
                        fullWidth
                      />
                    </Stack>
                    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.25}>
                      <TextField
                        label="Background Scale (%)"
                        type="number"
                        inputProps={{ step: 1, min: 10, max: 300 }}
                        value={officeDocBackgroundScale}
                        onChange={(e) => setOfficeDocBackgroundScale(Math.max(10, Math.min(300, Number(e.target.value || 72) || 72)))}
                        fullWidth
                      />
                      <TextField
                        label="Background Position X (%)"
                        type="number"
                        inputProps={{ step: 1, min: -300, max: 300 }}
                        value={officeDocBackgroundPosX}
                        onChange={(e) => setOfficeDocBackgroundPosX(Math.max(-300, Math.min(300, Number(e.target.value || -44) || -44)))}
                        fullWidth
                      />
                      <TextField
                        label="Background Position Y (%)"
                        type="number"
                        inputProps={{ step: 1, min: -300, max: 300 }}
                        value={officeDocBackgroundPosY}
                        onChange={(e) => setOfficeDocBackgroundPosY(Math.max(-300, Math.min(300, Number(e.target.value || 109) || 109)))}
                        fullWidth
                      />
                    </Stack>
                    <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                      Main Content Settings
                    </Typography>
                    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.25}>
                      <FormControlLabel
                        control={<Switch checked={officeDocShowTitle} onChange={(e) => setOfficeDocShowTitle(e.target.checked)} />}
                        label="Show Title"
                      />
                      <FormControlLabel
                        control={<Switch checked={officeDocShowRefNo} onChange={(e) => setOfficeDocShowRefNo(e.target.checked)} />}
                        label="Show Ref No"
                      />
                      <FormControlLabel
                        control={<Switch checked={officeDocShowDate} onChange={(e) => setOfficeDocShowDate(e.target.checked)} />}
                        label="Show Date"
                      />
                      <FormControlLabel
                        control={<Switch checked={officeDocShowPageNumbers} onChange={(e) => setOfficeDocShowPageNumbers(e.target.checked)} />}
                        label="Show Page No"
                      />
                      <FormControlLabel
                        control={<Switch checked={officeDocShowFooterContent} onChange={(e) => setOfficeDocShowFooterContent(e.target.checked)} />}
                        label="Show Footer"
                      />
                    </Stack>
                    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.25}>
                      <FormControl fullWidth>
                        <InputLabel id="doc-font-family-label">Document Font</InputLabel>
                        <Select
                          labelId="doc-font-family-label"
                          label="Document Font"
                          value={officeDocFontFamily}
                          onChange={(e) => setOfficeDocFontFamily(String(e.target.value || 'Arial'))}
                          disabled={officeDocUseCompanyFont}
                        >
                          {OFFICE_DOC_FONT_OPTIONS.map((font) => (
                            <MenuItem key={font} value={font}>
                              {font}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                      <TextField
                        label="Letter Spacing (px)"
                        type="number"
                        inputProps={{ step: 0.1, min: -2, max: 10 }}
                        value={officeDocLetterSpacingPx}
                        onChange={(e) =>
                          setOfficeDocLetterSpacingPx(
                            Math.max(-2, Math.min(10, Number(e.target.value || 0) || 0))
                          )
                        }
                        fullWidth
                      />
                      <FormControlLabel
                        control={
                          <Switch
                            checked={officeDocUseCompanyFont}
                            onChange={(e) => setOfficeDocUseCompanyFont(e.target.checked)}
                          />
                        }
                        label="Use Company Font"
                      />
                    </Stack>
                    <TextField
                      label="Title"
                      value={officeDocVisualTitle}
                      onChange={(e) => setOfficeDocVisualTitle(e.target.value)}
                      onFocus={() => setOfficeDocActiveField('title')}
                      fullWidth
                    />
                    <TextField
                      select
                      label="Title Alignment"
                      value={officeDocTitleAlign}
                      onChange={(e) => setOfficeDocTitleAlign((e.target.value as 'left' | 'center' | 'right') || 'left')}
                      fullWidth
                    >
                      <MenuItem value="left">Left</MenuItem>
                      <MenuItem value="center">Center</MenuItem>
                      <MenuItem value="right">Right</MenuItem>
                    </TextField>
                    <TextField
                      label="Subtitle (Optional)"
                      value={officeDocVisualSubtitle}
                      onChange={(e) => setOfficeDocVisualSubtitle(e.target.value)}
                      onFocus={() => setOfficeDocActiveField('subtitle')}
                      fullWidth
                    />
                    <TextField
                      label="Ref Prefix"
                      value={officeDocRefPrefix}
                      onChange={(e) => setOfficeDocRefPrefix(normalizeOfficeRefPrefix(e.target.value))}
                      fullWidth
                    />
                    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.25}>
                      <FormControl fullWidth>
                        <InputLabel id="doc-ref-mode-label">Ref Mode</InputLabel>
                        <Select
                          labelId="doc-ref-mode-label"
                          label="Ref Mode"
                          value={officeDocRefMode}
                          onChange={(e) =>
                            setOfficeDocRefMode(
                              String(e.target.value || 'sequence') === 'unit_short_booking_code'
                                ? 'unit_short_booking_code'
                                : 'sequence'
                            )
                          }
                        >
                          <MenuItem value="unit_short_booking_code">Prefix / Short Booking Code</MenuItem>
                          <MenuItem value="sequence">Prefix / Sequence</MenuItem>
                        </Select>
                      </FormControl>
                      <TextField
                        label="Ref Preview"
                        value={officeDocResolvedPreviewRefNo}
                        InputProps={{ readOnly: true }}
                        fullWidth
                      />
                    </Stack>
                    <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                      Body
                    </Typography>
                    <Tabs
                      value={officeDocEditorMode}
                      onChange={(_, v) => {
                        const nextMode = ((v as 'visual' | 'html') || 'visual');
                        if (nextMode === 'html') {
                          setOfficeDocumentTemplateBodyHtml(officeDocVisualBody);
                        } else {
                          setOfficeDocVisualBody(deriveVisualBodyFromHtml(officeDocumentTemplateBodyHtml));
                        }
                        setOfficeDocEditorMode(nextMode);
                      }}
                      variant="fullWidth"
                    >
                      <Tab value="visual" label="Visual Builder" />
                      <Tab value="html" label="Advanced HTML" />
                    </Tabs>
                    {officeDocEditorMode === 'visual' ? (
                      <>
                        <Alert severity="info">
                          Rich text editor auto-generates HTML.
                        </Alert>
                        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                          <Button size="small" variant="outlined" onMouseDown={(e) => onOfficeDocToolbarMouseDown(e, () => applyOfficeBodyCommand('bold'))}>Bold</Button>
                          <Button size="small" variant="outlined" onMouseDown={(e) => onOfficeDocToolbarMouseDown(e, () => applyOfficeBodyCommand('italic'))}>Italic</Button>
                          <Button size="small" variant="outlined" onMouseDown={(e) => onOfficeDocToolbarMouseDown(e, () => applyOfficeBodyCommand('underline'))}>Underline</Button>
                          <Button size="small" variant="outlined" onMouseDown={(e) => onOfficeDocToolbarMouseDown(e, () => applyOfficeBodyCommand('insertUnorderedList'))}>Bullets</Button>
                          <Button size="small" variant="outlined" onMouseDown={(e) => onOfficeDocToolbarMouseDown(e, () => applyOfficeBodyCommand('insertOrderedList'))}>Numbered</Button>
                          <Button size="small" variant="outlined" onMouseDown={(e) => onOfficeDocToolbarMouseDown(e, () => applyOfficeBodyCommand('justifyLeft'))}>Left</Button>
                          <Button size="small" variant="outlined" onMouseDown={(e) => onOfficeDocToolbarMouseDown(e, () => applyOfficeBodyCommand('justifyCenter'))}>Center</Button>
                          <Button size="small" variant="outlined" onMouseDown={(e) => onOfficeDocToolbarMouseDown(e, () => applyOfficeBodyCommand('justifyRight'))}>Right</Button>
                          <Button size="small" variant="outlined" onMouseDown={(e) => onOfficeDocToolbarMouseDown(e, () => applyOfficeBodyCommand('undo'))}>Undo</Button>
                          <Button size="small" variant="outlined" onMouseDown={(e) => onOfficeDocToolbarMouseDown(e, () => applyOfficeBodyCommand('redo'))}>Redo</Button>
                          <Button size="small" variant="outlined" onMouseDown={(e) => onOfficeDocToolbarMouseDown(e, () => applyOfficeBodyCommand('removeFormat'))}>Clear</Button>
                          <Button size="small" variant="outlined" onMouseDown={(e) => onOfficeDocToolbarMouseDown(e, insertOfficeBodyTable)}>Table</Button>
                          <FormControl size="small" sx={{ minWidth: 150 }}>
                            <InputLabel>Font</InputLabel>
                            <Select
                              label="Font"
                              value={officeDocRichFontFamily}
                          onChange={(e) => {
                            const value = String(e.target.value || 'Arial');
                            setOfficeDocRichFontFamily(value);
                            applyOfficeBodyCommand('fontName', value);
                          }}
                        >
                          {OFFICE_DOC_FONT_OPTIONS.map((font) => (
                            <MenuItem key={font} value={font}>
                              {font}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                          <FormControl size="small" sx={{ minWidth: 120 }}>
                            <InputLabel>Size</InputLabel>
                            <Select
                              label="Size"
                              value={officeDocRichFontSize}
                              onChange={(e) => {
                                const value = String(e.target.value || '16px');
                                setOfficeDocRichFontSize(value);
                                applyOfficeBodyInlineStyle(`font-size:${value};`);
                              }}
                            >
                              <MenuItem value="12px">12</MenuItem>
                              <MenuItem value="14px">14</MenuItem>
                              <MenuItem value="16px">16</MenuItem>
                              <MenuItem value="18px">18</MenuItem>
                              <MenuItem value="20px">20</MenuItem>
                              <MenuItem value="24px">24</MenuItem>
                            </Select>
                          </FormControl>
                        </Stack>
                        <Box
                          ref={officeDocBodyEditorRef}
                          contentEditable
                          suppressContentEditableWarning
                          onFocus={() => setOfficeDocActiveField('body')}
                          onInput={() => {
                            saveOfficeDocBodySelection();
                            syncOfficeDocBodyFromEditor();
                          }}
                          onKeyUp={saveOfficeDocBodySelection}
                          onMouseUp={saveOfficeDocBodySelection}
                          onBlur={() => {
                            saveOfficeDocBodySelection();
                            syncOfficeDocBodyFromEditor();
                          }}
                          sx={{
                            minHeight: 260,
                            border: '1px solid',
                            borderColor: 'divider',
                            borderRadius: 1,
                            p: 1.25,
                            bgcolor: '#fff',
                            overflow: 'auto',
                            '& table': { width: '100%', borderCollapse: 'collapse', borderSpacing: 0, my: 1 },
                            '& th, & td': { border: '1px solid #222', p: 0.75, verticalAlign: 'top' },
                            '& p': { my: 0.75 },
                          }}
                        />
                      </>
                    ) : (
                      <>
                        <Alert severity="info">
                          Edit only body HTML here. Header/Footer and all visual settings remain active.
                        </Alert>
                        <TextField
                          label="Body HTML"
                          value={officeDocumentTemplateBodyHtml}
                          onChange={(e) => {
                            const nextHtml = e.target.value;
                            setOfficeDocumentTemplateBodyHtml(nextHtml);
                            setOfficeDocVisualBody(nextHtml);
                          }}
                          onFocus={() => setOfficeDocActiveField('body')}
                          multiline
                          minRows={16}
                          fullWidth
                        />
                      </>
                    )}
                  </Stack>
                  <Box sx={{ position: { lg: 'sticky' }, top: { lg: 8 } }}>
                    <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 0.75 }}>
                      <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                        {officeDocPaperSize} Preview
                      </Typography>
                      {officeDocPreviewSampleLoading ? (
                        <Typography variant="caption" color="text.secondary">
                          Loading booked unit sample…
                        </Typography>
                      ) : null}
                      <Stack direction="row" spacing={0.75}>
                        <Button
                          size="small"
                          variant={officeDocPreviewMode === 'visual' ? 'contained' : 'outlined'}
                          onClick={() => setOfficeDocPreviewMode('visual')}
                        >
                          Visual
                        </Button>
                        <Button
                          size="small"
                          variant={officeDocPreviewMode === 'pdf' ? 'contained' : 'outlined'}
                          onClick={async () => {
                            setOfficeDocPreviewMode('pdf');
                            await generateOfficeTemplatePdfPreview();
                          }}
                          disabled={officeDocPdfPreviewLoading}
                        >
                          {officeDocPdfPreviewLoading && officeDocPreviewMode === 'pdf' ? 'Loading…' : 'PDF'}
                        </Button>
                      </Stack>
                    </Stack>
                    <FormControl size="small" fullWidth sx={{ mb: 1 }}>
                      <InputLabel id="preview-unit-select-label">Preview Unit</InputLabel>
                      <Select
                        labelId="preview-unit-select-label"
                        label="Preview Unit"
                        value={officeDocPreviewUnitId}
                        onChange={(e) => setOfficeDocPreviewUnitId(String(e.target.value || ''))}
                      >
                        {officeDocPreviewUnits.length === 0 ? (
                          <MenuItem value="">
                            {officeDocPreviewSampleLoading ? 'Loading units…' : 'No booked units found'}
                          </MenuItem>
                        ) : (
                          officeDocPreviewUnits.map((row) => {
                            const label = String(row?.unitNumber || row?.id || '');
                            const status = String(row?.bookingStatus || '').toUpperCase();
                            return (
                              <MenuItem key={String(row.id)} value={String(row.id)}>
                                {`${label}${status ? ` • ${status}` : ''}`}
                              </MenuItem>
                            );
                          })
                        )}
                      </Select>
                    </FormControl>
                    <Box sx={{ p: 1, border: '1px solid', borderColor: 'divider', borderRadius: 1, bgcolor: '#f4f6f8' }}>
                      <Box
                        ref={officeDocPreviewContainerRef}
                        sx={{
                          width: '100%',
                          height: { xs: '56vh', md: '62vh', lg: '72vh' },
                          bgcolor: '#fff',
                          border: '1px solid',
                          borderColor: 'divider',
                          overflow: 'auto',
                          position: 'relative',
                        }}
                      >
                        <Box
                          sx={{
                            position: 'relative',
                            width: `${officeDocPaperWidthPx}px`,
                            minHeight: `${officeDocPaperHeightPx}px`,
                            transform: 'scale(1)',
                            bgcolor: '#fff',
                          }}
                        >
                          {officeDocPreviewMode === 'visual' ? (
                            <Box dangerouslySetInnerHTML={{ __html: applyOfficeDocPreviewTokens(officeDocVisualHtml, true) }} />
                          ) : officeDocPdfPreviewUrl ? (
                            <Box sx={{ width: '100%', height: `${officeDocPaperHeightPx}px`, bgcolor: '#fff' }}>
                              <iframe
                                title="PDF Preview"
                                src={officeDocPdfPreviewUrl}
                                style={{ width: '100%', height: '100%', border: 'none' }}
                              />
                            </Box>
                          ) : (
                            <Stack sx={{ p: 2 }} spacing={1}>
                              {officeDocPdfPreviewError ? <Alert severity="error">{officeDocPdfPreviewError}</Alert> : null}
                              <Button
                                size="small"
                                variant="contained"
                                onClick={generateOfficeTemplatePdfPreview}
                                disabled={officeDocPdfPreviewLoading}
                              >
                                {officeDocPdfPreviewLoading ? 'Generating PDF…' : 'Generate PDF Preview'}
                              </Button>
                            </Stack>
                          )}
                        </Box>
                      </Box>
                    </Box>
                  </Box>
                </Box>
	              }

              {officeDocumentTemplateSaveError ? <Alert severity="error">{officeDocumentTemplateSaveError}</Alert> : null}
              {officeDocumentTemplateSaveSuccess ? <Alert severity="success">{officeDocumentTemplateSaveSuccess}</Alert> : null}

              <Stack direction="row" justifyContent="flex-end" spacing={1}>
                <Button
                  variant="outlined"
                  onClick={() => {
                    setOfficeDocumentTemplateDrawerOpen(false);
                    setOfficeDocumentTemplateSaveError(null);
                    setOfficeDocumentTemplateSaveSuccess(null);
                  }}
                >
                  Cancel
                </Button>
                <Button variant="contained" onClick={saveOfficeDocumentTemplate} disabled={officeDocumentTemplateSaving}>
                  {officeDocumentTemplateSaving ? 'Saving…' : editingOfficeDocumentTemplateId ? 'Save' : 'Create'}
                </Button>
              </Stack>
            </Stack>
          </Box>
	        </ResizableRightDrawer>

	      {/* Drawer: Add/Edit Lead Status */}
	      <Drawer anchor="right" open={statusDrawerOpen} onClose={() => setStatusDrawerOpen(false)}>
	        <Box sx={{ width: 420, p: 3 }}>
          <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
            <Typography variant="h6">{editingStatusId ? 'Edit Status' : 'Add Status'}</Typography>
            <IconButton onClick={() => setStatusDrawerOpen(false)}><CloseIcon /></IconButton>
          </Stack>
          <Box component="form" onSubmit={handleSaveStatus} noValidate>
            <TextField
              margin="normal"
              required
              fullWidth
              label="Status Name"
              value={statusLabel}
              onChange={(e) => setStatusLabel(e.target.value)}
            />
            <TextField
              margin="normal"
              fullWidth
              label="Order"
              type="number"
              value={statusOrder}
              onChange={(e) => setStatusOrder(Number(e.target.value))}
            />
            <Stack direction="row" spacing={2} alignItems="center" sx={{ mt: 2 }}>
              <Box sx={{ minWidth: 110 }}>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                  Color
                </Typography>
                <Box
                  component="input"
                  type="color"
                  value={/^#([0-9a-fA-F]{6})$/.test(statusColor.trim()) ? statusColor.trim() : '#1e88e5'}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => setStatusColor(e.target.value)}
                  style={{ width: 64, height: 40, border: 'none', background: 'transparent', padding: 0 }}
                />
              </Box>
              <TextField
                fullWidth
                label="Hex"
                value={statusColor}
                onChange={(e) => setStatusColor(e.target.value)}
                placeholder="#1e88e5"
                helperText="Optional. Example: #1e88e5"
              />
            </Stack>
            <FormControl fullWidth margin="normal">
              <InputLabel id="allowed-next-label">Allowed Next Statuses</InputLabel>
              <Select
                labelId="allowed-next-label"
                multiple
                value={statusAllowedNext}
                onChange={(e) => setStatusAllowedNext(e.target.value as string[])}
                renderValue={(selected) =>
                  selected
                    .map((id) => statuses.find((s) => s.id === id)?.label || id)
                    .join(', ')
                }
              >
                {statuses
                  .filter((s) => s.id !== editingStatusId)
                  .map((s) => (
                    <MenuItem key={s.id} value={s.id}>
                      <Checkbox checked={statusAllowedNext.indexOf(s.id) > -1} />
                      <ListItemText primary={s.label} />
                    </MenuItem>
                  ))}
              </Select>
            </FormControl>

            <Divider sx={{ my: 2 }} />
            <Typography variant="subtitle1">Prompts (on status change)</Typography>
            <TextField
              margin="dense"
              fullWidth
              label="Prompt Label"
              value={promptLabel}
              onChange={(e) => setPromptLabel(e.target.value)}
            />
            <FormControl fullWidth margin="dense">
              <InputLabel id="prompt-type-label">Type</InputLabel>
              <Select
                labelId="prompt-type-label"
                value={promptType}
                label="Type"
                onChange={(e) => setPromptType(e.target.value as any)}
              >
                <MenuItem value="text">Text</MenuItem>
                <MenuItem value="date">Date</MenuItem>
                <MenuItem value="number">Number</MenuItem>
                <MenuItem value="select">Select</MenuItem>
              </Select>
            </FormControl>
            {promptType === 'select' && (
              <TextField
                margin="dense"
                fullWidth
                label="Options (comma separated)"
                value={promptOptionsInput}
                onChange={(e) => setPromptOptionsInput(e.target.value)}
              />
            )}
            <Button sx={{ mt: 1 }} variant="outlined" onClick={addPrompt} disabled={!promptLabel.trim()}>
              Add Prompt
            </Button>

            <Stack direction="row" gap={1} flexWrap="wrap" sx={{ mt: 2 }}>
              {statusPrompts.map((p) => (
                <Chip
                  key={p.id}
                  label={`${p.label} (${p.type})`}
                  onDelete={() => removePrompt(p.id)}
                  size="small"
                />
              ))}
            </Stack>

            {statusError && <Alert severity="error" sx={{ mt: 2 }}>{statusError}</Alert>}

            <Button
              type="submit"
              fullWidth
              variant="contained"
              sx={{ mt: 3 }}
              disabled={statusSaving}
            >
              {statusSaving ? 'Saving...' : 'Save Status'}
            </Button>
          </Box>
        </Box>
      </Drawer>

      <Dialog open={unitsDialogOpen} onClose={() => setUnitsDialogOpen(false)} fullWidth maxWidth="md">
        <DialogTitle>
          Units{unitsProjectName ? ` — ${unitsProjectName}` : ''}{projectUnitsCount ? ` (${projectUnitsCount})` : ''}
        </DialogTitle>
        <DialogContent dividers>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} sx={{ mb: 2 }}>
            <TextField
              fullWidth
              size="small"
              placeholder="Search unit #, block, usage..."
              value={unitsSearch}
              onChange={(e) => setUnitsSearch(e.target.value)}
            />
            <Button
              variant="outlined"
              onClick={() => exportExistingUnitsCsv(unitsProjectId, unitsProjectName)}
              disabled={unitsLoading || unitsImporting}
            >
              Export CSV
            </Button>
            <Button
              variant="outlined"
              onClick={() => loadUnitsPage({ reset: true })}
              disabled={unitsLoading}
            >
              Refresh
            </Button>
          </Stack>

          {unitsLoadError && <Alert severity="error" sx={{ mb: 2 }}>{unitsLoadError}</Alert>}

          <TableContainer component={Paper} variant="outlined" sx={{ maxWidth: '100%', overflowX: 'auto' }}>
            <Table size="small" sx={{ minWidth: 1800 }}>
              <TableHead>
                <TableRow>
                  {unitDialogColumns.map((col, idx) => (
                    <TableCell
                      key={`unit-col-head-${col.key}`}
                      align={col.align}
                      sx={
                        idx === 0
                          ? {
                              position: 'sticky',
                              left: 0,
                              zIndex: 3,
                              bgcolor: 'background.paper',
                              minWidth: 140,
                              borderRight: '1px solid',
                              borderColor: 'divider',
                            }
                          : undefined
                      }
                    >
                      {col.label}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {(unitsRows || [])
                  .filter((r) => {
                    const q = unitsSearch.trim().toLowerCase();
                    if (!q) return true;
                    const hay = [
                      r.unitNumber,
                      r.blockType,
                      r.usage,
                    ]
                      .filter(Boolean)
                      .join(' ')
                      .toLowerCase();
                    return hay.includes(q);
                  })
                  .slice(0, 200)
                  .map((r) => (
                    <TableRow key={r.id} hover>
                      {unitDialogColumns.map((col, idx) => (
                        <TableCell
                          key={`unit-col-${r.id}-${col.key}`}
                          align={col.align}
                          sx={
                            idx === 0
                              ? {
                                  position: 'sticky',
                                  left: 0,
                                  zIndex: 2,
                                  bgcolor: 'background.paper',
                                  minWidth: 140,
                                  borderRight: '1px solid',
                                  borderColor: 'divider',
                                }
                              : undefined
                          }
                        >
                          {renderUnitColumnValue(r, col.key)}
                        </TableCell>
                      ))}
                    </TableRow>
                  ))}
                {unitsRows.length === 0 && !unitsLoading && (
                  <TableRow>
                    <TableCell colSpan={Math.max(1, unitDialogColumns.length)} align="center">
                      No units found.
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>

          {unitsLoading && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 2 }}>
              <CircularProgress size={18} />
              <Typography variant="body2">Loading units…</Typography>
            </Box>
          )}

          {unitsHasMore && (
            <Box sx={{ mt: 2 }}>
              <Button variant="outlined" onClick={() => loadUnitsPage()} disabled={unitsLoading}>
                Load more
              </Button>
            </Box>
          )}

          {projectUnitsLastImportedAt && (
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1.5 }}>
              Last imported: {projectUnitsLastImportedAt?.toDate ? projectUnitsLastImportedAt.toDate().toLocaleString() : String(projectUnitsLastImportedAt)}
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setUnitsDialogOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={projectGstDialogOpen}
        onClose={() => {
          if (projectGstSaving) return;
          setProjectGstDialogOpen(false);
        }}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>
          GST % + SB % by Usage{projectGstProjectName ? ` — ${projectGstProjectName}` : ''}
        </DialogTitle>
        <DialogContent dividers>
          <Stack spacing={1.25} sx={{ mt: 0.5 }}>
            <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 2 }}>
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ bgcolor: '#f8fafc' }}>
                    <TableCell sx={{ fontWeight: 800, width: '50%' }}>Type</TableCell>
                    <TableCell sx={{ fontWeight: 800, width: '25%' }}>GST %</TableCell>
                    <TableCell sx={{ fontWeight: 800, width: '25%' }}>SB %</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {projectPercentTypes.map((type) => (
                    <TableRow key={`percent_type_${type}`} hover>
                      <TableCell sx={{ fontWeight: type === 'Default' ? 700 : 500 }}>{type}</TableCell>
                      <TableCell>
                        <TextField
                          size="small"
                          value={type === 'Default' ? projectDefaultGstPercentInput : (projectGstValues[type] || '')}
                          onChange={(e) => {
                            if (type === 'Default') {
                              setProjectDefaultGstPercentInput(e.target.value);
                              return;
                            }
                            setProjectGstValues((prev) => ({ ...prev, [type]: e.target.value }));
                          }}
                          type="number"
                          inputProps={{ min: 0, step: '0.01' }}
                          sx={{ width: '100%' }}
                        />
                      </TableCell>
                      <TableCell>
                        <TextField
                          size="small"
                          value={type === 'Default' ? projectDefaultSbPercentInput : (projectSbValues[type] || '')}
                          onChange={(e) => {
                            if (type === 'Default') {
                              setProjectDefaultSbPercentInput(e.target.value);
                              return;
                            }
                            setProjectSbValues((prev) => ({ ...prev, [type]: e.target.value }));
                          }}
                          type="number"
                          inputProps={{ min: 0, step: '0.01' }}
                          sx={{ width: '100%' }}
                        />
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
            {projectGstError ? <Alert severity="error">{projectGstError}</Alert> : null}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setProjectGstDialogOpen(false)}
            disabled={projectGstSaving}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={saveProjectGstConfig}
            disabled={!projectGstProjectId || projectGstSaving}
          >
            {projectGstSaving ? 'Saving…' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Drawer: Add/Edit Meta Form Mapping */}
      <Drawer anchor="right" open={metaFormDrawerOpen} onClose={() => setMetaFormDrawerOpen(false)}>
        <Box sx={{ width: { xs: '100%', sm: 420 }, maxWidth: '100%', p: 3 }}>
          <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
            <Typography variant="h6">{editingMetaFormId ? 'Edit Mapping' : 'Add Mapping'}</Typography>
            <IconButton onClick={() => setMetaFormDrawerOpen(false)}><CloseIcon /></IconButton>
          </Stack>
          <Box component="form" onSubmit={saveMetaForm} noValidate>
            <TextField
              margin="normal"
              required
              fullWidth
              label="Form ID"
              value={metaFormId}
              onChange={(e) => setMetaFormId(e.target.value)}
              disabled={!!editingMetaFormId}
              helperText="Copy from Meta lead form (form_id)."
            />
            <FormControl fullWidth margin="normal">
              <InputLabel id="meta-project-label">Project</InputLabel>
              <Select
                labelId="meta-project-label"
                label="Project"
                value={metaFormProjectId}
                onChange={(e) => setMetaFormProjectId(String(e.target.value))}
              >
                <MenuItem value="">Unassigned</MenuItem>
                {projects.map((p: any) => (
                  <MenuItem key={p.id} value={p.id}>{p.name}</MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControlLabel
              control={<Switch checked={metaFormActive} onChange={(e) => setMetaFormActive(e.target.checked)} />}
              label="Active"
            />
            {metaFormsError && <Alert severity="error" sx={{ mt: 2 }}>{metaFormsError}</Alert>}
            <Button type="submit" fullWidth variant="contained" sx={{ mt: 3 }}>
              Save Mapping
            </Button>
          </Box>
        </Box>
      </Drawer>

      {/* Drawer: Add/Edit Meta Page */}
      <Drawer anchor="right" open={metaPageDrawerOpen} onClose={() => setMetaPageDrawerOpen(false)}>
        <Box sx={{ width: { xs: '100%', sm: 420 }, maxWidth: '100%', p: 3 }}>
          <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
            <Typography variant="h6">{editingMetaPageId ? 'Edit Page' : 'Add Page'}</Typography>
            <IconButton onClick={() => setMetaPageDrawerOpen(false)}><CloseIcon /></IconButton>
          </Stack>
          <Box component="form" onSubmit={saveMetaPage} noValidate>
            <TextField
              margin="normal"
              required
              fullWidth
              label="Page ID"
              value={metaPageId}
              onChange={(e) => setMetaPageId(e.target.value)}
              disabled={!!editingMetaPageId}
            />
            <TextField
              margin="normal"
              fullWidth
              label="Label"
              value={metaPageLabel}
              onChange={(e) => setMetaPageLabel(e.target.value)}
              placeholder="Optional (e.g., Swarna Surat Page)"
            />
            <FormControl fullWidth margin="normal">
              <InputLabel id="meta-page-type-label">Type</InputLabel>
              <Select
                labelId="meta-page-type-label"
                label="Type"
                value={metaPageType}
                onChange={(e) => setMetaPageType(e.target.value as any)}
              >
                <MenuItem value="own">Own</MenuItem>
                <MenuItem value="agency">Agency</MenuItem>
              </Select>
            </FormControl>
            <FormControlLabel
              control={<Switch checked={metaPageActive} onChange={(e) => setMetaPageActive(e.target.checked)} />}
              label="Active"
            />
            {metaPagesError && <Alert severity="error" sx={{ mt: 2 }}>{metaPagesError}</Alert>}
            <Button type="submit" fullWidth variant="contained" sx={{ mt: 3 }}>
              Save Page
            </Button>
          </Box>
        </Box>
      </Drawer>

      {/* Drawer: Global Office Workflow Templates */}
      <ResizableRightDrawer
        open={officeWorkflowDrawerOpen}
        onClose={() => {
          setOfficeWorkflowDrawerOpen(false);
          setOfficeWorkflowSaveError(null);
          setOfficeWorkflowSaveSuccess(null);
          setOfficeWorkflowApplyToProjectsError(null);
          setOfficeWorkflowApplyToProjectsSuccess(null);
        }}
        storageKey="drawerWidth.officeWorkflowEditor"
        defaultWidth={1100}
        minWidth={520}
        maxWidth={1400}
      >
        <Box sx={{ height: '100dvh', display: 'flex', flexDirection: 'column' }}>
          <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
            <Box sx={{ minWidth: 0 }}>
              <Typography variant="h6" noWrap>
                {editingOfficeWorkflowId ? 'Edit Workflow' : 'New Workflow'}
              </Typography>
              <Typography variant="body2" color="text.secondary" noWrap>
                {editingOfficeWorkflowId ? `ID: ${editingOfficeWorkflowId}` : 'Create a reusable template and apply to projects.'}
              </Typography>
            </Box>
            <IconButton
              onClick={() => {
                setOfficeWorkflowDrawerOpen(false);
                setOfficeWorkflowSaveError(null);
                setOfficeWorkflowSaveSuccess(null);
                setOfficeWorkflowApplyToProjectsError(null);
                setOfficeWorkflowApplyToProjectsSuccess(null);
              }}
              aria-label="Close"
            >
              <CloseIcon />
            </IconButton>
          </Stack>

          {/* GTM-style editor */}
          <Box sx={{ flex: 1, minHeight: 0, display: 'flex' }}>
            <Box
              sx={{
                width: { xs: 240, sm: 300 },
                borderRight: '1px solid',
                borderColor: 'divider',
                overflow: 'auto',
                p: 1.5,
              }}
            >
              <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 900 }}>
                  Steps
                </Typography>
                <Button size="small" variant="outlined" onClick={addOfficeWorkflowStep}>
                  Add
                </Button>
              </Stack>

              {officeWorkflowSteps.length === 0 ? (
                <Alert severity="info">Add steps to build your workflow.</Alert>
              ) : (
                <List dense disablePadding>
                  {officeWorkflowSteps.map((s: any, idx: number) => {
                    const selected = idx === officeWorkflowSelectedStepIdx;
                    return (
                      <Box key={`${s.key || idx}`} sx={{ mb: 0.5 }}>
                        <ListItemButton selected={selected} onClick={() => setOfficeWorkflowSelectedStepIdx(idx)} sx={{ borderRadius: 1 }}>
                          <Box sx={{ flex: 1, minWidth: 0 }}>
                            <Typography variant="body2" sx={{ fontWeight: 900 }} noWrap>
                              {s.title || s.key || `Step ${idx + 1}`}
                            </Typography>
                            <Stack direction="row" spacing={0.75} alignItems="center" sx={{ mt: 0.25 }} flexWrap="wrap" rowGap={0.5}>
                              <Chip size="small" label={`#${idx + 1}`} variant="outlined" />
                              <Chip size="small" label={String(s.kind || 'task')} variant="outlined" />
                            </Stack>
                          </Box>
                          <Stack direction="row" spacing={0.25} sx={{ ml: 1 }} onClick={(e) => e.stopPropagation()}>
                            <IconButton size="small" onClick={() => moveOfficeWorkflowStep(idx, -1)} disabled={idx === 0} aria-label="Move up">
                              <ArrowUpwardOutlinedIcon fontSize="inherit" />
                            </IconButton>
                            <IconButton
                              size="small"
                              onClick={() => moveOfficeWorkflowStep(idx, 1)}
                              disabled={idx === officeWorkflowSteps.length - 1}
                              aria-label="Move down"
                            >
                              <ArrowDownwardOutlinedIcon fontSize="inherit" />
                            </IconButton>
                            <IconButton size="small" color="error" onClick={() => deleteOfficeWorkflowStep(idx)} aria-label="Delete step">
                              <DeleteIcon fontSize="inherit" />
                            </IconButton>
                          </Stack>
                        </ListItemButton>
                      </Box>
                    );
                  })}
                </List>
              )}
            </Box>

            <Box sx={{ flex: 1, minWidth: 0, overflow: 'auto', p: 2 }}>
              <Stack spacing={1.5}>
                <Paper variant="outlined" sx={{ p: 2 }}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 900, mb: 1 }}>
                    Workflow
                  </Typography>
                  <Stack spacing={1.25}>
                    <TextField
                      label="Workflow Name"
                      value={officeWorkflowName}
                      onChange={(e) => setOfficeWorkflowName(e.target.value)}
                      fullWidth
                    />

                    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
                      <FormControl fullWidth>
                        <InputLabel id="office-wf-type-label">Type</InputLabel>
                        <Select
                          labelId="office-wf-type-label"
                          label="Type"
                          value={officeWorkflowType}
                          onChange={(e) => setOfficeWorkflowType(e.target.value as any)}
                        >
                          <MenuItem value="main">Main</MenuItem>
                          <MenuItem value="loan">Loan</MenuItem>
                        </Select>
                      </FormControl>
                      <TextField
                        label="Version"
                        type="number"
                        value={officeWorkflowVersion}
                        onChange={(e) => setOfficeWorkflowVersion(Number(e.target.value || 1))}
                        fullWidth
                        inputProps={{ min: 1 }}
                      />
                    </Stack>

                    <FormControl fullWidth>
                      <InputLabel id="office-wf-projects-label">Assign Projects (Apply)</InputLabel>
                      <Select
                        labelId="office-wf-projects-label"
                        multiple
                        value={officeWorkflowProjectIds}
                        label="Assign Projects (Apply)"
                        onChange={(e) => setOfficeWorkflowProjectIds(e.target.value as string[])}
                        renderValue={(selected) =>
                          (selected as string[])
                            .map((id) => projects.find((p: any) => p.id === id)?.name || id)
                            .join(', ')
                        }
                      >
                        {projects.length === 0 ? (
                          <MenuItem disabled>No projects yet.</MenuItem>
                        ) : (
                          projects.map((p: any) => (
                            <MenuItem key={p.id} value={p.id}>
                              <Checkbox checked={officeWorkflowProjectIds.indexOf(p.id) > -1} />
                              <ListItemText primary={p.name || p.id} secondary={p.location || ''} />
                            </MenuItem>
                          ))
                        )}
                      </Select>
                    </FormControl>
                  </Stack>
                </Paper>

                {officeWorkflowSaveError ? <Alert severity="error">{officeWorkflowSaveError}</Alert> : null}
                {officeWorkflowSaveSuccess ? <Alert severity="success">{officeWorkflowSaveSuccess}</Alert> : null}
                {officeWorkflowApplyToProjectsError ? <Alert severity="error">{officeWorkflowApplyToProjectsError}</Alert> : null}
                {officeWorkflowApplyToProjectsSuccess ? <Alert severity="success">{officeWorkflowApplyToProjectsSuccess}</Alert> : null}

                {officeWorkflowSteps.length === 0 ? null : (
                  <Paper variant="outlined" sx={{ p: 2 }}>
                    {(() => {
                      const idx = Math.min(officeWorkflowSelectedStepIdx, Math.max(0, officeWorkflowSteps.length - 1));
                      const s = officeWorkflowSteps[idx] || {};
                      const priorKeys = officeWorkflowSteps
                        .slice(0, idx)
                        .map((x: any) => String(x.key || '').trim())
                        .filter(Boolean);
                      const deps = Array.isArray(s.dependsOn) ? s.dependsOn : [];
                      return (
                        <Stack spacing={1.25}>
                          <Typography variant="subtitle1" sx={{ fontWeight: 900 }}>
                            Step #{idx + 1}
                          </Typography>

                          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
                            <TextField
                              label="Key (stable)"
                              value={s.key || ''}
                              onChange={(e) => {
                                const v = e.target.value;
                                setOfficeWorkflowSteps((prev) => prev.map((x, i) => (i === idx ? { ...x, key: v } : x)));
                              }}
                              fullWidth
                            />
                            <FormControl fullWidth>
                              <InputLabel id={`wf-kind-${idx}`}>Kind</InputLabel>
                              <Select
                                labelId={`wf-kind-${idx}`}
                                label="Kind"
                                value={s.kind || 'task'}
                                onChange={(e) => {
                                  const v = e.target.value;
                                  setOfficeWorkflowSteps((prev) => prev.map((x, i) => (i === idx ? { ...x, kind: v } : x)));
                                }}
                              >
                                <MenuItem value="task">Task</MenuItem>
                                <MenuItem value="document">Document</MenuItem>
                                <MenuItem value="payment">Payment</MenuItem>
                                <MenuItem value="form">Form</MenuItem>
                              </Select>
                            </FormControl>
                          </Stack>
                          {String(s.kind || 'task') === 'payment' ? (
                            <FormControl fullWidth size="small">
                              <InputLabel id={`wf-payment-kind-${idx}`}>Payment Type</InputLabel>
                              <Select
                                labelId={`wf-payment-kind-${idx}`}
                                label="Payment Type"
                                value={String(s.paymentReceiptType || 'both')}
                                onChange={(e) => {
                                  const v = String(e.target.value || 'both');
                                  setOfficeWorkflowSteps((prev) =>
                                    prev.map((x, i) => (i === idx ? { ...x, paymentReceiptType: v } : x))
                                  );
                                }}
                              >
                                <MenuItem value="both">Customer Receipt + GST Receipt</MenuItem>
                                <MenuItem value="customer">Customer Receipt only</MenuItem>
                                <MenuItem value="gst">GST Receipt only</MenuItem>
                              </Select>
                            </FormControl>
                          ) : null}

                          <TextField
                            label="Title"
                            value={s.title || ''}
                            onChange={(e) => {
                              const v = e.target.value;
                              setOfficeWorkflowSteps((prev) => prev.map((x, i) => (i === idx ? { ...x, title: v } : x)));
                            }}
                            fullWidth
                          />

                          <Divider sx={{ my: 0.5 }} />

                          <Stack direction="row" justifyContent="space-between" alignItems="center">
                            <Typography variant="body2" sx={{ fontWeight: 900 }}>
                              Fields
                            </Typography>
                            <Button
                              size="small"
                              variant="outlined"
                              onClick={() =>
                                setOfficeWorkflowSteps((prev) =>
                                  prev.map((x, i) =>
                                    i === idx
                                      ? {
                                          ...x,
                                          fields: [
                                            ...(Array.isArray(x.fields) ? x.fields : []),
                                            { key: `field_${(x.fields || []).length + 1}`, label: '', type: 'text', required: false },
                                          ],
                                        }
                                      : x
                                  )
                                )
                              }
                            >
                              Add field
                            </Button>
                          </Stack>

                          {(Array.isArray(s.fields) ? s.fields : []).length === 0 ? (
                            <Typography variant="body2" color="text.secondary">
                              No fields.
                            </Typography>
                          ) : (
                            <Stack spacing={1}>
                              {(s.fields || []).map((f: any, fidx: number) => (
                                <Paper key={`${f.key || fidx}`} variant="outlined" sx={{ p: 1 }}>
                                  <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ sm: 'center' }}>
                                    <TextField
                                      label="Key"
                                      value={f.key || ''}
                                      onChange={(e) => {
                                        const v = e.target.value;
                                        setOfficeWorkflowSteps((prev) =>
                                          prev.map((x, i) =>
                                            i === idx
                                              ? { ...x, fields: (x.fields || []).map((ff: any, j: number) => (j === fidx ? { ...ff, key: v } : ff)) }
                                              : x
                                          )
                                        );
                                      }}
                                      fullWidth
                                    />
                                    <TextField
                                      label="Label"
                                      value={f.label || ''}
                                      onChange={(e) => {
                                        const v = e.target.value;
                                        setOfficeWorkflowSteps((prev) =>
                                          prev.map((x, i) =>
                                            i === idx
                                              ? {
                                                  ...x,
                                                  fields: (x.fields || []).map((ff: any, j: number) => (j === fidx ? { ...ff, label: v } : ff)),
                                                }
                                              : x
                                          )
                                        );
                                      }}
                                      fullWidth
                                    />
                                    <FormControl sx={{ minWidth: 160 }} size="small">
                                      <InputLabel id={`wf-field-type-${idx}-${fidx}`}>Type</InputLabel>
                                      <Select
                                        labelId={`wf-field-type-${idx}-${fidx}`}
                                        label="Type"
                                        value={f.type || 'text'}
                                        onChange={(e) => {
                                          const v = e.target.value;
                                          setOfficeWorkflowSteps((prev) =>
                                            prev.map((x, i) =>
                                              i === idx
                                                ? {
                                                    ...x,
                                                    fields: (x.fields || []).map((ff: any, j: number) =>
                                                      j === fidx ? { ...ff, type: v, options: v === 'select' ? ff.options || [] : undefined } : ff
                                                    ),
                                                  }
                                                : x
                                            )
                                          );
                                        }}
                                      >
                                        <MenuItem value="text">Text</MenuItem>
                                        <MenuItem value="textarea">Textarea</MenuItem>
                                        <MenuItem value="number">Number</MenuItem>
                                        <MenuItem value="date">Date</MenuItem>
                                        <MenuItem value="boolean">Boolean</MenuItem>
                                        <MenuItem value="select">Select</MenuItem>
                                      </Select>
                                    </FormControl>
                                    <Stack direction="row" alignItems="center" spacing={1}>
                                      <Checkbox
                                        checked={!!f.required}
                                        onChange={(e) => {
                                          const checked = e.target.checked;
                                          setOfficeWorkflowSteps((prev) =>
                                            prev.map((x, i) =>
                                              i === idx
                                                ? {
                                                    ...x,
                                                    fields: (x.fields || []).map((ff: any, j: number) => (j === fidx ? { ...ff, required: checked } : ff)),
                                                  }
                                                : x
                                            )
                                          );
                                        }}
                                      />
                                      <Typography variant="body2">Required</Typography>
                                    </Stack>
                                    <Button
                                      size="small"
                                      color="error"
                                      variant="text"
                                      onClick={() =>
                                        setOfficeWorkflowSteps((prev) =>
                                          prev.map((x, i) => (i === idx ? { ...x, fields: (x.fields || []).filter((_: any, j: number) => j !== fidx) } : x))
                                        )
                                      }
                                    >
                                      Delete
                                    </Button>
                                  </Stack>
                                  {String(f.type || '') === 'select' ? (
                                    <TextField
                                      label="Options (comma separated)"
                                      value={Array.isArray(f.options) ? f.options.join(', ') : ''}
                                      onChange={(e) => {
                                        const opts = e.target.value
                                          .split(',')
                                          .map((x) => x.trim())
                                          .filter(Boolean);
                                        setOfficeWorkflowSteps((prev) =>
                                          prev.map((x, i) =>
                                            i === idx ? { ...x, fields: (x.fields || []).map((ff: any, j: number) => (j === fidx ? { ...ff, options: opts } : ff)) } : x
                                          )
                                        );
                                      }}
                                      fullWidth
                                      sx={{ mt: 1 }}
                                    />
                                  ) : null}
                                </Paper>
                              ))}
                            </Stack>
                          )}

                          <Divider sx={{ my: 0.5 }} />

                          <Stack direction="row" justifyContent="space-between" alignItems="center">
                            <Typography variant="body2" sx={{ fontWeight: 900 }}>
                              Documents
                            </Typography>
                            <Button
                              size="small"
                              variant="outlined"
                              onClick={() =>
                                setOfficeWorkflowSteps((prev) =>
                                  prev.map((x, i) =>
                                    i === idx
                                      ? {
                                          ...x,
                                          requiredDocs: [
                                            ...(Array.isArray(x.requiredDocs) ? x.requiredDocs : []),
                                            { key: `doc_${(x.requiredDocs || []).length + 1}`, label: '', required: false },
                                          ],
                                        }
                                      : x
                                  )
                                )
                              }
                            >
                              Add doc
                            </Button>
                          </Stack>

                          {(Array.isArray(s.requiredDocs) ? s.requiredDocs : []).length === 0 ? (
                            <Typography variant="body2" color="text.secondary">
                              No documents.
                            </Typography>
                          ) : (
                            <Stack spacing={1}>
                              {(s.requiredDocs || []).map((d: any, didx: number) => (
                                <Paper key={`${d.key || didx}`} variant="outlined" sx={{ p: 1 }}>
                                  <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ sm: 'center' }}>
                                    <TextField
                                      label="Key"
                                      value={d.key || ''}
                                      onChange={(e) => {
                                        const v = e.target.value;
                                        setOfficeWorkflowSteps((prev) =>
                                          prev.map((x, i) =>
                                            i === idx
                                              ? { ...x, requiredDocs: (x.requiredDocs || []).map((dd: any, j: number) => (j === didx ? { ...dd, key: v } : dd)) }
                                              : x
                                          )
                                        );
                                      }}
                                      fullWidth
                                    />
                                    <TextField
                                      label="Label"
                                      value={d.label || ''}
                                      onChange={(e) => {
                                        const v = e.target.value;
                                        setOfficeWorkflowSteps((prev) =>
                                          prev.map((x, i) =>
                                            i === idx
                                              ? { ...x, requiredDocs: (x.requiredDocs || []).map((dd: any, j: number) => (j === didx ? { ...dd, label: v } : dd)) }
                                              : x
                                          )
                                        );
                                      }}
                                      fullWidth
                                    />
                                    <Stack direction="row" alignItems="center" spacing={1}>
                                      <Checkbox
                                        checked={!!d.required}
                                        onChange={(e) => {
                                          const checked = e.target.checked;
                                          setOfficeWorkflowSteps((prev) =>
                                            prev.map((x, i) =>
                                              i === idx
                                                ? { ...x, requiredDocs: (x.requiredDocs || []).map((dd: any, j: number) => (j === didx ? { ...dd, required: checked } : dd)) }
                                                : x
                                            )
                                          );
                                        }}
                                      />
                                      <Typography variant="body2">Required</Typography>
                                    </Stack>
                                    <Button
                                      size="small"
                                      color="error"
                                      variant="text"
                                      onClick={() =>
                                        setOfficeWorkflowSteps((prev) =>
                                          prev.map((x, i) =>
                                            i === idx ? { ...x, requiredDocs: (x.requiredDocs || []).filter((_: any, j: number) => j !== didx) } : x
                                          )
                                        )
                                      }
                                    >
                                      Delete
                                    </Button>
                                  </Stack>
                                </Paper>
                              ))}
                            </Stack>
                          )}

                          <FormControl fullWidth>
                            <InputLabel id={`wf-deps-${idx}`}>Depends On</InputLabel>
                            <Select
                              labelId={`wf-deps-${idx}`}
                              label="Depends On"
                              multiple
                              value={deps}
                              onChange={(e) => {
                                const v = e.target.value as string[];
                                setOfficeWorkflowSteps((prev) => prev.map((x, i) => (i === idx ? { ...x, dependsOn: v } : x)));
                              }}
                              renderValue={(selected) => (selected as string[]).join(', ')}
                            >
                              {priorKeys.length === 0 ? (
                                <MenuItem disabled>No previous steps</MenuItem>
                              ) : (
                                priorKeys.map((k) => (
                                  <MenuItem key={k} value={k}>
                                    <Checkbox checked={deps.indexOf(k) > -1} />
                                    <ListItemText primary={k} />
                                  </MenuItem>
                                ))
                              )}
                            </Select>
                          </FormControl>
                        </Stack>
                      );
                    })()}
                  </Paper>
                )}

                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
                  <Button variant="contained" onClick={saveOfficeWorkflow} disabled={officeWorkflowSaving}>
                    {officeWorkflowSaving ? 'Saving…' : editingOfficeWorkflowId ? 'Save' : 'Create'}
                  </Button>
                  <Button
                    variant="outlined"
                    onClick={applyOfficeWorkflowToProjects}
                    disabled={officeWorkflowApplyingToProjects || !editingOfficeWorkflowId}
                  >
                    {officeWorkflowApplyingToProjects ? 'Applying…' : 'Apply to Selected Projects'}
                  </Button>
                </Stack>
              </Stack>
            </Box>
          </Box>

          {/* Old editor (kept for now, hidden) */}
          {false && (
          <Stack spacing={1.5}>
            <TextField
              label="Workflow Name"
              value={officeWorkflowName}
              onChange={(e) => setOfficeWorkflowName(e.target.value)}
              fullWidth
            />

            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
              <FormControl fullWidth>
                <InputLabel id="office-wf-type-label">Type</InputLabel>
                <Select
                  labelId="office-wf-type-label"
                  label="Type"
                  value={officeWorkflowType}
                  onChange={(e) => setOfficeWorkflowType(e.target.value as any)}
                >
                  <MenuItem value="main">Main</MenuItem>
                  <MenuItem value="loan">Loan</MenuItem>
                </Select>
              </FormControl>
              <TextField
                label="Version"
                type="number"
                value={officeWorkflowVersion}
                onChange={(e) => setOfficeWorkflowVersion(Number(e.target.value || 1))}
                fullWidth
                inputProps={{ min: 1 }}
              />
            </Stack>

            <FormControl fullWidth>
              <InputLabel id="office-wf-projects-label">Assign Projects (Apply)</InputLabel>
              <Select
                labelId="office-wf-projects-label"
                multiple
                value={officeWorkflowProjectIds}
                label="Assign Projects (Apply)"
                onChange={(e) => setOfficeWorkflowProjectIds(e.target.value as string[])}
                renderValue={(selected) =>
                  (selected as string[])
                    .map((id) => projects.find((p: any) => p.id === id)?.name || id)
                    .join(', ')
                }
              >
                {projects.length === 0 ? (
                  <MenuItem disabled>No projects yet.</MenuItem>
                ) : (
                  projects.map((p: any) => (
                    <MenuItem key={p.id} value={p.id}>
                      <Checkbox checked={officeWorkflowProjectIds.indexOf(p.id) > -1} />
                      <ListItemText primary={p.name || p.id} secondary={p.location || ''} />
                    </MenuItem>
                  ))
                )}
              </Select>
            </FormControl>

            <Divider />

            <Stack direction="row" justifyContent="space-between" alignItems="center">
              <Typography variant="subtitle1" sx={{ fontWeight: 800 }}>
                Steps
              </Typography>
              <Button size="small" variant="outlined" onClick={addOfficeWorkflowStep}>
                Add Step
              </Button>
            </Stack>

            {officeWorkflowSteps.length === 0 ? (
              <Alert severity="info">Add steps to build your workflow.</Alert>
            ) : (
              <Stack spacing={1}>
                {officeWorkflowSteps.map((s: any, idx: number) => {
                  const priorKeys = officeWorkflowSteps
                    .slice(0, idx)
                    .map((x: any) => String(x.key || '').trim())
                    .filter(Boolean);
                  const deps = Array.isArray(s.dependsOn) ? s.dependsOn : [];
                  return (
                    <Paper key={`${s.key || idx}`} variant="outlined" sx={{ p: 1.5 }}>
                      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
                        <Typography variant="body2" sx={{ fontWeight: 900 }}>
                          Step {idx + 1}
                        </Typography>
                        <Stack direction="row" spacing={0.5}>
                          <Button size="small" variant="text" onClick={() => moveOfficeWorkflowStep(idx, -1)} disabled={idx === 0}>
                            Up
                          </Button>
                          <Button
                            size="small"
                            variant="text"
                            onClick={() => moveOfficeWorkflowStep(idx, 1)}
                            disabled={idx === officeWorkflowSteps.length - 1}
                          >
                            Down
                          </Button>
                          <Button size="small" color="error" variant="text" onClick={() => deleteOfficeWorkflowStep(idx)}>
                            Delete
                          </Button>
                        </Stack>
                      </Stack>

                      <Stack spacing={1}>
                        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
                          <TextField
                            label="Key (stable)"
                            value={s.key || ''}
                            onChange={(e) => {
                              const v = e.target.value;
                              setOfficeWorkflowSteps((prev) => prev.map((x, i) => (i === idx ? { ...x, key: v } : x)));
                            }}
                            fullWidth
                          />
                          <FormControl fullWidth>
                            <InputLabel id={`wf-kind-${idx}`}>Kind</InputLabel>
                            <Select
                              labelId={`wf-kind-${idx}`}
                              label="Kind"
                              value={s.kind || 'task'}
                              onChange={(e) => {
                                const v = e.target.value;
                                setOfficeWorkflowSteps((prev) => prev.map((x, i) => (i === idx ? { ...x, kind: v } : x)));
                              }}
                            >
                              <MenuItem value="task">Task</MenuItem>
                              <MenuItem value="document">Document</MenuItem>
                              <MenuItem value="payment">Payment</MenuItem>
                              <MenuItem value="form">Form</MenuItem>
                            </Select>
                          </FormControl>
                        </Stack>
                        {String(s.kind || 'task') === 'payment' ? (
                          <FormControl fullWidth size="small">
                            <InputLabel id={`wf-payment-kind-${idx}`}>Payment Type</InputLabel>
                            <Select
                              labelId={`wf-payment-kind-${idx}`}
                              label="Payment Type"
                              value={String(s.paymentReceiptType || 'both')}
                              onChange={(e) => {
                                const v = String(e.target.value || 'both');
                                setOfficeWorkflowSteps((prev) =>
                                  prev.map((x, i) => (i === idx ? { ...x, paymentReceiptType: v } : x))
                                );
                              }}
                            >
                              <MenuItem value="both">Customer Receipt + GST Receipt</MenuItem>
                              <MenuItem value="customer">Customer Receipt only</MenuItem>
                              <MenuItem value="gst">GST Receipt only</MenuItem>
                            </Select>
                          </FormControl>
                        ) : null}

                        <TextField
                          label="Title"
                          value={s.title || ''}
                          onChange={(e) => {
                            const v = e.target.value;
                            setOfficeWorkflowSteps((prev) => prev.map((x, i) => (i === idx ? { ...x, title: v } : x)));
                          }}
                          fullWidth
                        />

                        <Divider sx={{ my: 0.5 }} />

                        <Stack direction="row" justifyContent="space-between" alignItems="center">
                          <Typography variant="body2" sx={{ fontWeight: 900 }}>
                            Fields
                          </Typography>
                          <Button
                            size="small"
                            variant="outlined"
                            onClick={() =>
                              setOfficeWorkflowSteps((prev) =>
                                prev.map((x, i) =>
                                  i === idx
                                    ? {
                                        ...x,
                                        fields: [...(Array.isArray(x.fields) ? x.fields : []), { key: `field_${(x.fields || []).length + 1}`, label: '', type: 'text', required: false }],
                                      }
                                    : x
                                )
                              )
                            }
                          >
                            Add field
                          </Button>
                        </Stack>
                        {(Array.isArray(s.fields) ? s.fields : []).length === 0 ? (
                          <Typography variant="body2" color="text.secondary">
                            No fields.
                          </Typography>
                        ) : (
                          <Stack spacing={1}>
                            {(s.fields || []).map((f: any, fidx: number) => (
                              <Paper key={`${f.key || fidx}`} variant="outlined" sx={{ p: 1 }}>
                                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ sm: 'center' }}>
                                  <TextField
                                    label="Key"
                                    value={f.key || ''}
                                    onChange={(e) => {
                                      const v = e.target.value;
                                      setOfficeWorkflowSteps((prev) =>
                                        prev.map((x, i) =>
                                          i === idx
                                            ? {
                                                ...x,
                                                fields: (x.fields || []).map((ff: any, j: number) => (j === fidx ? { ...ff, key: v } : ff)),
                                              }
                                            : x
                                        )
                                      );
                                    }}
                                    fullWidth
                                  />
                                  <TextField
                                    label="Label"
                                    value={f.label || ''}
                                    onChange={(e) => {
                                      const v = e.target.value;
                                      setOfficeWorkflowSteps((prev) =>
                                        prev.map((x, i) =>
                                          i === idx
                                            ? {
                                                ...x,
                                                fields: (x.fields || []).map((ff: any, j: number) => (j === fidx ? { ...ff, label: v } : ff)),
                                              }
                                            : x
                                        )
                                      );
                                    }}
                                    fullWidth
                                  />
                                  <FormControl sx={{ minWidth: 160 }} size="small">
                                    <InputLabel id={`wf-field-type-${idx}-${fidx}`}>Type</InputLabel>
                                    <Select
                                      labelId={`wf-field-type-${idx}-${fidx}`}
                                      label="Type"
                                      value={f.type || 'text'}
                                      onChange={(e) => {
                                        const v = e.target.value;
                                        setOfficeWorkflowSteps((prev) =>
                                          prev.map((x, i) =>
                                            i === idx
                                              ? {
                                                  ...x,
                                                  fields: (x.fields || []).map((ff: any, j: number) =>
                                                    j === fidx ? { ...ff, type: v, options: v === 'select' ? ff.options || [] : undefined } : ff
                                                  ),
                                                }
                                              : x
                                          )
                                        );
                                      }}
                                    >
                                      <MenuItem value="text">Text</MenuItem>
                                      <MenuItem value="textarea">Textarea</MenuItem>
                                      <MenuItem value="number">Number</MenuItem>
                                      <MenuItem value="date">Date</MenuItem>
                                      <MenuItem value="boolean">Boolean</MenuItem>
                                      <MenuItem value="select">Select</MenuItem>
                                    </Select>
                                  </FormControl>
                                  <Stack direction="row" alignItems="center" spacing={1}>
                                    <Checkbox
                                      checked={!!f.required}
                                      onChange={(e) => {
                                        const checked = e.target.checked;
                                        setOfficeWorkflowSteps((prev) =>
                                          prev.map((x, i) =>
                                            i === idx
                                              ? {
                                                  ...x,
                                                  fields: (x.fields || []).map((ff: any, j: number) => (j === fidx ? { ...ff, required: checked } : ff)),
                                                }
                                              : x
                                          )
                                        );
                                      }}
                                    />
                                    <Typography variant="body2">Required</Typography>
                                  </Stack>
                                  <Button
                                    size="small"
                                    color="error"
                                    variant="text"
                                    onClick={() =>
                                      setOfficeWorkflowSteps((prev) =>
                                        prev.map((x, i) =>
                                          i === idx ? { ...x, fields: (x.fields || []).filter((_: any, j: number) => j !== fidx) } : x
                                        )
                                      )
                                    }
                                  >
                                    Delete
                                  </Button>
                                </Stack>
                                {String(f.type || '') === 'select' ? (
                                  <TextField
                                    label="Options (comma separated)"
                                    value={Array.isArray(f.options) ? f.options.join(', ') : ''}
                                    onChange={(e) => {
                                      const opts = e.target.value
                                        .split(',')
                                        .map((x) => x.trim())
                                        .filter(Boolean);
                                      setOfficeWorkflowSteps((prev) =>
                                        prev.map((x, i) =>
                                          i === idx
                                            ? {
                                                ...x,
                                                fields: (x.fields || []).map((ff: any, j: number) => (j === fidx ? { ...ff, options: opts } : ff)),
                                              }
                                            : x
                                        )
                                      );
                                    }}
                                    fullWidth
                                    sx={{ mt: 1 }}
                                  />
                                ) : null}
                              </Paper>
                            ))}
                          </Stack>
                        )}

                        <Divider sx={{ my: 0.5 }} />

                        <Stack direction="row" justifyContent="space-between" alignItems="center">
                          <Typography variant="body2" sx={{ fontWeight: 900 }}>
                            Documents
                          </Typography>
                          <Button
                            size="small"
                            variant="outlined"
                            onClick={() =>
                              setOfficeWorkflowSteps((prev) =>
                                prev.map((x, i) =>
                                  i === idx
                                    ? {
                                        ...x,
                                        requiredDocs: [
                                          ...(Array.isArray(x.requiredDocs) ? x.requiredDocs : []),
                                          { key: `doc_${(x.requiredDocs || []).length + 1}`, label: '', required: false },
                                        ],
                                      }
                                    : x
                                )
                              )
                            }
                          >
                            Add doc
                          </Button>
                        </Stack>
                        {(Array.isArray(s.requiredDocs) ? s.requiredDocs : []).length === 0 ? (
                          <Typography variant="body2" color="text.secondary">
                            No documents.
                          </Typography>
                        ) : (
                          <Stack spacing={1}>
                            {(s.requiredDocs || []).map((d: any, didx: number) => (
                              <Paper key={`${d.key || didx}`} variant="outlined" sx={{ p: 1 }}>
                                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ sm: 'center' }}>
                                  <TextField
                                    label="Key"
                                    value={d.key || ''}
                                    onChange={(e) => {
                                      const v = e.target.value;
                                      setOfficeWorkflowSteps((prev) =>
                                        prev.map((x, i) =>
                                          i === idx
                                            ? {
                                                ...x,
                                                requiredDocs: (x.requiredDocs || []).map((dd: any, j: number) => (j === didx ? { ...dd, key: v } : dd)),
                                              }
                                            : x
                                        )
                                      );
                                    }}
                                    fullWidth
                                  />
                                  <TextField
                                    label="Label"
                                    value={d.label || ''}
                                    onChange={(e) => {
                                      const v = e.target.value;
                                      setOfficeWorkflowSteps((prev) =>
                                        prev.map((x, i) =>
                                          i === idx
                                            ? {
                                                ...x,
                                                requiredDocs: (x.requiredDocs || []).map((dd: any, j: number) => (j === didx ? { ...dd, label: v } : dd)),
                                              }
                                            : x
                                        )
                                      );
                                    }}
                                    fullWidth
                                  />
                                  <Stack direction="row" alignItems="center" spacing={1}>
                                    <Checkbox
                                      checked={!!d.required}
                                      onChange={(e) => {
                                        const checked = e.target.checked;
                                        setOfficeWorkflowSteps((prev) =>
                                          prev.map((x, i) =>
                                            i === idx
                                              ? {
                                                  ...x,
                                                  requiredDocs: (x.requiredDocs || []).map((dd: any, j: number) =>
                                                    j === didx ? { ...dd, required: checked } : dd
                                                  ),
                                                }
                                              : x
                                          )
                                        );
                                      }}
                                    />
                                    <Typography variant="body2">Required</Typography>
                                  </Stack>
                                  <Button
                                    size="small"
                                    color="error"
                                    variant="text"
                                    onClick={() =>
                                      setOfficeWorkflowSteps((prev) =>
                                        prev.map((x, i) =>
                                          i === idx ? { ...x, requiredDocs: (x.requiredDocs || []).filter((_: any, j: number) => j !== didx) } : x
                                        )
                                      )
                                    }
                                  >
                                    Delete
                                  </Button>
                                </Stack>
                              </Paper>
                            ))}
                          </Stack>
                        )}

                        <FormControl fullWidth>
                          <InputLabel id={`wf-deps-${idx}`}>Depends On</InputLabel>
                          <Select
                            labelId={`wf-deps-${idx}`}
                            label="Depends On"
                            multiple
                            value={deps}
                            onChange={(e) => {
                              const v = e.target.value as string[];
                              setOfficeWorkflowSteps((prev) => prev.map((x, i) => (i === idx ? { ...x, dependsOn: v } : x)));
                            }}
                            renderValue={(selected) => (selected as string[]).join(', ')}
                          >
                            {priorKeys.length === 0 ? (
                              <MenuItem disabled>No previous steps</MenuItem>
                            ) : (
                              priorKeys.map((k) => (
                                <MenuItem key={k} value={k}>
                                  <Checkbox checked={deps.indexOf(k) > -1} />
                                  <ListItemText primary={k} />
                                </MenuItem>
                              ))
                            )}
                          </Select>
                        </FormControl>
                      </Stack>
                    </Paper>
                  );
                })}
              </Stack>
            )}

            {officeWorkflowSaveError ? <Alert severity="error">{officeWorkflowSaveError}</Alert> : null}
            {officeWorkflowSaveSuccess ? <Alert severity="success">{officeWorkflowSaveSuccess}</Alert> : null}

            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
              <Button variant="contained" onClick={saveOfficeWorkflow} disabled={officeWorkflowSaving}>
                {officeWorkflowSaving ? 'Saving…' : editingOfficeWorkflowId ? 'Save' : 'Create'}
              </Button>
              <Button
                variant="outlined"
                onClick={applyOfficeWorkflowToProjects}
                disabled={officeWorkflowApplyingToProjects || !editingOfficeWorkflowId}
              >
                {officeWorkflowApplyingToProjects ? 'Applying…' : 'Apply to Selected Projects'}
              </Button>
            </Stack>

            {officeWorkflowApplyToProjectsError ? <Alert severity="error">{officeWorkflowApplyToProjectsError}</Alert> : null}
            {officeWorkflowApplyToProjectsSuccess ? <Alert severity="success">{officeWorkflowApplyToProjectsSuccess}</Alert> : null}
          </Stack>
          )}
        </Box>
      </ResizableRightDrawer>
    </>
  );
}
